import { Application, Assets, BlurFilter, Container, FillGradient, Graphics, NineSliceSprite, Rectangle, Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { applyLaunchLayout, gameConfig } from './config/gameConfig.js';
import { resolveGameVariant } from './config/gameVariants.js';
import {
  BUY_BONUS_GUARANTEED_MULTIPLIERS,
  BUY_BONUS_TIER_MULTIPLIERS,
  buyBonusPrice,
} from './config/buyBonus.js';
import {
  getInfoPaytableSymbolIds,
  getPaylinePayoutSpecs,
  getScatterPayoutSpecs,
  INFO_COIN_SYMBOL_ID,
  INFO_SCATTER_SYMBOL_ID,
  INFO_WILD_SYMBOL_ID,
  payoutAmountForInfoSpec,
  setSessionPaytable,
} from './config/infoPaytable.js';
import { AnimationController } from './animation/AnimationController.js';
import { attachLuxuryGoldFrameFx } from './effects/luxuryGoldFx.js';
import { loadSpineAssets } from './rendering/spineLoader.js';
import { drainSymbolSpinePool, prewarmSymbolSpinePool } from './rendering/symbolSpinePool.js';
import { createInfoSymbolOrBadge } from './rendering/infoSymbolSprite.js';
import { createInfoPaylineDiagram } from './rendering/infoPaylineDiagram.js';
import { SlotMachine } from './components/SlotMachine.js';
import {
  celebrationAmountAtElapsed,
  computeCelebrationAmountTimeline,
  getCelebrationSequenceDurationSec,
  winCelebrationMinMultiple,
  winMultipleToTier,
} from './components/WinCelebrationOverlay.js';
import { DebugMode } from './debug/DebugMode.js';
/* Opening photo layout — dormant; see src/debug/openingPhotoLayout.js
import {
  buildBugsWildsPhotoBaseMatrix,
  buildBugsWildsPhotoExpandingWild,
  getOpeningPhotoHudValues,
  isOpeningPhotoLayoutEnabled,
} from './debug/openingPhotoLayout.js';
*/
import { ShiningCrownService } from './api/shiningCrownService.js';
import { ACTION_STATUS, HubActionError } from './api/hubPayloadCodec.js';
import { setTranslationCulture, setTranslationVariant, translationStore } from './features/i18n/translationStore.js';
import { getParamsFromUrl, loadTranslations } from './features/i18n/loadTranslations.js';
import {
  formatCurrencyAmount,
  formatCurrencyDigits,
  formatCurrencyRulesLabel,
  moveCurrencyBeforeAmount,
  normalizeCurrencyCode,
} from './lib/formatCurrency.js';
import { isPrimaryPointerAction } from './utils/pointerPrimary.js';
import { assetUrl } from './utils/assetUrl.js';
import { createArcTextController } from './utils/arcTextPixi.js';
import { installGameIdleReload } from './utils/gameIdleReload.js';
import { createLandscapeOrientationBlocker } from './utils/landscapeOrientationBlocker.js';
import { CabinetJuice } from './effects/cabinetJuice.js';
import { AmbientIdleFx } from './effects/ambientIdleFx.js';
import { installHubConnectionMonitor } from './utils/hubConnectionMonitor.js';
import { createSystemMessagePanel, showSystemToast } from './utils/gameSystemMessage.js';
import { createGameStartScreen } from './utils/gameStartScreen.js';
import {
  clearRoundStatus,
  loadRoundStatus,
  roundStatusStorageKey,
  saveRoundStatus,
} from './utils/gameRoundStatusStore.js';
import { sound } from './utils/soundManager.js';
import {
  createQuickBetChipPixi,
  fontSizeWhenFormattedLong,
} from './components/quickBetChipPixi.js';

const T = gameConfig.theme;

/** @param {number} n 0xRRGGBB */
function themeHex(n) {
  return `#${(n >>> 0).toString(16).padStart(6, '0')}`;
}

/** Shared modal panel fill — warm sand gradient with subtle inner rim. */
function paintModalPanelBackground(g, w, h, cornerR = 20) {
  g.clear();
  const bodyGrad = new FillGradient({
    type: 'linear',
    start: { x: w / 2, y: 0 },
    end: { x: w / 2, y: h },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: themeHex(T.modalSurfaceHi) },
      { offset: 0.42, color: themeHex(T.modalSurface) },
      { offset: 1, color: themeHex(T.modalBorderDark) },
    ],
  });
  g.roundRect(0, 0, w, h, cornerR).fill(bodyGrad);
  g.roundRect(1.5, 1.5, w - 3, h - 3, Math.max(0, cornerR - 1.5)).stroke({
    width: 1,
    color: T.modalStroke,
    alpha: 0.38,
  });
}

/** Cover-fit a modal backdrop sprite inside a panel box (local coords); bleed avoids blur halos. */
function layoutModalBackdropCover(sprite, tex, boxW, boxH, bleed = MODAL_BACKDROP_BLEED) {
  if (!sprite || !tex?.width || !tex?.height) return;
  const visibleW = Math.max(1, boxW);
  const visibleH = Math.max(1, boxH);
  const visibleAspect = visibleW / visibleH;
  const tw = tex.width;
  const th = tex.height;
  const texAspect = tw / th;
  let bgW;
  let bgH;
  if (texAspect > visibleAspect) {
    bgH = visibleH + bleed * 2;
    bgW = bgH * texAspect;
  } else {
    bgW = visibleW + bleed * 2;
    bgH = bgW / texAspect;
  }
  sprite.texture = tex;
  sprite.width = bgW;
  sprite.height = bgH;
  sprite.x = (visibleW - bgW) / 2;
  sprite.y = (visibleH - bgH) / 2;
}

/** Idle wild art size inside each buy-bonus offer, before the row is scaled to the card. */
const BUY_BONUS_WILD_BOX = 120;

/**
 * @param {import('pixi.js').Container} row
 * @param {number} maxW
 * @param {number} maxH
 * @param {boolean} allowHeroScale Single-wild offers may draw slightly larger than the source box.
 * @returns {{ w: number, h: number }}
 */
function layoutBuyBonusWildRow(row, maxW, maxH, allowHeroScale) {
  const count = row?.children?.length ?? 0;
  if (!count) return { w: 0, h: 0 };
  const gap = count > 1 ? 12 : 0;
  const naturalW = count * BUY_BONUS_WILD_BOX + (count - 1) * gap;
  const naturalH = BUY_BONUS_WILD_BOX;
  const s = Math.min(
    Math.max(1, maxW) / naturalW,
    Math.max(1, maxH) / naturalH,
    allowHeroScale ? 1.28 : 1,
  );
  row.scale.set(s);
  for (let i = 0; i < count; i++) {
    const child = row.children[i];
    child.x = i * (BUY_BONUS_WILD_BOX + gap);
    child.y = 0;
  }
  return { w: naturalW * s, h: naturalH * s };
}

/**
 * @param {1 | 2 | 3} type
 * @param {boolean} guaranteed
 */
function formatBuyBonusStakeMultiple(type, guaranteed) {
  const table = guaranteed ? BUY_BONUS_GUARANTEED_MULTIPLIERS : BUY_BONUS_TIER_MULTIPLIERS;
  const n = Number(table[type - 1]);
  if (!Number.isFinite(n)) return '';
  return `${Number.isInteger(n) ? String(n) : String(n)}\u00d7`;
}

let sharedModalBackdropBlurFilter = null;
let buyBonusModalBackdropBlurFilter = null;
function ensureModalBackdropBlurFilter() {
  if (!sharedModalBackdropBlurFilter) {
    sharedModalBackdropBlurFilter = new BlurFilter({
      strength: MODAL_BACKDROP_BLUR_STRENGTH,
      strengthX: MODAL_BACKDROP_BLUR_STRENGTH,
      strengthY: MODAL_BACKDROP_BLUR_STRENGTH,
    });
  }
  return sharedModalBackdropBlurFilter;
}

/** Softer than the settings blur so the Egypt scene still reads inside Buy Bonus. */
function ensureBuyBonusModalBackdropBlur() {
  if (!buyBonusModalBackdropBlurFilter) {
    const strength = 2.5;
    buyBonusModalBackdropBlurFilter = new BlurFilter({
      strength,
      strengthX: strength,
      strengthY: strength,
    });
  }
  return buyBonusModalBackdropBlurFilter;
}

/** Horizontal gold/sand fade rule between modal sections. */
function paintModalDividerLine(g, width, height = 2) {
  g.clear();
  if (width <= 0) return;
  const grad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: height / 2 },
    end: { x: width, y: height / 2 },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: themeHex(T.modalBorderDark) },
      { offset: 0.1, color: themeHex(T.modalBorderGoldLo) },
      { offset: 0.45, color: themeHex(T.modalBorderGoldHi) },
      { offset: 0.55, color: themeHex(T.modalAccentHi) },
      { offset: 0.9, color: themeHex(T.modalBorderGoldLo) },
      { offset: 1, color: themeHex(T.modalBorderDark) },
    ],
  });
  g.rect(0, 0, width, height).fill(grad);
}

/** Primary CTA — ochre/bronze with gold rim (top-left anchored rect). */
function paintModalPrimaryButtonAt(g, x, y, w, h, cornerR = 12) {
  g.clear();
  const grad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: h },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: themeHex(T.modalAccentHi) },
      { offset: 0.55, color: themeHex(T.modalButtonFill) },
      { offset: 1, color: themeHex(T.modalBorderGoldLo) },
    ],
  });
  g.roundRect(x, y, w, h, cornerR).fill(grad);
  g.roundRect(x, y, w, h, cornerR).stroke({
    width: 1.5,
    color: T.modalBorderGoldHi,
    alpha: 0.75,
  });
}

/** Buy-bonus confirm — primary CTA plus inner gleam for a clear tap target. */
function paintBuyBonusCtaButtonAt(g, x, y, w, h, cornerR = 16) {
  paintModalPrimaryButtonAt(g, x, y, w, h, cornerR);
  g.roundRect(x + 3, y + 3, w - 6, h - 6, Math.max(6, cornerR - 3)).stroke({
    width: 1.25,
    color: T.goldBright,
    alpha: 0.42,
  });
  g.roundRect(x + 1.5, y + 1.5, w - 3, h - 3, cornerR - 1).stroke({
    width: 2.5,
    color: T.modalBorderGoldHi,
    alpha: 0.88,
  });
}

/** Full-width purchase bar — layered gold (collect-win style). */
function paintBuyBonusPurchaseShell(g, x, y, w, h, cornerR = 18) {
  g.clear();
  const body = new FillGradient({
    type: 'linear',
    start: { x: w / 2, y: 0 },
    end: { x: w / 2, y: h },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: themeHex(T.spinCoreHi) },
      { offset: 0.35, color: themeHex(T.modalAccentHi) },
      { offset: 0.72, color: themeHex(T.modalButtonFill) },
      { offset: 1, color: themeHex(T.modalBorderGoldLo) },
    ],
  });
  g.roundRect(x, y, w, h, cornerR).fill(body);
  g.roundRect(x + 2, y + 2, w - 4, h - 4, cornerR - 2).stroke({
    width: 2,
    color: T.goldBright,
    alpha: 0.55,
  });
  g.roundRect(x, y, w, h, cornerR).stroke({
    width: 3,
    color: T.modalBorderGoldHi,
    alpha: 0.92,
  });
}

const BUY_BONUS_GUARANTEED_ON_URL = '/assets/buy_bonus_guaranteed_on.png';
const BUY_BONUS_GUARANTEED_OFF_URL = '/assets/buy_bonus_guaranteed_off.png';
const BUY_BONUS_MODAL_FRAME_URL = '/assets/buy_bonus_modal_frame.png';
const BUY_BONUS_OPTION_FRAME_URL = '/assets/buy_bonus_option_frame.png';
const BUY_BONUS_PURCHASE_BANNER_URL = '/assets/buy_bonus_purchase_banner.png';
/** Unscaled corners of buy_bonus_modal_frame.png. The marble center is what stretches. */
const BUY_BONUS_FRAME_LEFT = 156;
const BUY_BONUS_FRAME_RIGHT = 156;
const BUY_BONUS_FRAME_TOP = 78;
const BUY_BONUS_FRAME_BOTTOM = 78;

/**
 * Emerald (on) and ruby (off) faces cut from one switch sheet.
 * Nine-slice keeps the ornate end caps; only the glass center stretches.
 * @param {import('pixi.js').NineSliceSprite} sprite
 * @param {import('pixi.js').Texture | null | undefined} texture
 * @param {number} rowW
 * @param {number} rowH
 * @returns {{ cap: number, rim: number }}
 */
function applyGuaranteedSwitchFace(sprite, texture, rowW, rowH) {
  const next = texture?.width > 1 ? texture : null;
  if (next) sprite.texture = next;
  const source = sprite.texture?.width > 1 ? sprite.texture : null;
  const texH = source?.height > 1 ? source.height : 188;
  const cap = Math.round(texH * 0.48);
  const rim = Math.round(texH * 0.2);
  sprite.visible = !!source;
  if (source) {
    sprite.leftWidth = cap;
    sprite.rightWidth = cap;
    sprite.topHeight = rim;
    sprite.bottomHeight = rim;
  }
  if (rowW > 0) sprite.width = rowW;
  if (rowH > 0) sprite.height = rowH;
  return { cap, rim };
}

/**
 * Ornate gold frame for the buy-bonus modal. Corners stay sharp; the marble field stretches.
 * @param {import('pixi.js').NineSliceSprite} sprite
 * @param {import('pixi.js').Texture | null | undefined} texture
 * @param {number} panelW
 * @param {number} panelH
 */
function applyBuyBonusModalFrame(sprite, texture, panelW, panelH) {
  const next = texture?.width > 1 ? texture : null;
  if (next) sprite.texture = next;
  const ready = sprite.texture?.width > 1 && panelW > 0 && panelH > 0;
  sprite.visible = !!ready;
  if (!ready) return;
  sprite.leftWidth = BUY_BONUS_FRAME_LEFT;
  sprite.rightWidth = BUY_BONUS_FRAME_RIGHT;
  sprite.topHeight = BUY_BONUS_FRAME_TOP;
  sprite.bottomHeight = BUY_BONUS_FRAME_BOTTOM;
  sprite.width = panelW;
  sprite.height = panelH;
}

/**
 * Shared gold frame for each buy-bonus tier card.
 * Corners stay put; the glowing center stretches to the card.
 * @param {import('pixi.js').NineSliceSprite} sprite
 * @param {import('pixi.js').Texture | null | undefined} texture
 * @param {number} w
 * @param {number} h
 * @returns {{ left: number, top: number } | null}
 */
function applyBuyBonusOptionFrame(sprite, texture, w, h) {
  const next = texture?.width > 1 ? texture : null;
  if (next) sprite.texture = next;
  const ready = sprite.texture?.width > 1 && w > 0 && h > 0;
  sprite.visible = !!ready;
  if (!ready) return null;
  const left = Math.max(28, Math.min(96, Math.round(w * 0.26)));
  const top = Math.max(22, Math.min(68, Math.round(h * 0.18)));
  sprite.leftWidth = left;
  sprite.rightWidth = left;
  sprite.topHeight = top;
  sprite.bottomHeight = top;
  sprite.width = w;
  sprite.height = h;
  return { left, top };
}

/** Native height / width of buy_bonus_purchase_banner.png. */
const BUY_BONUS_BANNER_ASPECT = 340 / 1015;
/** Desktop purchase banner width. Height follows the art so it is never stretched. */
const BUY_BONUS_BANNER_DESKTOP_W = 640;

/**
 * Draw the purchase banner at its own aspect ratio.
 * @param {import('pixi.js').Sprite} sprite
 * @param {import('pixi.js').Texture | null | undefined} texture
 * @param {number} w
 * @returns {{ w: number, h: number }}
 */
function applyBuyBonusPurchaseBanner(sprite, texture, w) {
  const next = texture?.width > 1 ? texture : null;
  if (next) sprite.texture = next;
  const source = sprite.texture?.width > 1 ? sprite.texture : null;
  const aspect = source ? source.height / source.width : BUY_BONUS_BANNER_ASPECT;
  const h = Math.max(1, w) * aspect;
  sprite.visible = !!source && w > 0;
  if (w > 0) {
    sprite.width = w;
    sprite.height = h;
  }
  sprite.position.set(0, 0);
  return { w, h };
}

const BUY_BONUS_TITLE_FONT = '"Cinzel", "Times New Roman", serif';

function buyBonusGoldTextFill(stops) {
  return new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    textureSpace: 'local',
    colorStops: stops,
  });
}

const BUY_BONUS_TITLE_FILL = buyBonusGoldTextFill([
  { offset: 0, color: '#fffef4' },
  { offset: 0.38, color: '#ffe58a' },
  { offset: 0.68, color: '#f0a018' },
  { offset: 1, color: '#9a4e08' },
]);

const BUY_BONUS_SUBTITLE_FILL = buyBonusGoldTextFill([
  { offset: 0, color: '#fff8e4' },
  { offset: 0.55, color: '#f3d48a' },
  { offset: 1, color: '#c4843a' },
]);

/** Primary CTA — ochre/bronze with gold rim. */
function paintModalPrimaryButton(g, w, h, cornerR = 12) {
  g.clear();
  const grad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: h },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: themeHex(T.modalAccentHi) },
      { offset: 0.55, color: themeHex(T.modalButtonFill) },
      { offset: 1, color: themeHex(T.modalBorderGoldLo) },
    ],
  });
  g.roundRect(-w / 2, -h / 2, w, h, cornerR).fill(grad);
  g.roundRect(-w / 2, -h / 2, w, h, cornerR).stroke({
    width: 1.5,
    color: T.modalBorderGoldHi,
    alpha: 0.75,
  });
}

/** Secondary action — warm bronze with gold rim. */
function paintModalSecondaryButton(g, w, h, cornerR = 12) {
  g.clear();
  const grad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: h },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: themeHex(T.modalBorderGoldLo) },
      { offset: 0.5, color: themeHex(T.modalButtonFillAlt) },
      { offset: 1, color: themeHex(T.modalBorderDark) },
    ],
  });
  g.roundRect(-w / 2, -h / 2, w, h, cornerR).fill(grad);
  g.roundRect(-w / 2, -h / 2, w, h, cornerR).stroke({
    width: 1.5,
    color: T.modalBorderGold,
    alpha: 0.65,
  });
}

/** Outline button for low-emphasis actions (exit lobby). */
function paintModalGhostButton(g, w, h, cornerR = 12) {
  g.clear();
  g.roundRect(-w / 2, -h / 2, w, h, cornerR).fill({ color: T.modalSurface, alpha: 0.92 });
  g.roundRect(-w / 2, -h / 2, w, h, cornerR).stroke({
    width: 1.5,
    color: T.modalStroke,
    alpha: 0.85,
  });
}

/** Tint settings-modal tab icons (white SVG assets). */
function applySettingsTabIconStyle(sprite, active) {
  if (!sprite) return;
  sprite.alpha = 1;
  sprite.tint = active ? T.modalTabActive : T.modalTabInactive;
}

/** Rounded card surface for a settings option row. */
function paintSettingsOptionCardBg(g, w, h, cornerR = 12) {
  g.clear();
  g.roundRect(0, 0, w, h, cornerR).fill({ color: T.modalSurfaceHi, alpha: 0.72 });
  g.roundRect(0, 0, w, h, cornerR).stroke({ width: 1, color: T.modalStroke, alpha: 0.55 });
}

/**
 * Pill-style ON/OFF radio pair for boolean settings.
 * @param {() => boolean} getValue
 * @param {(next: boolean) => void} setValue
 * @param {{ translate?: (key: string) => string }} [opts]
 * @returns {{ root: import('pixi.js').Container, refresh: () => void, width: number }}
 */
function createSegmentedOnOffControl(getValue, setValue, opts = {}) {
  const translate = opts.translate;
  const pillH = SETTINGS_SEGMENT_PILL_H;
  const gap = SETTINGS_SEGMENT_PILL_GAP;
  const minPillW = SETTINGS_SEGMENT_PILL_W;
  const padX = 8;
  const root = new Container();
  root.eventMode = 'passive';

  const captionFor = (key, fallback) => {
    const raw = translate ? translate(key) : fallback;
    const text = String(raw ?? fallback).trim();
    return text || fallback;
  };

  const measureLabelW = (lbl, text) => {
    lbl.text = text;
    if (typeof lbl.updateText === 'function') lbl.updateText(true);
    return Math.ceil(Math.max(lbl.width || 0, lbl.getLocalBounds().width));
  };

  const paintPill = (bg, selected, w) => {
    bg.clear();
    if (selected) {
      paintModalPrimaryButtonAt(bg, 0, 0, w, pillH, pillH / 2);
    } else {
      bg.roundRect(0, 0, w, pillH, pillH / 2).fill({ color: T.modalSurface, alpha: 0.92 });
      bg.roundRect(0, 0, w, pillH, pillH / 2).stroke({ width: 1, color: T.modalStroke, alpha: 0.65 });
    }
  };

  const makePill = (key, fallback, wantsOn) => {
    const pill = new Container();
    pill.eventMode = 'static';
    pill.cursor = 'pointer';
    const bg = new Graphics();
    const lbl = new Text({
      text: captionFor(key, fallback),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 11,
        fontWeight: '700',
        fill: T.modalBody,
        align: 'center',
      },
    });
    lbl.anchor.set(0.5);
    lbl.y = pillH / 2;
    pill.hitArea = new Rectangle(0, 0, minPillW, pillH);
    pill.addChild(bg, lbl);
    pill._bg = bg;
    pill._label = lbl;
    pill._labelKey = key;
    pill._labelFallback = fallback;
    pill._wantsOn = wantsOn;
    pill.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      if (!!getValue() !== wantsOn) {
        setValue(wantsOn);
        refresh();
      }
    });
    return pill;
  };

  const offPill = makePill('OFF', 'OFF', false);
  const onPill = makePill('ON', 'ON', true);
  root.addChild(offPill, onPill);

  const layoutPills = (on) => {
    const offW = Math.max(minPillW, measureLabelW(offPill._label, captionFor('OFF', 'OFF')) + padX * 2);
    const onW = Math.max(minPillW, measureLabelW(onPill._label, captionFor('ON', 'ON')) + padX * 2);
    offPill._pillW = offW;
    onPill._pillW = onW;
    offPill._label.x = offW / 2;
    onPill._label.x = onW / 2;
    offPill.hitArea = new Rectangle(0, 0, offW, pillH);
    onPill.hitArea = new Rectangle(0, 0, onW, pillH);
    paintPill(offPill._bg, !on, offW);
    paintPill(onPill._bg, on, onW);
    offPill._label.style.fill = !on ? T.modalHeading : T.modalBodyMuted;
    onPill._label.style.fill = on ? T.modalHeading : T.modalBodyMuted;
    onPill.x = offW + gap;
    return offW + gap + onW;
  };

  let totalW = minPillW * 2 + gap;
  const refresh = () => {
    totalW = layoutPills(!!getValue());
  };
  refresh();

  return {
    root,
    refresh,
    get width() {
      return totalW;
    },
  };
}

/**
 * Spin-count radio pill for the autoplay tab.
 * @param {{ spins: number | null, label: string }} spec
 * @param {boolean} selected
 * @param {() => void} onSelect
 */
function createAutoplaySpinRadio(spec, selected, onSelect) {
  const w = AUTOPLAY_SPIN_RADIO_W;
  const h = AUTOPLAY_SPIN_RADIO_H;
  const chip = new Container();
  chip.eventMode = 'static';
  chip.cursor = 'pointer';
  chip.hitArea = new Rectangle(0, 0, w, h);

  const ring = new Graphics();
  const dot = new Graphics();
  const numText = new Text({
    text: spec.label,
    style: {
      fontFamily: 'Montserrat',
      fontSize: spec.label === '∞' ? AUTOPLAY_FS.chipNumInf : AUTOPLAY_FS.chipNum,
      lineHeight: 18,
      fontWeight: '700',
      fill: T.modalHeading,
      align: 'center',
    },
  });
  numText.anchor.set(0.5, 0.5);
  numText.x = w / 2;
  numText.y = h * 0.38;
  numText.eventMode = 'none';

  const subText = new Text({
    text: '',
    style: {
      fontFamily: 'Montserrat',
      fontSize: 7,
      fontWeight: '600',
      fill: T.modalBody,
      align: 'center',
    },
  });
  subText.visible = false;
  subText.eventMode = 'none';

  const refresh = () => {
    ring.clear();
    dot.clear();
    if (selected()) {
      ring.roundRect(0, 0, w, h, 10).fill({ color: T.modalAccent, alpha: 0.28 });
      ring.roundRect(0, 0, w, h, 10).stroke({ width: 2, color: T.modalBorderGoldHi, alpha: 0.95 });
      dot.circle(w / 2, h - 9, 3.5).fill({ color: T.modalBorderGoldHi, alpha: 1 });
      numText.style.fill = T.modalHeading;
    } else {
      ring.roundRect(0, 0, w, h, 10).fill({ color: T.modalSurface, alpha: 0.45 });
      ring.roundRect(0, 0, w, h, 10).stroke({ width: 1, color: T.modalStroke, alpha: 0.7 });
      dot.circle(w / 2, h - 9, 3.5).stroke({ width: 1.25, color: T.modalTabInactive, alpha: 0.85 });
      numText.style.fill = T.modalBody;
    }
  };

  chip.addChild(ring, dot, numText, subText);
  chip._refresh = refresh;
  chip.on('pointertap', (e) => {
    if (!isPrimaryPointerAction(e)) return;
    e.stopPropagation();
    onSelect();
  });
  refresh();
  return chip;
}

/** Drop shadow for balance / last-win figures on gold-brown HUD panels. */
const HUD_VALUE_SHADOW = {
  alpha: 0.9,
  angle: Math.PI / 2,
  blur: 3,
  color: T.hudValueShadow,
  distance: 1,
};

/** Wait before connection-lost modal (avoids flash on brief reconnect). */
const CONNECTION_LOST_GRACE_MS = 4000;
/** Airplane mode / `offline` — show error quickly. */
const CONNECTION_OFFLINE_GRACE_MS = 800;
/** How long the “connection restored” toast stays visible. */
const CONNECTION_RESTORED_TOAST_MS = 3500;
/** After this long in background, force hub restart on return (iOS stale socket). */
const IOS_BACKGROUND_RESTART_MS = 2500;
/** Wait for ForceDisconnect after hub close before classifying disconnect (ms). */
const FORCE_DISCONNECT_WAIT_MS = 2000;
/** Ignore hub close/reconnect flaps right after a successful bootstrap (ms). */
const POST_BOOT_HUB_GRACE_MS = 8000;
/** Auto-retry hub ping while the connection-lost modal is open (ms). */
const CONNECTION_AUTO_RETRY_MS = 5000;
/** Shared across tabs — only the latest instance may show session-ended. */
const SESSION_INSTANCE_LS_KEY = 'shiningCrownLatestSession';
/** Fallback round-status key when partner/game params are unavailable during flush. */
const ROUND_STATUS_FALLBACK_KEY = 'scRoundStatus:last';

function claimLatestSessionInstance() {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SESSION_INSTANCE_LS_KEY, id);
    }
  } catch (_) {
    /* ignore */
  }
  return id;
}

function relinquishSessionInstance(id) {
  if (!id) return;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(SESSION_INSTANCE_LS_KEY) === id) {
      localStorage.removeItem(SESSION_INSTANCE_LS_KEY);
    }
  } catch (_) {
    /* ignore */
  }
}

function isLatestSessionInstance(id) {
  if (!id) return true;
  try {
    if (typeof localStorage === 'undefined') return true;
    const latest = localStorage.getItem(SESSION_INSTANCE_LS_KEY);
    return !latest || latest === id;
  } catch (_) {
    return true;
  }
}
/** Connection-lost modal — same chrome as settings ({@link SETTINGS_PANEL_DESKTOP}). */

/** Loading screen — 1922×1080 design space; background uses cover fit + blur. */
const LOADING_DESIGN_W = 1922;
const LOADING_DESIGN_H = 1080;
const LOADING_BLUR_STRENGTH = 8;
/** Extra bleed so blur filter does not expose empty edges. */
const LOADING_BG_BLEED = 48;
/** Bet options + settings modals — blurred stage backdrop (not loading / connection-lost). */
const MODAL_BACKDROP_BLUR_STRENGTH = 8;
const MODAL_BACKDROP_BLEED = 40;
/** Sand tint drawn over the blurred backdrop inside modal panels. */
const MODAL_PANEL_GLASS_ALPHA = 0.62;
/** Desktop progress bar cap (CSS px); mobile/tablet scale from viewport width. */
const LOADING_BAR_W_MAX = 420;
const LOADING_BAR_H_MAX = 14;
/** Minimum time the loading overlay stays visible (ms). */
const LOADING_MIN_DISPLAY_MS = 3000;
/** Inner progress trough padding inside the Egypt-style frame. */
const LOADING_BAR_FRAME_PAD = 5;

/** Vivid yellow-gold palette (HUD / win-tier), not brown modal sand tones. */
const LOADING_GOLD = {
  white: 0xffffff,
  gleam: 0xfff8dc,
  bright: 0xffe566,
  core: 0xffd700,
  amber: 0xffc125,
  deep: 0xd4af37,
  bronze: 0x8b6914,
  rim: 0xc9a227,
  shadow: 0x3d2208,
  trough: 0x2a1800,
  troughHi: 0x4a3208,
};

/** @type {import('pixi.js').Texture | null} */
let _loadingBarGoldFillTex = null;

/** Baked metallic gold strip — reliable vivid color in the progress fill. */
function getLoadingBarGoldFillTexture() {
  if (_loadingBarGoldFillTex) return _loadingBarGoldFillTex;
  const W = 512;
  const H = 48;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const base = ctx.createLinearGradient(0, 0, 0, H);
    base.addColorStop(0, '#FFF0A0');
    base.addColorStop(0.1, '#FFD700');
    base.addColorStop(0.35, '#FFEE58');
    base.addColorStop(0.55, '#FFE566');
    base.addColorStop(0.75, '#FFC125');
    base.addColorStop(0.92, '#DAA520');
    base.addColorStop(1, '#B8860B');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, W, H);

    const spec = ctx.createLinearGradient(0, 0, 0, H * 0.5);
    spec.addColorStop(0, 'rgba(255,240,160,0.72)');
    spec.addColorStop(0.35, 'rgba(255,230,100,0.38)');
    spec.addColorStop(1, 'rgba(255,215,0,0)');
    ctx.fillStyle = spec;
    ctx.fillRect(0, 0, W, H * 0.5);
  }
  _loadingBarGoldFillTex = Texture.from(canvas);
  return _loadingBarGoldFillTex;
}

function getLoadingBarInnerMetrics(w, h) {
  const inset = Math.max(1.5, h * 0.12);
  const innerW = Math.max(0, w - inset * 2);
  const innerH = Math.max(0, h - inset * 2);
  const radius = Math.max(0, h / 2 - inset);
  return { inset, innerW, innerH, radius };
}

/** @param {import('pixi.js').Text} text @param {{ large?: boolean }} [opts] */
function applyLoadingGoldTextStyle(text, opts = {}) {
  if (!text?.style) return;
  const large = !!opts.large;
  text.style.fill = large ? LOADING_GOLD.core : LOADING_GOLD.bright;
  text.style.stroke = { width: large ? 2.5 : 1.5, color: LOADING_GOLD.bronze };
  text.style.dropShadow = {
    alpha: 0.92,
    angle: Math.PI / 2,
    blur: large ? 7 : 4,
    color: LOADING_GOLD.shadow,
    distance: large ? 3 : 2,
  };
}

/** Ancient Egypt progress frame: warm dark trough + bright pharaoh gold rim. */
function paintLoadingBarFrame(g, w, h, pad = LOADING_BAR_FRAME_PAD) {
  g.clear();
  const outerH = h + pad * 2;
  const outerR = outerH / 2;
  const outerW = w + pad * 2;
  const troughGrad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: h },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: LOADING_GOLD.troughHi },
      { offset: 0.55, color: LOADING_GOLD.trough },
      { offset: 1, color: 0x140c02 },
    ],
  });
  const outerGrad = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: outerH },
    textureSpace: 'local',
    colorStops: [
      { offset: 0, color: LOADING_GOLD.bright },
      { offset: 0.45, color: LOADING_GOLD.core },
      { offset: 1, color: LOADING_GOLD.deep },
    ],
  });
  g.roundRect(-pad, -pad, outerW, outerH, outerR).fill(outerGrad);
  g.roundRect(-pad, -pad, outerW, outerH, outerR).stroke({ width: 2, color: LOADING_GOLD.deep, alpha: 1 });
  g.roundRect(0, 0, w, h, h / 2).fill(troughGrad);
}

/**
 * @param {{
 *   glow: import('pixi.js').Graphics | null,
 *   mask: import('pixi.js').Graphics | null,
 *   sprite: import('pixi.js').Sprite | null,
 *   gloss: import('pixi.js').Graphics | null,
 *   trackX: number,
 *   trackY: number,
 *   barW: number,
 *   barH: number,
 *   progress: number,
 * }} state
 */
function syncLoadingBarFillLayers(state) {
  const { glow, mask, sprite, gloss, trackX, trackY, barW, barH, progress } = state;
  const { inset, innerW, innerH, radius } = getLoadingBarInnerMetrics(barW, barH);
  const fx = Math.round(trackX + inset);
  const fy = Math.round(trackY + inset);
  const progressW = Math.max(0, innerW * Math.min(1, progress));
  const fillW = Math.round(Math.max(progressW, innerH));
  const fillH = Math.round(innerH);
  const fillR = Math.min(radius, fillW / 2);
  const visible = progressW > 0.5;

  if (glow) {
    glow.clear();
    const gp = LOADING_BAR_FRAME_PAD + 6;
    const glowH = barH + gp * 2;
    const glowR = glowH / 2;
    glow.roundRect(
      Math.round(trackX - gp),
      Math.round(trackY - gp),
      Math.round(barW + gp * 2),
      glowH,
      glowR,
    ).fill({
      color: LOADING_GOLD.core,
      alpha: 0.2,
    });
  }

  if (mask) {
    mask.clear();
    if (visible) {
      mask.roundRect(fx, fy, fillW, fillH, fillR).fill(0xffffff);
    }
  }

  if (sprite) {
    sprite.texture = getLoadingBarGoldFillTexture();
    sprite.x = fx;
    sprite.y = fy;
    sprite.width = fillW;
    sprite.height = fillH;
    sprite.visible = visible;
    sprite.alpha = 1;
  }

  if (gloss) {
    gloss.clear();
  }
}

function readWebGlRenderLimit() {
  if (typeof document === 'undefined') return Infinity;
  const canvas = document.createElement('canvas');
  const gl =
    canvas.getContext('webgl2') ||
    canvas.getContext('webgl') ||
    canvas.getContext('experimental-webgl');
  if (!gl) return Infinity;
  const maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE) || Infinity;
  const maxRenderbuffer = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || maxTexture;
  const loseContext = gl.getExtension('WEBGL_lose_context');
  loseContext?.loseContext();
  return Math.min(maxTexture, maxRenderbuffer);
}

function getPixiRendererProfile() {
  const dpr = Math.max(1, Number(window.devicePixelRatio) || 1);
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS =
    /iP(hone|ad|od)/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isTouch = window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  // Many Android WebViews omit deviceMemory — treat unknown as low-mem so we never
  // pick a "high-end" profile on a weak Adreno/Mali chip.
  const rawMem = Number(navigator.deviceMemory);
  const reportedMemory = Number.isFinite(rawMem) && rawMem > 0;
  const deviceMemory = reportedMemory ? rawMem : Infinity;
  const lowMemory = isAndroid && isTouch
    ? !reportedMemory || deviceMemory <= 4
    : reportedMemory && deviceMemory <= 4;
  const maxRenderLimit = readWebGlRenderLimit();
  const designW = 1922;
  const designH = 1080;
  const limitCap = Number.isFinite(maxRenderLimit)
    ? Math.max(1, Math.min(maxRenderLimit / designW, maxRenderLimit / designH) * 0.9)
    : Infinity;

  let resolutionCap = 2;
  let antialias = true;
  let powerPreference = 'high-performance';
  /** @type {number | null} */
  let maxFPS = null;

  if (isAndroid && isTouch) {
    // Adreno 610 / Mali low-end Android phones can freeze the entire browser
    // process with a 3844x2160 MSAA framebuffer. Keep DPR modest; Quick Spin
    // still multiplies Spine work, so also cap ticker FPS on low-memory devices.
    resolutionCap = lowMemory ? 1.15 : 1.25;
    antialias = false;
    powerPreference = 'default';
    maxFPS = lowMemory ? 30 : 45;
  } else if (isIOS && isTouch) {
    // Safari WebGL + Spine + masked reels: cap DPR and skip MSAA to reduce frame spikes
    // during deceleration without a visible layout change (canvas still CSS-scaled).
    resolutionCap = Math.min(resolutionCap, 1.75);
    antialias = false;
    powerPreference = 'default';
  } else if (isTouch && lowMemory) {
    resolutionCap = 1.35;
    antialias = false;
    powerPreference = 'default';
    maxFPS = 45;
  }

  return {
    antialias,
    powerPreference,
    resolution: Math.min(dpr, resolutionCap, limitCap),
    maxFPS,
    isAndroid,
    isTouch,
    lowMemory,
  };
}

/** Mobile / low-memory profiles skip full-frame GPU blur (dim overlay only). */
function shouldUseBackdropBlur() {
  return getPixiRendererProfile().antialias !== false;
}

/** @param {import('pixi.js').Sprite | null | undefined} sprite */
function applySpriteBackdropBlur(sprite) {
  if (!sprite) return;
  sprite.filters = shouldUseBackdropBlur() ? [ensureModalBackdropBlurFilter()] : null;
}

/** Horizontal nudge for BALANCE vs LAST WIN columns (portrait merged bar + desktop stat panels). */
const CANVAS_STAT_SHIFT_BALANCE_X = -40;
const CANVAS_STAT_SHIFT_LAST_WIN_X = 40;

/** Portrait merged-bar centre prompt (two lines; matches UILayer pattern). */
const PORTRAIT_BET_PROMPT_LINES = 'PLEASE PLACE\nYOUR BET';
/** Logical height of merged BALANCE | prompt | LAST WIN bar — keep in sync everywhere `portraitTopStatsH` is set. */
const PORTRAIT_MERGED_STATS_BAR_H = 124;
/** Background sprite height only (shorter than {@link PORTRAIT_MERGED_STATS_BAR_H}); slots/prompt layout unchanged. */
const PORTRAIT_MERGED_STATS_BG_HEIGHT = 95;
/** Widen merged stats bg only; BALANCE / prompt / LAST WIN columns keep {@link mergedW} layout. */
const PORTRAIT_MERGED_STATS_BG_EXTRA_W = 200;
const PORTRAIT_BET_PROMPT_FONT_SIZE = 26;
const PORTRAIT_BET_PROMPT_LINE_HEIGHT = 32;
/** Portrait-phone HUD scale (merged balance bar, quick bets, spin/autoplay/bet row). */
const MOBILE_HUD_FIT_TARGET = 1.20;
const MOBILE_HUD_FIT_MIN = 0.62;
const MOBILE_HUD_FIT_MAX = 1.24;
/** Quick-bet chip design size at hudFit = 1 (before {@link MOBILE_HUD_FIT_TARGET} multiplier). */
const DESKTOP_QB_CHIP_REF_W = 128;
const DESKTOP_QB_CHIP_REF_H = 95;
const MOBILE_QB_CHIP_REF_W = 140;
const MOBILE_QB_CHIP_REF_H = 94;
/** SPIN / autoplay / bet row scale base (multiplied by {@link GameApp#_getMobileHudFit}). */
const MOBILE_CONTROL_SCALE_BASE = 2.06;
/** Horizontal gap between SPIN edge and adjacent autoplay / bet / quick-bet column (portrait phone). */
const MOBILE_SPIN_SIDE_GAP = 16;
/** Fast-play plate below SPIN (logical px); height = aspect × {@link BET_SPEED_HEIGHT_MUL}. */
const BET_SPEED_PLATE_W = 108;
const BET_SPEED_HEIGHT_MUL = 1.28;
const BET_SPEED_ARROW_W = 9;
const BET_SPEED_MOBILE_SCALE = 1.65;
/** Desktop spin diameter the autoplay-remain label was tuned for. */
const SPIN_BTN_REF_SIZE = 194;
/** Desktop: horizontal gap from SPIN edge to the bet / autoplay column. */
const DESKTOP_SPIN_SIDE_GAP = -30;
/** Desktop: vertical gap between stacked bet-options and autoplay icons. */
const DESKTOP_SPIN_SIDE_BTN_GAP = 90;
/**
 * Buy-bonus plaque. Source art is 1024×353; the HUD uses a much smaller slice
 * so the button stays a secondary control next to spin.
 */
const BUY_BONUS_BTN_SRC_W = 1024;
const BUY_BONUS_BTN_SRC_H = 353;
const BUY_BONUS_BTN_W = 232;
const BUY_BONUS_BTN_H = Math.round((BUY_BONUS_BTN_W * BUY_BONUS_BTN_SRC_H) / BUY_BONUS_BTN_SRC_W);
/** Autoplay rounds counter on the stop button (desktop design px). */
const SPIN_AUTOPLAY_REMAIN_DESKTOP = { x: 5, y: -6, fontSize: 26, fontInf: 42 };
/** Portrait phone nudge for {@link SPIN_AUTOPLAY_REMAIN_DESKTOP} (+X right, −Y up). */
const SPIN_AUTOPLAY_REMAIN_MOBILE_NUDGE = { x: 6, y: -12 };
/** Portrait-phone cabinet lift at max width (760px); extra applied as width → 320px. Negative = up. */
/** Portrait reel lift (negative = up). Live 5×3 values match fixing-optimization. */
const MOBILE_GAME_VISUAL_LIFT_BASE = -130;
const MOBILE_GAME_VISUAL_LIFT_EXTRA_NARROW = -48;
/** 40-line and buy bonus sit a little lower so the top frame and logo stay on screen. */
const MOBILE_GAME_VISUAL_LIFT_BASE_COMPACT = -90;
const MOBILE_GAME_VISUAL_LIFT_EXTRA_NARROW_COMPACT = -28;
const MOBILE_PORTRAIT_WORLD_OFFSET_NARROW = -34;
const MOBILE_SLOT_ROOT_UP_BIAS_NARROW = 28;
/** Portrait stacked HUD: keep corner icons (home / sound / menu) inside the 1080px design stage. */
const PORTRAIT_DESIGN_H = 1080;
const PORTRAIT_CORNER_ICONS_BOTTOM_PAD = 10;
/** Logo in {@link GameApp#_gameVisualLayer} — fixed local size; scales with frame on portrait phones. */
const CABINET_LOGO_BASE_WIDTH = 440;
const CABINET_LOGO_GAP_ABOVE_FRAME = 67;
/**
 * Logo0.png canvas and the opaque artwork inside it.
 * The 40-line logo is fitted into this same on-screen art box so it stays the same size and place.
 */
const LEGACY_LOGO_METRICS = {
  canvasW: 1536,
  canvasH: 1024,
  artMinX: 254,
  artMinY: 266,
  artMaxX: 1292,
  artMaxY: 768,
};
const LINES40_LOGO_METRICS = {
  canvasW: 1983,
  canvasH: 793,
  artMinX: 122,
  artMinY: 70,
  artMaxX: 1862,
  artMaxY: 752,
};
/**
 * Opaque artwork inside `100_lines_logo.png` (wider than the 40 logo because of "100").
 * Fitted into the same on-screen box as the 40-line logo.
 */
const LINES100_LOGO_METRICS = {
  canvasW: 1984,
  canvasH: 793,
  artMinX: 42,
  artMinY: 26,
  artMaxX: 1967,
  artMaxY: 758,
};

/**
 * Figma mobile bet modal (Frame 207) — uniform scale to viewport; desktop path ignores these.
 * Grid Frame 132: 338×372, gap 12, square cells 58×58 (chip art uses uniform scale, no stretch).
 */
const MOBILE_BET_MODAL_REF = { w: 370, h: 474 };
const MOBILE_BET_MODAL_GRID_INNER_W = 338;
const MOBILE_BET_MODAL_GRID_INNER_H = 372;
const MOBILE_BET_CHIP_REF_W = 58;
const MOBILE_BET_CHIP_REF_H = 58;
const MOBILE_BET_CHIP_GAP_REF = 12;
const MOBILE_BET_MODAL_TITLE_FS = 18;
const MOBILE_BET_MODAL_GRID_INSET_X = 16;
const MOBILE_BET_MODAL_TITLE_TOP = 21;
const MOBILE_BET_MODAL_GRID_TOP = 65;
const MOBILE_BET_MODAL_GRID_BOTTOM = 37;
const MOBILE_BET_MODAL_CORNER_R = 20;
/** Portrait phone: CSS margin from viewport edges when centering bet modal. */
const MOBILE_BET_MODAL_VIEWPORT_MARGIN_CSS = 12;
const MOBILE_BET_MODAL_CLOSE_TOP_REF = 14;
const MOBILE_BET_MODAL_CLOSE_RIGHT_REF = 24;
const MOBILE_BET_MODAL_CLOSE_SIZE_REF = 32;

/**
 * Figma tablet bet modal (Frame 207) — 666×633; grid 602×490; square chips 74×74; gap 14.
 */
const TABLET_BET_MODAL_REF = { w: 666, h: 633 };
const TABLET_BET_MODAL_GRID_INNER_W = 602;
const TABLET_BET_MODAL_GRID_INNER_H = 490;
const TABLET_BET_CHIP_REF_W = 74;
const TABLET_BET_CHIP_REF_H = 74;
const TABLET_BET_CHIP_GAP_REF = 14;
const TABLET_BET_MODAL_TITLE_FS = 24;
const TABLET_BET_MODAL_TITLE_TOP = 40;
/** Title block shifted +4.5px from horizontal center (Figma). */
const TABLET_BET_MODAL_TITLE_CENTER_NUDGE_X = 4.5;
const TABLET_BET_MODAL_GRID_TOP = 104;
const TABLET_BET_MODAL_GRID_BOTTOM = 39;
const TABLET_BET_MODAL_CORNER_R = 20;
/** Panel root nudge in ref px (positive = move modal down). Figma had −33; increased so tablet sits lower. */
const TABLET_BET_MODAL_OFFSET_Y_REF = 78;
const TABLET_BET_MODAL_CLOSE_TOP_REF = 16;
const TABLET_BET_MODAL_CLOSE_RIGHT_REF = 28;
const TABLET_BET_MODAL_CLOSE_SIZE_REF = 44;

/** Figma desktop Frame 207 (900×706): scale {@link DESKTOP_BET_MODAL_CORNER_R} with panel width. */
const DESKTOP_BET_MODAL_CORNER_REF_W = 900;
const DESKTOP_BET_MODAL_CORNER_R = 20;
/** Bet modal border — tomb shadow at bottom, pharaoh gold + sand accent at top. */
const BET_POPUP_BORDER_GRADIENT = new FillGradient({
  type: 'linear',
  start: { x: 0, y: 1 },
  end: { x: 0, y: 0 },
  textureSpace: 'local',
  colorStops: [
    { offset: 0, color: themeHex(T.modalBorderDark) },
    { offset: 0.45, color: themeHex(T.modalBorderGold) },
    { offset: 0.72, color: themeHex(T.modalAccentHi) },
    { offset: 1, color: themeHex(T.modalBorderGoldHi) },
  ],
});

/**
 * Portrait canvas HUD — tweak here; landscape/desktop uses {@link GameApp#_canvasDesktopLayout} snapshot.
 * All portrait (phone + tablet) uses this mobile HUD tuning.
 */
const PORTRAIT_TUNING = {
  phone: {
    gameToHudGapBase: 12,
    gameToHudGapSpacingBiasMul: 8,
    /** Entire autoplay / SPIN / bet / turbo row; negative = up, positive = down (mobile only). */
    spinRowNudgeY: -38,
    /** Portrait phone quick-bet strip: fixed CSS px inset from the physical left edge (all phones). */
    quickBetLeftMarginCss: 12,
    /** Merged BALANCE | prompt | LAST WIN bar; positive = down (mobile only). */
    statsBarNudgeY: 100,
    /** Gap between merged stats bar and quick-bet strip (mobile phone only). */
    statsToQbGap: -25,
    /** Extra width on merged stats bg sprite only (text columns unchanged). */
    mergedStatsBgExtraW: 200,
    /** Pull BALANCE right / LAST WIN left toward the center prompt (mobile merged bar). */
    mergedStatsSideTowardCenterPx: 28,
    /** Home / sound / menu inset from the visible frame edge (not toward reel center). */
    cornerIconEdgePad: 14,
    /** Small inward nudge from frame corners (+X = toward center, mobile only). */
    cornerIconTowardCenter: 25,
    /** Portrait phone bottom HUD: CSS px gap used when estimating the screen edge. */
    bottomMenuRightMarginCss: 10,
    /** Bottom-left chrome row (sound / menu only on mobile — never fullscreen). */
    bottomChromeIconGap: 14,
    /** Extra px below spin / bet row before corner icons (mobile only). */
    bottomChromeIconDownNudge: 16,
    /** Max logical px to pull QB strip / corner icons inward from reel-block right (not the stats bar). */
    bottomMenuMaxPullbackPx: 28,
    /** Extra pullback cap at the narrowest phone widths (320px). */
    bottomMenuMaxPullbackNarrowPx: 20,
    /** Portrait phone: vertical quick-bet rail on the right (top aligns with bet-options button). */
    /** Gap above the bottom corner-icon row. */
    quickBetVerticalBottomPad: 8,
    /** Shave this much from the natural rail height (“a little bit” shorter, still multi-chip). */
    quickBetVerticalHeightTrimPx: 20,
    /** Target chips visible without scrolling when space allows. */
    quickBetVerticalMinVisibleChips: 3,
    /**
     * Portrait vertical rail: render chips at this scale (rail height stays full-size).
     * Slightly smaller chips leave a peek of the next chip — scroll affordance.
     */
    quickBetVerticalChipScale: 0.9,
    /**
     * Quick-bet rail viewport height as a multiple of SPIN diameter (same hudFit chain).
     * When unset, derived from {@link #quickBetVerticalMinVisibleChips} + chip stride.
     */
    quickBetVerticalSectionSpinRatio: null,
    /**
     * Optional extra X for merged stats bar after band-centering (usually 0).
     * Does not move SPIN / autoplay / bet or bottom-left sound / menu icons.
     */
    hudCenterShiftLeftPx: 0,
    /** Portrait phone: extra down nudge for SPIN + autoplay + bet-options + turbo together. */
    spinRowExtraDownNudgePx: 24,
    /** Horizontal gap between SPIN edge and autoplay/bet (left) or quick-bet rail (right). */
    spinSideGapPx: 60,
    /** Portrait phone: nudge autoplay icon down from SPIN row center (+Y = down). */
    autoplayBtnDownNudgePx: 90,
    /** Gap between bet-options button and autoplay icon when stacked on the left. */
    betBtnAboveAutoplayGapPx: 8,
  },
};

/** Settings modal panel size in design px (before the modal’s uniform {@link GameApp#_createSettingsModalPixi} SCALE). */
const SETTINGS_PANEL_DESKTOP = { w: 666, h: 580 };
/**
 * Portrait-phone settings shell reference (design px at modalFit = 1).
 * Final size comes from {@link GameApp#_getSettingsPanelCompactMetrics} so width/height
 * keep the same proportions on every device.
 */
const SETTINGS_PANEL_MOBILE = { w: 390, h: 850 };
/** Target share of the CSS viewport used by the rendered panel (after SCALE + shell correction). */
/** Rendered width ≈ cw × this value (height often binds modalFit — see {@link GameApp#_getSettingsPanelCompactMetrics}). */
const SETTINGS_PANEL_MOBILE_VIEWPORT_W = 0.99;
const SETTINGS_PANEL_MOBILE_VIEWPORT_H = 0.95;
/** Landscape tablet settings shell viewport targets. */
const SETTINGS_PANEL_TABLET_VIEWPORT_W = 0.9;
const SETTINGS_PANEL_TABLET_VIEWPORT_H = 0.8;
/** Uniform modalFit clamp for compact (phone/tablet) layouts. */
const SETTINGS_PANEL_COMPACT_FIT_MIN = 0.72;
const SETTINGS_PANEL_COMPACT_FIT_MAX = 1.28;
/** Uniform panel up-scale applied in {@link GameApp#_createSettingsModalPixi}. */
const SETTINGS_MODAL_PANEL_SCALE = 1.2;
/** CSS viewport width where legacy width-only fit = 1 (connection-lost modal). */
const SETTINGS_PANEL_MOBILE_REF_CW = 390;
/** Figma-width baseline for horizontal proportions (tab icons, switches block). */
const SETTINGS_PANEL_MOBILE_LAYOUT_REF_W = 350;
/** Height baseline matching {@link SETTINGS_PANEL_MOBILE.h} for vertical proportions. */
const SETTINGS_PANEL_MOBILE_LAYOUT_REF_H = 850;
/** Distance from panel bottom to the horizontal rule above the tab row (520 − 463). */
const SETTINGS_PANEL_MOBILE_BOTTOM_GAP = 57;
/** Extra Y offset for the active-tab underline below tab icons (+Y = lower). */
const SETTINGS_ACTIVE_TAB_LINE_Y_NUDGE = 6;
/** Default square slot for settings-modal tab icons (settings / autoplay / info). */
const SETTINGS_TAB_ICON_SIZE = 24;

/**
 * Fit a tab icon inside a square slot without stretching (settings.svg is 21×20, others 24×24).
 * @param {import('pixi.js').Sprite} sprite
 * @param {number} boxSize
 */
function fitSettingsTabIcon(sprite, boxSize) {
  const tex = sprite?.texture;
  const tw = tex?.width > 0 ? tex.width : boxSize;
  const th = tex?.height > 0 ? tex.height : boxSize;
  const k = Math.min(boxSize / tw, boxSize / th);
  sprite.width = tw * k;
  sprite.height = th * k;
}

/**
 * Close icons are scaled via width/height; hitArea must cover the full texture in local px.
 * A fixed 40×40 rect in texture space shrinks to ~8 screen px on a 128² asset shown at 26px.
 * @param {import('pixi.js').Sprite} sprite
 */
function setModalCloseButtonHitArea(sprite) {
  const tw = sprite?.texture?.width ?? 0;
  const th = sprite?.texture?.height ?? 0;
  if (tw > 0 && th > 0) {
    sprite.hitArea = new Rectangle(-tw / 2, -th / 2, tw, th);
  }
}
/**
 * Push the mobile settings modal down on the stage at a 1080px-tall viewport (+Y = lower).
 * Scaled by actual viewport height in {@link GameApp#_getSettingsPanelCompactMetrics}.
 */
const SETTINGS_PANEL_MOBILE_ROOT_OFFSET_Y = 180;
/** Gap (stage px, H=1080) between the bottom edge of the settings panel and the bottom of the stage on portrait mobile. */
const SETTINGS_PANEL_MOBILE_BOTTOM_STAGE_GAP = 0;

/** Autoplay tab layout widths (design px) — use actual sizes when centring, not rx multiples. */
const AUTOPLAY_SLIDER_ROW_W = 348;
const AUTOPLAY_BALANCE_BLOCK_W = 82;
const AUTOPLAY_BET_BLOCK_W = 48;
/** Desktop BALANCE/BET horizontal insets (design px). */
const AUTOPLAY_BALANCE_BLOCK_X = 33;
const AUTOPLAY_BET_BLOCK_X_INSET = 102;
/** Autoplay tab: slider row height (matches {@link GameApp#_buildAutoplaySlider} rowH). */
const AUTOPLAY_SLIDER_ROW_H = 78;
/** Gap below last slider before stats card on portrait mobile. */
const AUTOPLAY_MOBILE_BALANCE_BET_GAP = 18;
/**
 * Desktop keeps horizontal inset on the settings blur mask; mobile uses full panel width
 * (inset was ~100×scale design px total — too much on a narrow panel).
 */
const SETTINGS_PANEL_MOBILE_BLUR_INSET_X = 0;
/**
 * Extra horizontal inset (each side, panel design px) for the Information tab viewport only —
 * {@link GameApp#_syncSettingsModalLayout} scales it by `rx` on portrait phone; tablet uses this value as-is.
 */
const SETTINGS_INFO_VIEW_EXTRA_SIDE_INSET = 18;
/** Information tab — section headings, WILD/SCATTER titles, top-tier payout multipliers. */
const INFO_TAB_TEXT_YELLOW = T.modalTabActive;
/** Info tab body copy — must contrast with sand modal panels (not HUD `textMuted`). */
const INFO_TAB_BODY_FILL = T.modalBody;
const INFO_TAB_BODY_MUTED_FILL = T.modalBodyMuted;
/** Portrait phone: info-tab font/icon multipliers (word-wrap width stays fixed — no shell widening). */
const SETTINGS_INFO_MOBILE_TEXT_MUL = 1.62;
const SETTINGS_INFO_MOBILE_ICON_MUL = 1.22;
/** Portrait phone: settings-tab heading + switch labels. */
const SETTINGS_MOBILE_UI_TEXT_MUL = 1.5;
/** Settings modal tab bar labels (GAME SETTINGS / AUTOPLAY / INFO). */
const SETTINGS_TAB_LABEL_FONT_DESKTOP = 10;
const SETTINGS_TAB_LABEL_FONT_NARROW_DESKTOP = 9;
const SETTINGS_TAB_LABEL_FONT_MOBILE = 14;
const SETTINGS_TAB_LABEL_LINE_HEIGHT_MUL = 1.28;
/** Gap between a settings-tab switch label and its toggle (design px). */
const SETTINGS_SWITCH_LABEL_GAP = 14;
/** Extra safety pad after measured label width (glyph overhang / font load). */
const SETTINGS_SWITCH_LABEL_PAD = 4;
/** Horizontal gap between Sound and Quick Spin columns on desktop/tablet. */
const SETTINGS_SWITCH_COLUMN_GAP = 24;
/** Toggle width in {@link GameApp#_createSettingsModalPixi} makeSwitch rows. */
const SETTINGS_SWITCH_TOGGLE_W = 42;
/** Min horizontal inset from panel edge when sizing settings switch rows. */
const SETTINGS_SWITCH_EDGE_INSET = 28;
/** Top segmented tab bar (icon + label) — sits under the close row. */
const SETTINGS_TAB_BAR_Y = 46;
const SETTINGS_TAB_BAR_H = 56;
/** First horizontal rule below the tab bar. */
const SETTINGS_RULE_TOP_Y = 108;
/** Top of scrollable / stacked modal body content. */
const SETTINGS_CONTENT_TOP = 116;
/** Extra downward offset for the Game Settings tab stack only (Sound / Quick Spin / Exit). */
const SETTINGS_TAB_STACK_EXTRA_Y = 24;
/** Canvas HUD corner icons (home hidden, fullscreen desktop/tablet only, sound, menu) — design px. */
const CANVAS_BOTTOM_ICON_SIZE = 44;
/** Mobile bottom-left / bottom-bar sound + menu icons (scales with {@link GameApp#_getMobileHudFit}). */
const MOBILE_BOTTOM_MENU_ICON_BASE = 62;
const MOBILE_BOTTOM_MENU_ICON_MIN = 54;
/** Settings + bet modal close button — design px (desktop baseline). */
const MODAL_CLOSE_BTN_SIZE = 26;
const SETTINGS_CONTENT_BOTTOM_PAD = 20;
/** Stacked option cards on the Game Settings tab. */
const SETTINGS_OPTION_CARD_H = 58;
const SETTINGS_OPTION_CARD_GAP = 12;
const SETTINGS_OPTION_CARD_INSET = 24;
const SETTINGS_SEGMENT_PILL_W = 44;
const SETTINGS_SEGMENT_PILL_H = 30;
const SETTINGS_SEGMENT_PILL_GAP = 6;
/** Autoplay tab — stats card, spin radios, stop sliders (desktop baseline px). */
const AUTOPLAY_STATS_ROW_Y = 72;
const AUTOPLAY_STATS_CARD_H = 48;
const AUTOPLAY_STATS_CARD_INSET = 28;
const AUTOPLAY_SPIN_RADIO_W = 54;
const AUTOPLAY_SPIN_RADIO_H = 50;
const AUTOPLAY_SPIN_RADIO_GAP = 8;
const AUTOPLAY_CHIPS_TOTAL_W =
  5 * AUTOPLAY_SPIN_RADIO_W + 4 * AUTOPLAY_SPIN_RADIO_GAP;
/** Height of one spin radio + cost line beneath it. */
const AUTOPLAY_CHIP_BLOCK_H = AUTOPLAY_SPIN_RADIO_H + 14;
const AUTOPLAY_PLAY_BTN_W = 240;
const AUTOPLAY_PLAY_BTN_H = 38;
/** Autoplay settings tab — desktop baseline font sizes (px). */
const AUTOPLAY_FS = {
  statsLabel: 11,
  statsValue: 14,
  spinsHeading: 12,
  chipNum: 15,
  chipNumInf: 26,
  chipCost: 9,
  playBtn: 13,
  stopHeading: 14,
  sliderLabel: 12,
  sliderValue: 12,
  sliderMinMax: 11,
};
/** Portrait phone autoplay copy scale — large enough to read, capped so rows still fit. */
const AUTOPLAY_MOBILE_TEXT_MUL = 1.45;
/** Tablet compact autoplay copy scale. */
const AUTOPLAY_TABLET_TEXT_MUL = 1.2;

/**
 * Wraps label copy to a pixel width using Canvas measureText (same family of metrics as Pixi Text),
 * inserting newline characters only. Pixi {@link Text} should use wordWrap: false with this string
 * so layout does not depend on Pixi's internal word-wrap heuristics.
 * @param {string} raw
 * @param {number} maxWidthPx
 * @param {CanvasRenderingContext2D | null} ctx
 * @param {{ fontFamily: string, fontSize: number, fontWeight: string }} fontOpts
 * @returns {string}
 */
function wrapSettingsTableCellText(raw, maxWidthPx, ctx, fontOpts) {
  const text = String(raw ?? '');
  if (!ctx || !text) return text;
  const budget = Math.max(1, Math.floor(maxWidthPx) - 2);
  ctx.font = `${fontOpts.fontWeight} ${fontOpts.fontSize}px ${fontOpts.fontFamily}`;

  const widthOf = (s) => ctx.measureText(s).width;

  /** Longest prefix of s with width <= budget (at least one code unit if s is non-empty). */
  const longestFitPrefixLen = (s) => {
    if (!s) return 0;
    if (widthOf(s) <= budget) return s.length;
    let lo = 1;
    let hi = s.length;
    let best = 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (widthOf(s.slice(0, mid)) <= budget) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  };

  const lines = [];
  let line = '';
  const flush = () => {
    const t = line.replace(/\s+$/u, '');
    if (t.length > 0) lines.push(t);
    line = '';
  };

  const pushHardBroken = (word) => {
    let rest = word;
    while (rest.length > 0) {
      const n = longestFitPrefixLen(rest);
      lines.push(rest.slice(0, n));
      rest = rest.slice(n);
    }
  };

  for (const word of text.split(/\s+/u)) {
    if (!word) continue;
    const trial = line ? `${line} ${word}` : word;
    if (widthOf(trial) <= budget) {
      line = trial;
      continue;
    }
    if (line) flush();
    if (widthOf(word) <= budget) {
      line = word;
    } else {
      pushHardBroken(word);
    }
  }
  if (line) flush();

  return lines.length > 0 ? lines.join('\n') : text;
}

export class GameApp {
  constructor() {
    /** @type {Application | null} */
    this.app = null;
    this.animationController = new AnimationController();
    this.debug = new DebugMode();
    this.backend = new ShiningCrownService();

    /** @type {Record<number, any>} */
    this.spineAssets = {};
    this.world = new Container();
    /** @type {SlotMachine | null} */
    this.slotMachine = null;
    /** True while {@link handleSpin} is running (including post-win collect / delays). */
    this._spinCycleActive = false;
    /** True while reels are in motion and the player may request stop. */
    this._spinning = false;
    /** True only while reels are physically spinning (between beginPendingSpin and spinToGrid resolving). */
    this._reelsSpinning = false;
    this._initializing = false;
    /** True after InitialState succeeds (quick bets + balance from server). */
    this._gameDataReady = false;
    /** Live SignalR session; false while disconnected / reconnecting. */
    this._hubConnected = false;
    /** InitialState / bootstrap failed — show connection modal immediately. */
    this._sessionLoadFailed = false;
    /** True only after {@link CONNECTION_LOST_GRACE_MS} with no recovery. */
    this._connectionLostUIOpen = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._connLostGraceTimer = null;
    /** @type {'lost' | 'duplicate'} */
    this._connModalMode = 'lost';
    this._connGraceMs = CONNECTION_LOST_GRACE_MS;
    this._hadConnectionLoss = false;
    this._duplicateSessionEnded = false;
    /** @type {(() => void) | null} */
    this._hubMonitorDispose = null;
    /** @type {(() => void) | null} */
    this._connRestoredToastHide = null;
    this._sessionSyncInFlight = false;
    /** @type {Promise<boolean> | null} Deduped session ping / reconnect. */
    this._sessionSyncPromise = null;
    /** Last tab-background duration on foreground (iOS stale socket probe). */
    this._lastForegroundHiddenMs = 0;
    /** False until InitialState succeeds — blocks spurious hub/online handlers during boot. */
    this._connectionMonitoringEnabled = false;
    this._forceDisconnectReceived = false;
    /** Hub closed while tab was in background — recover silently on return (no error modal). */
    this._reconnectAfterTabVisible = false;
    /** @type {ReturnType<typeof setInterval> | null} */
    this._connectionAutoRetryTimer = null;
    this._connectionModalReconnecting = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._disconnectResolveTimer = null;
    /** Hub drop/reconnect ignored until this timestamp (post-bootstrap flap guard). */
    this._hubIgnoreUntilTs = 0;
    this._isDemo = false;
    this._oneTimeToken = null;
    this._accessToken = null;
    this._sessionParams = null;
    /** Browser-tab instance id — latest claim suppresses spurious session-ended UI. */
    this._sessionInstanceId = null;
    /** Launcher return URL (`LobbyUrl` / `lobbyUrl` / `lobbyURL`); empty hides Exit to lobby. */
    this._lobbyUrl = '';
    /** @type {(() => void) | null} */
    this._idleReloadDispose = null;
    /** @type {HTMLDivElement | null} */
    this._idleTimeoutOverlay = null;
    /** @type {{ sync: () => void, refreshCopy: () => void, dispose: () => void } | null} */
    this._landscapeBlocker = null;
    /** @type {ReturnType<typeof createSystemMessagePanel> | null} Connection / session-ended DOM shell. */
    this._systemMessagePanel = null;
    /** @type {ReturnType<typeof createGameStartScreen> | null} Post-load intro (max-win screenshot). */
    this._startScreenPanel = null;
    /** @type {(() => void) | null} Resolves {@link GameApp#_showStartScreenAndWait}. */
    this._startScreenResolve = null;
    /** @type {Promise<void> | null} Dedupes concurrent show passes. */
    this._connectionLostShowPromise = null;
    this.translation = translationStore;
    this._currencyCode = 'FUN';
    this._currencyPrecision = 2;
    this._currencySymbol = undefined;
    this._localizedTextRefs = [];
    /** Reposition inline WILD column icons after translated line widths change. */
    this._infoWildRelayout = null;
    /** Info-tab payout lines (always FUN); kept for localized-text refresh. */
    this._infoPayoutAmountTexts = [];
    /** RULES min/max + max-win rows; max-win visibility toggled after InitialState. */
    this._infoRuleLimitLineRefreshers = [];
    this._infoRuleMaxWinLines = [];
    /** Re-wrap + reflow the Information tab button table when translations change. */
    this._infoButtonTableRelayout = null;
    /** Reflow stacked Information tab blocks after copy height changes. */
    this._infoContentRelayout = null;
    /** Children of the info scroll `content` below the button table (Y-shifted when table height changes). */
    this._infoAfterTableChildren = null;
    this._infoLastTableTotalH = 0;
    this._infoViewportTop = 66;
    this._infoViewportBottom = 522;

    /** @type {HTMLElement | null} */
    this._containerEl = null;
    /** @type {ResizeObserver | null} */
    this._resizeObserver = null;
    /** @type {import('pixi.js').Container | null} */
    this._settingsRoot = null;
    /** @type {import('pixi.js').Container | null} Settings modal panel wrapper; counter-scaled so panel keeps aspect under non-uniform canvas CSS scale. */
    this._settingsPanelRoot = null;
    /** @type {import('pixi.js').Container | null} */
    this._backdropRoot = null;
    /** @type {Sprite | null} */
    this._backdropSprite = null;
    /** @type {Texture | null} */
    this._bgTexDesktop = null;
    /** @type {Texture | null} */
    this._bgTexMobile = null;
    /** @type {Texture | null} */
    this._settingsPanelBackdrop = null;
    this._settingsPanelBackdropMask = null;
    /** @type {import('pixi.js').Container | null} Full-screen dim layer (sibling of panel; not aspect-corrected). */
    this._betPopupDimRoot = null;
    /** @type {import('pixi.js').Container | null} Modal panel wrapper; counter-scaled so the panel is not stretched by canvas CSS. */
    this._betPopupPanelRoot = null;
    /** @type {import('pixi.js').Container | null} */
    this._betPopupChipsArea = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._betPopupDim = null;
    /** @type {Sprite | null} */
    this._betPopupTriggerBtn = null;
    this._betPopupTriggerControlsIndex = 0;
    /** @type {import('pixi.js').Text | null} */
    this._betPopupTitle = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._betPopupCloseBtn = null;
    /** @type {import('pixi.js').Container | null} Inner panel (bg + title + chips), centered under {@link #_betPopupPanelRoot}. */
    this._betPopupPanel = null;
    /** Native pixel size of quick-bets panel art (for desktop aspect fit). */
    this._betPopupBgNatW = 1000;
    this._betPopupBgNatH = 780;
    /** @type {import('pixi.js').Texture | null} */
    this._betPopupBgTexDefault = null;
    /** @type {import('pixi.js').Texture | null} */
    this._betPopupBgTexMobile = null;
    /** Last Figma scale factor for mobile bet modal (1 on desktop). */
    this._betPopupMobileModalFit = 1;
    /** Rounded black fill behind modal art (Figma Frame 207). */
    this._betPopupPanelFill = null;
    /** Rounded-rect mask for {@link #_betPopupBgSprite}. */
    this._betPopupRoundMask = null;
    /** Rounded stroke on the bet-options panel (desktop + compact). */
    this._betPopupPanelBorder = null;
    /** @type {HTMLDivElement | null} */
    this._betTapZone = null;
    this._muted = false;
    this._settingsSoundOn = true;
    this._settingsQuickSpinOn = false;
    this._paylinesVisible = false;
    this._autoplaying = false;
    /** 1–3: lit arrows on bet-speed chip; click cycles 1→2→3→1 */
    this._betSpeedTier = 1;
    /** Restored when Quick Spin is turned off after forcing tier 3. */
    this._betSpeedTierBeforeQuickSpin = null;
    /** @type {Sprite[] | null} */
    this._betSpeedArrowSprites = null;
    /** @type {Texture | null} */
    this._betSpeedActiveTex = null;
    /** @type {Texture | null} */
    this._betSpeedInactiveTex = null;
    this._turboHoldActive = false;
    /** Last spin used Quick Spin / hold-turbo — drives post-spin cooldown. */
    this._lastSpinUsedTurbo = false;
    this._spinHoldInProgress = false;
    this._spinHoldTriggeredTurbo = false;
    this._spinHoldPointerId = null;
    /** True after {@link HTMLCanvasElement#setPointerCapture} for the active spin-hold pointer. */
    this._spinHoldPointerCaptureActive = false;
    /** Window-level pointerup/cancel (capture) so release is seen even if Pixi misses it under load. */
    this._spinHoldWindowGuardActive = false;
    /** Window-level collect / big-win dismiss (capture) — same rationale as spin hold. */
    this._winCollectWindowGuardActive = false;
    this._onSpinHoldWindowPointerEnd = this._onSpinHoldWindowPointerEnd.bind(this);
    this._onWinCollectWindowPointerStart = this._onWinCollectWindowPointerStart.bind(this);
    this._onWinCollectWindowPointerEnd = this._onWinCollectWindowPointerEnd.bind(this);
    this._spinHoldRaf = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._spinHoldTimer = null;
    this._spinHoldStartTs = 0;
    this._spinButtonBaseScale = 1;
    this._spinHoldMs = 1300;
    this._spinHoldVisualDelayMs = 300;
    this._spinHoldMaxScale = 1.1;
    this._spinToggleCooldownMs = 80;
    this._spinToggleLockUntilTs = 0;
    /** Minimum idle gap enforced after a spin cycle ends before a manual re-spin. */
    this._postSpinRespinCooldownMs =
      Number.isFinite(gameConfig.postSpinRespinCooldownMs) && gameConfig.postSpinRespinCooldownMs > 0
        ? gameConfig.postSpinRespinCooldownMs
        : 50;
    /** Cached renderer profile (Android / low-memory flags for turbo pacing). */
    this._rendererProfile = null;
    /** @type {(() => void) | null} Pixi ticker callback that advances GSAP (see `_bindGsapToPixiTicker`). */
    this._gsapPixiTick = null;
    /** Timestamp (performance.now) before which a manual re-spin is blocked. Set at cycle end. */
    this._respinBlockUntilTs = 0;
    /** @type {Container | null} spin image + autoplay remaining counter */
    this._spinButtonRoot = null;
    /** @type {Sprite | null} */
    this._spinButtonSprite = null;
    /** @type {Text | null} */
    this._spinAutoplayRemainText = null;
    /** @type {ReturnType<typeof createArcTextController> | null} */
    this._spinTurboHint = null;
    this._spinPressAccepted = false;
    this._stopRequested = false;
    this._winRollupRaf = null;
    /** @type {(() => void) | null} */
    this._winRollupResolve = null;
    /** @type {number | null} */
    this._winRollupTarget = null;
    /** Collect-win tap is shown until rollup finishes or the player taps to skip. */
    this._winCollectPending = false;
    this._winCollectHandling = false;
    /** Absorbs duplicate pointerup from claim button + window capture on the same tap. */
    this._winCollectTapLockUntil = 0;
    /** @type {number | null} When set, win celebration skip/collect is blocked for 2s. */
    this._winCelebrationShownAt = null;
    /** Reload resume: show win animation but keep spin (no collect-hold UI). */
    this._resumeRoundSkipCollect = false;
    /** @type {(() => void) | null} */
    this._resolveWinCollectUser = null;
    /** User tapped spin during collect — chain the next spin when the round closes. */
    this._pendingSpinAfterWinClose = false;
    /** Pointer-down during win collect was on spin / quick-bet (used on pointer-up). */
    this._winCollectPointerMaySpin = false;
    /** Win celebration replay/static hold deferred until the loading overlay is gone. */
    this._deferredRoundResumeVisuals = null;
    /** @type {import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null} */
    this._roundStatusLive = null;
    /** @type {(() => void) | null} */
    this._roundStatusPageHideFlush = null;
    /** @type {number | null} */
    this._resizeRaf = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._orientTimer = null;
    /** @type {number | null} Follow-up rAF after orientationchange (iOS dimension lag). */
    this._orientRaf = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._autoplayTimer = null;
    /** Autoplay round preset picker (canvas dim + cluster). */
    this._autoplayPickerOpen = false;
    const launchVariant = resolveGameVariant(
      typeof window !== 'undefined' ? getParamsFromUrl(window.location.search).GameName : null,
    );
    /** True only for an explicit buy-bonus GameName (`CrownCrushBB`). */
    this._buyBonusEnabled = launchVariant.buyBonus;
    /** True for the 5×4 · 40-payline launch (AncientEgypt40 / CrownCrush40). */
    this._lines40Enabled = launchVariant.lines40;
    /** True for the 5×4 · 100-payline launch (AncientEgypt100 / CrownCrush100). */
    this._lines100Enabled = launchVariant.lines100;
    /** Hub GameName. Live uses URL name; 40-line → CrownCrush40; 100-line → CrownCrush100; buy bonus → CrownCrushBB. */
    this._backendGameName = launchVariant.backendGameName;
    /** @type {{ type: 1 | 2 | 3, guaranteedWins: boolean } | null} */
    this._buyBonusArmed = null;
    /** 0..2 while the buy-bonus panel is open. */
    this._buyBonusSelectedTier = 0;
    this._buyBonusGuaranteed = false;
    /** Server StartGame flag. Null means the hub did not say. */
    this._buyBonusServerAllowed = null;
    this._buyBonusConfirmOpen = false;
    /** @type {import('pixi.js').Container | null} */
    this._buyBonusButton = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._buyBonusButtonSprite = null;
    /** @type {import('pixi.js').Text | null} */
    this._buyBonusButtonLabelPrimary = null;
    /** @type {import('pixi.js').Text | null} */
    this._buyBonusButtonLabelSecondary = null;
    /** @type {import('pixi.js').Container | null} */
    this._buyBonusConfirmRoot = null;
    /** @type {import('pixi.js').Text | null} */
    this._buyBonusConfirmBody = null;
    /** null = unlimited; finite = spins left before autoplay stops */
    this._autoplayRoundsRemaining = null;
    /** @type {Container | null} */
    this._autoplayCluster = null;
    /** @type {Container | null} */
    this._autoplayPickerRow = null;
    /** @type {Graphics | null} fullscreen dim while autoplay preset picker is open */
    this._autoplayPixiDim = null;
    /** Saved `controls` child index when cluster is lifted onto `world` */
    this._autoplayClusterControlsIndex = 0;
    /** Selected preset spins for the autoplay-settings modal (null = ∞). */
    this._autoplayModalSpinsSelected = 10;
    /** Threshold: stop autoplay if balance drops by ≥ this much from start (0 = disabled). */
    this._autoplayStopOnBalanceDecreaseBy = 0;
    /** Threshold: stop autoplay if a single win is ≥ this much (0 = disabled). */
    this._autoplayStopOnSingleWinExceeds = 0;
    /** Threshold: stop autoplay if balance grew by ≥ this much from start (0 = disabled). */
    this._autoplayStopOnBalanceIncreaseBy = 0;
    /** Balance recorded when current autoplay run begins (used by the threshold checks). */
    this._autoplayStartBalance = null;
    /** Largest single-spin win observed during current autoplay run. */
    this._autoplayLastSingleWin = 0;
    /** @type {Container | null} Wrapper holding the original sound/quick-spin switches view. */
    this._settingsViewContainer = null;
    /** @type {Container | null} Wrapper holding the autoplay-settings view. */
    this._autoplayViewContainer = null;
    /** @type {Text | null} */
    this._settingsHeadingText = null;
    /** Bottom tab centres for the settings modal (design px); updated for portrait mobile layout. */
    this._settingsTabCenters = { left: 94, mid: 333, right: 572 };
    /** Active tab name for repositioning the underline after layout sync. */
    this._settingsModalActiveTab = 'settings';
    /** @type {boolean | undefined} Last portrait-mobile flag applied by {@link GameApp#_syncSettingsModalLayout}. */
    this._settingsLayoutMobile = undefined;
    /** @type {object | null} Pixi nodes mutated when switching mobile/desktop settings modal layout. */
    this._settingsModalRefs = null;
    /** @type {object | null} Autoplay tab controls repositioned with panel width. */
    this._autoplayModalLayoutParts = null;
    /** @type {object | null} Info tab scroll/mask targets for layout sync. */
    this._infoSettingsLayoutRefs = null;
    /** Total scrollable content height in the info tab (set in {@link GameApp#_buildInfoSettingsView}). */
    this._infoScrollContentHeight = 0;
    /** @type {{ minY: number, maxY: number, naturalTop: number } | null} Clamps for info scroll; rebuilt on resize. */
    this._infoScrollClamp = null;
    /** @type {object | null} Autoplay tab scroll/mask targets for layout sync. */
    this._autoplaySettingsLayoutRefs = null;
    /** Total scrollable content height in the autoplay tab. */
    this._autoplayScrollContentHeight = 0;
    /** @type {{ minY: number, maxY: number, naturalTop: number } | null} Clamps for autoplay scroll; rebuilt on resize. */
    this._autoplayScrollClamp = null;
    /** @type {Container[]} Preset chip buttons in autoplay-settings modal. */
    this._autoplayChipButtons = [];
    /** @type {((bet: number) => void) | null} */
    this._autoplayChipsRefresh = null;
    /** @type {((balance: number, bet: number) => void) | null} */
    this._autoplaySidebarRefresh = null;
    /** @type {Array<() => void>} Per-slider refresh callbacks (rebuild on bet/balance change). */
    this._autoplaySliderRefreshers = [];
    /** @type {import('pixi.js').Container | null} */
    this._loadingRoot = null;
    /** @type {import('pixi.js').Container | null} */
    this._loadingContent = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._loadingBgSprite = null;
    /** @type {import('pixi.js').Texture | null} */
    this._loadingBgTexDesktop = null;
    /** @type {import('pixi.js').Texture | null} */
    /** @type {import('pixi.js').Texture | null} */
    this._loadingBgTexMobile = null;
    /** @type {import('pixi.js').Texture | null} */
    this._loadingPoweredByTex = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._loadingBarGlow = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._loadingBarFillMask = null;
    /** @type {import('pixi.js').Container | null} */
    this._loadingBarFillRoot = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._loadingBarFillSprite = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._loadingBarFillGloss = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._loadingBarTrack = null;
    /** @type {{ stop: () => void } | null} */
    this._loadingBarShimmerFx = null;
    /** @type {import('pixi.js').Text | null} */
    this._loadingPercentText = null;
    /** @type {import('pixi.js').Text | null} */
    this._loadingCaptionText = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._loadingOverlay = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._loadingPoweredBySprite = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._loadingGameLogoSprite = null;
    /** @type {((ticker: import('pixi.js').Ticker) => void) | null} */
    this._loadingProgressTick = null;
    this._loadingProgress = 0;
    /** Real bootstrap fraction (0–1); bar eases toward this each frame. */
    this._loadingProgressTarget = 0;
    this._loadingBarW = LOADING_BAR_W_MAX;
    this._loadingBarH = LOADING_BAR_H_MAX;
    /** @type {number | null} */
    this._loadingShownAt = null;
    /** Portrait phones: uniform cover canvas while loading; cleared in {@link #_hideLoading}. */
    this._loadingCanvasUniform = true;
    /** @type {Container | null} */
    this._canvasControls = null;
    /** Portrait phone: sound/menu/fullscreen were moved off the bottom bar (legacy top-right path). */
    this._canvasIconsTopChrome = false;
    this._controlObjectUrls = [];
    /** @type {Sprite | null} */
    this._frameSprite = null;
    /** @type {Sprite | null} */
    this._glowSprite = null;
    /** @type {Sprite | null} */
    this._matrixBgSprite = null;
    /** Uniform scale for reel card backplate (matrix.png) only. */
    this._matrixBgScale = 1.12;
    /** @type {Sprite | null} */
    this._slotBackgroundLinesSprite = null;
    /** Uniform scale for reel divider lines only (matrix unchanged). */
    this._slotBackgroundLinesScale = 1.6;
    /** Vertical nudge for lines only (negative = up). */
    this._slotBackgroundLinesOffsetY = -44;
    /** @type {Sprite | null} */
    this._logoSprite = null;
    /** @type {import('./effects/cabinetJuice.js').CabinetJuice | null} */
    this._cabinetJuice = null;
    /** @type {import('./effects/ambientIdleFx.js').AmbientIdleFx | null} */
    this._ambientFx = null;
    /** @type {import('pixi.js').Container | null} Reels + frame + matrix/glow; shrinkable on phones — HUD sibling stays unscaled. */
    this._gameVisualLayer = null;
    /** Uniform scale for {@link #_gameVisualLayer} when {@link GameApp#_isPortraitMobileViewport} (buttons unchanged). */
    this._mobileGameVisualScale = 0.80;
    /** @deprecated Use {@link GameApp#_getMobileGameVisualLiftY} — kept for devtools overrides. */
    this._mobileGameVisualLiftY = this._usesCompactCabinet()
      ? MOBILE_GAME_VISUAL_LIFT_BASE_COMPACT
      : MOBILE_GAME_VISUAL_LIFT_BASE;
    /** Viewport-fit factor for mobile HUD controls (0..1), recalculated on resize. */
    this._mobileHudFitScale = 1;
    this._slotRootBaseX = null;
    this._slotRootBaseY = null;
    /** Snapshot of canvas control positions for restoring desktop after mobile layout. */
    this._canvasDesktopLayout = null;
    /** Desktop-only sizes/fonts for {@link GameApp#_restoreMobileCanvasChrome}. */
    this._desktopChromeDefaults = null;
    /** @type {Container | null} Vertical-only merged top stat row background. */
    this._portraitTopStatsBar = null;
    /** @type {Sprite | null} */
    this._portraitTopStatsBgSprite = null;
    /** @type {Texture | null} */
    this._portraitTopStatsDefaultTex = null;
    /** @type {Texture | null} */
    this._mobilePortraitTopStatsTex = null;
    /** @type {Text | null} */
    this._portraitBalanceValueText = null;
    /** @type {Text | null} */
    this._portraitWinValueText = null;
    /** @type {Text | null} */
    this._portraitTopPromptText = null;
    this._betPromptVisible = true;
    this._winLinePromptActive = false;
    /** Turbo plate height (logical px) — keeps turbo button glued under SPIN when repositioning. */
    this._betSpeedPlateH = 0;
    const compactCabinet = this._usesCompactCabinet();
    this._frameControl = {
      padX: 100,
      padYTop: 96,
      padYBottom: 70,
      offsetX: 0,
      offsetY: compactCabinet ? -15 : -14,
      scale: compactCabinet ? 1.07 : 1.10,
    };
    /** Quick-bet strip: drag handlers attached once (see {@link _ensureCanvasQbScrollHandlers}). */
    this._qbScrollHandlersInstalled = false;
    /** @type {import('pixi.js').Container | null} */
    this._canvasQbScrollWrapper = null;
    this._canvasQbBetCount = 0;
    /** @type {number | null} */
    this._qbDragAnchorGlobalX = null;
    /** @type {number | null} */
    this._qbDragAnchorGlobalY = null;
    /** @type {number | null} */
    this._qbDragLastGlobalX = null;
    /** @type {number | null} */
    this._qbDragLastGlobalY = null;
    this._qbDragScrollCommitted = false;
    /** CSS-pixel finger drift that distinguishes a “tap” (place bet) from a
     *  “drag” (scroll strip, no bet). Kept in CSS px and converted to stage
     *  units in {@link _qbTapDriftStagePx} so it feels identical on phones,
     *  tablets and desktop instead of varying with the viewport scale. */
    this._qbTapDriftCssPx = 6;
    /** Canvas DOM listeners while dragging quick bets (pointer may leave the masked strip). */
    this._qbCanvasDragListening = false;
    /** True if current pointer gesture on the strip was a drag (suppress bet on card pointerup). */
    this._qbGestureWasDrag = false;
    /** Flick coast velocity for quick-bet strip (qbGroup-local px/ms). */
    this._qbScrollVelocity = 0;
    this._qbScrollVelocityLastT = 0;
    /** @type {number | null} */
    this._qbInertiaRaf = null;
    this._qbInertiaLastT = 0;
    /** @type {number | null} Arrow nudge: coast stops at this qbGroup-local scroll offset. */
    this._qbInertiaTargetX = null;
    /** qbGroup-local scroll offset at the last quick-bet wheel tick (chip stride crossings). */
    this._qbWheelTickAnchor = null;
    /** When false, inertia coast is silent (arrow nudge — one uiClick on press). */
    this._qbInertiaWheelTicks = false;
    this._onResize = () => {
      if (this._resizeRaf != null) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => {
        this._resizeRaf = null;
        this._relayoutForViewportChange();
      });
    };
    this._onOrientation = () => {
      this._scheduleOrientationRelayout();
    };
  }

  /** True when the OS reports portrait while a layout rect still looks landscape (rotation lag). */
  _viewportRectLooksStale(rectW, rectH) {
    if (!(rectW >= 8 && rectH >= 8)) return true;
    const innerPortrait =
      typeof window !== 'undefined' && window.innerHeight > window.innerWidth;
    const rectPortrait = rectH > rectW;
    if (innerPortrait !== rectPortrait) return true;
    const screenType =
      typeof screen !== 'undefined' ? screen.orientation?.type : '';
    if (screenType) {
      const screenPortrait = screenType.startsWith('portrait');
      if (screenPortrait !== rectPortrait) return true;
    }
    return false;
  }

  _readContainerSize() {
    const barReserve = 0;
    const pick = (w, h) => ({
      width: Math.max(2, Math.floor(w)),
      height: Math.max(2, Math.floor(h) - barReserve),
    });

    const el = this._containerEl;
    let rectW = 0;
    let rectH = 0;
    if (el) {
      const r = el.getBoundingClientRect();
      rectW = r.width;
      rectH = r.height;
    }

    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const touch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

    if (vv && vv.width > 0 && vv.height > 0) {
      if (touch && this._viewportRectLooksStale(rectW, rectH)) {
        return pick(vv.width, vv.height);
      }
      if (rectW < 8 || rectH < 8) {
        return pick(vv.width, vv.height);
      }
    }

    if (rectW >= 2 && rectH >= 2) return pick(rectW, rectH);

    if (typeof window !== 'undefined') {
      const innerW = window.innerWidth;
      const innerH = window.innerHeight;
      if (innerW >= 2 && innerH >= 2) return pick(innerW, innerH);
    }

    return { width: 2, height: 2 };
  }

  /** Hide transient modals and refit the stage — used by resize and orientation handlers. */
  _relayoutForViewportChange() {
    this._hideSettingsPanel();
    this._hideBetPopup();
    this._hideAutoplayPicker();
    this._fitRendererToContainer();
  }

  /**
   * Mobile browsers often report stale container sizes for 1–3 frames after rotation.
   * Run an immediate pass plus rAF and a short delayed pass so modals/HUD pick up portrait metrics.
   */
  _scheduleOrientationRelayout() {
    if (this._orientTimer) clearTimeout(this._orientTimer);
    if (this._orientRaf != null) cancelAnimationFrame(this._orientRaf);
    this._relayoutForViewportChange();
    this._orientRaf = requestAnimationFrame(() => {
      this._orientRaf = null;
      this._relayoutForViewportChange();
      this._orientTimer = setTimeout(() => {
        this._orientTimer = null;
        this._relayoutForViewportChange();
      }, 280);
    });
  }

  /** Keep settings modal shell scale + inner layout aligned with the active CSS viewport. */
  _syncSettingsModalForViewport() {
    if (!this._settingsPanelRoot) return;
    const { width: cw, height: ch } = this._readContainerSize();
    const sx = cw / 1922;
    const sy = ch / 1080;
    this._applySettingsViewportScale(sx, sy);
    this._syncSettingsModalLayout();
  }

  _fitRendererToContainer() {
    const canvas = this.app?.canvas;
    const containerEl = this._containerEl;
    if (!canvas || !containerEl) return;
    const { width: cw, height: ch } = this._readContainerSize();
    const designW = 1922;
    const designH = 1080;
    const scaleX = cw / designW;
    const scaleY = ch / designH;
    let fitScaleX = scaleX;
    let fitScaleY = scaleY;
    if (this._loadingRoot && this._loadingUseUniformCanvas()) {
      const u = Math.max(scaleX, scaleY);
      const scaledW = designW * u;
      const scaledH = designH * u;
      const tx = (cw - scaledW) / 2;
      const ty = (ch - scaledH) / 2;
      canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${u}, ${u})`;
      this._loadingRoot.scale.set(1, 1);
      this._loadingRoot.x = 0;
      this._loadingRoot.y = 0;
    } else if (this._isDesktopFullscreen()) {
      /* Letterbox the fixed 1922×1080 stage so fullscreen aspect changes do not
       * stretch the HUD off the bottom / side edges. */
      const u = Math.min(scaleX, scaleY);
      const scaledW = designW * u;
      const scaledH = designH * u;
      const tx = (cw - scaledW) / 2;
      const ty = (ch - scaledH) / 2;
      canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${u}, ${u})`;
      fitScaleX = u;
      fitScaleY = u;
    } else {
      canvas.style.transform = `translate(0px, 0px) scale(${scaleX}, ${scaleY})`;
    }
    canvas.style.transformOrigin = '0 0';
    this._lastScales = { x: fitScaleX, y: fitScaleY };
    this._syncPortraitShellClass();
    this._syncStageBackdropForViewport();
    this._syncLandscapeBlocker();
    this.layout();
    this._applySlotAspectLock(fitScaleX, fitScaleY);
    this._applyMobileGameVisualScale();
    this._applyFrameLayout();
    this._applyCanvasControlsViewportLayout();
    this._syncLinesPanelForOrientation();
    this._applyBetPopupViewportScale(fitScaleX, fitScaleY);
    this._syncBetPopupLayout();
    this._syncSettingsModalForViewport();
    this._layoutBuyBonusConfirm();
    if (this._loadingRoot) this._layoutLoadingScreen();
  }

  _loadingUseUniformCanvas() {
    return !!(this._loadingCanvasUniform && this._isPortraitMobileViewport());
  }

  _pickBackdropTextureForViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    const portrait = ch > cw;
    // Portrait tablets use the same mobile shell/backdrop as phones.
    if (portrait) return this._bgTexMobile || this._bgTexDesktop || Texture.EMPTY;
    return this._bgTexDesktop || Texture.EMPTY;
  }

  _syncModalBackdropSprite(sprite, boxW, boxH) {
    if (!sprite) return;
    const tex = this._pickBackdropTextureForViewport();
    if (!tex?.width) return;
    layoutModalBackdropCover(sprite, tex, boxW, boxH);
  }

  _syncSettingsPanelBackdrop() {
    const backdrop = this._settingsPanelBackdrop;
    const mask = this._settingsPanelBackdropMask;
    const r = this._settingsModalRefs;
    if (!backdrop || !r?.panel) return;

    const mobile = this._isPortraitMobileViewport();
    const tablet = !mobile && this._isTabletViewport();
    const compact = mobile || tablet;
    const compactMetrics = compact
      ? this._getSettingsPanelCompactMetrics(mobile ? 'mobile' : 'tablet')
      : null;
    const panelW = compactMetrics ? compactMetrics.panelW : SETTINGS_PANEL_DESKTOP.w;
    const panelH = compactMetrics ? compactMetrics.panelH : SETTINGS_PANEL_DESKTOP.h;
    const cornerR = compact ? Math.round(20 * (compactMetrics?.modalFit ?? 1)) : 20;

    this._syncModalBackdropSprite(backdrop, panelW, panelH);
    if (mask) {
      mask.clear();
      mask.roundRect(0, 0, panelW, panelH, cornerR).fill(0xffffff);
    }
  }

  _syncStageBackdropForViewport() {
    const spr = this._backdropSprite;
    if (!spr) return;
    const W = 1922;
    const H = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const portrait = ch > cw;
    const tex = this._pickBackdropTextureForViewport();
    if (tex && spr.texture !== tex) spr.texture = tex;
    if (portrait) {
      // Portrait backgrounds must always fill the entire visible screen area.
      spr.scale.set(1);
      spr.x = 0;
      spr.y = 0;
      spr.width = W;
      spr.height = H;
    } else if (tex?.width > 0 && tex?.height > 0) {
      const s = Math.max(W / tex.width, H / tex.height);
      spr.scale.set(s);
      spr.x = (W - tex.width * s) / 2;
      spr.y = (H - tex.height * s) / 2;
    } else {
      spr.scale.set(1);
      spr.x = 0;
      spr.y = 0;
      spr.width = W;
      spr.height = H;
    }
  }

  /**
   * Matches index.html: `html/body.slot-portrait-mobile` + warm tomb-brown stage, bottom gradient (::before).
   */
  _syncPortraitShellClass() {
    if (typeof document === 'undefined') return;
    const mobile = this._isPortraitMobileViewport();
    document.documentElement.classList.toggle('slot-portrait-mobile', mobile);
    if (this._containerEl) {
      this._containerEl.classList.toggle('slot-portrait-mobile', mobile);
      this._containerEl.style.background = mobile ? themeHex(T.panelDeep) : '#000';
    }
  }

  /** Portrait phone/tablet — both use the mobile HUD/shell (not legacy tablet desktop chrome). */
  _isPortraitMobileViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    return ch > cw;
  }

  /**
   * Portrait phone: lift reel cabinet toward the top; narrower widths get more lift.
   * @returns {number} logical Y offset (negative = up), 0 when not portrait mobile
   */
  _getMobileGameVisualLiftY() {
    if (!this._isPortraitMobileViewport()) return 0;
    const { width: cw } = this._readContainerSize();
    const t = Math.max(0, Math.min(1, (760 - cw) / (760 - 320)));
    const compact = this._usesCompactCabinet();
    const base = this._mobileGameVisualLiftY
      ?? (compact ? MOBILE_GAME_VISUAL_LIFT_BASE_COMPACT : MOBILE_GAME_VISUAL_LIFT_BASE);
    const extra = compact
      ? MOBILE_GAME_VISUAL_LIFT_EXTRA_NARROW_COMPACT
      : MOBILE_GAME_VISUAL_LIFT_EXTRA_NARROW;
    return base + extra * t;
  }

  /** 0..1 how “narrow” the portrait phone is (760px wide → 0, 320px → 1). Wider portrait tablets → 0. */
  _getNarrowPortraitWidthT() {
    if (!this._isPortraitMobileViewport()) return 0;
    const { width: cw } = this._readContainerSize();
    return Math.max(0, Math.min(1, (760 - cw) / (760 - 320)));
  }

  /**
   * Portrait phone: rightmost reel-root-local X that still maps inside the CSS viewport
   * with a gap from the physical right edge. Derived from the live layout chain
   * ({@link #layout} world scale + {@link #_applySlotAspectLock}) so portrait width
   * boost does not push the bottom HUD past the screen edge.
   * @param {number} [marginCssPx]
   * @returns {number}
   */
  _getPortraitPhoneBottomHudMaxRightX(marginCssPx = 20) {
    if (!this._isPortraitMobileViewport() || !this.slotMachine) return Infinity;
    const { width: cw } = this._readContainerSize();
    const scaleX = this._lastScales?.x;
    const worldS = this.world?.scale?.x;
    if (!(cw > 0 && scaleX > 0 && worldS > 0)) return Infinity;

    const slotRoot = this.slotMachine.reelContainer?.root;
    const cabinetGroup = this.slotMachine._cabinetGroup;
    if (!slotRoot || !cabinetGroup) return Infinity;

    const margin = Math.max(0, marginCssPx);
    const stageRight = (cw - margin) / scaleX;
    const pivot = 1922 / 2;
    const cabX = cabinetGroup.x;
    const cabS = cabinetGroup.scale?.x ?? 1;
    const rootX = slotRoot.x;
    const rootS = slotRoot.scale?.x ?? 1;
    const chainS = cabS * rootS;
    if (!(chainS > 0)) return Infinity;

    const inner = (stageRight - pivot) / worldS + pivot;
    return (inner - cabX - rootX) / chainS;
  }

  /**
   * Portrait phone: leftmost reel-root-local X that still maps inside the CSS viewport
   * with a gap from the physical left edge.
   * @param {number} [marginCssPx]
   * @returns {number}
   */
  _getPortraitPhoneBottomHudMinLeftX(marginCssPx = 10) {
    if (!this._isPortraitMobileViewport() || !this.slotMachine) return 0;
    const scaleX = this._lastScales?.x;
    const worldS = this.world?.scale?.x;
    if (!(scaleX > 0 && worldS > 0)) return 0;

    const slotRoot = this.slotMachine.reelContainer?.root;
    const cabinetGroup = this.slotMachine._cabinetGroup;
    if (!slotRoot || !cabinetGroup) return 0;

    const margin = Math.max(0, marginCssPx);
    const stageLeft = margin / scaleX;
    const pivot = 1922 / 2;
    const cabX = cabinetGroup.x;
    const cabS = cabinetGroup.scale?.x ?? 1;
    const rootX = slotRoot.x;
    const rootS = slotRoot.scale?.x ?? 1;
    const chainS = cabS * rootS;
    if (!(chainS > 0)) return 0;

    const inner = (stageLeft - pivot) / worldS + pivot;
    return (inner - cabX - rootX) / chainS;
  }

  /**
   * Portrait phone QB strip: reel-root X for a fixed CSS left margin (same physical
   * inset on every phone width; derived from the live transform chain).
   * @returns {number}
   */
  _resolvePortraitPhoneQuickBetLeftX() {
    const marginCss = PORTRAIT_TUNING.phone.quickBetLeftMarginCss ?? 12;
    const localX = this._getPortraitPhoneBottomHudMinLeftX(marginCss);
    if (Number.isFinite(localX)) return Math.ceil(localX);
    const scaleX = this._lastScales?.x;
    return scaleX > 0 ? Math.ceil(marginCss / scaleX) : marginCss;
  }

  /**
   * Portrait phone: capped right bound for QB strip + corner icons only.
   * Full viewport solve over-pulls (~150 logical px); cap keeps a modest visible margin.
   * @param {number} reelBlockW
   * @returns {number}
   */
  _resolvePortraitPhoneBottomHudMaxRight(reelBlockW) {
    const p = PORTRAIT_TUNING.phone;
    const marginCss = p.bottomMenuRightMarginCss ?? 10;
    const hudMaxRight = this._getPortraitPhoneBottomHudMaxRightX(marginCss);
    if (!Number.isFinite(hudMaxRight)) return reelBlockW;
    const overflow = Math.max(0, reelBlockW - hudMaxRight);
    const cap = Math.round(
      (p.bottomMenuMaxPullbackPx ?? 28) +
        this._getNarrowPortraitWidthT() * (p.bottomMenuMaxPullbackNarrowPx ?? 20),
    );
    return reelBlockW - Math.min(overflow, cap);
  }

  /** Clamped portrait-phone HUD scale used by balance, quick bets, and spin row. */
  _getMobileHudFit() {
    const v = this._mobileHudFitScale ?? 1;
    return Math.max(MOBILE_HUD_FIT_MIN, Math.min(MOBILE_HUD_FIT_MAX, v));
  }

  /** Portrait phone: quick bets scroll vertically in a right-edge rail (not the bottom strip). */
  _isPortraitMobileVerticalQuickBets() {
    return this._isPortraitMobileViewport();
  }

  /**
   * Portrait phone: merged balance bar X — same horizontal center as the game ({@link reelBlockW} / 2).
   * @param {number} reelBlockW
   * @param {number} mergedW
   * @returns {number}
   */
  _resolvePortraitPhoneMergedStatsBarX(reelBlockW, mergedW) {
    const p = PORTRAIT_TUNING.phone;
    let x = Math.round((reelBlockW - mergedW) / 2);
    const extra = p.hudCenterShiftLeftPx ?? 0;
    if (extra) x -= extra;
    return x;
  }

  /**
   * Portrait phone HUD nudges after layout: SPIN row down only (X stays on game center).
   * Bottom-left mute stays put; menu is mirrored right via {@link #_layoutPortraitPhoneBottomLeftChrome}.
   */
  _applyPortraitPhoneHudNudges() {
    if (!this._isPortraitMobileViewport()) return;
    const p = PORTRAIT_TUNING.phone;

    const spinDy = p.spinRowExtraDownNudgePx ?? 0;
    if (spinDy) {
      for (const node of [
        this._spinButtonRoot,
        this._autoplayCluster,
        this._betPopupTriggerBtn,
        this._betSpeedWrap,
        this._buyBonusButton,
      ]) {
        if (!node) continue;
        node.y += spinDy;
      }
    }
  }

  /** @returns {'x' | 'y'} */
  _getQbScrollAxis() {
    return this._isPortraitMobileVerticalQuickBets() ? 'y' : 'x';
  }

  _applyQbScrollWrapperOffset() {
    const sw = this._canvasQbScrollWrapper;
    if (!sw) return;
    const v = this._qbScrollX || 0;
    if (this._getQbScrollAxis() === 'y') {
      sw.x = 0;
      sw.y = v;
    } else {
      sw.x = v;
      sw.y = 0;
    }
  }

  _resetQbWheelTickAnchor(scroll) {
    this._qbWheelTickAnchor = Number.isFinite(scroll) ? scroll : this._qbScrollX || 0;
  }

  /**
   * Layer uiClick ticks as the quick-bet strip crosses chip strides (drag / inertia coast).
   * @param {number} prevScroll qbGroup-local offset before this frame's move
   * @param {number} newScroll qbGroup-local offset after
   */
  _playQbWheelTicksForScroll(prevScroll, newScroll) {
    if (this._qbGesturesLocked()) return;
    const stride = this._qbChipStride;
    if (!stride || stride <= 0) return;
    if (!Number.isFinite(prevScroll) || !Number.isFinite(newScroll)) return;
    if (Math.abs(newScroll - prevScroll) < 0.5) return;

    let anchor = this._qbWheelTickAnchor;
    if (anchor == null || !Number.isFinite(anchor)) {
      anchor = prevScroll;
    }

    const maxTicks = 6;
    let ticks = 0;
    const stepPx = Math.abs(newScroll - prevScroll);
    const vol = Math.min(0.52, 0.26 + stepPx * 0.012);

    while (ticks < maxTicks) {
      const delta = newScroll - anchor;
      if (Math.abs(delta) < stride * 0.85) break;
      sound.playQuickBetWheelTick({ volume: vol });
      anchor += Math.sign(delta) * stride;
      ticks++;
    }
    this._qbWheelTickAnchor = anchor;
  }

  /** Move quick-bet strip back under the bottom bar (desktop / landscape). */
  _restoreQuickBetsToBottomBar() {
    const bottomBar = this._canvasBottomBar;
    const qbGroup = this._canvasQbGroup;
    const qbMask = this._canvasQbMaskG;
    if (!bottomBar || !qbGroup) return;
    const maskIdx = bottomBar.getChildIndex(qbMask);
    const groupIdx = bottomBar.getChildIndex(qbGroup);
    const insertAt = maskIdx >= 0 ? maskIdx : groupIdx >= 0 ? groupIdx : bottomBar.children.length;
    if (qbMask && qbMask.parent !== bottomBar) {
      qbMask.parent?.removeChild(qbMask);
      bottomBar.addChildAt(qbMask, insertAt);
    }
    if (qbGroup.parent !== bottomBar) {
      qbGroup.parent?.removeChild(qbGroup);
      bottomBar.addChild(qbGroup);
    }
    if (qbMask) qbMask.mask = null;
    qbGroup.mask = qbMask;
  }

  /**
   * Portrait phone: SPIN / autoplay / bet control sizes (shared scale from {@link #_getMobileHudFit}).
   * @returns {{ spin: number, side: number, spinHalf: number, gap: number }}
   */
  _getPortraitPhoneMobileControlSizes() {
    const hudFit = this._getMobileHudFit();
    const scale = MOBILE_CONTROL_SCALE_BASE * hudFit;
    const spin = Math.round(120 * scale);
    const side = Math.round(70 * scale);
    const gap = PORTRAIT_TUNING.phone.spinSideGapPx ?? MOBILE_SPIN_SIDE_GAP;
    return { spin, side, spinHalf: spin / 2, gap };
  }

  /**
   * Portrait phone: vertical quick-bet chip size — width matches autoplay / bet; height keeps chip aspect.
   * @param {{ forRailHeight?: boolean }} [options] When true, full-size metrics for the masked rail height (no peek scale).
   * @returns {{ btnW: number, btnH: number, gap: number, stride: number }}
   */
  _getPortraitPhoneVerticalQuickBetChipMetrics(options = {}) {
    const { side } = this._getPortraitPhoneMobileControlSizes();
    const gap = 8;
    const aspect = MOBILE_QB_CHIP_REF_H / MOBILE_QB_CHIP_REF_W;
    let btnW = side;
    let btnH = Math.max(50, Math.round(side * aspect));
    if (!options.forRailHeight) {
      const scale = PORTRAIT_TUNING.phone.quickBetVerticalChipScale ?? 0.9;
      if (scale > 0 && scale < 1) {
        btnW = Math.max(40, Math.round(btnW * scale));
        btnH = Math.max(44, Math.round(btnH * scale));
      }
    }
    return { btnW, btnH, gap, stride: btnH + gap };
  }

  /**
   * Portrait phone: SPIN half-size + side gap (mirrors {@link #_applyMobileCanvasChrome}).
   * @returns {{ spinHalf: number, gap: number }}
   */
  _getPortraitPhoneSpinSideGapMetrics() {
    const { spinHalf, gap } = this._getPortraitPhoneMobileControlSizes();
    return { spinHalf, gap };
  }

  /**
   * Portrait phone: left X for the vertical quick-bet rail — same edge gap from SPIN as autoplay/bet.
   * @returns {number}
   */
  _resolvePortraitPhoneQuickBetRailColX() {
    const spin = this._spinButtonRoot;
    if (!spin) return 0;
    const { spinHalf, gap } = this._getPortraitPhoneSpinSideGapMetrics();
    return Math.round(spin.x + spinHalf + gap);
  }

  /**
   * Portrait phone: bottom Y of the scaled cabinet frame in reel-root / controls space.
   * Uses the live {@link #_gameVisualLayer} transform so the rail clears frame art
   * (unlike {@link #_computePortraitCanvasHudLayout} bottomBarY which can sit higher).
   * @returns {number}
   */
  _resolvePortraitPhoneGameVisualBottomY() {
    const layer = this._gameVisualLayer;
    const fr = this._frameSprite;
    if (layer && fr && fr.height > 0) {
      const g = layer.scale?.y ?? 1;
      return layer.y + (fr.y + fr.height) * g;
    }
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    const cabinetScale = this._mobileGameVisualScale ?? 1;
    const liftY = this._getMobileGameVisualLiftY();
    return (reelBlockH * (1 + cabinetScale)) / 2 + liftY;
  }

  /**
   * Portrait phone: top Y for the vertical quick-bet rail — aligned with the bet-options button.
   * @returns {number}
   */
  _resolvePortraitPhoneQuickBetRailTopY() {
    const betBtn = this._betPopupTriggerBtn;
    if (betBtn) return Math.round(betBtn.y);

    const spin = this._spinButtonRoot;
    if (!spin) return 0;
    const { side: mobileSide } = this._getPortraitPhoneMobileControlSizes();
    const p = PORTRAIT_TUNING.phone;
    const autoplayDown = p.autoplayBtnDownNudgePx ?? 14;
    const betAboveGap = p.betBtnAboveAutoplayGapPx ?? 8;
    const autoplayY = spin.y - mobileSide / 2 + autoplayDown;
    return Math.round(autoplayY - mobileSide - betAboveGap);
  }

  /**
   * Portrait phone: menu icon top Y in `_canvasControls` space (null if not laid out).
   * @returns {number | null}
   */
  _resolvePortraitPhoneMenuIconTopY() {
    const bottomBar = this._canvasBottomBar;
    const menu = this._canvasIconSprites?.[3];
    if (!bottomBar || !menu?.visible) return null;
    return bottomBar.y + menu.y;
  }

  /**
   * Portrait phone: bottom Y for the vertical quick-bet rail (right edge, above corner icons).
   * @returns {number}
   */
  _resolvePortraitPhoneQuickBetRailBottomY() {
    const p = PORTRAIT_TUNING.phone;
    const pad = p.quickBetVerticalBottomPad ?? 18;
    const fallback = this._getPortraitCornerIconsMaxBottomY() - pad;
    const menuTopY = this._resolvePortraitPhoneMenuIconTopY();
    if (menuTopY == null) return fallback;
    return Math.min(fallback, menuTopY - pad);
  }

  /**
   * Portrait phone: quick-bet rail viewport height — scales with SPIN, capped above menu icon.
   * @param {number} naturalH
   * @returns {number}
   */
  _resolvePortraitPhoneQuickBetRailVisibleH(naturalH) {
    const p = PORTRAIT_TUNING.phone;
    const chip = this._getPortraitPhoneVerticalQuickBetChipMetrics({ forRailHeight: true });
    const gap = chip.gap;
    const stride = chip.stride;
    const minChips = Math.max(2, p.quickBetVerticalMinVisibleChips ?? 3);
    const { spin } = this._getPortraitPhoneMobileControlSizes();
    const ratioFromChips =
      spin > 0 ? (minChips * stride - gap) / spin : minChips * (70 / 120) * (MOBILE_QB_CHIP_REF_H / MOBILE_QB_CHIP_REF_W);
    const ratio = p.quickBetVerticalSectionSpinRatio ?? ratioFromChips;
    const spinLinkedH = Math.max(stride, Math.round(spin * ratio));
    return Math.min(spinLinkedH, Math.max(0, naturalH));
  }

  /**
   * Portrait phone: vertical quick-bet rail — right edge, top aligned with bet-options button.
   * Call after spin/bet chrome and {@link #_applyPortraitPhoneHudNudges} have run.
   */
  _layoutPortraitMobileVerticalQuickBets() {
    const controls = this._canvasControls;
    const bottomBar = this._canvasBottomBar;
    const qbGroup = this._canvasQbGroup;
    const qbMask = this._canvasQbMaskG;
    if (!this._isPortraitMobileVerticalQuickBets()) {
      this._restoreQuickBetsToBottomBar();
      return;
    }
    if (!controls || !bottomBar || !qbGroup) return;

    const show = this._shouldShowQuickBets();
    qbGroup.visible = show;
    qbGroup.eventMode = show ? 'static' : 'none';
    if (!show) {
      if (qbMask) qbMask.visible = false;
      return;
    }

    if (qbMask && qbMask.parent !== controls) {
      qbMask.parent?.removeChild(qbMask);
      controls.addChild(qbMask);
    }
    if (qbGroup.parent !== controls) {
      qbGroup.parent?.removeChild(qbGroup);
      controls.addChild(qbGroup);
    }

    const colTopY = this._resolvePortraitPhoneQuickBetRailTopY();
    const colBottomY = this._resolvePortraitPhoneQuickBetRailBottomY();
    const naturalH = colBottomY - colTopY;
    const visibleH = this._resolvePortraitPhoneQuickBetRailVisibleH(naturalH);

    const btnW = this._qbChipW ?? MOBILE_QB_CHIP_REF_W;
    const colW = btnW;
    const colX = this._resolvePortraitPhoneQuickBetRailColX();

    if (qbMask) {
      qbMask.clear();
      qbMask.rect(0, 0, colW, visibleH).fill(0xffffff);
      qbMask.x = colX;
      qbMask.y = colTopY;
      qbMask.visible = true;
      qbMask.eventMode = 'none';
    }

    qbGroup.mask = qbMask;
    qbGroup.x = colX;
    qbGroup.y = colTopY;
    qbGroup.zIndex = 18;

    this._qbScrollVisibleW = visibleH;
    this._syncPortraitMobileQuickBetScroll();
  }

  /** Sound + menu corner icons on phone-class mobile viewports. */
  _getMobileBottomMenuIconSize(hudFit = this._getMobileHudFit()) {
    const fit = Math.max(MOBILE_HUD_FIT_MIN, Math.min(MOBILE_HUD_FIT_MAX, hudFit));
    return Math.max(MOBILE_BOTTOM_MENU_ICON_MIN, Math.round(MOBILE_BOTTOM_MENU_ICON_BASE * fit));
  }

  /**
   * Portrait stacked layout: lowest allowed Y for the bottom edge of corner chrome (controls space).
   * @returns {number}
   */
  _getPortraitCornerIconsMaxBottomY() {
    const { height: ch } = this._readContainerSize();
    const mobile = this._isPortraitMobileViewport();
    const spacingBias = mobile ? Math.max(-1, Math.min(1, (ch - 860) / 220)) : 0;
    const pad = mobile
      ? Math.max(6, Math.round(PORTRAIT_CORNER_ICONS_BOTTOM_PAD + spacingBias * 3))
      : PORTRAIT_CORNER_ICONS_BOTTOM_PAD + 6;
    return PORTRAIT_DESIGN_H - pad;
  }

  /**
   * @param {number} iconY `_canvasBottomBar`-local top of corner icons
   * @param {number} iconSize
   * @returns {number}
   */
  _clampPortraitCornerIconY(iconY, iconSize) {
    const bottomBar = this._canvasBottomBar;
    if (!bottomBar || !(iconSize > 0)) return iconY;
    const maxBottom = this._getPortraitCornerIconsMaxBottomY();
    const iconBottom = bottomBar.y + iconY + iconSize;
    if (iconBottom <= maxBottom) return iconY;
    /* Visibility wins on critical viewports — pull icons up into the 1080px stage. */
    return maxBottom - bottomBar.y - iconSize;
  }

  /**
   * Portrait stacked HUD: left/right edges for corner chrome in controls (reel-root) space.
   * Mobile uses the scaled frame bounds so icons stay on screen corners as width changes.
   * @param {number} reelBlockW
   * @returns {{ left: number, right: number }}
   */
  _resolvePortraitCornerIconXBounds(reelBlockW) {
    const mobile = this._isPortraitMobileViewport();
    const layer = this._gameVisualLayer;
    const fr = this._frameSprite;
    if (mobile && layer && fr?.width > 0) {
      const g = layer.scale?.x ?? 1;
      const lx = layer.position?.x ?? 0;
      const frameLeft = lx + fr.x * g;
      const frameRight = lx + (fr.x + fr.width) * g;
      const span = frameRight - frameLeft;
      /* Frame art is offset slightly right ({@link #_frameControl.offsetX}) — center the span on
         the reel block so home vs menu get matching edge gaps. */
      if (span > 0) {
        const cx = reelBlockW / 2;
        return { left: cx - span / 2, right: cx + span / 2 };
      }
    }
    return { left: 0, right: reelBlockW };
  }

  /**
   * Map a CSS viewport Y to world-local Y (inverse of layout world pivot/scale + canvas scaleY).
   * @param {number} cssY
   * @returns {number}
   */
  _worldLocalYFromCssY(cssY) {
    const designH = 1080;
    const scaleY =
      this._lastScales?.y > 0 && Number.isFinite(this._lastScales.y)
        ? this._lastScales.y
        : cssY / designH;
    const designY = cssY / scaleY;
    const pivotY = designH / 2;
    const s = Math.max(1e-6, this.world?.scale?.y ?? 1);
    let worldOffY = 0;
    if (this._isPortraitMobileViewport()) {
      worldOffY = MOBILE_PORTRAIT_WORLD_OFFSET_NARROW * this._getNarrowPortraitWidthT();
    }
    const posY = pivotY + worldOffY;
    return pivotY + (designY - posY) / s;
  }

  /** Portrait phone: vertically centred in the viewport; clamped so the panel stays fully visible. */
  _resolvePhoneBetPopupCenterY(bgH) {
    const { height: ch } = this._readContainerSize();
    const margin = MOBILE_BET_MODAL_VIEWPORT_MARGIN_CSS;
    const localTop = this._worldLocalYFromCssY(margin);
    const localBottom = this._worldLocalYFromCssY(ch - margin);
    let centerY = (localTop + localBottom) / 2;
    const half = bgH / 2;
    if (centerY - half < localTop) centerY = localTop + half;
    if (centerY + half > localBottom) centerY = localBottom - half;
    return centerY;
  }

  /** Home (index 0) is disabled on all devices — keep slot for layout indices. */
  _hideCanvasHomeIcon() {
    const home = this._canvasIconSprites?.[0];
    if (!home) return;
    home.visible = false;
    home.renderable = false;
    home.eventMode = 'none';
  }

  /**
   * MOBILE POLICY: the fullscreen HUD icon must NEVER be shown on phone-class mobile
   * viewports (portrait width ≤760px or phone landscape). Fullscreen is desktop/tablet
   * only — do not re-enable this icon on mobile.
   */
  _hideCanvasFullscreenOnMobile() {
    if (!this._isPortraitMobileViewport() && !this._isMobileLandscapeViewport()) return;
    const fullscreen = this._canvasIconSprites?.[1] ?? this._canvasFullscreenIconSprite;
    if (!fullscreen) return;
    fullscreen.visible = false;
    fullscreen.renderable = false;
    fullscreen.eventMode = 'none';
  }

  /** Put HUD corner icons back under the bottom bar (desktop / tablet layouts). */
  _restoreCanvasIconsToBottomBar() {
    const icons = this._canvasIconSprites;
    const bottomBar = this._canvasBottomBar;
    if (!icons?.length || !bottomBar) return;
    icons.forEach((s) => {
      if (s.parent !== bottomBar) bottomBar.addChild(s);
      s.zIndex = 0;
    });
    this._canvasIconsTopChrome = false;
  }

  /**
   * Portrait phone: mute stays bottom-left; menu mirrored to the bottom-right.
   * Fullscreen is intentionally omitted — see {@link #_hideCanvasFullscreenOnMobile}.
   * @param {number} reelBlockW
   * @param {number} hudFit
   * @param {{ qbY: number, btnH: number }} strip quick-bet strip geometry in bottom-bar space
   */
  _layoutPortraitPhoneBottomLeftChrome(reelBlockW, hudFit, strip) {
    const icons = this._canvasIconSprites;
    const bottomBar = this._canvasBottomBar;
    if (!icons?.length || !bottomBar) return;

    this._restoreCanvasIconsToBottomBar();
    this._hideCanvasHomeIcon();
    this._hideCanvasFullscreenOnMobile();

    const sound = icons[2];
    const menu = icons[3];
    if (!sound || !menu) return;

    const p = PORTRAIT_TUNING.phone;
    const iz = this._getMobileBottomMenuIconSize(hudFit);
    const leftX = this._resolvePortraitPhoneQuickBetLeftX();

    const betBtn = this._betPopupTriggerBtn;
    const iconDownNudge = p.bottomChromeIconDownNudge ?? 16;
    const spin = this._spinButtonRoot;
    const spinH = this._spinButtonSprite?.height ?? 0;
    const apCluster = this._autoplayCluster;
    const apSprite = this._autoplaySprite;
    let chromeAnchorY = null;
    if (apCluster && apSprite) {
      chromeAnchorY = apCluster.y + apSprite.y + apSprite.height;
    } else if (spin && spinH > 0) {
      chromeAnchorY = spin.y + spinH / 2;
    } else if (betBtn) {
      chromeAnchorY = betBtn.y + betBtn.height;
    }
    let iconY =
      chromeAnchorY != null
        ? Math.round((chromeAnchorY + 28 + iconDownNudge) - bottomBar.y)
        : strip.qbY + strip.btnH + 10 + iconDownNudge;
    iconY = this._clampPortraitCornerIconY(iconY, iz);

    [sound, menu].forEach((s) => {
      s.visible = true;
      s.renderable = true;
      s.eventMode = 'static';
      s.width = iz;
      s.height = iz;
      s.y = iconY;
    });

    sound.x = leftX;
    /* Mirror mute inset on the right so both icons sit the same distance from center. */
    menu.x = reelBlockW - leftX - iz;

    this._syncCanvasSoundIcon();
  }

  /** Re-clamp home / sound / menu after tablet bottom-strip nudge or resize. */
  _clampPortraitCornerIconsToVisibleArea() {
    if (!this._isPortraitStackedHud() || this._canvasIconsTopChrome) return;
    const icons = this._canvasIconSprites;
    const bottomBar = this._canvasBottomBar;
    if (!icons?.length || !bottomBar) return;
    const visible = icons.filter((s) => s.visible);
    if (!visible.length) return;
    const iz = visible[0].height;
    const clamped = this._clampPortraitCornerIconY(visible[0].y, iz);
    if (clamped === visible[0].y) return;
    visible.forEach((s) => {
      s.y = clamped;
    });
  }

  /** Portrait merged stats bar: scale label/value fonts only (width stays {@link #_relayoutBottomBarForViewport}). */
  _syncPortraitTopStatsTextScale(mul) {
    const k = Math.max(1, mul);
    const setFs = (node, base) => {
      if (!node?.style) return;
      node.style.fontSize = Math.round(base * k);
    };
    setFs(this._portraitBalanceLabelText, 11);
    setFs(this._portraitBalanceValueText, 22);
    setFs(this._portraitBalanceCurrencyText, 22);
    setFs(this._portraitWinLabelText, 11);
    setFs(this._portraitWinValueText, 22);
    setFs(this._portraitWinCurrencyText, 22);
    if (this._portraitTopPromptText?.style) {
      this._portraitTopPromptText.style.fontSize = Math.round(PORTRAIT_BET_PROMPT_FONT_SIZE * k);
      this._portraitTopPromptText.style.lineHeight = Math.round(PORTRAIT_BET_PROMPT_LINE_HEIGHT * k);
    }
    this._relayoutPortraitBalanceStack?.();
    this._relayoutPortraitWinStack?.();
  }

  /**
   * Portrait phone: fit HUD stack under the scaled reel cabinet without clipping.
   * @param {number} cw CSS viewport width
   * @param {number} ch CSS viewport height
   * @param {number} reelBlockH logical reel grid height
   * @returns {number}
   */
  _computeMobileHudFitScale(cw, ch, reelBlockH) {
    const spacingBias = Math.max(-1, Math.min(1, (ch - 860) / 220));
    const portraitRatio = cw / Math.max(1, ch);
    const spacingTightness = Math.max(0, Math.min(1, (portraitRatio - 0.50) / 0.35));
    const p = PORTRAIT_TUNING.phone;
    const gameToHudGap = Math.max(
      0,
      Math.round(p.gameToHudGapBase + spacingBias * p.gameToHudGapSpacingBiasMul),
    );
    const gapStatsToQb = Math.max(4, Math.round(10 + spacingBias * 6 - spacingTightness * 4));
    const gapQbToSpin = Math.max(32, Math.round(76 + spacingBias * 10 - spacingTightness * 12));
    const spinHalf = Math.round((120 * MOBILE_CONTROL_SCALE_BASE) / 2);
    const g = this._mobileGameVisualScale ?? 1;
    const liftY = this._getMobileGameVisualLiftY();
    const reelBottom = (reelBlockH * (1 + g)) / 2 + liftY;
    const iconReserve = 92;
    const hudStackRef =
      gameToHudGap +
      PORTRAIT_MERGED_STATS_BAR_H +
      gapStatsToQb +
      MOBILE_QB_CHIP_REF_H +
      gapQbToSpin +
      spinHalf;
    const budget = 1080 - reelBottom - iconReserve;
    const spaceFit = budget / Math.max(1, hudStackRef);
    /* Reels scale up on phones while HUD stays in design px — never shrink below 1.0; boost
       modestly when tight, up to TARGET when the stack fits (existing nudges absorb overlap). */
    const boosted = Math.max(MOBILE_HUD_FIT_TARGET * 0.96, spaceFit * 1.18);
    return Math.max(
      1.12,
      Math.min(MOBILE_HUD_FIT_MAX, Math.max(MOBILE_HUD_FIT_MIN, Math.min(MOBILE_HUD_FIT_TARGET, boosted))),
    );
  }

  /**
   * Portrait phone only: legacy width-only fit (connection-lost modal).
   * @returns {number}
   */
  _getSettingsPanelMobileFit() {
    const { width: cw } = this._readContainerSize();
    const t = cw / SETTINGS_PANEL_MOBILE_REF_CW;
    return Math.max(SETTINGS_PANEL_COMPACT_FIT_MIN, Math.min(SETTINGS_PANEL_COMPACT_FIT_MAX, t));
  }

  /**
   * Portrait phone: design-width from compact metrics.
   * @returns {number}
   */
  _getSettingsPanelMobileWidth() {
    return this._getSettingsPanelCompactMetrics('mobile').panelW;
  }

  /**
   * Phone/tablet: size the settings shell from CSS viewport so rendered width/height keep
   * {@link SETTINGS_PANEL_MOBILE} (or narrowed desktop) proportions on every device.
   * Rendered size: panelDim × {@link SETTINGS_MODAL_PANEL_SCALE} × (ch/1080); width also uses shell sy/sx.
   * @param {'mobile' | 'tablet'} kind
   * @returns {{ panelW: number, panelH: number, modalFit: number, rootOffsetY: number, layoutRefW: number, layoutRefH: number }}
   */
  _getSettingsPanelCompactMetrics(kind) {
    const { width: cw, height: ch } = this._readContainerSize();
    const mobile = kind === 'mobile';
    const ref = mobile
      ? SETTINGS_PANEL_MOBILE
      : {
          w: SETTINGS_PANEL_DESKTOP.w - 2 * SETTINGS_INFO_VIEW_EXTRA_SIDE_INSET,
          h: SETTINGS_PANEL_DESKTOP.h,
        };
    const viewportW = mobile ? SETTINGS_PANEL_MOBILE_VIEWPORT_W : SETTINGS_PANEL_TABLET_VIEWPORT_W;
    const viewportH = mobile ? SETTINGS_PANEL_MOBILE_VIEWPORT_H : SETTINGS_PANEL_TABLET_VIEWPORT_H;
    const layoutRefW = mobile ? SETTINGS_PANEL_MOBILE_LAYOUT_REF_W : ref.w;
    const layoutRefH = mobile ? SETTINGS_PANEL_MOBILE_LAYOUT_REF_H : ref.h;

    if (!(cw > 0 && ch > 0)) {
      return {
        panelW: ref.w,
        panelH: ref.h,
        modalFit: 1,
        rootOffsetY: 0,
        layoutRefW,
        layoutRefH,
      };
    }

    const sy = ch / 1080;
    const denom = Math.max(1e-6, SETTINGS_MODAL_PANEL_SCALE * sy);
    const fitW = (cw * viewportW) / (ref.w * denom);
    const fitH = (ch * viewportH) / (ref.h * denom);

    let modalFit;
    let panelW;
    let panelH;

    if (mobile) {
      /* Tall phone shell: height sets scale; width follows VIEWPORT_W so raising that constant
         actually widens the modal (min(fitW, fitH) would ignore VIEWPORT_W whenever fitH wins). */
      modalFit = Math.max(
        SETTINGS_PANEL_COMPACT_FIT_MIN,
        Math.min(SETTINGS_PANEL_COMPACT_FIT_MAX, fitH),
      );
      panelH = Math.round(ref.h * modalFit);
      const panelWFromHeight = Math.round(ref.w * modalFit);
      const panelWFromViewport = Math.round((cw * viewportW) / denom);
      panelW = Math.max(panelWFromHeight, panelWFromViewport);
    } else {
      modalFit = Math.min(fitW, fitH);
      modalFit = Math.max(
        SETTINGS_PANEL_COMPACT_FIT_MIN,
        Math.min(SETTINGS_PANEL_COMPACT_FIT_MAX, modalFit),
      );
      panelW = Math.round(ref.w * modalFit);
      panelH = Math.round(ref.h * modalFit);
    }
    const rootOffsetY = mobile
      ? Math.round((ch / 1080) * SETTINGS_PANEL_MOBILE_ROOT_OFFSET_Y)
      : Math.round((ch / 1080) * 24);

    return { panelW, panelH, modalFit, rootOffsetY, layoutRefW, layoutRefH };
  }

  /**
   * Width-fill boost for portrait phones by aspect ratio (w/h), organized from wider to taller.
   * Wider ratios get lower boost to avoid vertical overflow.
   * @param {number} portraitRatio
   */
  _getMobilePortraitWidthBoost(portraitRatio) {
    const r = Number.isFinite(portraitRatio) ? portraitRatio : 0;
    const breakpoints = [
      { minRatio: 0.90, boost: 0.85 },
      { minRatio: 0.85, boost: 0.85 },
      { minRatio: 0.80, boost: 0.9 },
      { minRatio: 0.70, boost: 0.95 },
      { minRatio: 0.65, boost: 1.10 },
      { minRatio: 0.60, boost: 1.20 },
      { minRatio: 0.55, boost: 1.25 },
      { minRatio: 0.50, boost: 1.4 },
    ];
    for (const bp of breakpoints) {
      if (r >= bp.minRatio) return bp.boost;
    }
    return 1.42;
  }

  /** Any portrait viewport — balance / prompt / win stack above QB strip and spin row. */
  _isPortraitStackedHud() {
    const { width: cw, height: ch } = this._readContainerSize();
    return ch > cw;
  }

  /**
   * Tablet-class viewport (landscape only). Portrait tablets use the mobile view
   * via {@link GameApp#_isPortraitMobileViewport}.
   * Landscape: primary pointer is touch and dimensions match common tablets (excludes typical desktops).
   */
  _isTabletViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    if (ch > cw) return false;
    const touch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
    if (!touch) return false;
    const smin = Math.min(cw, ch);
    const smax = Math.max(cw, ch);
    return smin >= 768 && smax <= 1366;
  }

  /** Phone in landscape orientation (touch device, phone-sized short edge, not tablet). */
  _isMobileLandscapeViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    if (ch >= cw) return false;
    // Match portrait-phone cap (cw <= 760): in landscape the short edge is height.
    if (ch > 760) return false;
    // Laptop / desktop sizes (e.g. 1280×720 in DevTools) are not phones — even with touch emulation.
    // Real phones in landscape stay under ~932 CSS px wide (Pro Max class).
    if (cw >= 1024) return false;
    const touch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
    if (!touch) return false;
    if (this._isTabletViewport()) return false;
    return true;
  }

  _syncLandscapeBlocker() {
    this._landscapeBlocker?.sync?.();
  }

  /** Desktop landscape currently rendered into the browser's fullscreen element. */
  _isDesktopFullscreen() {
    if (typeof document === 'undefined') return false;
    if (!document.fullscreenElement) return false;
    if (this._isPortraitMobileViewport()) return false;
    if (this._isTabletViewport()) return false;
    return true;
  }

  /** Landscape desktop HUD (not tablet, not phone landscape, not portrait). */
  _isDesktopLandscapeViewport() {
    if (this._isPortraitStackedHud()) return false;
    if (this._isTabletViewport()) return false;
    if (this._isMobileLandscapeViewport()) return false;
    return true;
  }

  /** Hide the paylines/LINES chip on portrait viewports — layout targets horizontal screens only. */
  _syncLinesPanelForOrientation() {
    const lp = this._canvasLinesPanel;
    if (!lp) return;
    const { width: cw, height: ch } = this._readContainerSize();
    const vertical = ch > cw;
    lp.visible = !vertical;
    lp.eventMode = vertical ? 'none' : 'passive';
  }

  /** Shrink the lines badge label so long translations fit the 151px panel. */
  _relayoutCanvasLinesLabel() {
    const lbl = this._canvasLinesLabelText;
    if (!lbl) return;
    const maxW = 132;
    const baseSize = 20.4167;
    lbl.style.fontSize = baseSize;
    let size = baseSize;
    while (lbl.width > maxW && size > 11) {
      size -= 0.5;
      lbl.style.fontSize = size;
    }
  }

  async init(containerEl) {
    this._containerEl = containerEl;
    this._landscapeBlocker = createLandscapeOrientationBlocker({
      isActive: () => this._isMobileLandscapeViewport(),
      getTitle: () => this._tu('ROTATE_DEVICE_TITLE'),
      getBody: () => this._t('ROTATE_DEVICE_BODY'),
    });
    this._syncLandscapeBlocker();
    this._syncLobbyUrlFromSearch();
    Object.assign(containerEl.style, {
      position: 'fixed',
      inset: '0',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      background: '#000',
    });
    this._syncPortraitShellClass();

    /* ── Init Pixi with transparent background, fixed 1922×1080 ────────── */
    const rendererProfile = getPixiRendererProfile();
    this._rendererProfile = rendererProfile;
    this.app = new Application();
    await this.app.init({
      width: 1922,
      height: 1080,
      backgroundAlpha: 0,
      antialias: rendererProfile.antialias,
      powerPreference: rendererProfile.powerPreference,
      resolution: rendererProfile.resolution,
      autoDensity: true,
      preference: 'webgl',
    });
    if (rendererProfile.maxFPS != null && Number.isFinite(rendererProfile.maxFPS)) {
      this.app.ticker.maxFPS = rendererProfile.maxFPS;
    }
    // Blazing Sevens pattern: drive GSAP from the Pixi ticker so reel motion and
    // Spine/WebGL share one clock. Dual timers (GSAP 60Hz + Pixi capped FPS) were
    // a major source of Android freezes during Quick Spin.
    this._bindGsapToPixiTicker();

    const canvas = this.app.canvas;
    canvas.style.cssText =
      'display:block;position:absolute;top:0;left:0;width:1922px;height:1080px;touch-action:none;pointer-events:auto;z-index:1;transform-origin:0 0;';
    containerEl.appendChild(canvas);
    this._fitRendererToContainer();
    /* Full-screen overlay before any game content is attached so the canvas never flashes the cabinet. */
    await this._showLoading();
    await this._prefetchTranslationsFromUrl();

    /* Kick off audio decode in the background — fire-and-forget so it never
     * blocks first paint. Sound manager handles iOS unlock on first gesture. */
    sound.setMuted(!this._settingsSoundOn);
    sound.preload();

    // Load Spine assets for symbols
    this.spineAssets = await loadSpineAssets({
      dir: this._buyBonusEnabled ? '/assets/spines/bb_symbols' : undefined,
      preloadWildMultipliers: this._buyBonusEnabled,
      onProgress: (t) => this._setLoadingProgressTarget(0.06 + t * 0.42),
    });
    // Pre-create pooled Spines so the first Quick Spins never parse skeletons mid-frame.
    prewarmSymbolSpinePool(this.spineAssets, { perId: 8 });
    this._setLoadingProgressTarget(0.50);

    /* ── SlotMachine (Pixi) ───────────────────────────────────────────── */
    this.slotMachine = new SlotMachine({
      spineAssets: this.spineAssets,
      animationController: this.animationController,
      onSpinRequest: () => this.handleSpin(),
      translate: (key, replace) => this._t(key, replace),
      onBetSync: () => this._refreshInfoPayoutAmounts(),
    });
    this.slotMachine.reelContainer.setPaylineGuidesVisible(this._paylinesVisible);

    /* Hard cleanup: remove all non-card Pixi display objects from the tree */
    this.slotMachine._hideDecor = true;
    const smRoot = this.slotMachine.root;
    const detach = (node) => {
      if (node?.parent) node.parent.removeChild(node);
    };
    detach(this.slotMachine._headerLayer);
    detach(this.slotMachine._midBackdrop);
    detach(this.slotMachine.winFloater.root);

    /* Keep only reel container under cabinet group (remove old cabinet graphics). */
    this.slotMachine._cabinetGroup.children
      .slice()
      .forEach((child) => {
        if (child !== this.slotMachine.reelContainer.root) {
          this.slotMachine._cabinetGroup.removeChild(child);
        }
      });

    /* Remove payline/win overlays from reel container tree too. */
    detach(this.slotMachine.reelContainer.allLinesOverlay);
    detach(this.slotMachine.reelContainer.winOverlay);

    this.world.addChild(this.slotMachine.root);
    this._setLoadingProgressTarget(0.52);

    /* Slot window backplate (same container as frame & reels; behind glow + symbols) */
    let matrixTexture = null;
    for (const p of ['/assets/matrix.png', 'assets/matrix.png', '/matrix.png']) {
      try {
        matrixTexture = await Assets.load(p);
        if (matrixTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    this._setLoadingProgressTarget(0.56);
    const matrixSprite = matrixTexture ? new Sprite(matrixTexture) : Sprite.from('/assets/matrix.png');
    matrixSprite.eventMode = 'none';
    matrixSprite.zIndex = 0;
    if (matrixSprite.texture?.source) {
      matrixSprite.texture.source.scaleMode = 'linear';
    }
    this.slotMachine.reelContainer.root.sortableChildren = true;
    this.slotMachine.reelContainer.root.addChild(matrixSprite);
    this._matrixBgSprite = matrixSprite;

    let slotLinesTexture = null;
    for (const p of [
      '/assets/slot_background_lines.png',
      'assets/slot_background_lines.png',
      '/slot_background_lines.png',
    ]) {
      try {
        slotLinesTexture = await Assets.load(p);
        if (slotLinesTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    const slotLinesSprite = slotLinesTexture
      ? new Sprite(slotLinesTexture)
      : Sprite.from('/assets/slot_background_lines.png');
    slotLinesSprite.eventMode = 'none';
    slotLinesSprite.zIndex = 0.1;
    if (slotLinesSprite.texture?.source) {
      slotLinesSprite.texture.source.scaleMode = 'linear';
    }
    this.slotMachine.reelContainer.root.addChild(slotLinesSprite);
    this._slotBackgroundLinesSprite = slotLinesSprite;
    this._setLoadingProgressTarget(0.60);

    /* Glow behind frame (in same container as reels) */
    let glowTexture = null;
    for (const p of ['/assets/glow.png', 'assets/glow.png', '/glow.png']) {
      try {
        glowTexture = await Assets.load(p);
        if (glowTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    const glowSprite = glowTexture ? new Sprite(glowTexture) : Sprite.from('/assets/glow.png');
    glowSprite.anchor.set(0.5, 0.5);
    glowSprite.alpha = 1;
    glowSprite.eventMode = 'none';
    glowSprite.zIndex = -1;
    this.slotMachine.reelContainer.root.addChild(glowSprite);
    this._glowSprite = glowSprite;
    this._setLoadingProgressTarget(0.63);

    /* Frame in Pixi canvas, anchored to the actual card/reel block */
    let frameTexture = null;
    for (const p of ['/assets/frame.png', 'assets/frame.png', '/frame.png']) {
      try {
        frameTexture = await Assets.load(p);
        if (frameTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    const frameSprite = frameTexture ? new Sprite(frameTexture) : Sprite.from('/assets/frame.png');
    frameSprite.tint = 0xffffff;
    frameSprite.alpha = 1;
    frameSprite.eventMode = 'none';
    frameSprite.zIndex = 1;
    this.slotMachine.reelContainer.root.addChild(frameSprite);
    this._frameSprite = frameSprite;
    this._setLoadingProgressTarget(0.66);

    /* Logo at top center of frame, in same container */
    let logoTexture = null;
    for (const p of this._gameLogoAssetPaths()) {
      try {
        logoTexture = await Assets.load(p);
        if (logoTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    this._logoTexDefault = logoTexture || null;
    this._logoTexHorizontal = null;
    if (logoTexture) {
      const logoSprite = new Sprite(logoTexture);
      logoSprite.anchor.set(0.5, 0);
      logoSprite.eventMode = 'none';
      logoSprite.zIndex = 10;
      this.slotMachine.reelContainer.root.addChild(logoSprite);
      this._logoSprite = logoSprite;
    }
    this._setLoadingProgressTarget(0.68);

    this._applyFrameLayout();
    this._ensureGameVisualLayer();
    this._initVisualFx();
    this._applyMobileGameVisualScale();
    await this._buildCanvasControls();
    this._setLoadingProgressTarget(0.78);
    await this._createPixiStageBackdrop();
    this._setLoadingProgressTarget(0.84);
    this.app.stage.addChild(this._backdropRoot);
    this.app.stage.addChild(this.world);
    this._setLoadingProgressTarget(0.88);

    /* Canvas should show only cards + frame */

    window.addEventListener('resize', this._onResize);
    window.visualViewport?.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onOrientation);
    this._onFullscreenChange = () => {
      this._syncCanvasFullscreenIcon();
      this._onResize();
      requestAnimationFrame(() => this._onResize());
    };
    document.addEventListener('fullscreenchange', this._onFullscreenChange);
    window.addEventListener('keydown', this._onKey);
    this._attachWinCollectWindowPointerGuard();

    // ResizeObserver is not available on iOS 12 / iOS 13.0-13.3 and some old
    // Android WebViews; window/visualViewport resize listeners above cover
    // those fallbacks. Feature-detect so the rest of init can succeed.
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this._onResize());
      this._resizeObserver.observe(containerEl);
    }

    this._exposeDebugApi();
    this._installRoundStatusPageHideFlush();
    this._fitRendererToContainer();
    await this._loadInitialState();
    // Build settings after StartGame so the info paytable uses server odds × current bet.
    await this._createSettingsModalPixi();
    this.slotMachine.winFloater.root.visible = false;
    await this._ensureLoadingMinDisplayElapsed();
    await this._prepareGameRevealFromLoading();
    this._hideLoading();
    if (this._gameDataReady && !this._sessionLoadFailed) {
      await this._showStartScreenAndWait();
    }
    // this._applyOpeningPhotoLayoutIfEnabled(); // photo layout — see openingPhotoLayout.js
    this._initAmbientFx();
    this._scheduleDeferredRoundResumeVisuals({ replaySounds: true });
  }

  /** Wire cabinet juice + celebration hooks once reel/frame layer exists. */
  _initVisualFx() {
    if (this._cabinetJuice || !this.app?.stage || !this.slotMachine) return;
    this._cabinetJuice = new CabinetJuice({
      stage: this.app.stage,
      glowSprite: this._glowSprite,
      shakeTarget: this._gameVisualLayer,
      animationController: this.slotMachine.animationController,
      onCelebrationStart: () => {
        this.slotMachine?.reelContainer?.clearReelWellDim?.();
        this._syncStageBackdropForViewport();
      },
      onCelebrationEnd: () => {
        this._syncStageBackdropForViewport();
        this._applyMobileGameVisualScale();
      },
    });
    this.slotMachine.reelContainer.setCabinetJuice(this._cabinetJuice);
    this.slotMachine.winCelebration.setJuiceHost(this._cabinetJuice);
  }

  /** Slow ambient motion after the loading screen is gone. */
  _initAmbientFx() {
    if (this._ambientFx || !this._backdropRoot || !this.slotMachine) return;
    this._ambientFx = new AmbientIdleFx({
      backdropRoot: this._backdropRoot,
      glowSprite: this._glowSprite,
      logoSprite: this._logoSprite,
      spinButtonRoot: this._spinButtonRoot,
      animationController: this.slotMachine.animationController,
      isSpinIdle: () => !this._spinning && !this._isWinCollectDismissActive(),
    });
    this._ambientFx.start();
  }

  _syncVisualFxRefs() {
    this._cabinetJuice?.setGlowSprite(this._glowSprite);
    this._cabinetJuice?.setShakeTarget(this._gameVisualLayer);
    this._ambientFx?.setGlowSprite(this._glowSprite);
    this._ambientFx?.setLogoSprite(this._logoSprite);
    this._ambientFx?.setSpinButtonRoot(this._spinButtonRoot);
    this._syncStageBackdropForViewport();
  }

  _onKey = (e) => {
    if (e.key === '`' || e.key === '~') {
      this.debug.toggle();
      this.debug.refreshOverlay();
      return;
    }
    if (e.key !== ' ' && e.code !== 'Space') return;
    if (e.repeat) return;
    const target = e.target;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable)
    ) {
      return;
    }
    e.preventDefault();
    if (this._isStartScreenBlocking()) {
      this._dismissStartScreen();
      return;
    }
    if (this._isBlockingModalOpen()) return;
    if (this._isWinCollectDismissActive()) {
      this._pendingSpinAfterWinClose = true;
      void this._performWinCollectTap();
      return;
    }
    if (this._spinHoldInProgress) return;
    const now = performance.now();
    if (!this._spinning && (now < this._spinToggleLockUntilTs || now < this._respinBlockUntilTs)) return;
    this._spinOrStopMainButtonTap();
  };

  _t(key, replace) {
    return this.translation.t(key, replace);
  }

  _tu(key, replace) {
    return this._t(key, replace).toLocaleUpperCase();
  }

  _twoLineText(key) {
    const text = this._tu(key).replace(/\s+/g, ' ').trim();
    const words = text.split(' ').filter(Boolean);
    if (words.length <= 2) return text;
    const splitAt = Math.ceil(words.length / 2);
    return `${words.slice(0, splitAt).join(' ')}\n${words.slice(splitAt).join(' ')}`;
  }

  _currencyLabel() {
    return formatCurrencyRulesLabel(this._currencyCode, this._currencySymbol) || 'FUN';
  }

  _formatCurrencyDigits(amount) {
    return formatCurrencyDigits(Number(amount) || 0, this._currencyPrecision);
  }

  _formatCurrencyAmount(amount) {
    return formatCurrencyAmount(
      Number(amount) || 0,
      this._currencyCode,
      this._currencyPrecision,
      this._currencySymbol,
    );
  }

  /** Player-facing max win label from server multiplier (e.g. `5000x`). */
  _formatMaxWinMultiplier(maxOdd) {
    const odd = Number(maxOdd);
    if (!Number.isFinite(odd) || odd <= 0) return '';
    const normalized = Number.isInteger(odd)
      ? String(Math.trunc(odd))
      : odd.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return `${normalized}x`;
  }

  _setCurrency({ currencyCode, currencyPrecision, currencySymbol } = {}) {
    const nextCode = normalizeCurrencyCode(currencyCode || this._currencyCode || 'FUN') || 'FUN';
    const nextPrecision = Number.isFinite(Number(currencyPrecision))
      ? Number(currencyPrecision)
      : this._currencyPrecision;
    const nextSymbol =
      currencySymbol == null || String(currencySymbol).trim() === ''
        ? undefined
        : String(currencySymbol).trim();
    this._currencyCode = nextCode;
    this._currencyPrecision = nextPrecision;
    this._currencySymbol = nextSymbol;
    const cur = {
      currencyCode: this._currencyCode,
      currencyPrecision: this._currencyPrecision,
      currencySymbol: this._currencySymbol,
    };
    this.slotMachine?.winCelebration?.setCurrency?.(cur);
    this.slotMachine?.winFloater?.setCurrency?.(cur);
    this._refreshLocalizedText();
  }

  _applyLocalizedTextSpec(spec) {
    if (!spec?.text) return;
    const replace = typeof spec.replace === 'function' ? spec.replace() : spec.replace;
    let value = spec.upper ? this._tu(spec.key, replace) : this._t(spec.key, replace);
    if (typeof spec.transform === 'function') value = spec.transform(value);
    spec.text.text = `${value}${spec.suffix ?? ''}`;
    spec.afterApply?.();
  }

  _bindLocalizedText(text, key, opts = {}) {
    const spec = {
      text,
      key,
      upper: !!opts.upper,
      suffix: opts.suffix ?? '',
      replace: opts.replace,
      transform: opts.transform,
      afterApply: opts.afterApply,
    };
    this._localizedTextRefs.push(spec);
    this._applyLocalizedTextSpec(spec);
    return text;
  }

  _infoTotalBet() {
    const raw = Number(this.slotMachine?.hud?.betValue);
    if (Number.isFinite(raw) && raw > 0) return raw;
    return gameConfig.defaultBet;
  }

  _infoStakes() {
    const totalBet = this._infoTotalBet();
    // Selected chip is the stake the hub multiplies. Do not divide by payline count.
    return { totalBet, lineBet: totalBet };
  }

  _refreshInfoPayoutAmounts() {
    if (!this._infoPayoutAmountTexts?.length) return;
    const stakes = this._infoStakes();
    for (const row of this._infoPayoutAmountTexts) {
      row.apply?.(stakes);
    }
  }

  _formatInfoPayoutParts(spec, stakes = this._infoStakes()) {
    const amount = payoutAmountForInfoSpec(spec, stakes);
    return {
      multPart: `x${spec.count} - `,
      amountPart: this._formatCurrencyAmount(amount),
    };
  }

  _refreshLocalizedText() {
    this.slotMachine?.hud?.setTranslator?.((key, replace) => this._t(key, replace));
    this.slotMachine?.refreshLocalizedHeaderTexts?.();
    this._localizedTextRefs.forEach((spec) => this._applyLocalizedTextSpec(spec));
    this._infoWildRelayout?.();
    this._refreshInfoPayoutAmounts();
    if (this._infoRuleLimitLineRefreshers?.length) {
      for (const refreshLine of this._infoRuleLimitLineRefreshers) refreshLine();
    }
    if (this._infoRuleMaxWinLines?.length) {
      const maxOdd = Number(gameConfig.maxOdd);
      const showMaxWin = Number.isFinite(maxOdd) && maxOdd > 0;
      for (const line of this._infoRuleMaxWinLines) {
        if (line) line.visible = showMaxWin;
      }
    }
    this._infoButtonTableRelayout?.();
    this._infoContentRelayout?.();
    if (this._canvasBalanceLabelText) this._canvasBalanceLabelText.text = this._tu('BALANCE');
    if (this._canvasWinLabelText) this._canvasWinLabelText.text = this._tu('LAST_WIN');
    if (this._canvasLinesLabelText) {
      this._canvasLinesLabelText.text = this._tu('LINES');
      this._relayoutCanvasLinesLabel();
    }
    if (this._portraitBalanceLabelText) this._portraitBalanceLabelText.text = this._tu('BALANCE');
    if (this._portraitWinLabelText) this._portraitWinLabelText.text = this._tu('LAST_WIN');
    if (this._canvasBalanceCurrencyText) this._canvasBalanceCurrencyText.text = this._currencyLabel();
    if (this._canvasWinCurrencyText) this._canvasWinCurrencyText.text = this._currencyLabel();
    if (this._portraitBalanceCurrencyText) this._portraitBalanceCurrencyText.text = this._currencyLabel();
    if (this._portraitWinCurrencyText) this._portraitWinCurrencyText.text = this._currencyLabel();
    if (this._betPopupTitle) this._betPopupTitle.text = this._tu('BET_OPTIONS');
    if (this._settingsHeadingText) {
      this._settingsHeadingText.text =
        this._settingsModalActiveTab === 'autoplay'
          ? this._tu('AUTOPLAY_SETTINGS')
          : this._settingsModalActiveTab === 'info'
            ? this._tu('INFO')
            : this._tu('GAME_SETTINGS');
    }
    this._updateCanvasBetPrompt();
    this._canvasBalancePanelRelayout?.();
    this._canvasWinPanelRelayout?.();
    this._relayoutPortraitBalanceStack?.();
    this._relayoutPortraitWinStack?.();
    this._refreshConnectionLostModalCopy();
    this._landscapeBlocker?.refreshCopy?.();
    this._syncLandscapeBlocker();
    this._syncConnectionModalStatus();
    this._syncConnectionLostModal();
    this._refreshQuickBets();
    this._refreshAutoplaySettingsView();
    this._relayoutSettingsSwitchesOnly();
    if (this._spinTurboHint) this._spinTurboHint.setText(this._tu('HOLD_FOR_TURBO_SPIN'));
    this._layoutSpinTurboHint();
    this._syncSpinTurboHintVisible();
    this._layoutBuyBonusConfirm();
  }

  _setSpinButtonTexture(tex) {
    if (!this._spinButtonSprite) return;
    this._spinButtonSprite.texture = tex;
    this._syncSpinTurboHintVisible();
  }

  _layoutSpinTurboHint() {
    if (!this._spinTurboHint) return;
    const design = 194;
    const size = this._spinButtonSprite?.width ?? design;
    const scale = size / design;
    this._spinTurboHint.setLayout({
      radius: 75 * scale,
      fontSize: Math.max(8, 12 * scale),
      letterSpacing: 1 * scale,
      yOffset: -7 * scale,
      arcOffset: 0,
      arcSweep: Math.PI * 0.88,
    });
    this._spinTurboHint.container.x = 2 * scale;
  }

  _syncSpinTurboHintVisible() {
    if (!this._spinTurboHint) return;
    const showStart = this._spinButtonSprite?.texture === this._spinStartTex;
    this._spinTurboHint.container.visible = !!showStart;
  }

  _exposeDebugApi() {
    const symCount = gameConfig.symbols.length;
    window.slotGameDebug = {
      forceNextGrid: (grid) => this.debug.forceNextGrid(grid),
      /** Live layout from the same `gameConfig` GameApp mutates. */
      getLayout: () => ({
        rowCount: gameConfig.rowCount,
        paylines: gameConfig.paylines.length,
        cellWidth: gameConfig.cellWidth,
        cellHeight: gameConfig.cellHeight,
        lines40: !!this._lines40Enabled,
        lines100: !!this._lines100Enabled,
        backendGameName: this._backendGameName,
      }),
      /** Set overall content scale, e.g. slotGameDebug.setScale(0.75) */
      setScale: (s) => { this._contentScale = s; this.layout(); this._applySlotAspectLock(...Object.values(this._lastScales ?? { x: 1, y: 1 })); },
      /** Access bottom bar container (balance, win, quick-bets, icons) */
      get bottomBar() { return this._canvasBottomBar; },
      /** Five-of-kind on middle payline */
      forceMiddleLineJackpot: () => {
        const id = Math.min(5, symCount - 1);
        const grid = [];
        for (let c = 0; c < gameConfig.reelCount; c++) {
          grid[c] = [];
          for (let r = 0; r < gameConfig.rowCount; r++) {
            grid[c][r] = (r === 1 ? id : (c + r) % symCount) % symCount;
          }
        }
        this.debug.forceNextGrid(grid);
      },
      // applyOpeningPhotoLayout: () => this._applyOpeningPhotoLayout({ force: true }),
      frame: {
        get: () => ({ ...this._frameControl }),
        set: (next = {}) => {
          if (!next || typeof next !== 'object') return;
          if (Number.isFinite(next.padX)) this._frameControl.padX = Number(next.padX);
          if (Number.isFinite(next.padY)) {
            const p = Number(next.padY);
            this._frameControl.padYTop = p;
            this._frameControl.padYBottom = p;
          }
          if (Number.isFinite(next.padYTop)) this._frameControl.padYTop = Number(next.padYTop);
          if (Number.isFinite(next.padYBottom)) this._frameControl.padYBottom = Number(next.padYBottom);
          if (Number.isFinite(next.offsetX)) this._frameControl.offsetX = Number(next.offsetX);
          if (Number.isFinite(next.offsetY)) this._frameControl.offsetY = Number(next.offsetY);
          if (Number.isFinite(next.scale) && next.scale > 0) this._frameControl.scale = Number(next.scale);
          this._applyFrameLayout();
        },
        reset: () => {
          this._frameControl = {
            padX: 76,
            padYTop: 96,
            padYBottom: 70,
            offsetX: 13,
            offsetY: -34,
            scale: 1.15,
          };
          this._applyFrameLayout();
        },
      },
      roundStatus: {
        get: () => this._loadRoundStatus(),
        getLive: () => this._roundStatusLive,
        getDeferred: () => this._deferredRoundResumeVisuals,
        isWinCollectPending: () => this._winCollectPending,
        isCelebrationVisible: () => this._isWinCelebrationVisible(),
      },
    };
  }

  /**
   * Groups reels, mask, and frame cosmetics under a child container so we can scale only the
   * “cabinet” on narrow phones; {@link #_canvasControls} stays a sibling at scale 1.
   */
  _ensureGameVisualLayer() {
    const rc = this.slotMachine?.reelContainer;
    const root = rc?.root;
    if (!rc || !root || this._gameVisualLayer) return;

    const layer = new Container();
    layer.sortableChildren = true;
    layer.eventMode = 'passive';
    this._gameVisualLayer = layer;

    const steal = (node) => {
      if (!node) return;
      if (node.parent) node.parent.removeChild(node);
      layer.addChild(node);
    };

    steal(rc.reelsLayer);
    steal(rc.expandingWildFxLayer);
    steal(rc._symbolWinLiftLayer);
    steal(rc.reelsMaskShape);
    rc.reelsLayer.mask = rc.reelsMaskShape;

    steal(this._matrixBgSprite);
    steal(this._slotBackgroundLinesSprite);
    steal(this._glowSprite);
    steal(this._frameSprite);
    steal(this._logoSprite);

    rc._reelsMaskHost = layer;
    root.addChildAt(layer, 0);
  }

  /** Narrow portrait phones only: scale the reel/frame cabinet so its frame edges fill the viewport width (uniform scale — no deformation); controls keep logical size. */
  _applyMobileGameVisualScale() {
    const layer = this._gameVisualLayer;
    if (!layer) return;
    const mobile = this._isPortraitMobileViewport();
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;

    /**
     * Solve the cabinet's on-screen CSS width exactly from the live transform chain — no guessed magic
     * numbers, no clamps, no hardcoded assumptions about which ancestors exist.
     *
     * The frame sprite's design width times every ancestor scale on the way to the page must equal the
     * viewport's CSS width. We walk the live parent chain (so a new wrapper like `_cabinetGroup` from
     * {@link SlotMachine.layoutScreen} is picked up automatically) and read each `scale.x`, then add the
     * canvas's CSS-stretch factor (`_lastScales.x` set in {@link #_fitRendererToContainer}) and solve:
     *
     *     frameSprite.width · g · Π(ancestor.scale.x) · cssScaleX  =  cssW
     *
     * The single result `g` is applied uniformly to X and Y, so the cabinet's aspect ratio is preserved
     * exactly.
     */
    let g = 1;
    if (mobile && this._frameSprite && this._frameSprite.parent && this.app?.stage) {
      const { width: cssW } = this._readContainerSize();
      const frameLocalW = this._frameSprite.width;
      const cssScaleX = this._lastScales?.x ?? 1;
      /**
       * Cumulative scale.x of every ancestor *between* `_gameVisualLayer` and the Pixi stage
       * (so excluding the layer itself, whose scale is what we are solving for).
       */
      let ancestorScaleX = 1;
      const stage = this.app.stage;
      for (let node = layer.parent; node && node !== stage; node = node.parent) {
        const sx = node?.scale?.x;
        if (Number.isFinite(sx) && sx !== 0) ancestorScaleX *= sx;
      }
      const cssPerFrameLocal = frameLocalW * ancestorScaleX * cssScaleX;
      if (cssPerFrameLocal > 0 && cssW > 0) {
        g = cssW / cssPerFrameLocal;
      }
      const extraMul = this._usesCompactCabinet()
        ? (gameConfig.viewport?.compactMobileGameVisualScaleMul ?? 1.05)
        : (gameConfig.viewport?.mobileGameVisualScaleMul ?? 1.20);
      if (Number.isFinite(extraMul) && extraMul > 0 && extraMul !== 1) {
        g *= extraMul;
      }
    }
    this._mobileGameVisualScale = g;

    layer.scale.set(g, g);
    const liftY = mobile ? this._getMobileGameVisualLiftY() : 0;
    layer.position.set((reelBlockW * (1 - g)) / 2, (reelBlockH * (1 - g)) / 2 + liftY);
  }

  _applyFrameLayout() {
    if (!this._frameSprite || !this.slotMachine) return;
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    const c = this._frameControl;
    const padYTop = c.padYTop ?? c.padY ?? 70;
    const padYBottom = c.padYBottom ?? c.padY ?? 70;
    const baseW = reelBlockW + c.padX * 2;
    const baseH = reelBlockH + padYTop + padYBottom;
    const frameW = baseW * c.scale;
    const frameH = baseH * c.scale;
    this._frameSprite.width = frameW;
    this._frameSprite.height = frameH;
    this._frameSprite.x = -c.padX - (frameW - baseW) / 2 + c.offsetX;
    this._frameSprite.y = -padYTop - (frameH - baseH) / 2 + c.offsetY;

    // Matrix only behind the 5×3 symbol grid (not the full padded frame)
    if (this._matrixBgSprite) {
      const topFrameExtra = Math.max(0, padYTop - padYBottom) * c.scale;
      const matrixH = reelBlockH + topFrameExtra;
      const matrixScale = this._matrixBgScale ?? 1;
      const scaledW = reelBlockW * matrixScale;
      const scaledH = matrixH * matrixScale;
      this._matrixBgSprite.x = (reelBlockW - scaledW) / 2;
      this._matrixBgSprite.y = -topFrameExtra + (matrixH - scaledH) / 2;
      this._matrixBgSprite.width = scaledW;
      this._matrixBgSprite.height = scaledH;
    }

    if (this._slotBackgroundLinesSprite) {
      const topFrameExtra = Math.max(0, padYTop - padYBottom) * c.scale;
      const linesH = reelBlockH + topFrameExtra;
      const linesScale = this._slotBackgroundLinesScale ?? 1;
      const scaledW = reelBlockW * linesScale;
      const scaledH = linesH * linesScale;
      this._slotBackgroundLinesSprite.x = (reelBlockW - scaledW) / 2;
      this._slotBackgroundLinesSprite.y =
        -topFrameExtra + (linesH - scaledH) / 2 + (this._slotBackgroundLinesOffsetY ?? 0);
      this._slotBackgroundLinesSprite.width = scaledW;
      this._slotBackgroundLinesSprite.height = scaledH;
    }

    // Position glow behind frame (bigger and moved up slightly)
    if (this._glowSprite) {
      const glowScale = 1.4;  // 30% bigger than frame
      const topFrameExtra = Math.max(0, padYTop - padYBottom) * c.scale;
      const frameCenterX = this._frameSprite.x + frameW / 2;
      const frameCenterY = this._frameSprite.y + frameH / 2.2;
      this._glowSprite.width = frameW * glowScale;
      this._glowSprite.height = frameH * glowScale + topFrameExtra;
      this._glowSprite.x = frameCenterX;
      this._glowSprite.y = frameCenterY - 30 - topFrameExtra / 2;  // Extend extra glow upward when top frame padding is larger
    }

    // Position logo at top center of frame (same local proportions on every viewport —
    // logo + frame live in {@link #_gameVisualLayer} and scale together on portrait phones).
    if (this._logoSprite) {
      const frameCenterX = this._frameSprite.x + frameW / 2;
      const frameTopY = this._frameSprite.y;
      const desiredTex = this._logoTexDefault;
      if (desiredTex && this._logoSprite.texture !== desiredTex) {
        this._logoSprite.texture = desiredTex;
      }
      const logoTopY = frameTopY - CABINET_LOGO_GAP_ABOVE_FRAME;
      if (this._isWideLinesLaunch()) {
        const fit = this._fitWideLogo(CABINET_LOGO_BASE_WIDTH);
        this._logoSprite.width = fit.width;
        this._logoSprite.height = fit.height;
        this._logoSprite.x = frameCenterX + fit.dx;
        this._logoSprite.y = logoTopY + fit.dy;
      } else {
        this._logoSprite.x = frameCenterX;
        this._logoSprite.y = logoTopY;
        this._logoSprite.width = CABINET_LOGO_BASE_WIDTH;
        this._logoSprite.height =
          (this._logoSprite.texture.height / this._logoSprite.texture.width) * CABINET_LOGO_BASE_WIDTH;
      }
    }
    this._syncVisualFxRefs();
  }

  _attachSpinHoldWindowReleaseGuard() {
    if (this._spinHoldWindowGuardActive) return;
    this._spinHoldWindowGuardActive = true;
    window.addEventListener('pointerup', this._onSpinHoldWindowPointerEnd, true);
    window.addEventListener('pointercancel', this._onSpinHoldWindowPointerEnd, true);
  }

  _detachSpinHoldWindowReleaseGuard() {
    if (!this._spinHoldWindowGuardActive) return;
    this._spinHoldWindowGuardActive = false;
    window.removeEventListener('pointerup', this._onSpinHoldWindowPointerEnd, true);
    window.removeEventListener('pointercancel', this._onSpinHoldWindowPointerEnd, true);
  }

  /**
   * @param {import('pixi.js').FederatedPointerEvent | PointerEvent} e
   */
  _onSpinHoldWindowPointerEnd(e) {
    this._finalizeSpinHoldFromPointerEnd(e);
  }

  _isWinCelebrationVisible() {
    return !!this.slotMachine?.winCelebration?.root?.visible;
  }

  _isWinCelebrationSkipGuarded() {
    if (!this._winCelebrationShownAt) return false;
    return Date.now() - this._winCelebrationShownAt < 1000;
  }

  /** Collect button state or big-win overlay still on screen. */
  _isWinCollectDismissActive() {
    if (this._resumeRoundSkipCollect) return !!this._winCollectPending;
    return this._winCollectPending || this._isWinCelebrationVisible();
  }

  /**
   * Hit-test a Pixi display object using its `hitArea` in stage coordinates.
   * @param {import('pixi.js').Container | import('pixi.js').Sprite | null | undefined} displayObject
   * @param {number} stageX
   * @param {number} stageY
   */
  _isStagePointInDisplayHitArea(displayObject, stageX, stageY) {
    if (!displayObject?.visible || !displayObject.worldVisible || !displayObject.hitArea) return false;
    const local = displayObject.toLocal({ x: stageX, y: stageY });
    return displayObject.hitArea.contains(local.x, local.y);
  }

  /**
   * True when a DOM pointer release should chain the next spin after collect
   * (spin button or quick-bet chip). Other taps still collect but stay idle.
   * @param {PointerEvent} ev
   */
  _winCollectPointerOnSpinOrQuickBets(ev) {
    const pt = this._mapDomPointerToStage(ev);
    if (!pt) return false;
    if (this._isStagePointInDisplayHitArea(this._spinButtonRoot, pt.x, pt.y)) return true;
    const qbGroup = this._canvasQbGroup;
    if (!qbGroup?.visible) return false;
    const wrapper = this._canvasQbScrollWrapper;
    if (!wrapper) return false;
    for (const chip of wrapper.children) {
      if (chip.visible && this._isStagePointInDisplayHitArea(chip, pt.x, pt.y)) return true;
    }
    return false;
  }

  /**
   * Window-level collect dismiss: any tap collects; only spin / quick-bet taps also queue respin.
   * @param {PointerEvent} e
   */
  _dispatchWinCollectPointerEnd(e) {
    const triggerSpin =
      this._winCollectPointerMaySpin || this._winCollectPointerOnSpinOrQuickBets(e);
    this._winCollectPointerMaySpin = false;
    if (triggerSpin) this._pendingSpinAfterWinClose = true;
    void this._performWinCollectTap();
  }

  _attachWinCollectWindowPointerGuard() {
    if (this._winCollectWindowGuardActive) return;
    this._winCollectWindowGuardActive = true;
    window.addEventListener('pointerdown', this._onWinCollectWindowPointerStart, true);
    window.addEventListener('pointerup', this._onWinCollectWindowPointerEnd, true);
    window.addEventListener('pointercancel', this._onWinCollectWindowPointerEnd, true);
  }

  _detachWinCollectWindowPointerGuard() {
    if (!this._winCollectWindowGuardActive) return;
    this._winCollectWindowGuardActive = false;
    window.removeEventListener('pointerdown', this._onWinCollectWindowPointerStart, true);
    window.removeEventListener('pointerup', this._onWinCollectWindowPointerEnd, true);
    window.removeEventListener('pointercancel', this._onWinCollectWindowPointerEnd, true);
  }

  /**
   * Consume pointer events during collect/big-win dismiss mode so no other UI actions
   * (spin, settings, chips, etc.) can process the same click gesture.
   * @param {PointerEvent} e
   */
  _consumeWinCollectPointerEvent(e) {
    try { e.preventDefault(); } catch { /* ignore */ }
    try { e.stopImmediatePropagation(); } catch { /* ignore */ }
    try { e.stopPropagation(); } catch { /* ignore */ }
  }

  /** Window capture start while collect / big-win overlay is up. */
  _onWinCollectWindowPointerStart(e) {
    if (!isPrimaryPointerAction(e)) return;
    if (!this._isWinCollectDismissActive()) return;
    this._winCollectPointerMaySpin = this._winCollectPointerOnSpinOrQuickBets(e);
    this._consumeWinCollectPointerEvent(e);
  }

  /** Window capture release while collect / big-win overlay is up (same as collect button). */
  _onWinCollectWindowPointerEnd(e) {
    if (!isPrimaryPointerAction(e)) return;
    if (!this._isWinCollectDismissActive()) return;
    // Abort any pending spin-hold before consuming the event. Without this, stopImmediatePropagation
    // below prevents the spin-hold window guard and Pixi pointerup from running, leaving the
    // 1300 ms hold timer alive — which would fire turbo autoplay even though the user released.
    if (this._spinHoldInProgress) {
      const hadTurbo = this._spinHoldTriggeredTurbo;
      this._stopSpinHoldTracking();
      this._resetSpinButtonHoldVisual();
      this._spinHoldTriggeredTurbo = false;
      this._spinPressAccepted = false;
      if (hadTurbo) {
        this._turboHoldActive = false;
        this.handleAutoplayToggle(false);
      }
    }
    this._consumeWinCollectPointerEvent(e);
    this._dispatchWinCollectPointerEnd(e);
  }

  /**
   * Main spin/stop tap (same as a short press on the spin control).
   */
  _spinOrStopMainButtonTap() {
    const now = performance.now();
    if (this._reelsSpinning) {
      this._requestStopSpin();
    } else if (!this._spinCycleActive) {
      /* Enforce the post-spin cooldown at the action level too. A "stop" press can pass the
       * pointer-down gate (spinning at press time) but land here as a fresh start if the fast
       * spin cycle ends before the release — this is where that instant re-spin is blocked. */
      if (now < this._respinBlockUntilTs) return;
      this._spinToggleLockUntilTs = Math.max(
        this._spinToggleLockUntilTs,
        now + this._spinToggleCooldownMs,
      );
      this.handleSpin();
    }
    /* Cycle active but reels not moving yet (session/API wait) — ignore spam tap. */
  }

  /**
   * Ends spin-button hold tracking and either stops hold-turbo autoplay or performs a normal tap.
   * Shared by Pixi pointerup and window pointerup/cancel so release is never missed on slow devices.
   * @param {import('pixi.js').FederatedPointerEvent | PointerEvent} e
   */
  _finalizeSpinHoldFromPointerEnd(e) {
    if (!isPrimaryPointerAction(e)) return;

    // Reject mismatched pointer IDs before touching any state.
    if (
      this._spinHoldInProgress &&
      this._spinHoldPointerId != null &&
      e.pointerId != null &&
      e.pointerId !== this._spinHoldPointerId
    ) {
      return;
    }

    // Snapshot state before any mutation so we can decide what action to take afterwards.
    const wasHoldInProgress = this._spinHoldInProgress;
    const hadTurbo = this._spinHoldTriggeredTurbo;
    const wasAccepted = this._spinPressAccepted;

    // Always abort hold tracking on pointer release — regardless of win-collect state.
    // Deferring this until after the win-collect check was the original bug: the hold timer
    // could fire turbo autoplay after the pointer was already released.
    if (wasHoldInProgress) {
      this._stopSpinHoldTracking();
      this._resetSpinButtonHoldVisual();
      this._spinHoldTriggeredTurbo = false;
      this._spinPressAccepted = false;
      if (hadTurbo) {
        this._turboHoldActive = false;
        this.handleAutoplayToggle(false);
      }
    }

    // Win-collect dismiss is handled by the window capture guard (_onWinCollectWindowPointerEnd).
    if (this._isWinCollectDismissActive()) return;

    // Nothing to do if this press was never accepted or no hold was in progress.
    if (!wasAccepted || !wasHoldInProgress || hadTurbo) return;
    this._spinOrStopMainButtonTap();
  }

  async _buildCanvasControls() {
    if (!this.slotMachine) return;
    const reelRoot = this.slotMachine.reelContainer?.root;
    if (!reelRoot) return;

    const controls = new Container();
    controls.eventMode = 'passive';
    controls.zIndex = 15;
    controls.sortableChildren = true;
    this._canvasControls = controls;
    reelRoot.addChild(controls);

    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap -4;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;

    const loadSvgTexture = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(Texture.from(img));
        img.onerror = () => resolve(Texture.EMPTY);
        img.src = assetUrl(url);
      });

    const [startTex, stopTex, collectWinTex, autoplayTex, betTex, buyBonusBtnTex, betSpeedBgTex, betSpeedActiveTex, betSpeedInactiveTex, autoplayPickerQbTex] =
      await Promise.all([
      loadSvgTexture('/assets/start.png'),
      loadSvgTexture('/assets/stop.png'),
      loadSvgTexture('/assets/collect_win.png'),
      loadSvgTexture('/assets/autoplay.png'),
      loadSvgTexture('/assets/bet.png'),
      loadSvgTexture('/assets/buy_bonus_button.png'),
      loadSvgTexture('/assets/bet_speed.png'),
      loadSvgTexture('/assets/bet_speed_active_arrow.png'),
      loadSvgTexture('/assets/bet_speed_inactive_arrow.png'),
      loadSvgTexture('/assets/lines_background.png'),
    ]);

    const makeButton = ({ texture, width, height, x, y, onClick }) => {
      // Set texture to use better quality scaling
      if (texture.source) {
        texture.source.scaleMode = 'linear';
      }
      
      const sprite = new Sprite(texture);
      sprite.width = width;
      sprite.height = height;
      sprite.x = x;
      sprite.y = y;
      sprite.cursor = 'pointer';
      sprite.eventMode = 'static';
      sprite.roundPixels = true;
      sprite.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        onClick();
      });
      controls.addChild(sprite);
      return sprite;
    };

    // padX=76 → frame right edge is at reelBlockW+76; start spin button ~40px before that so it straddles the frame border
    const spinX = reelBlockW;
    /** Negative = move start / autoplay / bet cluster upward together */
    const spinY = Math.round((reelBlockH - 194) / 2) - 55;

    // Single spin/stop toggle button (container: glyph + autoplay remaining on stop)
    if (startTex.source) startTex.source.scaleMode = 'linear';
    const spinRoot = new Container();
    spinRoot.x = spinX + 194 / 2;
    spinRoot.y = spinY + 194 / 2;
    spinRoot.eventMode = 'static';
    spinRoot.cursor = 'pointer';
    spinRoot.roundPixels = true;
    spinRoot.hitArea = new Rectangle(-97, -97, 194, 194);

    const spinImg = new Sprite(startTex);
    spinImg.width = 194;
    spinImg.height = 194;
    spinImg.anchor.set(0.5);

    const remainLabel = new Text({
      text: '',
      style: {
        fontFamily: '"Montserrat", "Arial", sans-serif',
        fontSize: 26,
        fontWeight: '900',
        fill: 0x000000,
        align: 'center',
      },
    });
    remainLabel.anchor.set(0.5, 0.5);
    remainLabel.x = 5;
    remainLabel.y = -6;
    remainLabel.visible = false;
    remainLabel.eventMode = 'none';

    const spinTurboHint = createArcTextController({
      radius: 70,
      fontSize: 12,
      fontWeight: '800',
      fill: 0xffffff,
      letterSpacing: 1,
      yOffset: -2,
    });
    spinTurboHint.setText(this._tu('HOLD_FOR_TURBO_SPIN'));
    spinTurboHint.container.zIndex = 2;

    spinRoot.addChild(spinImg, spinTurboHint.container, remainLabel);
    spinRoot.sortableChildren = true;
    controls.addChild(spinRoot);
    this._spinButtonRoot = spinRoot;
    this._spinButtonSprite = spinImg;
    this._spinAutoplayRemainText = remainLabel;
    this._spinTurboHint = spinTurboHint;
    this._spinStartTex = startTex;
    this._spinStopTex = stopTex;
    this._spinCollectTex = collectWinTex;
    this._spinButtonBaseScale = 1;
    this._layoutSpinTurboHint();
    this._syncSpinTurboHintVisible();
    spinRoot.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (this._isWinCollectDismissActive()) return;
      const now = performance.now();
      // Cooldown only applies to starting a spin from idle — never block an immediate stop tap
      // after start (same button reuses the lock from the previous pointerdown).
      if (!this._spinning && (now < this._spinToggleLockUntilTs || now < this._respinBlockUntilTs)) {
        this._spinPressAccepted = false;
        return;
      }
      if (!this._spinning) {
        this._spinToggleLockUntilTs = now + this._spinToggleCooldownMs;
      }
      if (this._spinHoldInProgress) return;
      this._spinPressAccepted = true;
      this._spinHoldInProgress = true;
      this._spinHoldTriggeredTurbo = false;
      this._spinHoldPointerId = e.pointerId;
      this._spinHoldStartTs = performance.now();
      this._attachSpinHoldWindowReleaseGuard();
      const canvas = this.app?.canvas;
      const native = e.nativeEvent;
      if (
        canvas &&
        native &&
        typeof canvas.setPointerCapture === 'function' &&
        typeof native.pointerId === 'number'
      ) {
        try {
          canvas.setPointerCapture(native.pointerId);
          this._spinHoldPointerCaptureActive = true;
        } catch {
          this._spinHoldPointerCaptureActive = false;
        }
      } else {
        this._spinHoldPointerCaptureActive = false;
      }
      const tick = () => {
        if (!this._spinHoldInProgress) return;
        const elapsed = performance.now() - this._spinHoldStartTs;
        let progress = 0;
        const visualDelay = Math.max(0, Number(this._spinHoldVisualDelayMs) || 0);
        if (elapsed <= visualDelay) {
          this._resetSpinButtonHoldVisual();
        } else {
          const holdSpan = Math.max(1, this._spinHoldMs - visualDelay);
          progress = (elapsed - visualDelay) / holdSpan;
          this._setSpinButtonHoldProgress(progress);
        }
        if (progress < 1) {
          this._spinHoldRaf = requestAnimationFrame(tick);
        } else {
          this._spinHoldRaf = null;
        }
      };
      this._spinHoldRaf = requestAnimationFrame(tick);
      if (this._spinHoldTimer != null) clearTimeout(this._spinHoldTimer);
      this._spinHoldTimer = setTimeout(() => {
        this._spinHoldTimer = null;
        if (!this._spinHoldInProgress || this._spinHoldTriggeredTurbo) return;
        this._activateTurboFromHold();
      }, this._spinHoldMs);
    });
    const onSpinPointerRelease = (e) => {
      this._finalizeSpinHoldFromPointerEnd(e);
    };
    spinRoot.on('pointerup', onSpinPointerRelease);
    spinRoot.on('pointerupoutside', onSpinPointerRelease);

    if (
      betSpeedBgTex?.width > 0 &&
      betSpeedActiveTex?.width > 0 &&
      betSpeedInactiveTex?.width > 0
    ) {
      if (betSpeedBgTex.source) betSpeedBgTex.source.scaleMode = 'linear';
      if (betSpeedActiveTex.source) betSpeedActiveTex.source.scaleMode = 'linear';
      if (betSpeedInactiveTex.source) betSpeedInactiveTex.source.scaleMode = 'linear';

      this._betSpeedActiveTex = betSpeedActiveTex;
      this._betSpeedInactiveTex = betSpeedInactiveTex;

      const betSpeedWrap = new Container();
      betSpeedWrap.eventMode = 'static';
      betSpeedWrap.cursor = 'pointer';

      const bgW = BET_SPEED_PLATE_W;
      const bgH = (betSpeedBgTex.height / betSpeedBgTex.width) * bgW * BET_SPEED_HEIGHT_MUL;
      const betBg = new Sprite(betSpeedBgTex);
      betBg.anchor.set(0.5);
      betBg.width = bgW;
      betBg.height = bgH;

      const arrowSprites = [];
      const arrowW = BET_SPEED_ARROW_W;
      /** Horizontal gap between arrow edges (row stays centered on the plate). */
      const gapBetweenArrows = 4;
      const totalArrowRowW = 3 * arrowW + 2 * gapBetweenArrows;
      const arrowRow = new Container();
      for (let i = 0; i < 3; i++) {
        const a = new Sprite(betSpeedInactiveTex);
        a.anchor.set(0.5);
        const iw = betSpeedInactiveTex.width;
        const ih = betSpeedInactiveTex.height;
        a.width = arrowW;
        a.height = (ih / iw) * arrowW;
        a.x = -totalArrowRowW / 2 + arrowW / 2 + i * (arrowW + gapBetweenArrows);
        arrowSprites.push(a);
        arrowRow.addChild(a);
      }
      arrowRow.y = -bgH * 0.02;
      this._betSpeedArrowSprites = arrowSprites;

      betSpeedWrap.addChild(betBg, arrowRow);
      betSpeedWrap.x = spinRoot.x;
      const betSpeedLowerOffset = 14;
      betSpeedWrap.y = spinRoot.y + 194 / 2 - bgH / 2 + betSpeedLowerOffset;

      const hitPad = 3;
      betSpeedWrap.hitArea = new Rectangle(
        -bgW / 2 - hitPad,
        -bgH / 2 - hitPad,
        bgW + 2 * hitPad,
        bgH + 2 * hitPad,
      );

      const stopBetSpeedBubble = (e) => {
        if (typeof e.stopPropagation === 'function') e.stopPropagation();
      };
      betSpeedWrap.on('pointerdown', stopBetSpeedBubble);
      betSpeedWrap.on('pointerup', stopBetSpeedBubble);
      betSpeedWrap.on('pointerupoutside', stopBetSpeedBubble);
      betSpeedWrap.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        stopBetSpeedBubble(e);
        if (this._settingsQuickSpinOn) return;
        this._betSpeedTier = this._betSpeedTier >= 3 ? 1 : this._betSpeedTier + 1;
        this._syncBetSpeedArrows();
        sound.play('uiSpinSpeed');
      });

      controls.addChild(betSpeedWrap);
      this._betSpeedWrap = betSpeedWrap;
      this._betSpeedPlateH = bgH;
      this._betSpeedTier = 1;
      this._syncBetSpeedArrows();
    }

    const autoplayBtnW = 100;
    const autoplayBtnH = 100;
    const pickBtnW = 100;
    const pickBtnH = 95;
    const autoplayCluster = new Container();
    autoplayCluster.x = 0;
    autoplayCluster.y = 0;
    autoplayCluster.eventMode = 'static';
    autoplayCluster.sortableChildren = true;
    autoplayCluster.zIndex = 20;

    const pickerRow = new Container();
    pickerRow.visible = false;
    pickerRow.zIndex = 1;
    const fontQB = '"Montserrat", "Arial", sans-serif';
    const roundSpecs = [
      { label: '10', remaining: 10 },
      { label: '20', remaining: 20 },
      { label: '50', remaining: 50 },
      { label: '100', remaining: 100 },
      { label: '∞', remaining: null },
    ];
    const qbTex = autoplayPickerQbTex?.width > 0 ? autoplayPickerQbTex : Texture.EMPTY;
    if (qbTex?.source) qbTex.source.scaleMode = 'linear';
    roundSpecs.forEach((spec, i) => {
      const bg = new Sprite(qbTex);
      bg.width = pickBtnW;
      bg.height = pickBtnH;
      const amt = new Text({
        text: spec.label,
        style: {
          fontFamily: fontQB,
          fontSize: spec.label === '∞' ? 50 : fontSizeWhenFormattedLong(spec.label, 26, 18),
          fontWeight: '700',
          fill: 0xffffff,
        },
      });
      amt.anchor.set(0.5, 0.5);
      amt.x = pickBtnW / 2;
      amt.y = pickBtnH * 0.48;

      const btn = new Container();
      btn.addChild(bg, amt);
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.hitArea = new Rectangle(0, 0, pickBtnW, pickBtnH);
      btn.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        sound.play('uiClick');
        this._startAutoplayWithRounds(spec.remaining);
      });
      pickerRow.addChild(btn);
    });

    const autoplaySprite = new Sprite(autoplayTex);
    autoplaySprite.width = autoplayBtnW;
    autoplaySprite.height = autoplayBtnH;
    if (autoplayTex?.source) autoplayTex.source.scaleMode = 'linear';
    autoplaySprite.cursor = 'pointer';
    autoplaySprite.eventMode = 'static';
    autoplaySprite.roundPixels = true;
    autoplaySprite.zIndex = 2;
    autoplaySprite.on('pointerup', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._onAutoplayIconPointerUp();
    });

    autoplayCluster.addChild(pickerRow, autoplaySprite);
    controls.addChild(autoplayCluster);
    this._autoplayCluster = autoplayCluster;
    this._autoplaySprite = autoplaySprite;
    this._autoplayPickerRow = pickerRow;

    const betPopupBtn = makeButton({
      texture: betTex,
      width: 100,
      height: 100,
      x: 0,
      y: 0,
      onClick: () => this._toggleBetPopup(),
    });
    this._betPopupTriggerBtn = betPopupBtn;
    if (this._buyBonusEnabled) this._createBuyBonusButton(controls, buyBonusBtnTex);
    this._layoutDesktopSpinSideButtons(spinX, spinY);
    this._betPopupTriggerControlsIndex = controls.getChildIndex(betPopupBtn);

    /* ── Balance / Last-win / Quick-bets panels ──────────────────────────
     * HTML design positions (1922×1080):
     *   balance:    left=30,   top=933
     *   last win:   left=1696, top=933
     *   quick bets: left=631,  top=930
     *
     * Converting to reelRoot-local via effective scale ≈ 1.183:
     *   balX ≈ -344,  winX ≈ 1063,  qbStartX ≈ 164,  panelY ≈ reelBlockH+76
     * ------------------------------------------------------------------ */
    const [balBgTex, homeTex, fullscreenTex, fullscreenOffTex, soundOnTex, soundOffTex, menuTex, arrowLeftTex, arrowRightTex, qbNormTex, qbSelTex] = await Promise.all([
      loadSvgTexture('/assets/balance_background.png'),
      // Assets.load('/assets/mobile_balance_background.png').catch(() => Texture.EMPTY),
      loadSvgTexture('/assets/home.svg'),
      loadSvgTexture('/assets/full_screen.png'),
      loadSvgTexture('/assets/full_screen_off.png'),
      loadSvgTexture('/assets/sound_on.png'),
      loadSvgTexture('/assets/sound_off.png'),
      loadSvgTexture('/assets/menu.png'),
      loadSvgTexture('/assets/arrow_left.svg'),
      loadSvgTexture('/assets/arrow_right.svg'),
      loadSvgTexture('/assets/quick_bet.png'),
      loadSvgTexture('/assets/selected_quick_bet.png'),
    ]);
    this._mobilePortraitTopStatsTex = null;

    const linesBgTex = autoplayPickerQbTex;
    this._qbNormTex = qbNormTex?.width ? qbNormTex : linesBgTex;
    this._qbSelTex = qbSelTex?.width ? qbSelTex : this._qbNormTex;

    /* Lines panel (left side, shows active line count) — hidden for now.
    const linesPanelW = 151;
    const linesPanelH = 133;
    
    const linesPanel = new Container();
    
    const linesBg = new Sprite(linesBgTex);
    linesBg.width = linesPanelW;
    linesBg.height = linesPanelH;
    
    const linesNum = new Text({
      text: `${gameConfig.paylines.length}`,
      style: {
        fontFamily: 'Montserrat',
        fontSize: 37.9167,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'center',
      }
    });
    linesNum.anchor.set(0.5, 0.5);
    linesNum.x = linesPanelW / 2;
    linesNum.y = linesPanelH * 0.35;
    
    const linesLabel = new Text({
      text: this._tu('LINES'),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 20.4167,
        fontWeight: '600',
        fill: 0xffffff,
        align: 'center',
      }
    });
    linesLabel.anchor.set(0.5, 0.5);
    linesLabel.x = linesPanelW / 2;
    linesLabel.y = linesPanelH * 0.60;
    this._canvasLinesLabelText = linesLabel;
    this._relayoutCanvasLinesLabel();
    
    linesPanel.addChild(linesBg, linesNum, linesLabel);
    linesPanel.x = -250;  // Position to the left of reels, closer to frame
    linesPanel.y = spinY + 30;  // Align roughly with spin button
    linesPanel.eventMode = 'passive';
    
    controls.addChild(linesPanel);
    this._canvasLinesPanel = linesPanel;
    */
    this._canvasLinesPanel = null;
    this._canvasLinesLabelText = null;

    /* ── Bottom Bar Container (balance, win, quick-bets, icons) ──────────── */
    const bottomBar = new Container();
    bottomBar.sortableChildren = true;
    bottomBar.y = reelBlockH + 50;  // Moved closer to reels (was +76)
    bottomBar.eventMode = 'passive';
    controls.addChild(bottomBar);
    this._canvasBottomBar = bottomBar;

    const panelW  = 196;
    const panelH  = 90;
    /** Merged portrait top row (BALANCE | prompt | LAST WIN): height & column split — also applied in {@link #_relayoutBottomBarForViewport}. */
    const portraitTopStatsH = PORTRAIT_MERGED_STATS_BAR_H;
    const portraitTopColBal = 0.38;
    const portraitTopColMid = 0.26;
    const portraitTopColWin = 0.36;
    const font    = '"Montserrat", "Arial", sans-serif';

    const layoutStatPanelStack = (lbl, val, cur, boxH, centerX) => {
      const gapLabelRow = 2;
      const gapInline = 4;
      lbl.anchor.set(0.5, 0);
      cur.anchor.set(0, 0.5);
      val.anchor.set(0, 0.5);
      lbl.x = centerX;
      const rowW = cur.width + gapInline + val.width;
      const rowH = Math.max(cur.height, val.height);
      const total = lbl.height + gapLabelRow + rowH;
      const stackBiasY = -3;
      const y = Math.max(4, (boxH - total) / 2) + stackBiasY;
      const titleLower = 2;
      lbl.y = y + titleLower;
      const rowY = lbl.y + lbl.height + gapLabelRow + rowH / 2;
      const rowLeft = centerX - rowW / 2;
      cur.x = rowLeft;
      val.x = rowLeft + cur.width + gapInline;
      cur.y = rowY;
      val.y = rowY;
    };

    const makeStatPanel = (x, labelStr) => {
      const bg = new Sprite(balBgTex);
      bg.width = panelW; bg.height = panelH;

      const lbl = new Text({ text: labelStr, style: { fontFamily: font, fontSize: 10, fontWeight: '700', fill: 0xffffff } });
      const valStyle = {
        fontFamily: font,
        fontSize: 20,
        fontWeight: '700',
        fill: T.hudValue,
        dropShadow: HUD_VALUE_SHADOW,
      };
      const val = new Text({ text: '0.00', style: valStyle });
      const cur = new Text({
        text: this._currencyLabel(),
        style: { ...valStyle, fill: T.hudCurrency },
      });

      const relayout = () => layoutStatPanelStack(lbl, val, cur, panelH, panelW / 2);
      relayout();

      const panel = new Container();
      panel.addChild(bg, lbl, val, cur);
      panel.x = x; panel.y = 0;
      bottomBar.addChild(panel);
      return { label: lbl, val, currency: cur, panel, relayout };
    };

    /* Quick-bets scrollable masked group (show ~6 buttons at a time) */
    const qbMaskG = new Graphics();
    const qbVisibleW = 6 * (DESKTOP_QB_CHIP_REF_W + 8);  // 6 buttons visible
    const qbH = DESKTOP_QB_CHIP_REF_H;
    qbMaskG.rect(0, 0, qbVisibleW, qbH).fill(0xffffff);
    
    // Center the entire bottom bar layout (balance + arrow + qb + arrow + win)
    const reelCenter = reelBlockW / 2;
    const arrowBtnW = arrowLeftTex.width;  // Use natural arrow dimensions
    const arrowBtnH = arrowLeftTex.height;
    const qbToArrowGap = 20;  // 20px between quick bets and arrows
    const arrowToPanelGap = 40;  // 40px between arrows and balance/win panels
    
    // Total width of the centered group: panel + gap + arrow + gap + qb + gap + arrow + gap + panel
    const totalGroupW = panelW + arrowToPanelGap + arrowBtnW + qbToArrowGap + qbVisibleW + qbToArrowGap + arrowBtnW + arrowToPanelGap + panelW;
    const groupStartX = reelCenter - totalGroupW / 2;
    
    // Calculate positions from left to right
    const balanceX = groupStartX;
    const leftArrowX = balanceX + panelW + arrowToPanelGap;
    const qbX = leftArrowX + arrowBtnW + qbToArrowGap;
    const rightArrowX = qbX + qbVisibleW + qbToArrowGap;
    const winX = rightArrowX + arrowBtnW + arrowToPanelGap;
    
    const balPanel = makeStatPanel(winX + CANVAS_STAT_SHIFT_LAST_WIN_X, this._tu('BALANCE'));
    const winPanel = makeStatPanel(balanceX + CANVAS_STAT_SHIFT_BALANCE_X, this._tu('LAST_WIN'));
    this._canvasBalanceLabelText = balPanel.label;
    this._canvasWinLabelText = winPanel.label;
    this._canvasBalanceText = balPanel.val;
    this._canvasWinText = winPanel.val;
    this._canvasBalanceCurrencyText = balPanel.currency;
    this._canvasWinCurrencyText = winPanel.currency;
    this._canvasBalancePanel = balPanel.panel;
    this._canvasWinPanel = winPanel.panel;
    this._canvasBalancePanelRelayout = balPanel.relayout;
    this._canvasWinPanelRelayout = winPanel.relayout;

    /* Vertical-only merged header background: LAST WIN | two-line bet prompt | BALANCE */
    const portraitTopBar = new Container();
    portraitTopBar.visible = false;
    const portraitBg = new Sprite(balBgTex);
    portraitBg.width = reelBlockW - 20;
    portraitBg.height = PORTRAIT_MERGED_STATS_BG_HEIGHT;
    portraitBg.y = (portraitTopStatsH - PORTRAIT_MERGED_STATS_BG_HEIGHT) / 2;
    portraitTopBar.addChild(portraitBg);
    /* Currency matches amount size on the same row. */
    const slotLabelStyle = { fontFamily: font, fontSize: 11, fontWeight: '700', fill: 0xffffff };
    const slotValueStyle = { fontFamily: font, fontSize: 22, fontWeight: '700', fill: T.hudValue, dropShadow: HUD_VALUE_SHADOW };
    const slotCurrencyStyle = { ...slotValueStyle, fill: T.hudCurrency };
    const makeTopSlot = (label, valueText, x) => {
      const g = new Container();
      g.x = x;
      const lbl = new Text({ text: label, style: slotLabelStyle });
      const val = new Text({ text: valueText, style: slotValueStyle });
      const cur = new Text({ text: this._currencyLabel(), style: slotCurrencyStyle });
      const gapLabelRow = 2;
      const gapInline = 4;
      const relayout = () => {
        lbl.anchor.set(0.5, 0);
        cur.anchor.set(0, 0.5);
        val.anchor.set(0, 0.5);
        lbl.x = 0;
        const rowW = cur.width + gapInline + val.width;
        const rowH = Math.max(cur.height, val.height);
        const total = lbl.height + gapLabelRow + rowH;
        const stackBiasY = -3;
        const y0 = Math.max(4, (portraitTopStatsH - total) / 2) + stackBiasY;
        const titleLower = 2;
        lbl.y = y0 + titleLower;
        const rowY = lbl.y + lbl.height + gapLabelRow + rowH / 2;
        const rowLeft = -rowW / 2;
        cur.x = rowLeft;
        val.x = rowLeft + cur.width + gapInline;
        cur.y = rowY;
        val.y = rowY;
      };
      relayout();
      g.addChild(lbl, val, cur);
      portraitTopBar.addChild(g);
      return { label: lbl, val, currency: cur, relayout };
    };
    const ptW0 = portraitBg.width;
    const ptWB = ptW0 * portraitTopColBal;
    const ptWC = ptW0 * portraitTopColMid;
    const ptWW = ptW0 * portraitTopColWin;
    const balTop = makeTopSlot(
      this._tu('BALANCE'),
      this._canvasBalanceText.text || '0.00',
      ptWB + ptWC + ptWW * 0.5 + CANVAS_STAT_SHIFT_LAST_WIN_X,
    );
    this._portraitBalanceLabelText = balTop.label;
    this._portraitBalanceValueText = balTop.val;
    this._portraitBalanceCurrencyText = balTop.currency;
    this._relayoutPortraitBalanceStack = balTop.relayout;
    const centerSlot = new Container();
    centerSlot.x = ptWB + ptWC * 0.5;
    const centerPrompt = new Text({
      text: this._twoLineText('PLEASE_PLACE_YOUR_BET'),
      style: {
        fontFamily: font,
        fontSize: PORTRAIT_BET_PROMPT_FONT_SIZE,
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
        lineHeight: PORTRAIT_BET_PROMPT_LINE_HEIGHT,
      },
    });
    centerPrompt.anchor.set(0.5, 0.5);
    centerPrompt.x = 0;
    centerPrompt.y = portraitTopStatsH * 0.46;
    centerSlot.addChild(centerPrompt);
    this._portraitTopPromptText = centerPrompt;
    portraitTopBar.addChild(centerSlot);
    const winTop = makeTopSlot(
      this._tu('LAST_WIN'),
      this._canvasWinText.text || '0.00',
      ptWB * 0.5 + CANVAS_STAT_SHIFT_BALANCE_X,
    );
    this._portraitWinLabelText = winTop.label;
    this._portraitWinValueText = winTop.val;
    this._portraitWinCurrencyText = winTop.currency;
    this._relayoutPortraitWinStack = winTop.relayout;
    bottomBar.addChildAt(portraitTopBar, 0);
    this._portraitTopStatsBar = portraitTopBar;
    this._portraitTopStatsBgSprite = portraitBg;
    this._portraitTopStatsDefaultTex = balBgTex;

    qbMaskG.x = qbX; qbMaskG.y = 2;
    bottomBar.addChild(qbMaskG);
    this._canvasQbMaskG = qbMaskG;

    const qbGroup = new Container();
    qbGroup.mask = qbMaskG;
    qbGroup.x = qbX; qbGroup.y = 2;
    qbGroup.eventMode = 'static';
    bottomBar.addChild(qbGroup);
    this._canvasQbGroup = qbGroup;
    this._qbScrollX = 0;
    this._qbChipW = DESKTOP_QB_CHIP_REF_W;
    this._qbChipH = DESKTOP_QB_CHIP_REF_H;
    this._qbChipGap = 8;
    this._qbChipStride = 108;
    this._ensureCanvasQbScrollHandlers();

    /* Arrow buttons for scrolling quick bets */
    const arrowY = 2 + (qbH - arrowBtnH) / 2;  // Vertically centered with quick bets
    const makeArrowButton = (tex, x, scrollDir) => {
      const sprite = new Sprite(tex);
      // Use natural texture dimensions (no width/height override)
      sprite.x = x;
      sprite.y = arrowY;
      sprite.cursor = 'pointer';
      sprite.eventMode = 'static';
      sprite.roundPixels = true;
      sprite.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        this._nudgeQbScrollByArrow(scrollDir);
      });
      bottomBar.addChild(sprite);
      return sprite;
    };
    
    this._qbArrowLeft = makeArrowButton(arrowLeftTex, leftArrowX, 1);  // Scroll right (positive direction)
    this._qbArrowRight = makeArrowButton(arrowRightTex, rightArrowX, -1);  // Scroll left (negative direction)

    /* "Please place your bets" — just under the symbol grid / frame, above the bottom bar */
    const underFramePromptY = reelBlockH + 35;
    const betPromptText = this._tu('PLEASE_PLACE_YOUR_BETS');
    const betPrompt = new Text({
      text: betPromptText,
      style: {
        fontFamily: '"Montserrat", "Arial", sans-serif',
        fontSize: 20,
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
        letterSpacing: 0.5,
        wordWrap: true,
        wordWrapWidth: Math.max(280, reelBlockW - 48),
      },
    });
    betPrompt.anchor.set(0.5, 0.5);
    betPrompt.x = reelBlockW / 2;
    betPrompt.y = underFramePromptY;
    betPrompt.eventMode = 'none';
    betPrompt.roundPixels = true;
    betPrompt.zIndex = 16;
    controls.addChild(betPrompt);
    this._canvasBetPrompt = betPrompt;
    this._setBetPromptVisible(this._betPromptVisible);

    /* Side icon buttons (home, fullscreen, sound, menu) bottom-aligned with balance/win */
    const iconBtnSize = CANVAS_BOTTOM_ICON_SIZE;
    const iconY = panelH - iconBtnSize;  // bottom-aligned with balance/win panels
    const iconGap = 80;  // 60px distance from balance/win panels

    this._canvasIconSprites = [];
    const makeIconButton = (tex, x, onClick) => {
      const sprite = new Sprite(tex);
      sprite.width = iconBtnSize;
      sprite.height = iconBtnSize;
      sprite.x = x;
      sprite.y = iconY;
      sprite.cursor = 'pointer';
      sprite.eventMode = 'static';
      sprite.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        onClick();
      });
      bottomBar.addChild(sprite);
      this._canvasIconSprites.push(sprite);
      return sprite;
    };

    // Calculate icon button positions based on the centered panels
    const balanceLeftEdge = balanceX + CANVAS_STAT_SHIFT_BALANCE_X;
    const winRightEdge = winX + panelW + CANVAS_STAT_SHIFT_LAST_WIN_X;

    // Left side: Home (left) + Fullscreen (right)
    const iconSpacing = 10;  // Small gap between the two icons on same side
    // Home icon hidden on all devices.
    // makeIconButton(homeTex, balanceLeftEdge - iconGap - iconBtnSize * 2 - iconSpacing, () => {
    //   sound.play('uiClick');
    //   window.location.href = '/';
    // });
    const homeHidden = new Sprite(Texture.EMPTY);
    homeHidden.visible = false;
    homeHidden.renderable = false;
    homeHidden.eventMode = 'none';
    this._canvasIconSprites.push(homeHidden);
    this._fullscreenTex = fullscreenTex;
    this._fullscreenOffTex = fullscreenOffTex;
    this._canvasFullscreenIconSprite = makeIconButton(fullscreenTex, balanceLeftEdge - iconGap - iconBtnSize * 2 - iconSpacing, () => {
      sound.play('uiClick');
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    });
    this._syncCanvasFullscreenIcon();

    // Right side: Sound + Menu (60px right of win panel)
    this._soundOnTex = soundOnTex;
    this._soundOffTex = soundOffTex;
    this._canvasSoundIconSprite = makeIconButton(soundOnTex, winRightEdge + iconGap, () => {
      this._settingsSoundOn = !this._settingsSoundOn;
      sound.setMuted(!this._settingsSoundOn);
      this._syncCanvasSoundIcon();
      /* Play *after* setMuted: when turning sound back ON we want the
       * confirmation click to be audible. */
      sound.play('uiClick');
    });
    this._syncCanvasSoundIcon();
    makeIconButton(menuTex, winRightEdge + iconGap + iconBtnSize + iconSpacing, () => {
      this._toggleSettingsPanel();
    });

    await this._createBetPopupPixi();

    this._snapshotDesktopCanvasLayout();
  }

  _syncCanvasSoundIcon() {
    const sprite = this._canvasSoundIconSprite;
    if (!sprite || !this._soundOnTex || !this._soundOffTex) return;
    sprite.texture = this._settingsSoundOn ? this._soundOnTex : this._soundOffTex;
  }

  _syncCanvasFullscreenIcon() {
    const sprite = this._canvasFullscreenIconSprite;
    if (!sprite || !this._fullscreenTex || !this._fullscreenOffTex) return;
    sprite.texture = document.fullscreenElement ? this._fullscreenOffTex : this._fullscreenTex;
  }

  /** Called once after canvas controls are built — restores baseline positions when exiting mobile. */
  _snapshotDesktopCanvasLayout() {
    const spin = this._spinButtonRoot;
    const ap = this._autoplayCluster;
    const betBtn = this._betPopupTriggerBtn;
    const bb = this._canvasBottomBar;
    const prompt = this._canvasBetPrompt;
    const lines = this._canvasLinesPanel;
    const speed = this._betSpeedWrap;
    if (!spin || !bb) return;
    this._canvasDesktopLayout = {
      spinRoot: { x: spin.x, y: spin.y },
      autoplayCluster: ap ? { x: ap.x, y: ap.y } : null,
      betPopupTrigger: betBtn ? { x: betBtn.x, y: betBtn.y } : null,
      buyBonusButton: this._buyBonusButton
        ? { x: this._buyBonusButton.x, y: this._buyBonusButton.y }
        : null,
      bottomBar: { y: bb.y },
      betPrompt: prompt ? { x: prompt.x, y: prompt.y } : null,
      betPromptWordWrapWidth: prompt?.style?.wordWrapWidth ?? null,
      linesPanel: lines ? { x: lines.x, y: lines.y } : null,
      betSpeedWrap: speed ? { x: speed.x, y: speed.y } : null,
    };
    const sp = this._spinButtonSprite;
    const aps = this._autoplaySprite;
    this._desktopChromeDefaults = {
      spinImg: sp ? { w: sp.width, h: sp.height } : { w: 194, h: 194 },
      spinHitHalf: 97,
      autoplay: aps ? { w: aps.width, h: aps.height } : { w: 100, h: 100 },
      betPopup: betBtn ? { w: betBtn.width, h: betBtn.height } : { w: 100, h: 100 },
      betPromptFontSize: prompt?.style?.fontSize ?? 20,
      iconBtnSize: CANVAS_BOTTOM_ICON_SIZE,
      betSpeedScale: speed ? speed.scale.x : 1,
    };
  }

  /**
   * Bottom-bar local X → stage X, including cabinet scale, slot-root aspect lock, and world pivot.
   * @param {number} localX
   * @returns {number}
   */
  _stageXFromBottomBarLocal(localX) {
    let x = localX;
    let y = 0;
    let node = this._canvasBottomBar;
    const stage = this.app?.stage ?? null;
    while (node && node !== stage) {
      const sx = node.scale?.x ?? 1;
      const sy = node.scale?.y ?? 1;
      x = node.x + (x - (node.pivot?.x ?? 0)) * sx;
      y = node.y + (y - (node.pivot?.y ?? 0)) * sy;
      node = node.parent;
    }
    return x;
  }

  /**
   * Recenters balance / quick-bet strip / win row when chip size changes (mobile vs desktop).
   * Portrait (phone + tablet): vertical quick-bet rail + merged stats bar (mobile HUD).
   */
  _relayoutBottomBarForViewport() {
    if (!this._canvasBottomBar || !this._canvasBalancePanel || !this._canvasWinPanel) return;

    const mobile = this._isPortraitMobileViewport();
    const hudFit = mobile ? this._getMobileHudFit() : 1;
    let btnW = mobile ? Math.max(70, Math.round(MOBILE_QB_CHIP_REF_W * hudFit)) : DESKTOP_QB_CHIP_REF_W;
    let btnH = mobile ? Math.max(58, Math.round(MOBILE_QB_CHIP_REF_H * hudFit)) : DESKTOP_QB_CHIP_REF_H;
    let gap = 8;
    let stride = btnW + gap;
    if (this._isPortraitMobileVerticalQuickBets()) {
      const chip = this._getPortraitPhoneVerticalQuickBetChipMetrics();
      btnW = chip.btnW;
      btnH = chip.btnH;
      gap = chip.gap;
      stride = chip.stride;
    }
    this._qbChipW = btnW;
    this._qbChipH = btnH;
    this._qbChipGap = gap;
    this._qbChipStride = stride;

    const panelW = 196;
    const panelH = 90;
    /** Keep in sync with merged stats row in {@link #_buildCanvasControls}. */
    const portraitTopStatsH = PORTRAIT_MERGED_STATS_BAR_H;
    const portraitTopColBal = 0.38;
    const portraitTopColMid = 0.26;
    const portraitTopColWin = 0.36;
    const pad = 10;
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap - 4;

    const arL = this._qbArrowLeft;
    const arR = this._qbArrowRight;

    if (this._isPortraitStackedHud()) {
      const hudFit = this._getMobileHudFit();
      const statsBarNudgeY = PORTRAIT_TUNING.phone.statsBarNudgeY ?? 0;
      const qbY = portraitTopStatsH * hudFit + statsBarNudgeY;

      if (arL) arL.visible = false;
      if (arR) arR.visible = false;

      this._portraitPhoneQbMinLeft = null;
      if (this._canvasQbMaskG) {
        this._canvasQbMaskG.visible = false;
        this._canvasQbMaskG.eventMode = 'none';
      }
      if (this._canvasQbGroup) this._canvasQbGroup.mask = null;
      this._qbScrollVisibleW = null;

      if (this._canvasBalancePanel) this._canvasBalancePanel.visible = false;
      if (this._canvasWinPanel) this._canvasWinPanel.visible = false;
      this._setBetPromptVisible(this._betPromptVisible);
      if (this._portraitTopStatsBar) {
        this._portraitTopStatsBar.visible = true;
        this._portraitTopStatsBar.y = statsBarNudgeY;
        const bg = this._portraitTopStatsBgSprite ?? this._portraitTopStatsBar.children?.[0];
        const leftSlot = this._portraitTopStatsBar.children?.[1];
        const centerSlot = this._portraitTopStatsBar.children?.[2];
        const rightSlot = this._portraitTopStatsBar.children?.[3];

        const fullW = Math.max(240, reelBlockW - 2 * pad);
        const mergedW = Math.min(
          reelBlockW - 2 * pad,
          Math.max(200, Math.floor(fullW * 0.82)),
        );
        this._portraitTopStatsBar.scale.set(1);
        this._portraitTopStatsBar.x = this._resolvePortraitPhoneMergedStatsBarX(reelBlockW, mergedW);
        if (bg) {
          bg.visible = true;
          if (this._mobilePortraitTopStatsTex) {
            bg.texture = this._mobilePortraitTopStatsTex;
          } else if (this._portraitTopStatsDefaultTex) {
            bg.texture = this._portraitTopStatsDefaultTex;
          }
          const bgExtraW = PORTRAIT_TUNING.phone.mergedStatsBgExtraW ?? PORTRAIT_MERGED_STATS_BG_EXTRA_W;
          const bgW = mergedW + bgExtraW;
          bg.width = bgW;
          bg.x = -bgExtraW / 2;
          const bgH = Math.round(PORTRAIT_MERGED_STATS_BG_HEIGHT * hudFit);
          bg.height = bgH;
          bg.y = (portraitTopStatsH - bgH) / 2;
        }
        this._syncPortraitTopStatsTextScale(hudFit);
        const wB = mergedW * portraitTopColBal;
        const wC = mergedW * portraitTopColMid;
        const wW = mergedW * portraitTopColWin;
        const sideIn = PORTRAIT_TUNING.phone.mergedStatsSideTowardCenterPx ?? 0;
        if (leftSlot) {
          leftSlot.visible = true;
          leftSlot.x = wB + wC + wW * 0.5 + CANVAS_STAT_SHIFT_LAST_WIN_X - sideIn;
        }
        if (centerSlot) centerSlot.x = wB + wC * 0.5;
        if (rightSlot) {
          rightSlot.visible = true;
          rightSlot.x = wB * 0.5 + CANVAS_STAT_SHIFT_BALANCE_X + sideIn;
        }
        this._portraitTopStatsBar.zIndex = 0;
        if (this._canvasBalancePanel) this._canvasBalancePanel.zIndex = 0;
        if (this._canvasWinPanel) this._canvasWinPanel.zIndex = 0;
      }

      if (this._canvasQbMaskG) this._canvasQbMaskG.zIndex = 8;
      if (this._canvasQbGroup) this._canvasQbGroup.zIndex = 8;

      if (this._canvasBalancePanel) {
        this._canvasBalancePanel.x =
          reelBlockW - panelW - pad + CANVAS_STAT_SHIFT_LAST_WIN_X;
        this._canvasBalancePanel.y = 0;
      }
      if (this._canvasWinPanel) {
        this._canvasWinPanel.x = pad + CANVAS_STAT_SHIFT_BALANCE_X;
        this._canvasWinPanel.y = 0;
      }

      const icons = this._canvasIconSprites;
      if (icons?.length >= 4) {
        this._layoutPortraitPhoneBottomLeftChrome(reelBlockW, hudFit, { qbY, btnH });
      }

      if (this._canvasBetPrompt?.style) {
        const midGap = 16;
        const ww = Math.max(80, reelBlockW - 2 * panelW - 2 * pad - midGap);
        this._canvasBetPrompt.style.wordWrapWidth = ww;
      }

      this._refreshQuickBets();
      this._hideCanvasFullscreenOnMobile();
      return;
    }

    this._restoreQuickBetsToBottomBar();
    this._qbScrollVisibleW = null;
    if (this._canvasBalancePanel) this._canvasBalancePanel.visible = true;
    if (this._canvasWinPanel) this._canvasWinPanel.visible = true;
    this._setBetPromptVisible(this._betPromptVisible);
    if (this._portraitTopStatsBar) {
      this._portraitTopStatsBar.visible = false;
      this._portraitTopStatsBar.scale.set(1);
      this._syncPortraitTopStatsTextScale(1);
    }

    const qbVisibleW = 6 * stride;
    const arrowBtnW = arL?.width ?? 32;
    const arrowBtnH = arL?.height ?? 32;
    const reelCenter = reelBlockW / 2;

    /* Default air between LAST WIN / BALANCE and the quick-bet strip. When the
       corner buttons would leave the stage, this air shrinks first so fullscreen
       and menu keep their usual offset from the panels and stay on screen. */
    let arrowToPanelGap = 40;
    let qbToArrowGap = 20;
    let panelShift = Math.abs(CANVAS_STAT_SHIFT_LAST_WIN_X);
    const iconGap = 80;
    const iconSpacing = 10;
    const icons = this._canvasIconSprites;
    const mobileLand = this._isMobileLandscapeViewport();
    const tabletLandIconGap =
      this._isTabletViewport() ? (gameConfig.viewport?.tabletLandscapeBottomIconsGapExtra ?? 0) : 0;
    const tabletLandInward =
      this._isTabletViewport()
        ? (gameConfig.viewport?.tabletLandscapeBottomIconsTowardCenterPx ?? 0)
        : 0;
    const desktopInward = this._isDesktopLandscapeViewport()
      ? (gameConfig.viewport?.desktopBottomIconsTowardCenterPx ?? 0)
      : 0;
    let iconInward = desktopInward + tabletLandInward;
    let iz = CANVAS_BOTTOM_ICON_SIZE;
    if (icons && icons.length >= 4) {
      iz = mobileLand
        ? this._getMobileBottomMenuIconSize(this._getMobileHudFit())
        : icons[1]?.width || CANVAS_BOTTOM_ICON_SIZE;
      if (mobileLand) {
        icons[2].width = iz;
        icons[2].height = iz;
        icons[3].width = iz;
        icons[3].height = iz;
      }
    }
    const stageAt0 = this._stageXFromBottomBarLocal(0);
    const stagePerLocal = (this._stageXFromBottomBarLocal(100) - stageAt0) / 100;
    if (stagePerLocal > 0.02 && icons && icons.length >= 4) {
      const stageW = this.app?.renderer?.width || 1922;
      const edgeMargin = 16;
      const sideOverflow = () => {
        const inner =
          arrowBtnW + qbToArrowGap + qbVisibleW + qbToArrowGap + arrowBtnW;
        const total = panelW * 2 + inner + arrowToPanelGap * 2;
        const groupStart = reelCenter - total / 2;
        const outer = iconGap + iz * 2 + iconSpacing - iconInward;
        const fsLeft = groupStart - panelShift - outer;
        const menuRight = groupStart + total + panelShift + outer;
        const fsStage = stageAt0 + fsLeft * stagePerLocal;
        const menuStage = stageAt0 + menuRight * stagePerLocal;
        const leftOver = fsStage < edgeMargin ? (edgeMargin - fsStage) / stagePerLocal : 0;
        const rightOver =
          menuStage > stageW - edgeMargin ? (menuStage - (stageW - edgeMargin)) / stagePerLocal : 0;
        return Math.max(leftOver, rightOver);
      };
      const eat = (current, min, amount) => {
        const cut = Math.min(Math.max(0, current - min), Math.max(0, amount));
        return [current - cut, amount - cut];
      };
      let over = sideOverflow();
      if (over > 0.5) [arrowToPanelGap, over] = eat(arrowToPanelGap, 8, over);
      if (over > 0.5) [panelShift, over] = eat(panelShift, 0, over);
      if (over > 0.5) [qbToArrowGap, over] = eat(qbToArrowGap, 6, over);
      if (over > 0.5) iconInward += over;
    }

    const totalGroupW =
      panelW +
      arrowToPanelGap +
      arrowBtnW +
      qbToArrowGap +
      qbVisibleW +
      qbToArrowGap +
      arrowBtnW +
      arrowToPanelGap +
      panelW;
    const groupStartX = reelCenter - totalGroupW / 2;
    const balanceX = groupStartX;
    const leftArrowX = balanceX + panelW + arrowToPanelGap;
    const qbX = leftArrowX + arrowBtnW + qbToArrowGap;
    const rightArrowX = qbX + qbVisibleW + qbToArrowGap;
    const winX = rightArrowX + arrowBtnW + arrowToPanelGap;

    this._canvasBalancePanel.x = winX + panelShift;
    this._canvasBalancePanel.y = 0;
    this._canvasWinPanel.x = balanceX - panelShift;
    this._canvasWinPanel.y = 0;

    const arrowY = 2 + (btnH - arrowBtnH) / 2;
    if (arL) {
      arL.visible = true;
      arL.x = leftArrowX;
      arL.y = arrowY;
    }
    if (arR) {
      arR.visible = true;
      arR.x = rightArrowX;
      arR.y = arrowY;
    }

    if (this._canvasQbMaskG) {
      this._canvasQbMaskG.clear();
      this._canvasQbMaskG.rect(0, 0, qbVisibleW, btnH).fill(0xffffff);
      this._canvasQbMaskG.x = qbX;
      this._canvasQbMaskG.y = 2;
      this._canvasQbMaskG.visible = true;
      this._canvasQbMaskG.eventMode = 'none';
    }
    if (this._canvasQbGroup) {
      this._canvasQbGroup.mask = this._canvasQbMaskG;
      this._canvasQbGroup.x = qbX;
      this._canvasQbGroup.y = 2;
    }

    /* Corner chrome icons — same offset from the panels; the panel-to-bet gap above
       keeps fullscreen (left) and menu (right) inside the stage. */
    const balanceLeftEdge = balanceX - panelShift;
    const winRightEdge = winX + panelW + panelShift;
    if (icons && icons.length >= 4) {
      this._restoreCanvasIconsToBottomBar();
      icons.forEach((s, i) => {
        s.visible = i !== 0;
      });
      this._hideCanvasHomeIcon();
      const iconY = panelH - iz + tabletLandIconGap;
      icons[1].x = balanceLeftEdge - iconGap - iz * 2 - iconSpacing + iconInward;
      icons[0].x = balanceLeftEdge - iconGap - iz + iconInward;
      icons[2].x = winRightEdge + iconGap - iconInward;
      icons[3].x = winRightEdge + iconGap + iz + iconSpacing - iconInward;
      icons.forEach((s) => {
        s.y = iconY;
      });
      this._hideCanvasFullscreenOnMobile();
      this._hideCanvasHomeIcon();
    }

    this._refreshQuickBets();
    this._hideCanvasFullscreenOnMobile();
    this._clampCanvasQbScroll();
  }

  /**
   * Maximum left-scroll distance (positive number, applied as a negative
   * `sw.x`) in qbGroup-local pixels. Single source of truth for the three
   * places that clamp the strip: arrow buttons, drag, and post-layout
   * clamp.
   *
   * `_qbScrollVisibleW` is the qbGroup-local x where the right edge of the
   * **last chip** must land when fully scrolled. On portrait this is set in
   * {@link _relayoutBottomBarForViewport} to the LAST WIN section's right
   * edge minus `qbX`, so the strip's scroll-end aligns with the LAST WIN
   * panel/bar on both phone and tablet. Using the precise content extent
   * `(n−1)·stride + btnW` (= `n·stride − gap`) — rather than `n·stride` —
   * makes the alignment exact instead of off-by-`gap`.
   */
  _qbScrollMaxLocal() {
    const n = this._canvasQbBetCount || 0;
    if (n <= 0) return 0;
    const btnStride = this._qbChipStride ?? 108;
    const gap = this._qbChipGap ?? 8;
    const contentExtent = Math.max(0, n * btnStride - gap);
    const target = this._qbScrollVisibleW ?? 6 * btnStride;
    return Math.max(0, contentExtent - target);
  }

  _clampQbScrollX(x) {
    const maxScroll = this._qbScrollMaxLocal();
    return Math.max(-maxScroll, Math.min(0, x));
  }

  _clampCanvasQbScroll() {
    const sw = this._canvasQbScrollWrapper;
    if (!sw) return;
    this._qbScrollX = this._clampQbScrollX(this._qbScrollX || 0);
    this._applyQbScrollWrapperOffset();
  }

  /** Portrait phone: center chips in the column when they fit; otherwise keep the active bet in view. */
  _syncPortraitMobileQuickBetScroll() {
    if (!this._isPortraitMobileVerticalQuickBets() || !this._canvasQbScrollWrapper) {
      this._clampCanvasQbScroll();
      return;
    }
    const n = this._canvasQbBetCount || 0;
    if (n <= 0) {
      this._clampCanvasQbScroll();
      return;
    }
    const stride = this._qbChipStride ?? 108;
    const gap = this._qbChipGap ?? 8;
    const contentExtent = Math.max(0, n * stride - gap);
    const visibleSpan = this._qbScrollVisibleW ?? contentExtent;
    if (contentExtent <= visibleSpan) {
      this._qbScrollX = this._clampQbScrollX((visibleSpan - contentExtent) * 0.5);
      this._applyQbScrollWrapperOffset();
      return;
    }
    const bet = this.slotMachine?.hud?.betValue;
    if (bet != null) {
      this._scrollQuickBetsToAmount(bet);
    } else {
      this._clampCanvasQbScroll();
    }
  }

  /**
   * Scroll the canvas quick-bet strip so `amount` is visible (centered when possible).
   * Call after {@link #_refreshQuickBets} so chip count/stride match the layout.
   * @param {number} amount
   */
  _scrollQuickBetsToAmount(amount) {
    if (!this._shouldShowQuickBets()) return;
    const bets = this.slotMachine?.hud?.quickBets ?? [];
    if (!bets.length || !this._canvasQbScrollWrapper) return;
    const idx = bets.findIndex((b) => Math.abs(Number(b) - Number(amount)) < 0.001);
    if (idx < 0) return;

    const stride = this._qbChipStride ?? 108;
    const gap = this._qbChipGap ?? 8;
    const axis = this._getQbScrollAxis();
    const btnSpan = Math.max(1, stride - gap);
    const chipStart = idx * stride;
    const chipEnd = chipStart + btnSpan;
    const visibleSpan = this._qbScrollVisibleW ?? 6 * stride;

    let scroll = visibleSpan * 0.5 - (chipStart + btnSpan * 0.5);
    if (chipEnd + scroll > visibleSpan) scroll = visibleSpan - chipEnd;
    if (chipStart + scroll < 0) scroll = -chipStart;

    scroll = this._clampQbScrollX(scroll);
    this._qbScrollX = scroll;
    this._applyQbScrollWrapperOffset();
  }

  _applyBetFromBetPopup(amount) {
    sound.play('uiClick');
    this.slotMachine?.hud.setBet(amount);
    this._updateCanvasBetPrompt(amount);
    this._refreshQuickBets();
    this._scrollQuickBetsToAmount(amount);
    this._hideBetPopup();
  }

  _applyMobileCanvasChrome(reelCx, spinCy) {
    if (!this._spinButtonSprite || !this._spinButtonRoot) return;

    /** Keep autoplay/start/bet on one shared scale factor for phone portrait. */
    const hudFit = this._getMobileHudFit();
    const { spin: MOBILE_SPIN, side: MOBILE_SIDE, gap: spinSideGap } =
      this._getPortraitPhoneMobileControlSizes();

    this._spinButtonSprite.width = MOBILE_SPIN;
    this._spinButtonSprite.height = MOBILE_SPIN;
    this._spinButtonRoot.hitArea = new Rectangle(-MOBILE_SPIN / 2, -MOBILE_SPIN / 2, MOBILE_SPIN, MOBILE_SPIN);
    this._layoutSpinTurboHint();

    if (this._autoplaySprite) {
      this._autoplaySprite.width = MOBILE_SIDE;
      this._autoplaySprite.height = MOBILE_SIDE;
    }
    if (this._betPopupTriggerBtn) {
      this._betPopupTriggerBtn.width = MOBILE_SIDE;
      this._betPopupTriggerBtn.height = MOBILE_SIDE;
    }

    const leftBtnCenterX = reelCx - (MOBILE_SPIN / 2 + spinSideGap + MOBILE_SIDE / 2);
    const p = PORTRAIT_TUNING.phone;
    const autoplayDown = p.autoplayBtnDownNudgePx ?? 14;
    const betAboveGap = p.betBtnAboveAutoplayGapPx ?? 8;
    const leftColX = leftBtnCenterX - MOBILE_SIDE / 2;
    const autoplayY = spinCy - MOBILE_SIDE / 2 + autoplayDown;
    if (this._autoplayCluster && this._autoplaySprite) {
      this._layoutAutoplayPickerCluster(true);
      this._autoplayCluster.x = leftColX;
      this._autoplayCluster.y = autoplayY;
    }
    if (this._betPopupTriggerBtn) {
      this._betPopupTriggerBtn.x = leftColX;
      this._betPopupTriggerBtn.y = autoplayY - MOBILE_SIDE - betAboveGap;
    }
    this._layoutBuyBonusButtonMobile(reelCx, MOBILE_SIDE);

    if (this._betSpeedWrap) {
      const bgH = this._betSpeedPlateH || 40;
      const betSpeedLowerOffset = 14;
      const turboScale = BET_SPEED_MOBILE_SCALE;
      this._betSpeedWrap.scale.set(turboScale);
      this._betSpeedWrap.x = this._spinButtonRoot.x;
      this._betSpeedWrap.y =
        this._spinButtonRoot.y + MOBILE_SPIN / 2 - (bgH * turboScale) / 2 + betSpeedLowerOffset;
    }

    if (this._canvasBetPrompt) {
      const promptFs = Math.round(19 * hudFit);
      this._canvasBetPrompt.style.fontSize = promptFs;
      this._canvasBetPrompt.style.lineHeight = Math.round(promptFs * 1.22);
    }

    this._refreshSpinAutoplayRemainLabel();

    const iconDesign = this._getMobileBottomMenuIconSize(hudFit);
    if (this._canvasIconSprites?.length) {
      this._canvasIconSprites.forEach((s, i) => {
        if (i === 2 || i === 3) {
          s.width = iconDesign;
          s.height = iconDesign;
        }
      });
    }

  }

  _restoreMobileCanvasChrome() {
    const d = this._desktopChromeDefaults;
    if (!d || !this._spinButtonSprite || !this._spinButtonRoot) return;

    this._spinButtonSprite.width = d.spinImg.w;
    this._spinButtonSprite.height = d.spinImg.h;
    const hh = d.spinHitHalf;
    this._spinButtonRoot.hitArea = new Rectangle(-hh, -hh, hh * 2, hh * 2);
    this._layoutSpinTurboHint();

    if (this._autoplaySprite) {
      this._autoplaySprite.width = d.autoplay.w;
      this._autoplaySprite.height = d.autoplay.h;
    }
    if (this._betPopupTriggerBtn) {
      this._betPopupTriggerBtn.width = d.betPopup.w;
      this._betPopupTriggerBtn.height = d.betPopup.h;
    }
    if (this._betSpeedWrap) {
      this._betSpeedWrap.scale.set(d.betSpeedScale);
    }
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.style.fontSize = d.betPromptFontSize;
      this._canvasBetPrompt.style.lineHeight = Math.round(d.betPromptFontSize * 1.2);
    }
    const isz = d.iconBtnSize;
    if (this._canvasIconSprites?.length) {
      this._canvasIconSprites.forEach((s) => {
        s.width = isz;
        s.height = isz;
      });
    }
    if (this._autoplayCluster && this._autoplaySprite) {
      this._layoutAutoplayPickerCluster(false);
    }
    this._refreshSpinAutoplayRemainLabel();
  }

  /**
   * Desktop landscape: bet-options + autoplay stacked on the right of SPIN (autoplay above bet).
   * @param {number} spinX left edge of the spin button image
   * @param {number} spinY top edge of the spin button image
   */
  _layoutDesktopSpinSideButtons(spinX, spinY) {
    const side = this._autoplaySprite?.width ?? 100;
    const spinCenterY = spinY + SPIN_BTN_REF_SIZE / 2;
    const rightColLeft = spinX + SPIN_BTN_REF_SIZE + DESKTOP_SPIN_SIDE_GAP;
    const gap = DESKTOP_SPIN_SIDE_BTN_GAP;
    const stackCount = 2;
    const stackH = side * stackCount + gap * (stackCount - 1);
    const stackTop = Math.round(spinCenterY - stackH / 2);

    if (this._betPopupTriggerBtn) {
      this._betPopupTriggerBtn.x = rightColLeft;
      this._betPopupTriggerBtn.y = stackTop + side + gap;
    }
    if (this._autoplayCluster && this._autoplaySprite) {
      this._layoutAutoplayPickerCluster(false);
      this._autoplayCluster.x = rightColLeft - this._autoplaySprite.x;
      this._autoplayCluster.y = stackTop;
    }
    this._layoutBuyBonusButtonDesktop(spinX, rightColLeft, side, stackTop, stackH);
  }

  /**
   * Horizontal row (desktop / landscape): presets to the left of the autoplay icon.
   * Vertical (portrait mobile): presets stacked above the icon.
   */
  _layoutAutoplayPickerCluster(vertical) {
    const pickerRow = this._autoplayPickerRow;
    const sprite = this._autoplaySprite;
    if (!pickerRow || !sprite) return;

    const buttons = pickerRow.children;
    const n = buttons.length;
    if (!n) return;

    const gap = 8;
    const firstBg = buttons[0]?.children?.[0];
    const pickBtnW = firstBg?.width > 0 ? firstBg.width : 100;
    const pickBtnH = firstBg?.height > 0 ? firstBg.height : 95;
    const side = sprite.width;
    const sideH = sprite.height;

    pickerRow.position.set(0, 0);

    if (!vertical) {
      for (let i = 0; i < n; i++) {
        buttons[i].x = i * (pickBtnW + gap);
        buttons[i].y = (sideH - pickBtnH) / 2;
      }
      const pickRowW = n * pickBtnW + (n - 1) * gap;
      sprite.x = pickRowW + gap;
      sprite.y = 0;
      sprite.zIndex = 2;
      pickerRow.zIndex = 1;
      return;
    }

    const colW = Math.max(pickBtnW, side);
    const insetX = (colW - pickBtnW) / 2;
    for (let i = 0; i < n; i++) {
      const slotFromIcon = n - 1 - i;
      const y = -gap - (slotFromIcon + 1) * pickBtnH - slotFromIcon * gap;
      buttons[i].x = insetX;
      buttons[i].y = y;
    }
    sprite.x = (colW - side) / 2;
    sprite.y = 0;
    sprite.zIndex = 1;
    pickerRow.zIndex = 2;
  }

  /** True while spin/collect/init — quick-bet strip must not pan (arrows + drag). */
  _qbGesturesLocked() {
    return !!(this._spinCycleActive || this._spinning || this._winCollectPending || this._initializing);
  }

  _ensureCanvasQbScrollHandlers() {
    const group = this._canvasQbGroup;
    if (!group || this._qbScrollHandlersInstalled) return;
    this._qbScrollHandlersInstalled = true;
    this._qbBoundDown = this._onCanvasQbGroupPointerDown.bind(this);
    this._qbBoundUp = this._onCanvasQbGroupPointerEnd.bind(this);
    this._qbDomMove = (ev) => this._onCanvasQbDomPointerMove(ev);
    this._qbDomEnd = (ev) => this._onCanvasQbDomPointerEnd(ev);
    group.on('pointerdown', this._qbBoundDown);
    group.on('pointerup', this._qbBoundUp);
    group.on('pointerupoutside', this._qbBoundUp);
  }

  _attachQbCanvasDragListeners() {
    const canvas = this.app?.canvas;
    if (!canvas || this._qbCanvasDragListening) return;
    this._qbCanvasDragListening = true;
    canvas.addEventListener('pointermove', this._qbDomMove, { passive: true });
    canvas.addEventListener('pointerup', this._qbDomEnd);
    canvas.addEventListener('pointercancel', this._qbDomEnd);
  }

  _detachQbCanvasDragListeners() {
    const canvas = this.app?.canvas;
    if (!canvas || !this._qbCanvasDragListening) return;
    this._qbCanvasDragListening = false;
    canvas.removeEventListener('pointermove', this._qbDomMove);
    canvas.removeEventListener('pointerup', this._qbDomEnd);
    canvas.removeEventListener('pointercancel', this._qbDomEnd);
  }

  _clearQbDragState() {
    this._qbDragAnchorGlobalX = null;
    this._qbDragAnchorGlobalY = null;
    this._qbDragLastGlobalX = null;
    this._qbDragLastGlobalY = null;
    this._qbDragScrollCommitted = false;
    this._detachQbCanvasDragListeners();
  }

  _stopQbScrollInertia() {
    if (this._qbInertiaRaf != null) {
      cancelAnimationFrame(this._qbInertiaRaf);
      this._qbInertiaRaf = null;
    }
    this._qbScrollVelocity = 0;
    this._qbInertiaLastT = 0;
    this._qbInertiaTargetX = null;
    this._qbInertiaWheelTicks = false;
  }

  /** Track flick speed from recent drag deltas (qbGroup-local px/ms). */
  _recordQbScrollVelocity(localDx) {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const lastT = this._qbScrollVelocityLastT;
    if (lastT > 0) {
      const dt = now - lastT;
      if (dt > 0 && dt < 140) {
        const instant = localDx / dt;
        const alpha = 0.4;
        this._qbScrollVelocity = this._qbScrollVelocity * (1 - alpha) + instant * alpha;
      } else if (dt >= 140) {
        this._qbScrollVelocity = localDx / dt;
      }
    }
    this._qbScrollVelocityLastT = now;
  }

  /**
   * Coast the quick-bet strip with the same friction used after a drag flick.
   * @param {number} velocity qbGroup-local px/ms
   * @param {{ targetX?: number, wheelTicks?: boolean }} [options]
   *   `targetX` — coast stops at this scroll offset (arrow nudge).
   *   `wheelTicks` — layered click ticks while coasting (default true; false for arrows).
   */
  _startQbScrollInertia(velocity, options = {}) {
    this._stopQbScrollInertia();
    if (!Number.isFinite(velocity) || Math.abs(velocity) < 0.1) return;
    const sw = this._canvasQbScrollWrapper;
    if (!sw) return;

    const targetX = options.targetX ?? null;
    this._qbInertiaTargetX = targetX;
    this._qbScrollVelocity = velocity;
    this._qbInertiaWheelTicks = options.wheelTicks !== false;
    if (this._qbInertiaWheelTicks) {
      this._resetQbWheelTickAnchor(this._qbScrollX || 0);
    }
    this._qbInertiaLastT = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const frictionPerRef = 0.91;
    const refMs = 16.67;
    const minVelocity = 0.035;

    const tick = (now) => {
      if (!this._canvasQbScrollWrapper) {
        this._stopQbScrollInertia();
        return;
      }
      if (this._qbGesturesLocked()) {
        this._stopQbScrollInertia();
        return;
      }

      const last = this._qbInertiaLastT || now;
      const dtMs = Math.min(Math.max(now - last, 0), 48);
      this._qbInertiaLastT = now;
      if (dtMs <= 0) {
        this._qbInertiaRaf = requestAnimationFrame(tick);
        return;
      }

      let v = this._qbScrollVelocity * Math.pow(frictionPerRef, dtMs / refMs);
      let x = (this._qbScrollX || 0) + v * dtMs;

      const coastTarget = this._qbInertiaTargetX;
      if (coastTarget != null) {
        if (v > 0 && x >= coastTarget) {
          x = coastTarget;
          v = 0;
        } else if (v < 0 && x <= coastTarget) {
          x = coastTarget;
          v = 0;
        }
      }

      const clamped = this._clampQbScrollX(x);
      if (clamped !== x) v = 0;
      x = clamped;

      const prevScroll = this._qbScrollX || 0;
      this._qbScrollX = x;
      this._applyQbScrollWrapperOffset();
      if (this._qbInertiaWheelTicks) {
        this._playQbWheelTicksForScroll(prevScroll, x);
      }
      this._qbScrollVelocity = v;

      if (coastTarget != null && Math.abs(x - coastTarget) < 0.5) {
        this._stopQbScrollInertia();
        this._clampCanvasQbScroll();
        return;
      }

      if (Math.abs(v) < minVelocity) {
        this._stopQbScrollInertia();
        this._clampCanvasQbScroll();
        return;
      }

      this._qbInertiaRaf = requestAnimationFrame(tick);
    };

    this._qbInertiaRaf = requestAnimationFrame(tick);
  }

  /** Arrow buttons: coast one chip stride with the same friction as a drag flick. */
  _nudgeQbScrollByArrow(scrollDir) {
    if (!this._canvasQbScrollWrapper) return;
    if (this._qbGesturesLocked()) return;

    const stride = this._qbChipStride ?? 108;
    const maxScroll = this._qbScrollMaxLocal();
    const current = this._qbScrollX || 0;
    const target = Math.max(-maxScroll, Math.min(0, current + scrollDir * stride));
    if (Math.abs(target - current) < 0.5) return;

    sound.play('uiClick');
    const dist = target - current;
    const velocity = Math.sign(dist) * 0.65;
    this._startQbScrollInertia(velocity, { targetX: target, wheelTicks: false });
  }

  _finishQbDragGesture() {
    const wasScroll = this._qbDragScrollCommitted;
    const releaseVelocity = this._qbScrollVelocity;
    this._clearQbDragState();
    if (!wasScroll || this._qbGesturesLocked()) return;
    this._startQbScrollInertia(releaseVelocity * 1.12);
  }

  /**
   * True if the pointer drifted beyond {@link _qbTapDriftStagePx} from where it
   * pressed (in either axis) — used by chip pointertap to reject accidental bets
   * on drags. `e` is a Pixi FederatedPointerEvent on the chip itself; `global`
   * is in stage coords just like the anchor stored in pointerdown.
   */
  _qbPointerWasDrag(e) {
    if (this._qbGestureWasDrag) return true;
    const ax = this._qbDragAnchorGlobalX;
    const ay = this._qbDragAnchorGlobalY;
    if (ax == null || ay == null) return false;
    const dx = e.global.x - ax;
    const dy = e.global.y - ay;
    return Math.hypot(dx, dy) >= this._qbTapDriftStagePx();
  }

  _onCanvasQbGroupPointerDown(e) {
    if (!isPrimaryPointerAction(e)) return;
    if (this._qbGesturesLocked()) return;
    this._stopQbScrollInertia();
    this._qbScrollVelocity = 0;
    this._qbScrollVelocityLastT = 0;
    this._qbGestureWasDrag = false;
    this._qbDragAnchorGlobalX = e.global.x;
    this._qbDragAnchorGlobalY = e.global.y;
    this._qbDragLastGlobalX = e.global.x;
    this._qbDragLastGlobalY = e.global.y;
    this._qbDragScrollCommitted = false;
    this._attachQbCanvasDragListeners();
  }

  /**
   * Tap-vs-drag drift threshold expressed in Pixi stage pixels (the unit of
   * `e.global.x/y`). Derives from a stable CSS-pixel constant so the same
   * finger movement feels like “the same drag” on every viewport — without
   * this, the threshold was ~2 CSS px on phone and ~8 CSS px on desktop.
   */
  _qbTapDriftStagePx() {
    const cssPx = this._qbTapDriftCssPx ?? 6;
    const scaleX = this._lastScales?.x;
    if (!scaleX || !Number.isFinite(scaleX) || scaleX <= 0) return cssPx;
    return cssPx / scaleX;
  }

  /**
   * Map a DOM pointer event to Pixi stage coordinates (same basis as `e.global`).
   * Uses {@link #_lastScales} from the canvas CSS transform — do not use
   * `mapPositionToPoint` here; it does not match `e.global` when we apply our
   * own non-uniform `scale(scaleX, scaleY)` on the canvas element.
   * @param {PointerEvent} ev
   * @returns {{ x: number, y: number } | null}
   */
  _mapDomPointerToStage(ev) {
    const canvas = this.app?.canvas;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const sx = this._lastScales?.x;
    const sy = this._lastScales?.y;
    if (sx > 0 && sy > 0) {
      return {
        x: (ev.clientX - rect.left) / sx,
        y: (ev.clientY - rect.top) / sy,
      };
    }
    const resolution = this.app?.renderer?.resolution || 1;
    const bufScaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const bufScaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return {
      x: (ev.clientX - rect.left) * bufScaleX / resolution,
      y: (ev.clientY - rect.top) * bufScaleY / resolution,
    };
  }

  _onCanvasQbDomPointerMove(ev) {
    const pt = this._mapDomPointerToStage(ev);
    if (!pt) return;
    this._applyQbDragFromGlobal(pt.x, pt.y);
  }

  /** @param {number} globalX stage-space X (same basis as Pixi FederatedEvent.global.x) */
  /** @param {number} globalY stage-space Y */
  _applyQbDragFromGlobal(globalX, globalY) {
    if (this._qbGesturesLocked()) return;
    const axis = this._getQbScrollAxis();
    const current = axis === 'y' ? globalY : globalX;
    const last = axis === 'y' ? this._qbDragLastGlobalY : this._qbDragLastGlobalX;
    if (last === null) return;
    const anchor = axis === 'y' ? this._qbDragAnchorGlobalY : this._qbDragAnchorGlobalX;
    if (anchor === null) return;
    if (!this._qbDragScrollCommitted) {
      if (Math.abs(current - anchor) < this._qbTapDriftStagePx()) return;
      this._qbDragScrollCommitted = true;
      this._qbGestureWasDrag = true;
      this._resetQbWheelTickAnchor(this._qbScrollX || 0);
      if (axis === 'y') this._qbDragLastGlobalY = current;
      else this._qbDragLastGlobalX = current;
      return;
    }
    const d = current - last;
    if (axis === 'y') this._qbDragLastGlobalY = current;
    else this._qbDragLastGlobalX = current;
    this._applyCanvasQbScrollDelta(d);
  }

  _onCanvasQbGroupPointerEnd() {
    this._finishQbDragGesture();
  }

  _onCanvasQbDomPointerEnd() {
    this._finishQbDragGesture();
  }

  /** @param {number} d Pointer delta in Pixi stage coords along the active scroll axis. */
  _applyCanvasQbScrollDelta(d) {
    const sw = this._canvasQbScrollWrapper;
    if (!sw) return;
    const prev = this._qbScrollX || 0;
    const axis = this._getQbScrollAxis();
    /* Scroll offset lives in qbGroup's LOCAL space, while `d` is in stage space. */
    const wt = this._canvasQbGroup?.worldTransform;
    const groupScale =
      axis === 'y'
        ? wt?.d
        : wt?.a;
    const localD =
      groupScale && Number.isFinite(groupScale) && Math.abs(groupScale) > 0.0001 ? d / groupScale : d;
    const nextScroll = this._clampQbScrollX(prev + localD);
    this._qbScrollX = nextScroll;
    this._applyQbScrollWrapperOffset();
    if (this._qbDragScrollCommitted) {
      this._playQbWheelTicksForScroll(prev, nextScroll);
    }
    if (Math.abs(this._qbScrollX - prev) > 0.25) {
      this._qbGestureWasDrag = true;
      this._recordQbScrollVelocity(localD);
    }
  }

  _buildCanvasQuickBets(bets, currentBet) {
    const group = this._canvasQbGroup;
    if (!group || !this._qbNormTex) return;
    group.removeChildren();

    const vertical = this._isPortraitMobileVerticalQuickBets();
    const mobile = this._isPortraitMobileViewport();
    const hudFit = mobile ? this._getMobileHudFit() : 1;
    let btnW = mobile ? Math.max(70, Math.round(MOBILE_QB_CHIP_REF_W * hudFit)) : DESKTOP_QB_CHIP_REF_W;
    let btnH = mobile ? Math.max(58, Math.round(MOBILE_QB_CHIP_REF_H * hudFit)) : DESKTOP_QB_CHIP_REF_H;
    let gap = 8;
    let stride = btnW + gap;
    if (vertical) {
      const chip = this._getPortraitPhoneVerticalQuickBetChipMetrics();
      btnW = chip.btnW;
      btnH = chip.btnH;
      gap = chip.gap;
      stride = chip.stride;
    }
    this._qbChipW = btnW;
    this._qbChipH = btnH;
    this._qbChipGap = gap;
    this._qbChipStride = stride;
    const font = '"Montserrat", "Arial", sans-serif';
    const scrollWrapper = new Container();
    scrollWrapper.x = 0;
    scrollWrapper.y = 0;
    group.addChild(scrollWrapper);
    this._canvasQbScrollWrapper = scrollWrapper;
    this._canvasQbBetCount = bets.length;
    this._applyQbScrollWrapperOffset();

    bets.forEach((amount, i) => {
      const sel = Math.abs(amount - currentBet) < 0.001;
      const showCollect = this._isWinCollectDismissActive();
      const showStopAll = !!this._spinning && !showCollect;
      const bottomLabel = showCollect
        ? this._tu('COLLECT')
        : showStopAll
          ? this._tu('STOP')
          : this._tu('BET');
      const armed = this._buyBonusEnabled ? this._buyBonusArmed : null;
      const shownAmount = armed
        ? buyBonusPrice(amount, armed.type, armed.guaranteedWins)
        : amount;

      const btn = createQuickBetChipPixi({
        betValue: shownAmount,
        selected: sel,
        textureNormal: this._qbNormTex,
        textureSelected: this._qbSelTex,
        cellW: btnW,
        cellH: btnH,
        bottomText: bottomLabel,
        currencyLabel: this._currencyLabel(),
        currencyPrecision: this._currencyPrecision,
        fontFamily: font,
      });
      if (vertical) {
        btn.x = 0;
        btn.y = i * stride;
      } else {
        btn.x = i * stride;
        btn.y = 0;
      }
      btn.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        /* Full click only — ignore scroll drags that still look like a tap. */
        if (this._qbPointerWasDrag(e)) {
          this._qbGestureWasDrag = false;
          return;
        }
        this._handleCanvasQuickBet(amount);
      });
      scrollWrapper.addChild(btn);
    });
  }

  _applySlotAspectLock(worldScaleX, worldScaleY) {
    if (!this.slotMachine) return;
    const slotRoot = this.slotMachine.reelContainer?.root;
    if (!slotRoot) return;
    if (!(worldScaleX > 0) || !(worldScaleY > 0)) return;

    if (this._slotRootBaseX == null || this._slotRootBaseY == null) {
      this._slotRootBaseX = slotRoot.x;
      this._slotRootBaseY = slotRoot.y;
    }

    const { width: cssW, height: cssH } = this._readContainerSize();
    const portraitMobile = this._isPortraitMobileViewport();

    let localScaleX;
    let localScaleY;
    if (portraitMobile) {
      /** Phones: bias uniform scale toward filling the viewport width (reference ~390px-wide layout). */
      const wx = worldScaleX;
      const wy = worldScaleY;
      const portraitRatio = cssW / Math.max(1, cssH);
      const widthBoost = this._getMobilePortraitWidthBoost(portraitRatio);
      const uniform = Math.min(wx * widthBoost, wy * 1.06);
      localScaleX = uniform / wx;
      localScaleY = uniform / wy;
    } else {
      const uniformScreenScale = Math.min(worldScaleX, worldScaleY);
      localScaleX = uniformScreenScale / worldScaleX;
      localScaleY = uniformScreenScale / worldScaleY;
    }
    slotRoot.scale.set(localScaleX, localScaleY);

    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    slotRoot.x = this._slotRootBaseX + (reelBlockW * (1 - localScaleX)) / 2;
    let slotY = this._slotRootBaseY + (reelBlockH * (1 - localScaleY)) / 2;
    if (portraitMobile) {
      slotY -= MOBILE_SLOT_ROOT_UP_BIAS_NARROW * this._getNarrowPortraitWidthT();
    }
    slotRoot.y = slotY;
  }

  /**
   * Portrait-only: vertical positions for canvas HUD (logical px, reel-root space).
   * Stack is additive: reels → merged stats bar → gap → QB strip → gap → SPIN center.
   * Tune in {@link PORTRAIT_TUNING}; landscape uses snapshot layout, not this.
   *
   * @param {number} cw
   * @param {number} ch
   * @param {number} reelBlockH
   * @param {number} reelBlockW
   */
  _computePortraitCanvasHudLayout(cw, ch, reelBlockH, reelBlockW) {
    const cx = reelBlockW / 2;
    const portraitTopStatsH = PORTRAIT_MERGED_STATS_BAR_H;
    const stackedStatsHudFit = this._getMobileHudFit();

    const spacingBias = Math.max(-1, Math.min(1, (ch - 860) / 220));
    const portraitRatio = cw / Math.max(1, ch);
    const spacingTightness = Math.max(0, Math.min(1, (portraitRatio - 0.50) / 0.35));

    const gapStatsToQb = 0;
    const gapQbToSpin = Math.max(16, Math.round(24 + spacingBias * 6 - spacingTightness * 6));

    const hudFit = this._getMobileHudFit();
    const mobileControlScale = MOBILE_CONTROL_SCALE_BASE * hudFit;
    const spinHalfHeight = Math.round((120 * mobileControlScale) / 2);
    const portraitLiftY = this._getMobileGameVisualLiftY();

    const p = PORTRAIT_TUNING.phone;
    const gameToHudGap = Math.max(
      0,
      Math.round(p.gameToHudGapBase + spacingBias * p.gameToHudGapSpacingBiasMul),
    );

    /** Mirrors the exact full-width scale computed in {@link #_applyMobileGameVisualScale}; may exceed 1 on shorter portrait aspects so the cabinet can fill the viewport width. */
    const mobileCabinetScale = this._mobileGameVisualScale ?? 1;
    const reelVisualBottomY = (reelBlockH * (1 + mobileCabinetScale)) / 2 + portraitLiftY;

    const bottomBarY = reelVisualBottomY + gameToHudGap;
    const statsBarHeight = portraitTopStatsH * stackedStatsHudFit;
    const statsBarNudgeY = p.statsBarNudgeY ?? 0;
    const betPromptCenterY = bottomBarY + statsBarNudgeY + statsBarHeight / 2;

    const spinCenterY =
      bottomBarY +
      statsBarNudgeY +
      statsBarHeight +
      gapStatsToQb +
      gapQbToSpin +
      spinHalfHeight +
      p.spinRowNudgeY;

    return { cx, bottomBarY, betPromptCenterY, spinCenterY };
  }

  /**
   * Repositions Pixi canvas controls for portrait: stats + QB strip under reels, then spin row.
   * Landscape restores the snapshot from build (desktop beside-the-reels layout).
   */
  _applyCanvasControlsViewportLayout() {
    const snap = this._canvasDesktopLayout;
    const spin = this._spinButtonRoot;
    const bottomBar = this._canvasBottomBar;
    if (!snap || !spin || !bottomBar) return;

    const { width: cw, height: ch } = this._readContainerSize();
    const portraitStacked = ch > cw;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    this._mobileHudFitScale = portraitStacked
      ? this._computeMobileHudFitScale(cw, ch, reelBlockH)
      : 1;

    const restoreDesktop = () => {
      this._mobileHudFitScale = 1;
      spin.x = snap.spinRoot.x;
      spin.y = snap.spinRoot.y;
      const spinW = this._spinButtonSprite?.width ?? SPIN_BTN_REF_SIZE;
      const spinH = this._spinButtonSprite?.height ?? SPIN_BTN_REF_SIZE;
      this._layoutDesktopSpinSideButtons(spin.x - spinW / 2, spin.y - spinH / 2);
      bottomBar.y = snap.bottomBar.y;
      if (this._canvasBetPrompt && snap.betPrompt) {
        this._canvasBetPrompt.x = snap.betPrompt.x;
        this._canvasBetPrompt.y = snap.betPrompt.y;
        if (snap.betPromptWordWrapWidth != null && this._canvasBetPrompt.style) {
          this._canvasBetPrompt.style.wordWrapWidth = snap.betPromptWordWrapWidth;
        }
      }
      if (this._canvasLinesPanel && snap.linesPanel) {
        this._canvasLinesPanel.x = snap.linesPanel.x;
        this._canvasLinesPanel.y = snap.linesPanel.y;
      }
      if (this._betSpeedWrap && snap.betSpeedWrap) {
        this._betSpeedWrap.x = snap.betSpeedWrap.x;
        this._betSpeedWrap.y = snap.betSpeedWrap.y;
      }
      this._restoreMobileCanvasChrome();
      this._relayoutBottomBarForViewport();
      this._applyTabletBottomMenuNudge();
    };

    if (!portraitStacked) {
      restoreDesktop();
      return;
    }

    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap - 4;

    const hud = this._computePortraitCanvasHudLayout(cw, ch, reelBlockH, reelBlockW);
    bottomBar.y = hud.bottomBarY;
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.x = hud.cx;
      this._canvasBetPrompt.y = hud.betPromptCenterY;
    }
    spin.x = hud.cx;
    spin.y = hud.spinCenterY;

    this._applyMobileCanvasChrome(hud.cx, hud.spinCenterY);
    this._relayoutBottomBarForViewport();
    this._applyPortraitPhoneHudNudges();
    this._nudgeMobileSpinForBuyBonus();
    this._refreshQuickBets();
    this._layoutPortraitMobileVerticalQuickBets();
    this._clampPortraitCornerIconsToVisibleArea();
    this._layoutPortraitMobileVerticalQuickBets();
  }

  /**
   * Shifts only the canvas bottom menu strip on landscape tablets
   * ({@link gameConfig.viewport.tabletBottomMenuOffsetY}). Spin stays beside reels.
   */
  _applyTabletBottomMenuNudge() {
    const dy = this._isTabletViewport()
      ? (gameConfig.viewport?.tabletBottomMenuOffsetY ?? 0)
      : 0;
    if (!dy || !this._canvasBottomBar) return;
    this._canvasBottomBar.y += dy;
  }

  layout() {
    if (!this.app || !this.slotMachine) return;
    const width = 1922;
    const height = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const portraitMobile = this._isPortraitMobileViewport();
    const base = this._contentScale ?? 0.90;
    /** Uniform scale applied to the whole game world — values < 1 shrink everything. */
    let s = portraitMobile ? Math.min(1.32, base * 1.46) : base;
    /* Landscape tablets: shrink the whole world a touch so the corner chrome
       icons gain a margin from the screen edges instead of sitting flush.
       Portrait tablets use the mobile path above. */
    const tabletLandscape = this._isTabletViewport();
    const tabLandMul = gameConfig.viewport?.tabletLandscapeWorldScaleMul ?? 1;
    if (tabLandMul !== 1 && tabletLandscape) {
      s = s * tabLandMul;
    }
    const fsMul = gameConfig.viewport?.desktopFullscreenWorldScaleMul ?? 1;
    if (fsMul !== 1 && this._isDesktopFullscreen()) {
      s = s * fsMul;
    }
    this.slotMachine.setViewportProfile('desktop-landscape');
    
    // Scale the world from its center to keep content centered
    this.world.pivot.set(width / 2, height / 2);
    this.world.scale.set(s);
    let worldOffY = 0;
    if (portraitMobile) {
      worldOffY = MOBILE_PORTRAIT_WORLD_OFFSET_NARROW * this._getNarrowPortraitWidthT();
    }
    this.world.position.set(width / 2, height / 2 + worldOffY);
    
    // Position slotMachine.root at origin (0, 0) in world-local space
    this.slotMachine.root.position.set(0, 0);

    const portrait = ch > cw;
    const vp = gameConfig.viewport ?? {};
    let celebrationMul = 1;
    if (portraitMobile) {
      const t = Math.max(0, Math.min(1, (cw - 320) / (760 - 320)));
      celebrationMul =
        (0.44 + t * 0.2) * (vp.narrowPortraitWinCelebrationMul ?? 1);
    }
    if (this._isTabletViewport()) {
      celebrationMul *= vp.tabletWinCelebrationScaleMul ?? 1;
    }
    const desktopCelebration =
      !this._isTabletViewport() && !portraitMobile;
    if (desktopCelebration) {
      celebrationMul *= vp.desktopWinCelebrationScaleMul ?? 1;
    }
    const sx = this._lastScales?.x ?? 1;
    const sy = this._lastScales?.y ?? 1;
    const cssStretchRatio = portrait && sx > 0 && sy > 0 ? sy / sx : 1;
    this.slotMachine.winCelebration?.setCanvasStretchMulX?.(cssStretchRatio);

    this.slotMachine.layoutScreen(width, height, 'desktop-landscape');

    this.slotMachine.winCelebration?.setCelebrationContentScale(celebrationMul);
    this.slotMachine.winCelebration?.setCelebrationRootExtraOffsetYPx(
      this._isTabletViewport() ? (gameConfig.viewport?.tabletWinCelebrationOffsetYPx ?? 0) : 0,
    );
  }

  /* ─── Bet selection modal (Pixi, same assets as {@link _buildCanvasQuickBets}) ─── */

  /**
   * Undo non-uniform CSS canvas scaling for the quick-bets modal only (dim stays full-frame).
   * @param {number} scaleX
   * @param {number} scaleY
   */
  _applyBetPopupViewportScale(scaleX, scaleY) {
    const shell = this._betPopupPanelRoot;
    if (!shell) return;
    if (scaleX > 0 && scaleY > 0 && Number.isFinite(scaleX) && Number.isFinite(scaleY)) {
      shell.scale.set(scaleY / scaleX, 1);
    } else {
      shell.scale.set(1, 1);
    }
  }

  /**
   * Minimum chip-area height for Figma mobile/tablet bet grids.
   * @param {number} innerW
   * @param {number} betsCount
   * @param {boolean} phoneLayout narrow portrait phone ({@link GameApp#_isPortraitMobileViewport}); else tablet spec.
   * @returns {number}
   */
  _betPopupComputeMinChipAreaHeight(innerW, betsCount, phoneLayout) {
    const gw = phoneLayout ? MOBILE_BET_MODAL_GRID_INNER_W : TABLET_BET_MODAL_GRID_INNER_W;
    const gridInnerH = phoneLayout ? MOBILE_BET_MODAL_GRID_INNER_H : TABLET_BET_MODAL_GRID_INNER_H;
    const cw0 = phoneLayout ? MOBILE_BET_CHIP_REF_W : TABLET_BET_CHIP_REF_W;
    const ch0 = phoneLayout ? MOBILE_BET_CHIP_REF_H : TABLET_BET_CHIP_REF_H;
    const g0 = phoneLayout ? MOBILE_BET_CHIP_GAP_REF : TABLET_BET_CHIP_GAP_REF;
    const n = Math.max(0, Math.floor(betsCount));
    if (n === 0) return 80;
    const w = Math.max(1, innerW);
    const u = w / gw;
    let cw = cw0 * u;
    let ch = ch0 * u;
    let g = g0 * u;
    let cols = Math.max(1, Math.floor((w + g) / (cw + g)));
    cols = Math.min(cols, n);
    const rows = Math.ceil(n / cols);
    let needW = cols * cw + (cols - 1) * g;
    let needH = rows * ch + (rows - 1) * g;
    const innerHAvail = gridInnerH * (w / gw);
    const s = Math.min(w / Math.max(1, needW), innerHAvail / Math.max(1, needH), 1.55);
    cw *= s;
    ch *= s;
    g *= s;
    needH = rows * ch + (rows - 1) * g;
    return needH;
  }

  /**
   * Size the bet-options panel: desktop keeps art aspect; phones/tablets fill viewport width and grow height for the chip grid.
   */
  _syncBetPopupLayout() {
    const bg = this._betPopupBgSprite;
    const panel = this._betPopupPanel;
    const title = this._betPopupTitle;
    const closeBtn = this._betPopupCloseBtn;
    const chipsArea = this._betPopupChipsArea;
    if (!bg || !panel || !title || !closeBtn || !chipsArea) return;

    const designH = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const scaleY =
      this._lastScales?.y > 0 && Number.isFinite(this._lastScales.y) ? this._lastScales.y : ch / designH;

    const compact = this._isPortraitMobileViewport() || this._isTabletViewport();
    const marginCss = compact ? 10 : 32;
    const maxScreenW = Math.max(100, cw - 2 * marginCss);
    const maxScreenH = Math.max(100, ch - 2 * marginCss);

    const tw = this._betPopupBgNatW > 0 ? this._betPopupBgNatW : 1000;
    const th = this._betPopupBgNatH > 0 ? this._betPopupBgNatH : 780;

    let maxBgW = Infinity;
    let maxBgH = Infinity;
    if (compact) {
      const worldS = Math.max(1e-6, this.world?.scale?.x ?? 1);
      const screenMul = scaleY * worldS;
      maxBgW = maxScreenW / screenMul;
      maxBgH = maxScreenH / screenMul;
      if (this._isPortraitMobileViewport()) {
        const m = MOBILE_BET_MODAL_VIEWPORT_MARGIN_CSS;
        const localTop = this._worldLocalYFromCssY(m);
        const localBottom = this._worldLocalYFromCssY(ch - m);
        const maxPhoneBgH = Math.max(160, (localBottom - localTop) * 0.96);
        maxBgH = Math.min(maxBgH, maxPhoneBgH, ((ch * 0.92) - 2 * m) / screenMul);
      }
    }

    let bgW;
    let bgH;
    let padX;
    let padTop;
    let padBot;
    let yNudge = 0;
    let betPopupCornerR = 20;

    if (compact) {
      const isPhone = this._isPortraitMobileViewport();
      const ref = isPhone ? MOBILE_BET_MODAL_REF : TABLET_BET_MODAL_REF;
      const gw = isPhone ? MOBILE_BET_MODAL_GRID_INNER_W : TABLET_BET_MODAL_GRID_INNER_W;
      let modalFit = Math.min(maxBgW / ref.w, maxBgH / ref.h);
      const n = this.slotMachine?.hud?.quickBets?.length ?? 0;

      for (let iter = 0; iter < 14; iter++) {
        bgW = ref.w * modalFit;
        bgH = ref.h * modalFit;
        padX = (bgW - gw * modalFit) / 2;
        padTop = (isPhone ? MOBILE_BET_MODAL_GRID_TOP : TABLET_BET_MODAL_GRID_TOP) * modalFit;
        padBot = (isPhone ? MOBILE_BET_MODAL_GRID_BOTTOM : TABLET_BET_MODAL_GRID_BOTTOM) * modalFit;
        const innerW = Math.max(1, bgW - 2 * padX);
        const innerH = bgH - padTop - padBot;
        const minChipH = this._betPopupComputeMinChipAreaHeight(innerW, n || 1, isPhone);
        if (minChipH <= innerH + 0.5 || modalFit < 0.18) break;
        modalFit *= 0.94;
      }

      bgW = ref.w * modalFit;
      bgH = ref.h * modalFit;
      padX = (bgW - gw * modalFit) / 2;
      padTop = (isPhone ? MOBILE_BET_MODAL_GRID_TOP : TABLET_BET_MODAL_GRID_TOP) * modalFit;
      padBot = (isPhone ? MOBILE_BET_MODAL_GRID_BOTTOM : TABLET_BET_MODAL_GRID_BOTTOM) * modalFit;

      title.style.fontSize = Math.round(
        (isPhone ? MOBILE_BET_MODAL_TITLE_FS : TABLET_BET_MODAL_TITLE_FS) * modalFit,
      );
      title.y = (isPhone ? MOBILE_BET_MODAL_TITLE_TOP : TABLET_BET_MODAL_TITLE_TOP) * modalFit;
      yNudge = isPhone ? 0 : TABLET_BET_MODAL_OFFSET_Y_REF * modalFit;
      this._betPopupMobileModalFit = modalFit;

      const fill = this._betPopupPanelFill;
      const mask = this._betPopupRoundMask;
      const cornerRef = isPhone ? MOBILE_BET_MODAL_CORNER_R : TABLET_BET_MODAL_CORNER_R;
      const rr = Math.min(cornerRef * modalFit, bgW * 0.075, bgH * 0.04);
      betPopupCornerR = rr;
      if (fill) {
        fill.visible = true;
        paintModalPanelBackground(fill, bgW, bgH, rr);
        fill.alpha = MODAL_PANEL_GLASS_ALPHA;
      }
      if (mask) {
        mask.clear();
        mask.roundRect(0, 0, bgW, bgH, rr).fill({ color: 0xffffff });
        bg.mask = mask;
      }
      bg.alpha = 1;
    } else {
      /* Desktop / large: sand panel fill, 20px corners (scaled), line art clipped on top. */
      this._betPopupMobileModalFit = 1;
      const maxPanelW = 1000;
      const maxPanelH = 780;
      const fitScale = Math.min(maxPanelW / tw, maxPanelH / th);
      bgW = tw * fitScale;
      bgH = th * fitScale;

      const fill = this._betPopupPanelFill;
      const mask = this._betPopupRoundMask;
      const rrDesk = Math.min(
        DESKTOP_BET_MODAL_CORNER_R * (bgW / DESKTOP_BET_MODAL_CORNER_REF_W),
        bgW * 0.075,
        bgH * 0.04,
      );
      betPopupCornerR = rrDesk;
      if (fill) {
        fill.visible = true;
        paintModalPanelBackground(fill, bgW, bgH, rrDesk);
        fill.alpha = MODAL_PANEL_GLASS_ALPHA;
      }
      if (mask) {
        mask.clear();
        mask.roundRect(0, 0, bgW, bgH, rrDesk).fill({ color: 0xffffff });
        bg.mask = mask;
      }
      bg.alpha = 1;

      title.style.fontSize = 25;
      padX = 48;
      padTop = 124;
      padBot = 26;
    }

    this._syncModalBackdropSprite(bg, bgW, bgH);

    if (compact) {
      const isPhone = this._isPortraitMobileViewport();
      const mf = this._betPopupMobileModalFit;
      title.x = isPhone ? bgW / 2 : bgW / 2 + TABLET_BET_MODAL_TITLE_CENTER_NUDGE_X * mf;
    } else {
      title.x = bgW / 2;
      title.y = 38;
    }

    if (!compact) {
      const closeW = Math.min(52, Math.max(36, bgW * 0.065));
      closeBtn.width = closeW;
      closeBtn.height = closeW;
      closeBtn.x = bgW - 40;
      closeBtn.y = 30;
    } else {
      const mf = this._betPopupMobileModalFit;
      const isPhone = this._isPortraitMobileViewport();
      const closeS = (isPhone ? MOBILE_BET_MODAL_CLOSE_SIZE_REF : TABLET_BET_MODAL_CLOSE_SIZE_REF) * mf;
      closeBtn.width = closeS;
      closeBtn.height = closeS;
      const closeRight = isPhone ? MOBILE_BET_MODAL_CLOSE_RIGHT_REF : TABLET_BET_MODAL_CLOSE_RIGHT_REF;
      const closeTop = isPhone ? MOBILE_BET_MODAL_CLOSE_TOP_REF : TABLET_BET_MODAL_CLOSE_TOP_REF;
      closeBtn.x = bgW - closeRight * mf - closeS / 2;
      closeBtn.y = closeTop * mf + closeS / 2;
    }

    chipsArea.x = padX;
    chipsArea.y = padTop;

    const borderG = this._betPopupPanelBorder;
    if (borderG) {
      borderG.clear();
      borderG.roundRect(0, 0, bgW, bgH, betPopupCornerR).stroke({
        width: 3,
        fill: BET_POPUP_BORDER_GRADIENT,
        join: 'round',
      });
    }

    this._betPopupChipInnerW = bgW - 2 * padX;
    this._betPopupChipInnerH = bgH - padTop - padBot;

    panel.hitArea = new Rectangle(0, 0, bgW, bgH);
    panel.x = -bgW / 2;
    panel.y = -bgH / 2;

    const pr = this._betPopupPanelRoot;
    if (pr) {
      let centerY = designH / 2 + yNudge;
      if (compact && this._isPortraitMobileViewport()) {
        centerY = this._resolvePhoneBetPopupCenterY(bgH);
      } else if (compact) {
        const worldS = Math.max(1e-6, this.world?.scale?.y ?? 1);
        const screenMul = scaleY * worldS;
        const minTop = marginCss / screenMul;
        const maxBottom = (ch - marginCss) / screenMul;
        const panelTop = centerY - bgH / 2;
        if (panelTop < minTop) centerY = minTop + bgH / 2;
        const panelBottom = centerY + bgH / 2;
        if (panelBottom > maxBottom) centerY = maxBottom - bgH / 2;
      }
      pr.position.set(1922 / 2, centerY);
    }

    if (this._betPopupDimRoot?.visible) {
      this._refreshBetPopupChips();
      this._syncBetPopupTriggerForModalOpen(true);
    }
  }

  /**
   * Undo non-uniform CSS canvas scaling for the settings modal panel only.
   * @param {number} scaleX
   * @param {number} scaleY
   */
  _applySettingsViewportScale(scaleX, scaleY) {
    const shell = this._settingsPanelRoot;
    if (!shell) return;
    if (scaleX > 0 && scaleY > 0 && Number.isFinite(scaleX) && Number.isFinite(scaleY)) {
      shell.scale.set(scaleY / scaleX, 1);
    } else {
      shell.scale.set(1, 1);
    }
  }

  /** Reliable label width for settings toggles (Pixi bounds + canvas measureText). */
  _measureSettingsSwitchLabelWidth(label) {
    if (!(label instanceof Text)) return 0;
    label.style.align = 'left';
    if (typeof label.updateText === 'function') label.updateText(true);

    const boundsW = Math.ceil(label.getLocalBounds().width);
    let canvasW = 0;
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const s = label.style;
        const weight = s.fontWeight || '600';
        const size = s.fontSize || 18;
        const family = s.fontFamily || 'Montserrat';
        ctx.font = `${weight} ${size}px ${family}`;
        canvasW = Math.ceil(ctx.measureText(String(label.text ?? '')).width);
      }
    }
    return Math.max(boundsW, canvasW, Math.ceil(label.width || 0));
  }

  /**
   * Place a settings-tab toggle after its localized label (or pin to the row's right edge when stacked).
   * @param {import('pixi.js').Container} row
   * @param {number} gap
   * @param {number | null} [rowW] Full row width — enables wrap + right-pinned toggle.
   */
  _layoutSettingsSwitchRow(row, gap = SETTINGS_SWITCH_LABEL_GAP, rowW = null) {
    const label = row?.children?.[0];
    const sw = row?.children?.[1];
    if (!(label instanceof Text) || !sw) return;

    const toggleW = SETTINGS_SWITCH_TOGGLE_W;
    const pinToggleRight = rowW != null && Number.isFinite(rowW);
    if (pinToggleRight) {
      const wrapW = Math.max(40, rowW - toggleW - gap);
      label.style.wordWrap = true;
      label.style.wordWrapWidth = wrapW;
    } else {
      label.style.wordWrap = false;
      label.style.wordWrapWidth = 0;
    }

    const labelW =
      this._measureSettingsSwitchLabelWidth(label) + SETTINGS_SWITCH_LABEL_PAD;
    sw.x = pinToggleRight ? rowW - toggleW : labelW + gap;
    sw.y = label.y - (label.height || 22) / 2;
  }

  /** Re-run settings option-card layout after locale / viewport changes. */
  _relayoutSettingsSwitchesOnly() {
    this._relayoutSettingsOptionsOnly();
  }

  _relayoutSettingsOptionsOnly() {
    if (!this._settingsModalRefs?.optionsStack) return;
    const state = this._getSettingsModalLayoutState();
    this._layoutSettingsOptionsStack({
      mobile: state.mobile,
      panelW: state.panelW,
      rx: state.rx,
      ry: state.ry,
      contentTop: state.contentTop,
    });
  }

  /** Autoplay tab copy scale for the active viewport (1 = desktop baseline). */
  _getAutoplayTypographyMul(state) {
    const { mobile, tablet, ry } = state;
    if (mobile) {
      return AUTOPLAY_MOBILE_TEXT_MUL * Math.min(1.08, Math.max(0.95, ry));
    }
    if (tablet) {
      return AUTOPLAY_TABLET_TEXT_MUL * Math.min(1.05, Math.max(0.95, ry));
    }
    return 1;
  }

  _setAutoplayTextSize(textNode, baseFontSize, mul, lineHeightMul = 1.28) {
    if (!textNode?.style) return;
    const fs = Math.max(8, Math.round(baseFontSize * mul));
    textNode.style.fontSize = fs;
    textNode.style.lineHeight = Math.round(fs * lineHeightMul);
  }

  /** Resize autoplay-tab labels for compact viewports; relayout sliders so wrapped copy stays readable. */
  _syncAutoplayTabTypography(state) {
    const ap = this._autoplayModalLayoutParts;
    if (!ap) return;
    const { panelW, rx } = state;
    const mul = this._getAutoplayTypographyMul(state);
    const apply = (node, base) => this._setAutoplayTextSize(node, base, mul);

    if (ap.balanceCol?.children?.[0]) apply(ap.balanceCol.children[0], AUTOPLAY_FS.statsLabel);
    if (ap.balanceCol?.children?.[1]) apply(ap.balanceCol.children[1], AUTOPLAY_FS.statsValue);
    if (ap.betCol?.children?.[0]) apply(ap.betCol.children[0], AUTOPLAY_FS.statsLabel);
    if (ap.betCol?.children?.[1]) apply(ap.betCol.children[1], AUTOPLAY_FS.statsValue);
    if (ap.spinsHeading) apply(ap.spinsHeading, AUTOPLAY_FS.spinsHeading);
    if (ap.stopHeading) apply(ap.stopHeading, AUTOPLAY_FS.stopHeading);
    if (ap.playBtnLabel) apply(ap.playBtnLabel, AUTOPLAY_FS.playBtn);

    if (ap.chipsRow) {
      ap.chipsRow.children.forEach((chip) => {
        const numText = chip.children?.[2];
        const funText = chip.children?.[4];
        if (numText?.style) {
          const base = String(numText.text) === '∞' ? AUTOPLAY_FS.chipNumInf : AUTOPLAY_FS.chipNum;
          apply(numText, base);
        }
        if (funText) apply(funText, AUTOPLAY_FS.chipCost);
      });
    }

    [ap.slider1, ap.slider2, ap.slider3].forEach((slider) => {
      if (!slider) return;
      const label = slider.children?.[0];
      const value = slider.children?.[1];
      const minT = slider.children?.[5];
      const maxT = slider.children?.[6];
      if (label?.style) {
        apply(label, AUTOPLAY_FS.sliderLabel);
        label.style.wordWrapWidth =
          mul > 1 ? Math.max(200, panelW - Math.round(56 * rx)) : AUTOPLAY_SLIDER_ROW_W;
      }
      if (value) apply(value, AUTOPLAY_FS.sliderValue);
      if (minT) apply(minT, AUTOPLAY_FS.sliderMinMax);
      if (maxT) apply(maxT, AUTOPLAY_FS.sliderMinMax);
    });

    if (Array.isArray(this._autoplaySliderRefreshers)) {
      this._autoplaySliderRefreshers.forEach((fn) => fn?.());
    }
  }

  /**
   * Stack Sound / Quick Spin cards and pin ON/OFF radios to the right edge.
   * @param {{ mobile: boolean, panelW: number, rx: number, ry: number, contentTop?: number }} opts
   */
  _layoutSettingsOptionsStack({ mobile, panelW, rx, ry, contentTop }) {
    const r = this._settingsModalRefs;
    if (!r?.optionsStack || !r.soundCard || !r.quickCard) return;

    const inset = Math.round(SETTINGS_OPTION_CARD_INSET * rx);
    const cardW = Math.max(160, panelW - inset * 2);
    const gap = Math.round(SETTINGS_OPTION_CARD_GAP * ry);
    const stack = r.optionsStack;
    stack.x = inset;
    const bodyTop = contentTop ?? Math.round(SETTINGS_CONTENT_TOP * ry);
    stack.y = bodyTop + Math.round(SETTINGS_TAB_STACK_EXTRA_Y * ry);

    const layoutCard = (card) => {
      card._segment?.refresh?.();
      const seg = card._segment;
      const labelPad = Math.round(16 * rx);
      const pillPad = Math.round(14 * rx);
      const columnGap = Math.round(18 * rx);
      const pillBlockW = seg.width;
      const pillBlockH = SETTINGS_SEGMENT_PILL_H;
      const labelMaxW = Math.max(
        64,
        cardW - pillBlockW - pillPad - columnGap - labelPad,
      );
      card._label.style.wordWrap = true;
      card._label.style.wordWrapWidth = labelMaxW;
      if (typeof card._label.updateText === 'function') card._label.updateText(true);
      if (mobile) {
        card._label.style.fontSize = Math.round(15 * ry * SETTINGS_MOBILE_UI_TEXT_MUL);
        card._label.style.lineHeight = Math.round(18 * ry * SETTINGS_MOBILE_UI_TEXT_MUL);
      } else {
        card._label.style.fontSize = 15;
        card._label.style.lineHeight = 18;
      }
      const labelH = Math.max(18, Math.ceil(card._label.height));
      const stackVertically = labelH > pillBlockH + 2;
      let cardHActual;

      if (stackVertically) {
        card._label.anchor.set(0, 0);
        card._label.x = labelPad;
        card._label.y = Math.round(12 * ry);
        seg.root.x = cardW - pillBlockW - pillPad;
        seg.root.y = card._label.y + labelH + Math.round(10 * ry);
        cardHActual = seg.root.y + pillBlockH + Math.round(12 * ry);
      } else {
        cardHActual = Math.max(
          Math.round(SETTINGS_OPTION_CARD_H * ry),
          pillBlockH + Math.round(24 * ry),
        );
        card._label.anchor.set(0, 0.5);
        card._label.x = labelPad;
        card._label.y = cardHActual / 2;
        seg.root.x = cardW - pillBlockW - pillPad;
        seg.root.y = (cardHActual - pillBlockH) / 2;
      }

      paintSettingsOptionCardBg(card._bg, cardW, cardHActual, 12);
      return cardHActual;
    };

    const soundH = layoutCard(r.soundCard);
    r.quickCard.y = soundH + gap;
    const quickH = layoutCard(r.quickCard);

    const ew = r.exitLobbyWrap;
    if (ew?.visible) {
      ew.x = panelW / 2;
      ew.y = stack.y + soundH + gap + quickH + Math.round(28 * ry);
    }
  }

  /**
   * Flow-layout for the autoplay tab inside its scroll content container.
   * @param {object} ap
   * @param {ReturnType<GameApp['_getSettingsModalLayoutState']>} state
   * @returns {number} Total content height (for scroll bounds).
   */
  _layoutAutoplayTabContent(ap, state) {
    if (!ap) return 0;
    const { panelW, ry } = state;
    const mul = this._getAutoplayTypographyMul(state);
    const rx = state.rx;
    const rowCenterX = (rowW) => (panelW - rowW) / 2;
    const sliderX = rowCenterX(AUTOPLAY_SLIDER_ROW_W);
    let y = Math.round(6 * ry);
    const statsH = Math.round(AUTOPLAY_STATS_CARD_H * (mul > 1.1 ? 1.22 : 1));
    const chipBlockH = Math.round(AUTOPLAY_CHIP_BLOCK_H * (mul > 1.1 ? 1.18 : 1));

    if (ap.statsCard) {
      const statsInset = Math.round(AUTOPLAY_STATS_CARD_INSET * rx);
      const statsW = Math.max(180, panelW - statsInset * 2);
      ap.statsCard.x = statsInset;
      ap.statsCard.y = y;
      if (ap.statsCardBg) paintSettingsOptionCardBg(ap.statsCardBg, statsW, statsH, 12);
      if (ap.statsDivider) {
        ap.statsDivider.clear();
        ap.statsDivider
          .rect(statsW / 2 - 0.5, 10, 1, statsH - 20)
          .fill({ color: T.modalStroke, alpha: 0.55 });
      }
      if (this._autoplayStatsColLayout) this._autoplayStatsColLayout(statsW);
      y += statsH + Math.round(14 * ry);
    }

    if (ap.spinsHeading) {
      ap.spinsHeading.x = panelW / 2;
      ap.spinsHeading.y = y;
      y += Math.round((mul > 1.1 ? 22 : 18) * ry);
    }

    if (ap.chipsRow) {
      ap.chipsRow.x = rowCenterX(AUTOPLAY_CHIPS_TOTAL_W) + 0.5;
      ap.chipsRow.y = y;
      y += chipBlockH + Math.round(14 * ry);
    }

    if (ap.playBtn) {
      ap.playBtn.x = rowCenterX(AUTOPLAY_PLAY_BTN_W);
      ap.playBtn.y = y;
      if (ap.playBtnBg) {
        paintModalPrimaryButtonAt(ap.playBtnBg, 0, 0, AUTOPLAY_PLAY_BTN_W, AUTOPLAY_PLAY_BTN_H, 10);
      }
      if (ap.playBtnLabel) {
        ap.playBtnLabel.x = AUTOPLAY_PLAY_BTN_W / 2;
        ap.playBtnLabel.y = AUTOPLAY_PLAY_BTN_H / 2;
      }
      ap.playBtn.hitArea = new Rectangle(0, 0, AUTOPLAY_PLAY_BTN_W, AUTOPLAY_PLAY_BTN_H);
      y += AUTOPLAY_PLAY_BTN_H + Math.round(16 * ry);
    }

    if (ap.stopRule) {
      ap.stopRule.y = y;
      paintModalDividerLine(ap.stopRule, panelW, 2);
      y += Math.round(12 * ry);
    }

    if (ap.stopHeading) {
      ap.stopHeading.anchor.set(0.5, 0);
      ap.stopHeading.x = panelW / 2;
      ap.stopHeading.y = y;
      y += Math.round((mul > 1.1 ? 28 : 24) * ry);
    }

    const sliders = [ap.slider1, ap.slider2, ap.slider3].filter(Boolean);
    const sliderGap = Math.max(8, Math.round((mul > 1.1 ? 12 : 10) * ry));

    sliders.forEach((slider) => {
      y += sliderGap;
      slider.x = sliderX;
      slider.y = y;
      const sliderH = slider._layoutRowH ?? AUTOPLAY_SLIDER_ROW_H;
      y += sliderH;
    });

    return y + Math.round(16 * ry);
  }

  /**
   * Recompute autoplay-tab scroll limits when the settings panel viewport height changes.
   * @param {number} viewportTop
   * @param {number} viewportBottom
   */
  _refreshAutoplayScrollBounds(viewportTop, viewportBottom) {
    const ar = this._autoplaySettingsLayoutRefs;
    const totalH = this._autoplayScrollContentHeight;
    if (!ar?.scroll || !totalH) return;
    const viewportH = viewportBottom - viewportTop;
    const naturalTop = viewportTop;
    const minY = totalH > viewportH ? naturalTop - (totalH - viewportH) : naturalTop;
    const maxY = naturalTop;
    this._autoplayScrollClamp = { minY, maxY, naturalTop };
    ar.scroll.y = Math.max(minY, Math.min(maxY, ar.scroll.y));
  }

  /** @deprecated Use {@link GameApp#_layoutSettingsOptionsStack}. */
  _layoutSettingsSwitchesWrap(opts) {
    this._layoutSettingsOptionsStack({ mobile: opts.mobile, panelW: opts.panelW, rx: opts.rx, ry: opts.ry });
  }

  /**
   * Live settings-modal shell metrics from the current CSS viewport (never cached at create time).
   * @returns {{
   *   mobile: boolean,
   *   tablet: boolean,
   *   compact: boolean,
   *   compactMetrics: ReturnType<GameApp['_getSettingsPanelCompactMetrics']> | null,
   *   panelW: number,
   *   panelH: number,
   *   rx: number,
   *   ry: number,
   *   tabBarH: number,
   *   tabInset: number,
   *   contentTop: number,
   *   contentBottom: number,
   * }}
   */
  _getSettingsModalLayoutState() {
    const mobile = this._isPortraitMobileViewport();
    const tablet = !mobile && this._isTabletViewport();
    const compact = mobile || tablet;
    const compactMetrics = compact
      ? this._getSettingsPanelCompactMetrics(mobile ? 'mobile' : 'tablet')
      : null;
    const panelW = compactMetrics ? compactMetrics.panelW : SETTINGS_PANEL_DESKTOP.w;
    const panelH = compactMetrics ? compactMetrics.panelH : SETTINGS_PANEL_DESKTOP.h;
    const rx = compactMetrics ? panelW / compactMetrics.layoutRefW : 1;
    const ry = compactMetrics ? panelH / compactMetrics.layoutRefH : 1;
    const tabStripY = compact ? Math.round(SETTINGS_TAB_BAR_Y * ry) : SETTINGS_TAB_BAR_Y;
    const tabBarH = compact
      ? Math.round(
          SETTINGS_TAB_BAR_H *
            (mobile ? Math.min(1.38, 0.95 + ry * 0.35) : Math.min(1.15, ry)),
        )
      : SETTINGS_TAB_BAR_H;
    const tabInset = compact ? Math.round(SETTINGS_OPTION_CARD_INSET * rx) : SETTINGS_OPTION_CARD_INSET;
    const ruleTop = tabStripY + tabBarH + (compact ? Math.round(6 * ry) : 8);
    const contentTop = ruleTop + (compact ? Math.round(8 * ry) : 8);
    const contentBottom = panelH - Math.round(SETTINGS_CONTENT_BOTTOM_PAD * ry);
    return {
      mobile,
      tablet,
      compact,
      compactMetrics,
      panelW,
      panelH,
      rx,
      ry,
      tabStripY,
      tabBarH,
      tabInset,
      ruleTop,
      contentTop,
      contentBottom,
    };
  }

  /**
   * Repaint the settings tab strip using live panel metrics.
   * Must not rely on closure-captured panelW from {@link GameApp#_createSettingsModalPixi}.
   * @param {string} activeId
   * @param {ReturnType<GameApp['_getSettingsModalLayoutState']>} [state]
   */
  _paintSettingsTabStrip(activeId, state = this._getSettingsModalLayoutState()) {
    const r = this._settingsModalRefs;
    if (!r?.tabCells?.length || !r.tabBg || !r.activeLine) return;

    const { panelW, tabBarH, tabInset, mobile, ry } = state;
    const inset = tabInset;
    const stripW = panelW - inset * 2;
    if (r.tabStripMask) {
      r.tabStripMask.clear();
      r.tabStripMask.roundRect(inset, 0, stripW, tabBarH, 12).fill(0xffffff);
    }
    r.tabBg.clear();
    r.tabBg.roundRect(inset, 0, stripW, tabBarH, 12).fill({ color: T.modalSurface, alpha: 0.55 });
    r.tabBg.roundRect(inset, 0, stripW, tabBarH, 12).stroke({ width: 1, color: T.modalStroke, alpha: 0.45 });
    const cellW = stripW / 3;
    r.tabCells.forEach((cell, i) => {
      const active = cell._tabId === activeId;
      cell.x = inset + i * cellW + cellW / 2;
      cell.y = 0;
      cell.hitArea = new Rectangle(-cellW / 2, 0, cellW, tabBarH);

      const labelFs = mobile
        ? Math.round(SETTINGS_TAB_LABEL_FONT_MOBILE * Math.min(1.1, Math.max(0.95, ry)))
        : cellW < 108
          ? SETTINGS_TAB_LABEL_FONT_NARROW_DESKTOP
          : SETTINGS_TAB_LABEL_FONT_DESKTOP;
      const labelLh = Math.round(labelFs * SETTINGS_TAB_LABEL_LINE_HEIGHT_MUL);
      cell._label.style.fontSize = labelFs;
      cell._label.style.lineHeight = labelLh;
      cell._label.style.wordWrap = true;
      cell._label.style.wordWrapWidth = Math.max(48, cellW - 10);
      if (typeof cell._label.updateText === 'function') cell._label.updateText(true);

      const iconBox = mobile ? 22 : 20;
      fitSettingsTabIcon(cell._icon, iconBox);
      cell._icon.x = -cell._icon.width / 2;
      const labelGap = mobile ? 5 : 3;
      const contentH = cell._icon.height + labelGap + cell._label.height;
      const contentTop = Math.max(4, (tabBarH - contentH) / 2);
      cell._icon.y = contentTop;
      cell._label.x = 0;
      cell._label.y = contentTop + cell._icon.height + labelGap;

      cell._hi.clear();
      if (active) {
        const hiPad = 4;
        cell._hi.roundRect(-cellW / 2 + hiPad, hiPad, cellW - hiPad * 2, tabBarH - hiPad * 2, 10).fill({
          color: T.modalAccent,
          alpha: 0.22,
        });
      }
      applySettingsTabIconStyle(cell._icon, active);
      cell._label.style.fill = active ? T.modalHeading : T.modalBodyMuted;
      this._settingsTabCenters[cell._tabId === 'settings' ? 'left' : cell._tabId === 'autoplay' ? 'mid' : 'right'] = cell.x;
    });
    r.activeLine.width = Math.min(cellW - 12, 72);
    const cx =
      activeId === 'settings'
        ? r.tabCells[0].x
        : activeId === 'autoplay'
          ? r.tabCells[1].x
          : r.tabCells[2].x;
    r.activeLine.x = cx;
    r.activeLine.y = tabBarH - 2;
  }

  /**
   * Portrait mobile uses a narrow/tall panel ({@link SETTINGS_PANEL_MOBILE}); landscape/desktop keeps 666×580.
   * Tab icons and typography stay the same design sizes — only the shell and positions update.
   */
  _syncSettingsModalLayout() {
    const r = this._settingsModalRefs;
    if (!r) return;

    const state = this._getSettingsModalLayoutState();
    const {
      mobile,
      compact,
      compactMetrics,
      panelW,
      panelH,
      rx,
      ry,
      tabStripY,
      ruleTop,
      contentTop: VIEWPORT_TOP,
      contentBottom,
    } = state;
    const SCALE = r.SCALE;
    const W = 1922;
    const H = 1080;

    const upShift = (panelH * (SCALE - 1)) / 2;
    const mobileRootLower = compactMetrics?.rootOffsetY ?? 0;
    const panelRootY = mobile
      ? H - SETTINGS_PANEL_MOBILE_BOTTOM_STAGE_GAP - (panelH * SCALE) / 2
      : H / 2 - 47 - upShift + mobileRootLower;
    r.panelRoot.position.set(W / 2, panelRootY);

    r.panel.x = (-panelW * SCALE) / 2;
    r.panel.y = (-panelH * SCALE) / 2;
    r.panel.hitArea = new Rectangle(0, 0, panelW, panelH);

    r.panelBg.clear();
    paintModalPanelBackground(r.panelBg, panelW, panelH, 20);

    r.borderBase.clear();
    r.borderBase.roundRect(0, 0, panelW, panelH, 20).stroke({ width: 4, color: T.modalBorderDark, alpha: 1 });

    const edgeInset = compact ? Math.round(28 * rx) : 28;
    const edgeW = Math.max(8, panelW - edgeInset * 2);
    r.topBorder.x = edgeInset;
    r.topBorder.width = edgeW;
    r.bottomBorder.x = edgeInset;
    r.bottomBorder.y = panelH - 1;
    r.bottomBorder.width = edgeW;

    r.heading.visible = false;

    r.tabStrip.y = tabStripY;
    r.lineTop.y = ruleTop;

    r.closeBtn.x = compact ? panelW - Math.round((mobile ? 27 : 29) * rx) : panelW - 29;
    r.closeBtn.y = compact ? Math.round((mobile ? 28 : 29) * ry) : 28;
    if (compact) {
      const closeSize = Math.max(24, Math.round((mobile ? 32 : 28) * Math.min(rx, ry)));
      r.closeBtn.width = closeSize;
      r.closeBtn.height = closeSize;
    } else {
      r.closeBtn.width = MODAL_CLOSE_BTN_SIZE;
      r.closeBtn.height = MODAL_CLOSE_BTN_SIZE;
    }
    setModalCloseButtonHitArea(r.closeBtn);

    paintModalDividerLine(r.lineTop, panelW, 2);

    this._layoutSettingsOptionsStack({ mobile, panelW, rx, ry, contentTop: VIEWPORT_TOP });

    const ew = r.exitLobbyWrap;
    if (ew) {
      ew.visible = !!(this._lobbyUrl && String(this._lobbyUrl).trim());
    }

    this._paintSettingsTabStrip(this._settingsModalActiveTab ?? 'settings', state);

    this._syncSettingsPanelBackdrop();

    const ap = this._autoplayModalLayoutParts;
    if (ap) {
      this._syncAutoplayTabTypography(state);
      this._autoplayScrollContentHeight = this._layoutAutoplayTabContent(ap, state);
      const ar = this._autoplaySettingsLayoutRefs;
      if (ar?.mask && ar.view) {
        ar.mask.clear();
        ar.mask
          .rect(0, VIEWPORT_TOP, panelW, contentBottom - VIEWPORT_TOP)
          .fill(0xffffff);
        ar.view.hitArea = new Rectangle(
          0,
          VIEWPORT_TOP,
          panelW,
          contentBottom - VIEWPORT_TOP,
        );
        this._refreshAutoplayScrollBounds(VIEWPORT_TOP, contentBottom);
      }
    }

    const ir = this._infoSettingsLayoutRefs;
    if (ir?.mask && ir.content) {
      /* Content is authored at the desktop baseline width and uniformly scaled to the
         active inner width (full panel on desktop; portrait phone + tablet use a small
         side inset so the Information copy reads slightly narrower). Word-wrap widths stay
         in desktop space so line breaks never shift. */
      const baselinePanelW = SETTINGS_PANEL_DESKTOP.w;
      const BASE_PAD_X = 28;
      const infoTight = mobile || this._isTabletViewport();
      const infoSideInset = infoTight
        ? (mobile ? 0 : SETTINGS_INFO_VIEW_EXTRA_SIDE_INSET)
        : 0;
      const innerPanelW = Math.max(240, panelW - infoSideInset * 2);
      const contentScale = innerPanelW / baselinePanelW;
      ir.mask.clear();
      ir.mask
        .rect(infoSideInset, VIEWPORT_TOP, innerPanelW, contentBottom - VIEWPORT_TOP)
        .fill(0xffffff);
      ir.view.hitArea = new Rectangle(
        infoSideInset,
        VIEWPORT_TOP,
        innerPanelW,
        contentBottom - VIEWPORT_TOP,
      );
      ir.content.scale.set(contentScale);
      ir.content.x = infoSideInset + BASE_PAD_X * contentScale;
      this._infoContentScale = contentScale;
      this._infoViewportTop = VIEWPORT_TOP;
      this._infoViewportBottom = contentBottom;
      this._refreshInfoScrollBounds(VIEWPORT_TOP, contentBottom);
    }

    this._settingsLayoutMobile = mobile;
  }

  /**
   * Recompute info-tab scroll limits when the settings panel viewport height changes (mobile responsive).
   * @param {number} viewportTop
   * @param {number} viewportBottom
   */
  _refreshInfoScrollBounds(viewportTop, viewportBottom) {
    const ir = this._infoSettingsLayoutRefs;
    const totalH = this._infoScrollContentHeight;
    if (!ir?.scroll || !totalH) return;
    /* Content is scaled uniformly to fit the active panel width — scroll limits
       must follow the scaled height, otherwise the bottom of the info tab is
       unreachable on smaller panels. */
    const scale = this._infoContentScale ?? 1;
    const effectiveH = totalH * scale;
    const viewportH = viewportBottom - viewportTop;
    const naturalTop = viewportTop + 6;
    const minY = effectiveH > viewportH ? naturalTop - (effectiveH - viewportH) : naturalTop;
    const maxY = naturalTop;
    this._infoScrollClamp = { minY, maxY, naturalTop };
    ir.scroll.y = Math.max(minY, Math.min(maxY, ir.scroll.y));
  }

  async _createBetPopupPixi() {
    if (this._betPopupDimRoot || !this.world || !this.app) return;

    const W = 1922;
    const H = 1080;
    this.world.sortableChildren = true;

    const dimRoot = new Container();
    dimRoot.visible = false;
    dimRoot.zIndex = 200;
    dimRoot.eventMode = 'static';

    /* Oversized vs design rect so the dim always covers the full canvas after world scale/pivot (same idea as autoplay dim). */
    const dim = new Graphics();
    dim.rect(-8000, -8000, 16000, 16000).fill({ color: T.modalDim, alpha: 0.78 });
    dim.eventMode = 'static';
    dim.cursor = 'default';
    dim.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._hideBetPopup();
    });
    dimRoot.addChild(dim);

    let closeTex = Texture.EMPTY;
    try {
      closeTex = await Assets.load('/assets/close_button.png');
    } catch {
      /* optional */
    }

    const panel = new Container();
    panel.eventMode = 'static';

    const panelFill = new Graphics();
    panelFill.eventMode = 'none';
    panelFill.alpha = MODAL_PANEL_GLASS_ALPHA;
    const roundMask = new Graphics();
    roundMask.eventMode = 'none';
    const panelBorder = new Graphics();
    panelBorder.eventMode = 'none';

    const bgSprite = new Sprite(Texture.EMPTY);
    bgSprite.eventMode = 'none';
    applySpriteBackdropBlur(bgSprite);
    const tw = 1000;
    const th = 780;
    this._betPopupBgNatW = tw;
    this._betPopupBgNatH = th;
    /* Final width/height come from {@link #_syncBetPopupLayout}. */
    bgSprite.width = tw;
    bgSprite.height = th;
    bgSprite.eventMode = 'none';

    const title = new Text({
      text: this._tu('BET_OPTIONS'),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 25,
        fontWeight: '900',
        fill: T.modalHeading,
        align: 'center',
        letterSpacing: 1.2,
      },
    });
    title.anchor.set(0.5, 0);
    title.eventMode = 'none';

    const closeBtn = new Sprite(closeTex);
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.anchor.set(0.5, 0.5);
    closeBtn.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      this._hideBetPopup();
    });

    const chipsArea = new Container();
    chipsArea.eventMode = 'static';

    panel.addChild(bgSprite, panelFill, roundMask, panelBorder, title, closeBtn, chipsArea);
    bgSprite.mask = roundMask;

    this._betPopupPanelFill = panelFill;
    this._betPopupRoundMask = roundMask;
    this._betPopupPanelBorder = panelBorder;

    const panelRoot = new Container();
    panelRoot.visible = false;
    panelRoot.zIndex = 201;
    panelRoot.eventMode = 'static';
    panelRoot.position.set(W / 2, H / 2);
    panelRoot.addChild(panel);

    this.world.addChild(dimRoot, panelRoot);
    this._betPopupDimRoot = dimRoot;
    this._betPopupPanelRoot = panelRoot;
    this._betPopupDim = dim;
    this._betPopupChipsArea = chipsArea;
    this._betPopupBgSprite = bgSprite;
    this._betPopupTitle = title;
    this._betPopupCloseBtn = closeBtn;
    this._betPopupPanel = panel;

    const cr = this._readContainerSize();
    const sx = cr.width / W;
    const sy = cr.height / H;
    this._applyBetPopupViewportScale(sx, sy);
    this._syncBetPopupLayout();
  }

  _refreshBetPopupChips() {
    const area = this._betPopupChipsArea;
    if (!area || !this.slotMachine || !this._qbNormTex) return;
    area.removeChildren();

    const ui = this.slotMachine.hud;
    const bets = ui.quickBets ?? [];
    const current = ui.betValue;
    const innerW = this._betPopupChipInnerW ?? 800;
    const innerH = this._betPopupChipInnerH ?? 500;
    const font = '"Montserrat", "Arial", sans-serif';

    const n = bets.length;
    if (n === 0) return;

    const mobileUi = this._isPortraitMobileViewport() || this._isTabletViewport();

    if (mobileUi) {
      const isPhone = this._isPortraitMobileViewport();
      const gw = isPhone ? MOBILE_BET_MODAL_GRID_INNER_W : TABLET_BET_MODAL_GRID_INNER_W;
      const cw0 = isPhone ? MOBILE_BET_CHIP_REF_W : TABLET_BET_CHIP_REF_W;
      const ch0 = isPhone ? MOBILE_BET_CHIP_REF_H : TABLET_BET_CHIP_REF_H;
      const g0 = isPhone ? MOBILE_BET_CHIP_GAP_REF : TABLET_BET_CHIP_GAP_REF;
      const gridInnerH = isPhone ? MOBILE_BET_MODAL_GRID_INNER_H : TABLET_BET_MODAL_GRID_INNER_H;
      const w = Math.max(1, innerW);
      const u = w / gw;
      let cw = cw0 * u;
      let ch = ch0 * u;
      let g = g0 * u;
      let cols = Math.max(1, Math.floor((w + g) / (cw + g)));
      cols = Math.min(cols, n);
      const rows = Math.ceil(n / cols);
      let needW = cols * cw + (cols - 1) * g;
      let needH = rows * ch + (rows - 1) * g;
      const innerHAvail = gridInnerH * (w / gw);
      let s = Math.min(w / Math.max(1, needW), innerHAvail / Math.max(1, needH), 1.55);
      if (!(s > 0) || !Number.isFinite(s)) s = 1;
      cw *= s;
      ch *= s;
      g *= s;
      needW = cols * cw + (cols - 1) * g;
      needH = rows * ch + (rows - 1) * g;
      const rowW = needW;

      bets.forEach((amount, i) => {
        const sel = Math.abs(amount - current) < 0.001;
        const btn = createQuickBetChipPixi({
          betValue: amount,
          selected: sel,
          textureNormal: this._qbNormTex,
          textureSelected: this._qbSelTex,
          cellW: cw,
          cellH: ch,
          bottomText: this._tu('BET'),
          currencyLabel: this._currencyLabel(),
          currencyPrecision: this._currencyPrecision,
          fontFamily: font,
        });

        const col = i % cols;
        const row = Math.floor(i / cols);
        btn.x = (innerW - rowW) / 2 + col * (cw + g);
        btn.y = row * (ch + g);

        btn.on('pointertap', (e) => {
          if (!isPrimaryPointerAction(e)) return;
          this._applyBetFromBetPopup(amount);
        });

        area.addChild(btn);
      });
      return;
    }

    /* Desktop: square chips (unchanged layout). */
    const baseSide = 130;
    const gap = 15;
    let cols = Math.max(1, Math.floor((innerW + gap) / (baseSide + gap)));
    cols = Math.min(cols, Math.max(1, n));
    const rows = Math.ceil(n / cols);
    let needW = cols * (baseSide + gap) - gap;
    let needH = rows * (baseSide + gap) - gap;
    let chipScale = Math.min(innerW / needW, innerH / needH);
    chipScale = Math.min(chipScale, 1.55);
    if (!(chipScale > 0) || !Number.isFinite(chipScale)) chipScale = 1;

    const btnW = baseSide * chipScale;
    const btnH = btnW;
    const g = gap * chipScale;
    const rowW = cols * (btnW + g) - g;

    bets.forEach((amount, i) => {
      const sel = Math.abs(amount - current) < 0.001;
      const btn = createQuickBetChipPixi({
        betValue: amount,
        selected: sel,
        textureNormal: this._qbNormTex,
        textureSelected: this._qbSelTex,
        cellW: btnW,
        cellH: btnH,
        bottomText: this._tu('BET'),
        currencyLabel: this._currencyLabel(),
        currencyPrecision: this._currencyPrecision,
        fontFamily: font,
      });

      const col = i % cols;
      const row = Math.floor(i / cols);
      btn.x = (innerW - rowW) / 2 + col * (btnW + g);
      btn.y = row * (btnH + g);

      btn.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        this._applyBetFromBetPopup(amount);
      });

      area.addChild(btn);
    });
  }

  /** Desktop only — on phone/tablet the trigger stays hidden while the modal is open. */
  _betPopupTriggerVisibleWhenModalOpen() {
    return (
      !this._isPortraitMobileViewport() &&
      !this._isTabletViewport() &&
      !this._isMobileLandscapeViewport()
    );
  }

  _showBetPopupTriggerAboveModal() {
    const btn = this._betPopupTriggerBtn;
    const world = this.world;
    const controls = this._canvasControls;
    if (!btn || !world || !controls || btn.parent !== controls) return;
    this._betPopupTriggerControlsIndex = controls.getChildIndex(btn);
    world.sortableChildren = true;
    btn.zIndex = 202;
    world.reparentChildAt(btn, world.children.length);
  }

  _restoreBetPopupTriggerToControls() {
    const btn = this._betPopupTriggerBtn;
    const world = this.world;
    const controls = this._canvasControls;
    if (!btn || !world || !controls || btn.parent !== world) return;
    const idx = Math.min(
      Math.max(0, this._betPopupTriggerControlsIndex),
      controls.children.length,
    );
    controls.reparentChildAt(btn, idx);
    btn.zIndex = 0;
  }

  _syncBetPopupTriggerForModalOpen(isOpen) {
    const btn = this._betPopupTriggerBtn;
    if (!btn) return;
    if (isOpen) {
      if (this._betPopupTriggerVisibleWhenModalOpen()) {
        this._showBetPopupTriggerAboveModal();
      } else {
        btn.visible = false;
        btn.eventMode = 'none';
        btn.cursor = 'default';
      }
      return;
    }
    this._restoreBetPopupTriggerToControls();
    btn.visible = true;
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
  }

  _toggleBetPopup() {
    if (!this._betPopupDimRoot || !this._betPopupPanelRoot) return;
    if (this._betPopupDimRoot.visible) {
      this._hideBetPopup();
    } else {
      if (this._isBlockingModalOpen('bet')) return;
      this._dismissOverlappingModals('bet');
      this._syncBetPopupLayout();
      this._refreshBetPopupChips();
      this._betPopupDimRoot.visible = true;
      this._betPopupPanelRoot.visible = true;
      this._syncBetPopupTriggerForModalOpen(true);
      sound.play('uiPopupOpen');
    }
  }

  _hideBetPopup() {
    /* Only emit close sfx if it was actually open — _hideBetPopup is also
     * called preemptively on resize / orientation / settings open. */
    const wasOpen = !!(this._betPopupDimRoot && this._betPopupDimRoot.visible);
    if (this._betPopupDimRoot) this._betPopupDimRoot.visible = false;
    if (this._betPopupPanelRoot) this._betPopupPanelRoot.visible = false;
    this._syncBetPopupTriggerForModalOpen(false);
    if (wasOpen) sound.play('uiPopupClose');
  }

  _ensureAutoplayPixiDim() {
    if (this._autoplayPixiDim) return;
    const g = new Graphics();
    g.rect(-8000, -8000, 16000, 16000).fill({ color: T.modalDim, alpha: 0.78 });
    g.eventMode = 'static';
    g.cursor = 'default';
    g.zIndex = 10;
    g.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._hideAutoplayPicker();
    });
    this._autoplayPixiDim = g;
  }

  _showAutoplayPicker() {
    const cluster = this._autoplayCluster;
    const world = this.world;
    const controls = this._canvasControls;
    if (!this._autoplayPickerRow || !cluster || !world || !controls || cluster.parent !== controls) return;
    if (this._isBlockingModalOpen('autoplay')) return;

    this._dismissOverlappingModals('autoplay');
    this._ensureAutoplayPixiDim();
    const dim = this._autoplayPixiDim;
    if (!dim) return;

    this._autoplayClusterControlsIndex = controls.getChildIndex(cluster);
    this._autoplayPickerOpen = true;
    this._autoplayPickerRow.visible = true;

    world.sortableChildren = true;
    if (this.slotMachine?.root) this.slotMachine.root.zIndex = 0;
    dim.zIndex = 10;
    cluster.zIndex = 20;

    world.reparentChildAt(dim, world.children.length);
    world.reparentChildAt(cluster, world.children.length);
    sound.play('uiPopupOpen');
  }

  _hideAutoplayPicker() {
    const wasOpen = this._autoplayPickerOpen;
    this._autoplayPickerOpen = false;
    if (this._autoplayPickerRow) this._autoplayPickerRow.visible = false;

    const cluster = this._autoplayCluster;
    const world = this.world;
    const controls = this._canvasControls;
    const dim = this._autoplayPixiDim;

    if (cluster && controls && cluster.parent === world) {
      const idx = Math.min(
        Math.max(0, this._autoplayClusterControlsIndex),
        controls.children.length,
      );
      controls.reparentChildAt(cluster, idx);
      cluster.zIndex = 20;
    }

    dim?.removeFromParent();
    if (wasOpen) sound.play('uiPopupClose');
  }

  _onAutoplayIconPointerUp() {
    if (this._autoplaying) {
      sound.play('uiClick');
      this.handleAutoplayToggle(false);
      this._hideAutoplayPicker();
      return;
    }
    if (this._autoplayPickerOpen) {
      this._hideAutoplayPicker();
      return;
    }
    this._showAutoplayPicker();
  }

  /**
   * @param {number | null} remainingSpins null = unlimited
   */
  _startAutoplayWithRounds(remainingSpins) {
    this._autoplayRoundsRemaining = remainingSpins;
    this._hideAutoplayPicker();
    this.handleAutoplayToggle(true);
  }

  /** Full-screen background image + bottom vignette (design space 1922×1080). */
  async _createPixiStageBackdrop() {
    if (this._backdropRoot) return;
    const W = 1922;
    const H = 1080;
    const root = new Container();
    root.eventMode = 'none';

    if (!this._bgTexDesktop?.width) {
      try { this._bgTexDesktop = await Assets.load('/assets/background.png'); } catch { this._bgTexDesktop = Texture.EMPTY; }
    }
    try { this._bgTexMobile = await Assets.load('/assets/mobile_background.png'); } catch { this._bgTexMobile = this._bgTexDesktop; }

    const spr = new Sprite(this._pickBackdropTextureForViewport());
    spr.eventMode = 'none';
    this._backdropSprite = spr;
    this._syncStageBackdropForViewport();

    root.addChild(spr);
    this._backdropRoot = root;
  }

  /** Settings (paylines toggle) — Pixi modal on stage, above {@link this.world}. */
  async _createSettingsModalPixi() {
    if (this._settingsRoot || !this.app) return;

    const W = 1922;
    const H = 1080;
    const mobileInit = this._isPortraitMobileViewport();
    const tabletInit = !mobileInit && this._isTabletViewport();
    const compactInit = mobileInit || tabletInit;
    const compactMetricsInit = compactInit
      ? this._getSettingsPanelCompactMetrics(mobileInit ? 'mobile' : 'tablet')
      : null;
    const panelW = compactMetricsInit ? compactMetricsInit.panelW : SETTINGS_PANEL_DESKTOP.w;
    const panelH = compactMetricsInit ? compactMetricsInit.panelH : SETTINGS_PANEL_DESKTOP.h;
    /** Uniform up-scale (preserves all internal aspect ratios — no stretching of children). */
    const SCALE = SETTINGS_MODAL_PANEL_SCALE;

    const root = new Container();
    root.visible = false;
    root.eventMode = 'static';
    root.zIndex = 999990;

    const dim = new Graphics();
    dim.rect(-8000, -8000, 16000, 16000).fill({ color: T.modalDim, alpha: 0.78 });
    dim.eventMode = 'static';
    dim.cursor = 'default';
    dim.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._hideSettingsPanel();
    });

    const panel = new Container();
    panel.eventMode = 'static';
    panel.scale.set(SCALE);
    panel.x = (-panelW * SCALE) / 2;
    panel.y = (-panelH * SCALE) / 2;

    const panelRoot = new Container();
    panelRoot.eventMode = 'static';
    const panelHForShift = panelH;
    const upShiftInit = (panelHForShift * (SCALE - 1)) / 2;
    const mobileRootLowerInit = compactMetricsInit?.rootOffsetY ?? 0;
    const panelRootYInit = mobileInit
      ? H - SETTINGS_PANEL_MOBILE_BOTTOM_STAGE_GAP - (panelH * SCALE) / 2
      : H / 2 - 47 - upShiftInit + mobileRootLowerInit;
    panelRoot.position.set(W / 2, panelRootYInit);
    panelRoot.addChild(panel);

    const panelBackdrop = new Sprite(Texture.EMPTY);
    panelBackdrop.eventMode = 'none';
    applySpriteBackdropBlur(panelBackdrop);
    const panelBackdropMask = new Graphics();
    panelBackdropMask.eventMode = 'none';
    panelBackdrop.mask = panelBackdropMask;

    const panelBg = new Graphics();
    paintModalPanelBackground(panelBg, panelW, panelH, 20);
    panelBg.eventMode = 'none';
    panelBg.alpha = MODAL_PANEL_GLASS_ALPHA;

    const borderBase = new Graphics();
    const borderFx = new Container();
    const edgeInset = 28;
    const edgeW = panelW - edgeInset * 2;
    const gradCanvas = document.createElement('canvas');
    gradCanvas.width = 512;
    gradCanvas.height = 2;
    const gradCtx = gradCanvas.getContext('2d');
    if (gradCtx) {
      const g = gradCtx.createLinearGradient(0, 0, gradCanvas.width, 0);
      g.addColorStop(0, themeHex(T.modalBorderDark));
      g.addColorStop(0.25, themeHex(T.modalBorderGoldLo));
      g.addColorStop(0.5, themeHex(T.modalBorderGoldHi));
      g.addColorStop(0.75, themeHex(T.modalAccentHi));
      g.addColorStop(1, themeHex(T.modalBorderDark));
      gradCtx.fillStyle = g;
      gradCtx.fillRect(0, 0, gradCanvas.width, gradCanvas.height);
    }
    const borderGradTex = Texture.from(gradCanvas);
    borderBase.roundRect(0, 0, panelW, panelH, 20).stroke({ width: 4, color: T.modalBorderDark, alpha: 1 });
    borderBase.eventMode = 'none';
    const topBorder = new Sprite(borderGradTex);
    topBorder.x = edgeInset;
    topBorder.y = -2;
    topBorder.width = edgeW;
    topBorder.height = 4;
    topBorder.eventMode = 'none';
    const bottomBorder = new Sprite(borderGradTex);
    bottomBorder.x = edgeInset;
    bottomBorder.y = panelH - 1;
    bottomBorder.width = edgeW;
    bottomBorder.height = 4;
    bottomBorder.eventMode = 'none';
    borderFx.addChild(topBorder, bottomBorder);
    borderFx.eventMode = 'none';

    const heading = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 16,
        fontWeight: '700',
        lineHeight: 20,
        fill: T.modalHeading,
        align: 'center',
      },
    }), 'GAME_SETTINGS', { upper: true });
    heading.anchor.set(0.5, 0);
    heading.x = panelW / 2;
    heading.y = 24;
    heading.eventMode = 'none';
    this._settingsHeadingText = heading;

    let closeTex = Texture.EMPTY;
    try {
      closeTex = await Assets.load('/assets/close_button.png');
    } catch {
      /* optional fallback */
    }
    const closeBtn = new Sprite(closeTex);
    closeBtn.anchor.set(0.5, 0.5);
    closeBtn.x = panelW - 29;
    closeBtn.y = 28;
    closeBtn.width = MODAL_CLOSE_BTN_SIZE;
    closeBtn.height = MODAL_CLOSE_BTN_SIZE;
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    setModalCloseButtonHitArea(closeBtn);
    closeBtn.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      this._hideSettingsPanel();
    });

    const lineTop = new Graphics();
    lineTop.x = 0;
    lineTop.y = SETTINGS_RULE_TOP_Y;
    lineTop.eventMode = 'none';
    paintModalDividerLine(lineTop, panelW, 2);

    const makeOptionCard = ({ labelKey, getValue, setValue, onChange }) => {
      const card = new Container();
      card.eventMode = 'passive';
      const bg = new Graphics();
      bg.eventMode = 'none';
      const label = this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: 16,
            lineHeight: 20,
            fontWeight: '600',
            fill: T.modalHeading,
            align: 'left',
          },
        }),
        labelKey,
        { afterApply: () => this._relayoutSettingsOptionsOnly() },
      );
      label.anchor.set(0, 0.5);
      label.x = 16;
      label.style.wordWrap = true;
      label.style.wordWrapWidth = 280;
      label.eventMode = 'none';
      let segmentCtrl;
      segmentCtrl = createSegmentedOnOffControl(
        getValue,
        (next) => {
          setValue(next);
          segmentCtrl.refresh();
          onChange?.();
        },
        { translate: (key) => this._tu(key) },
      );
      card.addChild(bg, label, segmentCtrl.root);
      card._bg = bg;
      card._label = label;
      card._segment = segmentCtrl;
      return card;
    };

    const settingsView = new Container();
    settingsView.eventMode = 'passive';
    const optionsStack = new Container();
    optionsStack.y = SETTINGS_CONTENT_TOP + SETTINGS_TAB_STACK_EXTRA_Y;
    optionsStack.addChild(
      makeOptionCard({
        labelKey: 'SOUND',
        getValue: () => this._settingsSoundOn,
        setValue: (next) => {
          this._settingsSoundOn = !!next;
          sound.setMuted(!this._settingsSoundOn);
          this._syncCanvasSoundIcon();
          sound.play('uiClick');
        },
      }),
      makeOptionCard({
        labelKey: 'SETTINGS_LABEL_QUICK_SPIN',
        getValue: () => this._settingsQuickSpinOn,
        setValue: (next) => {
          const on = !!next;
          const wasOn = this._settingsQuickSpinOn;
          if (on && !wasOn) {
            this._betSpeedTierBeforeQuickSpin = this._betSpeedTier;
            this._betSpeedTier = 3;
            this._syncBetSpeedArrows();
          } else if (!on && wasOn) {
            const prev = this._betSpeedTierBeforeQuickSpin;
            this._betSpeedTierBeforeQuickSpin = null;
            if (prev != null) {
              this._betSpeedTier = Math.min(3, Math.max(1, prev));
            }
            this._syncBetSpeedArrows();
          }
          this._settingsQuickSpinOn = on;
          sound.play('uiClick');
        },
      }),
    );
    const soundCard = optionsStack.children[0];
    const quickCard = optionsStack.children[1];
    quickCard.y = SETTINGS_OPTION_CARD_H + SETTINGS_OPTION_CARD_GAP;

    const exitLobbyWrap = new Container();
    exitLobbyWrap.eventMode = 'passive';
    exitLobbyWrap.visible = !!this._lobbyUrl;

    const exitTap = new Container();
    exitTap.eventMode = 'static';
    exitTap.cursor = 'pointer';
    const exitW = 280;
    const exitH = 44;
    const exitBg = new Graphics();
    paintModalGhostButton(exitBg, exitW, exitH, 12);
    const exitLabel = this._bindLocalizedText(
      new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: 15,
          fontWeight: '700',
          fill: T.modalHeading,
          align: 'center',
        },
      }),
      'EXIT_TO_LOBBY',
    );
    exitLabel.anchor.set(0.5, 0.5);
    exitTap.hitArea = new Rectangle(-exitW / 2, -exitH / 2, exitW, exitH);
    exitTap.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      const url = String(this._lobbyUrl ?? '').trim();
      if (!url) return;
      sound.play('uiClick');
      window.location.assign(url);
    });
    exitTap.addChild(exitBg, exitLabel);
    exitLobbyWrap.addChild(exitTap);

    settingsView.addChild(optionsStack, exitLobbyWrap);
    this._settingsViewContainer = settingsView;

    const autoplayView = await this._buildAutoplaySettingsView({ panelW, panelH });
    autoplayView.visible = false;
    this._autoplayViewContainer = autoplayView;

    const infoView = await this._buildInfoSettingsView({ panelW, panelH });
    infoView.visible = false;
    this._infoViewContainer = infoView;

    let settingsIconTex = Texture.EMPTY;
    let autoplayIconTex = Texture.EMPTY;
    let infoIconTex = Texture.EMPTY;
    let activeSettingsLineTex = Texture.EMPTY;
    try { settingsIconTex = await Assets.load('/assets/settings/settings.svg'); } catch { /* noop */ }
    try { autoplayIconTex = await Assets.load('/assets/settings/autoplay.svg'); } catch { /* noop */ }
    try { infoIconTex = await Assets.load('/assets/settings/info.svg'); } catch { /* noop */ }
    try { activeSettingsLineTex = await Assets.load('/assets/settings/active_settings_icon.svg'); } catch { /* noop */ }

    heading.visible = false;
    heading.eventMode = 'none';

    const tabStrip = new Container();
    tabStrip.y = SETTINGS_TAB_BAR_Y;
    tabStrip.eventMode = 'passive';
    const tabStripMask = new Graphics();
    tabStripMask.eventMode = 'none';
    tabStrip.addChild(tabStripMask);
    tabStrip.mask = tabStripMask;
    const tabBg = new Graphics();
    tabBg.eventMode = 'none';
    tabStrip.addChild(tabBg);

    const tabDefs = [
      { id: 'settings', labelKey: 'GAME_SETTINGS', tex: settingsIconTex },
      { id: 'autoplay', labelKey: 'AUTOPLAY_SETTINGS', tex: autoplayIconTex },
      { id: 'info', labelKey: 'INFO', tex: infoIconTex },
    ];
    const tabCells = [];
    const tabIcons = {};
    const tabLabels = {};
    const tabHighlights = [];

    tabDefs.forEach((def, i) => {
      const cell = new Container();
      cell.eventMode = 'static';
      cell.cursor = 'pointer';
      const hi = new Graphics();
      hi.eventMode = 'none';
      const icon = new Sprite(def.tex);
      fitSettingsTabIcon(icon, 20);
      icon.x = 0;
      icon.y = 2;
      const lbl = this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: SETTINGS_TAB_LABEL_FONT_DESKTOP,
            lineHeight: Math.round(SETTINGS_TAB_LABEL_FONT_DESKTOP * SETTINGS_TAB_LABEL_LINE_HEIGHT_MUL),
            fontWeight: '700',
            fill: T.modalBodyMuted,
            align: 'center',
          },
        }),
        def.labelKey,
        { upper: true },
      );
      lbl.anchor.set(0.5, 0);
      lbl.y = 24;
      cell.addChild(hi, icon, lbl);
      cell._tabId = def.id;
      cell._hi = hi;
      cell._icon = icon;
      cell._label = lbl;
      tabCells.push(cell);
      tabIcons[def.id] = icon;
      tabLabels[def.id] = lbl;
      tabHighlights.push(hi);
      tabStrip.addChild(cell);
    });

    const activeLine = new Sprite(activeSettingsLineTex);
    activeLine.height = 3;
    activeLine.anchor.set(0.5, 1);
    activeLine.eventMode = 'none';
    tabStrip.addChild(activeLine);

    this._settingsTabCenters = { left: panelW * 0.17, mid: panelW / 2, right: panelW * 0.83 };

    const setActiveTab = (tabName) => {
      const prev = this._settingsModalActiveTab;
      if (prev !== tabName) {
        sound.play('uiClick');
      }
      this._settingsModalActiveTab = tabName;
      if (tabName === 'settings') {
        settingsView.visible = true;
        autoplayView.visible = false;
        infoView.visible = false;
      } else if (tabName === 'autoplay') {
        settingsView.visible = false;
        autoplayView.visible = true;
        infoView.visible = false;
        this._autoplayScroll?.reset();
      } else {
        settingsView.visible = false;
        autoplayView.visible = false;
        infoView.visible = true;
        this._infoScroll?.reset();
      }
      /* Full relayout from live viewport metrics — tab strip must not use create-time panelW. */
      this._syncSettingsModalForViewport();
      if (tabName === 'autoplay') {
        this._refreshAutoplaySettingsView();
      } else if (tabName === 'info') {
        this._refreshInfoPayoutAmounts();
      }
    };
    setActiveTab('settings');
    tabCells.forEach((cell) => {
      cell.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        e.stopPropagation();
        setActiveTab(cell._tabId);
      });
    });

    panel.addChild(
      panelBackdrop,
      panelBackdropMask,
      panelBg,
      borderBase,
      borderFx,
      closeBtn,
      tabStrip,
      lineTop,
      settingsView,
      autoplayView,
      infoView,
    );
    panel.hitArea = new Rectangle(0, 0, panelW, panelH);

    this._settingsModalRefs = {
      SCALE,
      panelRoot,
      panel,
      panelBg,
      borderBase,
      topBorder,
      bottomBorder,
      heading,
      closeBtn,
      lineTop,
      optionsStack,
      soundCard,
      quickCard,
      exitLobbyWrap,
      tabStrip,
      tabStripMask,
      tabBg,
      tabCells,
      tabIcons,
      activeLine,
      setActiveTab,
    };

    root.addChild(dim, panelRoot);
    this.app.stage.sortableChildren = true;
    this.app.stage.addChild(root);
    this._settingsRoot = root;
    this._settingsPanelRoot = panelRoot;
    this._settingsPanelBackdrop = panelBackdrop;
    this._settingsPanelBackdropMask = panelBackdropMask;

    this._syncSettingsModalForViewport();
  }

  /**
   * Build the “AUTOPLAY SETTINGS” view (preset chips, PLAY NOW, three Stop-Autoplay sliders,
   * BALANCE / BET sidebar). Returns a Container positioned absolutely within the modal panel.
   * @param {{ panelW: number, panelH: number }} args
   * @returns {Promise<Container>}
   */
  async _buildAutoplaySettingsView({ panelW }) {
    const view = new Container();
    view.eventMode = 'static';

    const VIEWPORT_TOP = SETTINGS_CONTENT_TOP;
    const VIEWPORT_BOTTOM = SETTINGS_PANEL_DESKTOP.h - SETTINGS_CONTENT_BOTTOM_PAD;
    const VIEWPORT_H = VIEWPORT_BOTTOM - VIEWPORT_TOP;
    view.hitArea = new Rectangle(0, VIEWPORT_TOP, panelW, VIEWPORT_H);

    const mask = new Graphics();
    mask.rect(0, VIEWPORT_TOP, panelW, VIEWPORT_H).fill(0xffffff);
    mask.eventMode = 'none';
    view.addChild(mask);

    const scroll = new Container();
    scroll.eventMode = 'passive';
    scroll.mask = mask;
    view.addChild(scroll);

    const content = new Container();
    content.eventMode = 'passive';
    scroll.addChild(content);

    const chipSpecs = [
      { spins: 10, label: '10' },
      { spins: 20, label: '20' },
      { spins: 50, label: '50' },
      { spins: 100, label: '100' },
      { spins: null, label: '∞' },
    ];

    /* ── Session stats card: BALANCE | BET at the top ── */
    const statsCard = new Container();
    statsCard.eventMode = 'none';
    const statsCardBg = new Graphics();
    statsCardBg.eventMode = 'none';
    const statsDivider = new Graphics();
    statsDivider.eventMode = 'none';

    const makeStatCol = (labelKey, valueRef) => {
      const col = new Container();
      const label = this._bindLocalizedText(new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: AUTOPLAY_FS.statsLabel,
          lineHeight: Math.round(AUTOPLAY_FS.statsLabel * 1.28),
          fontWeight: '700',
          fill: T.modalBodyMuted,
          align: 'center',
        },
      }), labelKey, { upper: true, suffix: ':' });
      label.alpha = 1;
      label.anchor.set(0.5, 0);
      label.y = 8;
      const value = new Text({
        text: this._formatCurrencyAmount(0),
        style: {
          fontFamily: 'Montserrat',
          fontSize: AUTOPLAY_FS.statsValue,
          lineHeight: Math.round(AUTOPLAY_FS.statsValue * 1.28),
          fontWeight: '700',
          fill: T.currencyAccent,
          align: 'center',
        },
      });
      value.anchor.set(0.5, 0);
      value.y = 24;
      col.addChild(label, value);
      valueRef.value = value;
      return col;
    };
    const balanceValueRef = { value: null };
    const betValueRef = { value: null };
    const balanceCol = makeStatCol('BALANCE', balanceValueRef);
    const betCol = makeStatCol('BET', betValueRef);
    statsCard.addChild(statsCardBg, statsDivider, balanceCol, betCol);

    this._autoplaySidebarRefresh = (balance, bet) => {
      if (balanceValueRef.value) balanceValueRef.value.text = this._formatCurrencyAmount(balance);
      if (betValueRef.value) betValueRef.value.text = this._formatCurrencyAmount(bet);
    };

    const spinsHeading = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: AUTOPLAY_FS.spinsHeading,
        lineHeight: Math.round(AUTOPLAY_FS.spinsHeading * 1.28),
        fontWeight: '700',
        fill: T.modalHeading,
        align: 'center',
        letterSpacing: 0.8,
      },
    }), 'SPINS', { upper: true });
    spinsHeading.anchor.set(0.5, 0);
    spinsHeading.x = panelW / 2;

    const chipsRow = new Container();
    chipsRow.eventMode = 'passive';
    chipsRow.x = (panelW - AUTOPLAY_CHIPS_TOTAL_W) / 2 + 0.5;

    const chipButtons = [];
    const chipFunTexts = [];
    chipSpecs.forEach((spec, i) => {
      const chip = createAutoplaySpinRadio(
        spec,
        () => this._autoplayModalSpinsSelected === spec.spins,
        () => {
          this._autoplayModalSpinsSelected = spec.spins;
          chipButtons.forEach((c) => c._refresh?.());
        },
      );
      chip.x = i * (AUTOPLAY_SPIN_RADIO_W + AUTOPLAY_SPIN_RADIO_GAP);

      const funText = new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: AUTOPLAY_FS.chipCost,
          lineHeight: Math.round(AUTOPLAY_FS.chipCost * 1.28),
          fontWeight: '600',
          fill: T.modalBody,
          align: 'center',
        },
      });
      funText.anchor.set(0.5, 0);
      funText.x = AUTOPLAY_SPIN_RADIO_W / 2;
      funText.y = AUTOPLAY_SPIN_RADIO_H + 2;
      funText.eventMode = 'none';
      chipFunTexts.push(funText);
      chip.addChild(funText);
      chipsRow.addChild(chip);
      chipButtons.push(chip);
    });
    this._autoplayChipButtons = chipButtons;

    const refreshChips = (bet) => {
      const b = Number(bet) || 0;
      chipSpecs.forEach((spec, i) => {
        const t = chipFunTexts[i];
        if (!t) return;
        if (spec.spins == null) {
          t.text = '';
        } else {
          t.text = this._formatCurrencyAmount(b * spec.spins);
        }
      });
      chipButtons.forEach((c) => c._refresh?.());
    };
    this._autoplayChipsRefresh = refreshChips;

    const playBtn = new Container();
    playBtn.eventMode = 'static';
    playBtn.cursor = 'pointer';
    playBtn.hitArea = new Rectangle(0, 0, AUTOPLAY_PLAY_BTN_W, AUTOPLAY_PLAY_BTN_H);
    const playBtnBg = new Graphics();
    paintModalPrimaryButtonAt(playBtnBg, 0, 0, AUTOPLAY_PLAY_BTN_W, AUTOPLAY_PLAY_BTN_H, 10);
    playBtnBg.eventMode = 'none';
    const playBtnLabel = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: AUTOPLAY_FS.playBtn,
        lineHeight: Math.round(AUTOPLAY_FS.playBtn * 1.28),
        fontWeight: '700',
        fill: T.modalHeading,
        align: 'center',
      },
    }), 'START', { upper: true });
    playBtnLabel.anchor.set(0.5, 0.5);
    playBtnLabel.x = AUTOPLAY_PLAY_BTN_W / 2;
    playBtnLabel.y = AUTOPLAY_PLAY_BTN_H / 2;
    playBtnLabel.eventMode = 'none';
    playBtn.addChild(playBtnBg, playBtnLabel);
    playBtn.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      this._startAutoplayFromModal();
    });

    const stopRule = new Graphics();
    paintModalDividerLine(stopRule, panelW, 2);

    const stopHeading = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: AUTOPLAY_FS.stopHeading,
        lineHeight: Math.round(AUTOPLAY_FS.stopHeading * 1.28),
        fontWeight: '700',
        fill: T.modalHeading,
        align: 'center',
        letterSpacing: 0.6,
      },
    }), 'INFO_CTRL_STOP_AUTO_NAME', { upper: true });
    stopHeading.anchor.set(0.5, 0);
    stopHeading.x = panelW / 2;

    const sliderRefreshers = [];
    const slider1 = this._buildAutoplaySlider({
      label: 'IF_BALANCE_DECREASES_BY',
      getMax: () => {
        const bet = Number(this.slotMachine?.hud?.betValue) || 0;
        return Math.max(0, bet * 100);
      },
      getValue: () => this._autoplayStopOnBalanceDecreaseBy,
      setValue: (v) => {
        this._autoplayStopOnBalanceDecreaseBy = v;
      },
    });
    slider1.container.x = (panelW - AUTOPLAY_SLIDER_ROW_W) / 2;
    sliderRefreshers.push(slider1.refresh);

    const slider2 = this._buildAutoplaySlider({
      label: 'IF_SINGLE_WIN_EXCEEDS',
      getMax: () => {
        const bet = Number(this.slotMachine?.hud?.betValue) || 0;
        return Math.max(0, bet * 100);
      },
      getValue: () => this._autoplayStopOnSingleWinExceeds,
      setValue: (v) => {
        this._autoplayStopOnSingleWinExceeds = v;
      },
    });
    slider2.container.x = (panelW - AUTOPLAY_SLIDER_ROW_W) / 2;
    sliderRefreshers.push(slider2.refresh);

    const slider3 = this._buildAutoplaySlider({
      label: 'IF_BALANCE_INCREASES_BY',
      getMax: () => {
        const bet = Number(this.slotMachine?.hud?.betValue) || 0;
        return Math.max(0, bet * 200);
      },
      getValue: () => this._autoplayStopOnBalanceIncreaseBy,
      setValue: (v) => {
        this._autoplayStopOnBalanceIncreaseBy = v;
      },
    });
    slider3.container.x = (panelW - AUTOPLAY_SLIDER_ROW_W) / 2;
    sliderRefreshers.push(slider3.refresh);
    this._autoplaySliderRefreshers = sliderRefreshers;

    content.addChild(
      statsCard,
      spinsHeading,
      chipsRow,
      playBtn,
      stopRule,
      stopHeading,
      slider1.container,
      slider2.container,
      slider3.container,
    );
    this._autoplayModalLayoutParts = {
      statsCard,
      statsCardBg,
      statsDivider,
      balanceCol,
      betCol,
      spinsHeading,
      chipsRow,
      playBtn,
      playBtnBg,
      playBtnLabel,
      stopRule,
      stopHeading,
      slider1: slider1.container,
      slider2: slider2.container,
      slider3: slider3.container,
    };

    const setScrollY = (next) => {
      const c = this._autoplayScrollClamp;
      if (!c) return;
      scroll.y = Math.max(c.minY, Math.min(c.maxY, next));
    };

    this._autoplayScroll = {
      reset: () => {
        const c = this._autoplayScrollClamp;
        if (c) scroll.y = c.naturalTop;
      },
      scrollBy: (dy) => {
        const c = this._autoplayScrollClamp;
        if (!c) return;
        scroll.y = Math.max(c.minY, Math.min(c.maxY, scroll.y - dy));
      },
    };

    view.on('wheel', (e) => {
      if (!view.visible) return;
      const dy = typeof e.deltaY === 'number' ? e.deltaY : 0;
      if (dy === 0) return;
      setScrollY(scroll.y - dy);
      if (typeof e.preventDefault === 'function') e.preventDefault();
    });

    let dragging = false;
    let lastY = 0;
    let pointerId = null;
    view.on('pointerdown', (e) => {
      if (!view.visible) return;
      if (e.target !== view && e.target !== scroll && e.target !== content) return;
      dragging = true;
      lastY = e.global?.y ?? 0;
      pointerId = e.pointerId;
    });
    const endDrag = (e) => {
      if (pointerId != null && e?.pointerId !== pointerId) return;
      dragging = false;
      pointerId = null;
    };
    view.on('pointermove', (e) => {
      if (!dragging) return;
      if (pointerId != null && e.pointerId !== pointerId) return;
      const ny = e.global?.y ?? 0;
      const dy = ny - lastY;
      lastY = ny;
      setScrollY(scroll.y + dy);
    });
    view.on('pointerup', endDrag);
    view.on('pointerupoutside', endDrag);
    view.on('pointercancel', endDrag);

    this._autoplaySettingsLayoutRefs = {
      mask,
      scroll,
      content,
      view,
    };

    const layoutStatsCols = (cardW) => {
      balanceCol.x = cardW * 0.25;
      betCol.x = cardW * 0.75;
      balanceCol.children[0].x = 0;
      balanceCol.children[1].x = 0;
      betCol.children[0].x = 0;
      betCol.children[1].x = 0;
    };
    layoutStatsCols(panelW - AUTOPLAY_STATS_CARD_INSET * 2);
    this._autoplayStatsColLayout = layoutStatsCols;

    return view;
  }

  /**
   * Build a single horizontal slider row matching the Frame 263/264/265 design.
   * Returns the container and a refresh function used by `_refreshAutoplaySettingsView`.
   * @param {{
   *   label: string,
   *   getMax: () => number,
   *   getValue: () => number,
   *   setValue: (v: number) => void,
   * }} args
   */
  _buildAutoplaySlider({ label, getMax, getValue, setValue }) {
    const rowW = AUTOPLAY_SLIDER_ROW_W;
    const trackH = 4;
    const knobR = 9;

    const row = new Container();
    row.eventMode = 'passive';
    row._layoutRowH = AUTOPLAY_SLIDER_ROW_H;
    row.hitArea = new Rectangle(0, 0, rowW, AUTOPLAY_SLIDER_ROW_H);

    const labelText = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: AUTOPLAY_FS.sliderLabel,
        lineHeight: Math.round(AUTOPLAY_FS.sliderLabel * 1.28),
        fontWeight: '600',
        fill: T.modalBody,
        align: 'left',
        wordWrap: true,
        wordWrapWidth: rowW,
      },
    }), label);
    labelText.anchor.set(0, 0);
    labelText.x = 0;
    labelText.y = 0;
    labelText.eventMode = 'none';

    const valueText = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: AUTOPLAY_FS.sliderValue,
        lineHeight: Math.round(AUTOPLAY_FS.sliderValue * 1.28),
        fontWeight: '700',
        fill: T.currencyAccent,
        align: 'left',
      },
    });
    valueText.anchor.set(0, 0);
    valueText.x = 0;
    valueText.y = 16;
    valueText.eventMode = 'none';

    const minText = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: AUTOPLAY_FS.sliderMinMax,
        lineHeight: Math.round(AUTOPLAY_FS.sliderMinMax * 1.28),
        fontWeight: '500',
        fill: T.modalBodyMuted,
        align: 'left',
      },
    });
    minText.alpha = 1;
    minText.anchor.set(0, 0);
    minText.eventMode = 'none';

    const maxText = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: AUTOPLAY_FS.sliderMinMax,
        lineHeight: Math.round(AUTOPLAY_FS.sliderMinMax * 1.28),
        fontWeight: '500',
        fill: T.modalBodyMuted,
        align: 'right',
      },
    });
    maxText.alpha = 1;
    maxText.anchor.set(1, 0);
    maxText.x = rowW;
    maxText.eventMode = 'none';

    const trackBg = new Graphics();
    trackBg.eventMode = 'none';

    const trackFill = new Graphics();
    trackFill.eventMode = 'none';

    const knob = new Graphics();
    knob.eventMode = 'static';
    knob.cursor = 'pointer';
    knob.hitArea = new Rectangle(-knobR - 4, -knobR - 4, (knobR + 4) * 2, (knobR + 4) * 2);

    const drawKnob = () => {
      const on = getValue() > 0;
      knob.clear();
      knob.circle(0, 0, knobR).fill({ color: T.modalSurfaceHi, alpha: 1 });
      knob.circle(0, 0, knobR).stroke({ width: 2, color: T.modalBorderGoldHi, alpha: 0.9 });
      if (on) {
        knob.circle(0, 0, 4).fill({ color: T.modalAccentHi, alpha: 1 });
      } else {
        knob.circle(0, 0, 4).stroke({ width: 1, color: T.modalTabInactive, alpha: 0.8 });
      }
    };

    /** Re-render fill width / knob position based on current value. */
    const redraw = () => {
      if (typeof labelText.updateText === 'function') labelText.updateText(true);
      const labelH = Math.max(14, Math.ceil(labelText.height));
      valueText.y = labelH + 2;
      const valueH = Math.max(14, Math.ceil(valueText.height));
      const trackY = valueText.y + valueH + 8;
      const minMaxY = trackY + trackH + 10;

      minText.x = 0;
      minText.y = minMaxY;
      maxText.x = rowW;
      maxText.y = minMaxY;

      trackBg.clear();
      trackBg.roundRect(0, trackY, rowW, trackH, trackH / 2).fill({
        color: T.modalStroke,
        alpha: 0.45,
      });

      const max = Math.max(0, getMax());
      const value = Math.max(0, Math.min(max, getValue()));
      const t = max > 0 ? value / max : 0;
      const fillW = Math.max(0, t * rowW);
      trackFill.clear();
      trackFill.roundRect(0, trackY, fillW, trackH, trackH / 2).fill({
        color: T.modalAccentHi,
        alpha: 0.95,
      });
      knob.x = t * rowW;
      knob.y = trackY + trackH / 2;
      drawKnob();
      valueText.text = this._formatCurrencyAmount(value);
      minText.text = this._formatCurrencyAmount(0);
      maxText.text = this._formatCurrencyAmount(max);

      row._layoutRowH = minMaxY + Math.max(12, Math.ceil(minText.height)) + 6;
      row.hitArea = new Rectangle(0, 0, rowW, row._layoutRowH);
    };

    let dragging = false;
    let activePointerId = null;

    const setValueFromLocalX = (localX) => {
      const max = Math.max(0, getMax());
      const t = Math.max(0, Math.min(1, localX / rowW));
      const next = max > 0 ? t * max : 0;
      setValue(next);
      redraw();
    };

    knob.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      dragging = true;
      activePointerId = e.pointerId ?? null;
    });
    /* Click on the track jumps the knob to that position. */
    row.eventMode = 'static';
    row.cursor = 'pointer';
    row.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      const local = row.toLocal(e.global);
      const trackY = knob.y - trackH / 2;
      if (local.y < trackY - 12 || local.y > trackY + trackH + 12) return;
      e.stopPropagation();
      dragging = true;
      activePointerId = e.pointerId ?? null;
      setValueFromLocalX(local.x);
    });
    row.on('globalpointermove', (e) => {
      if (!dragging) return;
      if (activePointerId != null && e.pointerId != null && e.pointerId !== activePointerId) {
        return;
      }
      const local = row.toLocal(e.global);
      setValueFromLocalX(local.x);
    });
    const endDrag = (e) => {
      if (!dragging) return;
      if (activePointerId != null && e.pointerId != null && e.pointerId !== activePointerId) {
        return;
      }
      dragging = false;
      activePointerId = null;
    };
    row.on('pointerup', endDrag);
    row.on('pointerupoutside', endDrag);
    knob.on('pointerup', endDrag);
    knob.on('pointerupoutside', endDrag);

    row.addChild(labelText, valueText, trackBg, trackFill, knob, minText, maxText);
    redraw();
    return { container: row, refresh: redraw };
  }

  /** Refresh chip FUN values, sidebar BALANCE/BET, and slider min/max/value displays. */
  _refreshAutoplaySettingsView() {
    const bet = Number(this.slotMachine?.hud?.betValue) || 0;
    const balance = Number(this.slotMachine?.hud?.balanceValue) || 0;
    if (this._autoplayChipsRefresh) this._autoplayChipsRefresh(bet);
    if (this._autoplaySidebarRefresh) this._autoplaySidebarRefresh(balance, bet);
    /* Reclamp current threshold values in case max changed (e.g. balance/bet updated). */
    if (Array.isArray(this._autoplaySliderRefreshers)) {
      this._autoplaySliderRefreshers.forEach((fn) => fn?.());
    }
  }

  /**
   * Build the “INFORMATION” view shown in the settings modal.
   * Content area is masked between the modal’s top/bottom rules and is wheel/drag scrollable
   * so the heading and the bottom tab row stay fixed.
   * Jackpot, Bonus Spin Mode and Gamble sections are intentionally omitted.
   * @param {{ panelW: number, panelH: number }} args
   * @returns {Promise<Container>}
   */
  async _buildInfoSettingsView({ panelW }) {
    this._infoPayoutAmountTexts = [];
    this._infoRuleLimitLineRefreshers = [];
    this._infoRuleMaxWinLines = [];
    const view = new Container();
    view.eventMode = 'static';

    /* Visible bounds — below top tab bar through panel bottom padding. */
    const VIEWPORT_TOP = SETTINGS_CONTENT_TOP;
    const VIEWPORT_BOTTOM = SETTINGS_PANEL_DESKTOP.h - SETTINGS_CONTENT_BOTTOM_PAD;
    const VIEWPORT_H = VIEWPORT_BOTTOM - VIEWPORT_TOP;
    /* Layout is built at the desktop baseline; {@link GameApp#_syncSettingsModalLayout}
       scales the content uniformly so it looks identical on every device. */
    const BASE_PANEL_W = SETTINGS_PANEL_DESKTOP.w;
    const PAD_X = 28;
    const INNER_W = BASE_PANEL_W - PAD_X * 2;
    const infoMobile = this._isPortraitMobileViewport();
    const INFO_TEXT_MUL = infoMobile ? SETTINGS_INFO_MOBILE_TEXT_MUL : 1;
    const INFO_ICON_MUL = infoMobile ? SETTINGS_INFO_MOBILE_ICON_MUL : 1;
    const scaledText = (v) => Math.max(1, Math.round(v * INFO_TEXT_MUL));
    const scaledIcon = (v) => Math.max(1, Math.round(v * INFO_ICON_MUL));

    /* Hit area covers viewport so wheel + drag events have a target even over empty space. */
    view.hitArea = new Rectangle(0, VIEWPORT_TOP, panelW, VIEWPORT_H);

    /* Mask is a sibling of the scroll container (Pixi v8 requires a display-tree node). */
    const mask = new Graphics();
    mask.rect(0, VIEWPORT_TOP, panelW, VIEWPORT_H).fill(0xffffff);
    mask.eventMode = 'none';
    view.addChild(mask);

    const scroll = new Container();
    scroll.eventMode = 'passive';
    scroll.mask = mask;
    view.addChild(scroll);

    const content = new Container();
    content.x = PAD_X;
    content.y = 0;
    scroll.addChild(content);

    /** Vertical stack registry — reflowed when locale changes so wrapped copy is never clipped. */
    const infoLayoutBlocks = [];
    const registerInfoBlock = (node, gapAfter = 0, opts = {}) => {
      infoLayoutBlocks.push({
        node,
        gapAfter,
        gapBefore: opts.gapBefore ?? 0,
        centerX: !!opts.centerX,
        heightFor: opts.heightFor ?? null,
        skip: !!opts.skip,
      });
    };
    const measureCtx =
      typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
    const applyInfoTableCellText = (textNode, raw, interiorW, fontOpts) => {
      const wrapped = wrapSettingsTableCellText(raw, interiorW, measureCtx, fontOpts);
      textNode.text = wrapped;
      textNode.style.wordWrap = false;
      textNode.style.wordWrapWidth = 0;
    };

    /* Reusable dummy icon texture; we’ll swap real assets later. */
    let dummyIconTex = Texture.EMPTY;
    try { dummyIconTex = await Assets.load('/assets/settings/info.svg'); } catch { /* noop */ }

    const spineAssets = this.spineAssets ?? {};

    /** Section heading helper (translation key). */
    const makeHeadingFromKey = (key) =>
      this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: scaledText(16),
            fontWeight: '700',
            letterSpacing: 1,
            fill: INFO_TAB_TEXT_YELLOW,
            align: 'center',
          },
        }),
        key,
      );

    /** Body paragraph helper (translation key). */
    const makeBodyFromKey = (key, w = INNER_W, opts = {}) =>
      this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: scaledText(12),
            lineHeight: scaledText(17),
            fontWeight: '500',
            fill: INFO_TAB_BODY_FILL,
            align: opts.align || 'left',
            wordWrap: true,
            wordWrapWidth: w,
            breakWords: true,
          },
        }),
        key,
        { replace: opts.replace },
      );

    /** Small caption helper. */
    const makeCaption = (txt, color = T.modalBodyMuted) =>
      new Text({
        text: txt,
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(11),
          fontWeight: '600',
          fill: color,
          align: 'center',
        },
      });

    /* Section divider — gold/sand fade rule matching modal chrome. */
    const makeDivider = () => {
      const d = new Graphics();
      d.x = 0;
      d.y = 0;
      d.eventMode = 'none';
      paintModalDividerLine(d, INNER_W, 2);
      return d;
    };

    /** Dummy icon badge used as a placeholder when an info asset fails to load. */
    const makeIconBadge = (label, w, h, accent = T.badgeAccent) => {
      const c = new Container();
      const bg = new Graphics();
      bg.roundRect(0, 0, w, h, 14)
        .fill({ color: T.panelDeep, alpha: 1 })
        .stroke({ width: 2, color: accent, alpha: 0.9 });
      bg.eventMode = 'none';
      const t = new Text({
        text: label,
        style: {
          fontFamily: 'Montserrat',
          fontSize: Math.max(11, Math.round(Math.min(w, h) * 0.18)),
          fontWeight: '800',
          fill: accent,
          letterSpacing: 1,
          align: 'center',
        },
      });
      t.anchor.set(0.5, 0.5);
      t.x = w / 2;
      t.y = h / 2;
      c.addChild(bg, t);
      return c;
    };

    /**
     * Render a symbol from Spine (idle pose) scaled to fit a (w × h) box.
     * Falls back to the dummy badge if the asset failed to load.
     */
    const makeSymbolSprite = (symbolId, w, h, opts = {}) =>
      createInfoSymbolOrBadge(symbolId, spineAssets, w, h, opts, makeIconBadge);

    const INFO_PAYOUT_AMOUNT_FILL = T.modalHeading;
    /** Payout row: multiplier (x5/x4/…) in #FFEA00 + formatted win amount. */
    const makePayoutLine = (payoutSpec) => {
      const multFill = INFO_TAB_TEXT_YELLOW;
      const baseStyle = {
        fontFamily: 'Montserrat',
        fontSize: scaledText(12),
        fontWeight: '700',
        letterSpacing: 0.4,
      };
      const row = new Container();
      const multText = new Text({ text: '', style: { ...baseStyle, fill: multFill } });
      const amountText = new Text({ text: '', style: { ...baseStyle, fill: INFO_PAYOUT_AMOUNT_FILL } });
      row.addChild(multText, amountText);

      const apply = (stakes = this._infoStakes()) => {
        const parts = this._formatInfoPayoutParts(payoutSpec, stakes);
        multText.text = parts.multPart;
        amountText.text = moveCurrencyBeforeAmount(parts.amountPart);
        amountText.x = multText.width;
        if (row.__infoCenterX != null) {
          row.pivot.set(row.width / 2, 0);
          row.x = row.__infoCenterX;
        }
      };

      apply();
      this._infoPayoutAmountTexts.push({ multText, amountText, payoutSpec, apply });
      return row;
    };

    /** RULES limit line — same yellow label + white amount styling as payout rows. */
    const makeRuleLimitLine = (key, replaceFn) => {
      const baseStyle = {
        fontFamily: 'Montserrat',
        fontSize: scaledText(12),
        fontWeight: '700',
        letterSpacing: 0.4,
      };
      const row = new Container();
      const labelText = new Text({ text: '', style: { ...baseStyle, fill: INFO_TAB_TEXT_YELLOW } });
      const valueText = new Text({ text: '', style: { ...baseStyle, fill: INFO_PAYOUT_AMOUNT_FILL } });
      row.addChild(labelText, valueText);

      const applyLine = () => {
        const rep = typeof replaceFn === 'function' ? replaceFn() : replaceFn;
        const full = this._t(key, rep);
        const colonIdx = full.indexOf(':');
        if (colonIdx >= 0) {
          labelText.text = `${full.slice(0, colonIdx + 1)} `;
          valueText.text = full.slice(colonIdx + 1).trim();
        } else {
          labelText.text = full;
          valueText.text = '';
        }
        valueText.x = labelText.width;
      };

      applyLine();
      this._infoRuleLimitLineRefreshers.push(applyLine);
      return row;
    };

    let cursorY = 4;

    /* ── Game logo ───────────────────────────────────────────────── */
    let logoTex = Texture.EMPTY;
    try { logoTex = await Assets.load(this._gameLogoAssetPath()); } catch { /* noop */ }
    const logoTargetH = scaledIcon(96 * 2);
    const logoSlot = new Container();
    logoSlot.eventMode = 'none';
    const logoSprite = new Sprite(logoTex);
    logoSprite.eventMode = 'none';
    if (logoTex && logoTex.width > 0 && logoTex.height > 0) {
      const aspect = logoTex.width / logoTex.height;
      logoSprite.height = logoTargetH;
      logoSprite.width = logoTargetH * aspect;
      if (logoSprite.width > INNER_W) {
        logoSprite.width = INNER_W;
        logoSprite.height = INNER_W / aspect;
      }
      logoSprite.x = (INNER_W - logoSprite.width) / 2;
      logoSprite.y = 0;
      logoSlot.x = 0;
      logoSlot.addChild(logoSprite);
    } else {
      logoSprite.width = Math.min(INNER_W, scaledIcon(160 * 4));
      logoSprite.height = logoTargetH;
      logoSprite.x = (INNER_W - logoSprite.width) / 2;
      logoSprite.y = 0;
      logoSlot.addChild(logoSprite);
    }
    const logoBlockH = logoSprite.height;
    logoSlot.y = cursorY;
    content.addChild(logoSlot);
    registerInfoBlock(logoSlot, scaledIcon(16), { heightFor: () => logoBlockH });
    cursorY += logoBlockH + scaledIcon(16);

    /* ── "Introduction" sub-heading ─────────────────────────────── */
    const introHeading = this._bindLocalizedText(
      new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(14),
          fontWeight: '700',
          fill: T.modalHeading,
          align: 'center',
        },
      }),
      'INFO_INTRO_TITLE',
    );
    introHeading.anchor.set(0.5, 0);
    introHeading.x = INNER_W / 2;
    introHeading.y = cursorY;
    content.addChild(introHeading);
    registerInfoBlock(introHeading, scaledText(22), { centerX: true });
    cursorY += scaledText(22);

    /* ── Intro paragraph ─────────────────────────────────────────── */
    const introBodyWrapW = infoMobile ? INNER_W : Math.min(INNER_W, 410);
    const introBody = makeBodyFromKey('INFO_INTRO_BODY', introBodyWrapW, { align: 'center' });
    introBody.anchor.set(0.5, 0);
    introBody.x = INNER_W / 2;
    introBody.y = cursorY;
    content.addChild(introBody);
    registerInfoBlock(introBody, scaledText(14), { centerX: true });
    cursorY += introBody.height + scaledText(14);

    /* ── RETURN TO PLAYER (top of info tab) ─────────────────────── */
    const rtpHeading = makeHeadingFromKey('INFO_HEADING_RETURN_TO_PLAYER');
    rtpHeading.anchor.set(0.5, 0);
    rtpHeading.x = INNER_W / 2;
    rtpHeading.y = cursorY;
    content.addChild(rtpHeading);
    registerInfoBlock(rtpHeading, scaledText(22), { centerX: true });
    cursorY += scaledText(22);

    const rtpBody = makeBodyFromKey('INFO_RTP_BODY', introBodyWrapW, { align: 'center' });
    rtpBody.anchor.set(0.5, 0);
    rtpBody.x = INNER_W / 2;
    rtpBody.y = cursorY;
    content.addChild(rtpBody);
    registerInfoBlock(rtpBody, scaledText(14), { centerX: true });
    cursorY += rtpBody.height + scaledText(14);

    const introDivider = makeDivider();
    introDivider.y = cursorY;
    content.addChild(introDivider);
    registerInfoBlock(introDivider, scaledText(18), { heightFor: () => 2 });
    cursorY += scaledText(18);

    /* ── WILD section ────────────────────────────────────────────── */
    const wildLeftW = INNER_W * 0.46;
    const wildIconW = scaledIcon(130);
    const wildIconH = scaledIcon(100);
    const wildSection = new Container();
    wildSection.y = cursorY;

    const wildHeading = this._bindLocalizedText(
      new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(18),
          fontWeight: '800',
          fill: INFO_TAB_TEXT_YELLOW,
          letterSpacing: 1.5,
        },
      }),
      'INFO_WILD_HEADING',
    );
    wildHeading.anchor.set(0.5, 0);
    wildHeading.x = wildLeftW / 2;
    wildHeading.y = 0;
    wildSection.addChild(wildHeading);

    const wildBadge = makeSymbolSprite(INFO_WILD_SYMBOL_ID, wildIconW, wildIconH, {
      fallbackLabel: 'WILD',
      fallbackAccent: T.badgeAccent,
    });
    wildBadge.x = (wildLeftW - wildIconW) / 2;
    wildBadge.y = scaledText(30);
    wildSection.addChild(wildBadge);

    /* Right-side description; built as multiple Text + Sprite parts so the inline coin/scatter icons sit on the same baseline as the words. */
    const wildTextStyle = {
      fontFamily: 'Montserrat',
      fontSize: scaledText(13),
      lineHeight: scaledText(20),
      fontWeight: '500',
      fill: INFO_TAB_BODY_FILL,
    };
    const wildRightX = wildLeftW + (infoMobile ? 4 : scaledText(14));
    const wildRightW = INNER_W - wildRightX - 4;
    const wildWrapStyle = {
      ...wildTextStyle,
      wordWrap: true,
      wordWrapWidth: wildRightW,
      breakWords: true,
    };
    const wildText = new Container();
    wildText.x = wildRightX;
    const wildLine1 = this._bindLocalizedText(new Text({ text: '', style: wildWrapStyle }), 'INFO_WILD_LINE1');
    const wildLine2 = this._bindLocalizedText(new Text({ text: '', style: wildWrapStyle }), 'INFO_WILD_LINE2');
    const wildLine3 = this._bindLocalizedText(new Text({ text: '', style: wildWrapStyle }), 'INFO_WILD_LINE3');
    const wildLine4 = this._bindLocalizedText(new Text({ text: '', style: wildWrapStyle }), 'INFO_WILD_LINE4');
    wildLine1.y = 0;
    const wildLineGap = scaledText(4);
    wildLine2.y = wildLine1.height + wildLineGap;
    wildLine3.y = wildLine2.y + wildLine2.height + wildLineGap;
    /* Inline Cleopatra (coin) and pyramides (scatter) icons on the wild description lines. */
    const wildCoinIcon = makeSymbolSprite(INFO_COIN_SYMBOL_ID, scaledIcon(22), scaledIcon(22), {
      fallbackLabel: 'COIN',
      fallbackAccent: 0xf4c850,
    });
    wildCoinIcon.x = wildLine3.width;
    wildCoinIcon.y = wildLine3.y + scaledText(1);
    const wildScatterIcon = makeSymbolSprite(INFO_SCATTER_SYMBOL_ID, scaledIcon(24), scaledIcon(24), {
      fallbackLabel: 'SCATTER',
      fallbackAccent: 0xffd166,
    });
    wildScatterIcon.x = wildLine4.width;
    wildScatterIcon.y = wildLine4.y + scaledText(1);
    wildText.addChild(wildLine1, wildLine2, wildLine3, wildCoinIcon, wildLine4, wildScatterIcon);
    const relayoutWildSection = () => {
      let lineY = 0;
      wildLine1.y = lineY;
      lineY += wildLine1.height + wildLineGap;
      wildLine2.y = lineY;
      lineY += wildLine2.height + wildLineGap;
      wildLine3.y = lineY;
      wildCoinIcon.x = wildLine3.width;
      wildCoinIcon.y = wildLine3.y + scaledText(1);
      lineY += wildLine3.height + wildLineGap;
      wildLine4.y = lineY;
      wildScatterIcon.x = wildLine4.width;
      wildScatterIcon.y = wildLine4.y + scaledText(1);
      lineY += wildLine4.height;
      const textBlockH = lineY;
      const minSectionH = wildIconH + scaledText(36);
      const sectionH = Math.max(minSectionH, textBlockH + scaledText(30));
      wildText.y = Math.max(scaledText(30), (sectionH - textBlockH) / 2 + scaledText(2));
      return sectionH;
    };
    relayoutWildSection();
    wildSection.addChild(wildText);
    this._infoWildRelayout = relayoutWildSection;

    content.addChild(wildSection);
    registerInfoBlock(wildSection, scaledText(18), { heightFor: relayoutWildSection });
    cursorY += relayoutWildSection() + scaledText(18);

    /* ── Two SCATTER columns ─────────────────────────────────────── */
    const scatterColW = INNER_W / 2;
    const scatterIconW = scaledIcon(120);
    const scatterIconH = scaledIcon(110);
    const scatterRow = new Container();
    scatterRow.y = cursorY;

    const buildScatterCol = ({ x, symbolId, fallbackLabel, fallbackAccent, footerLines }) => {
      const col = new Container();
      col.x = x;
      const head = this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: scaledText(16),
            fontWeight: '800',
            fill: INFO_TAB_TEXT_YELLOW,
            letterSpacing: 1.5,
          },
        }),
        'INFO_SCATTER_HEADING',
      );
      head.anchor.set(0.5, 0);
      head.x = scatterColW / 2;
      head.y = 0;
      col.addChild(head);

      const badge = makeSymbolSprite(symbolId, scatterIconW, scatterIconH, {
        fallbackLabel,
        fallbackAccent,
      });
      badge.x = (scatterColW - scatterIconW) / 2;
      badge.y = scaledText(26);
      col.addChild(badge);

      let lineY = scaledText(26) + scatterIconH + scaledText(12);
      footerLines.forEach((ln) => {
        const style = {
          fontFamily: 'Montserrat',
          fontSize: scaledText(12),
          fontWeight: ln.muted ? '500' : '700',
          fill: ln.color,
          align: 'center',
          wordWrap: true,
          wordWrapWidth: scatterColW - 16,
          lineHeight: scaledText(17),
          breakWords: true,
        };
        let t;
        if (ln.payoutSpec) {
          t = makePayoutLine(ln.payoutSpec);
          t.pivot.set(t.width / 2, 0);
        } else if (ln.key) {
          t = this._bindLocalizedText(new Text({ text: '', style }), ln.key);
          t.anchor.set(0.5, 0);
        } else {
          return;
        }
        t.x = scatterColW / 2;
        t.y = lineY;
        col.addChild(t);
        lineY += t.height + scaledText(2);
      });
      col.__bottomY = lineY;
      return col;
    };

    const scLeft = buildScatterCol({
      x: 0,
      symbolId: INFO_COIN_SYMBOL_ID,
      fallbackLabel: 'COIN',
      fallbackAccent: 0xf4c850,
      footerLines: getScatterPayoutSpecs(INFO_COIN_SYMBOL_ID).map((payoutSpec) => ({ payoutSpec })),
    });
    const scRight = buildScatterCol({
      x: scatterColW,
      symbolId: INFO_SCATTER_SYMBOL_ID,
      fallbackLabel: 'SCATTER',
      fallbackAccent: 0xffd166,
      footerLines: [
        { key: 'INFO_SCATTER_STAR_INTRO', color: INFO_TAB_BODY_MUTED_FILL, muted: true },
        ...getScatterPayoutSpecs(INFO_SCATTER_SYMBOL_ID).map((payoutSpec) => ({ payoutSpec })),
      ],
    });
    scatterRow.addChild(scLeft, scRight);
    const relayoutScatterRow = () => {
      const relayoutCol = (col) => {
        let lineY = scaledText(26) + scatterIconH + scaledText(12);
        for (const child of col.children) {
          if (child === col.children[0] || child === col.children[1]) continue;
          child.y = lineY;
          lineY += child.height + scaledText(2);
        }
        return lineY;
      };
      return Math.max(relayoutCol(scLeft), relayoutCol(scRight));
    };
    content.addChild(scatterRow);
    registerInfoBlock(scatterRow, scaledText(18), { heightFor: relayoutScatterRow });
    const scatterRowH = relayoutScatterRow();
    cursorY += scatterRowH + scaledText(18);

    const scatterDivider = makeDivider();
    scatterDivider.y = cursorY;
    content.addChild(scatterDivider);
    registerInfoBlock(scatterDivider, scaledText(18), { heightFor: () => 2 });
    cursorY += scaledText(18);

    /* ── Symbol paytable (3 columns × N rows); odds from hub when available ── */
    const paytable = getInfoPaytableSymbolIds().map((symbolId) => ({
      symbolId,
      lines: getPaylinePayoutSpecs(symbolId),
    }));
    const ptCols = 3;
    const ptColW = INNER_W / ptCols;
    const ptIconW = scaledIcon(80);
    const ptIconH = scaledIcon(80);
    const ptRowMaxLines = Math.max(1, ...paytable.map((s) => s.lines.length));
    const ptRowH = ptIconH + scaledText(12) + ptRowMaxLines * scaledText(17) + scaledText(10);
    const paytableGroup = new Container();
    paytableGroup.y = cursorY;

    paytable.forEach((sym, i) => {
      const r = Math.floor(i / ptCols);
      const cIdx = i % ptCols;
      const cell = new Container();
      cell.x = cIdx * ptColW;
      cell.y = r * ptRowH;

      const boxW = ptIconW;
      const boxH = ptIconH;
      const iconsLeftX = (ptColW - boxW) / 2;
      const sprite = makeSymbolSprite(sym.symbolId, boxW, boxH);
      sprite.x = iconsLeftX;
      sprite.y = 0;
      cell.addChild(sprite);

      sym.lines.forEach((payoutSpec, li) => {
        const row = makePayoutLine(payoutSpec);
        row.__infoCenterX = ptColW / 2;
        row.pivot.set(row.width / 2, 0);
        row.x = ptColW / 2;
        row.y = ptIconH + scaledText(8) + li * scaledText(17);
        cell.addChild(row);
      });
      paytableGroup.addChild(cell);
    });
    content.addChild(paytableGroup);
    registerInfoBlock(paytableGroup, scaledText(16), {
      heightFor: () => Math.ceil(paytable.length / ptCols) * ptRowH,
    });
    cursorY += Math.ceil(paytable.length / ptCols) * ptRowH + scaledText(16);

    /* ── PAYLINES AND RULES ──────────────────────────────────────── */
    const paylinesHeading = makeHeadingFromKey('INFO_HEADING_PAYLINES_AND_RULES');
    paylinesHeading.anchor.set(0.5, 0);
    paylinesHeading.x = INNER_W / 2;
    paylinesHeading.y = cursorY;
    content.addChild(paylinesHeading);
    registerInfoBlock(paylinesHeading, scaledText(30), { centerX: true });
    cursorY += scaledText(30);

    /* Payline map from live config (10-line 5×3, 40-line 5×4, or 100-line 5×4). */
    const paylinesDiagramW = INNER_W * (infoMobile ? 0.96 : 0.92);
    const paylineCount = gameConfig.paylines.length;
    const paylinesDiagram = createInfoPaylineDiagram({
      width: paylinesDiagramW,
      columns: paylineCount >= 100 ? (infoMobile ? 5 : 10) : infoMobile ? 4 : 5,
      mobile: infoMobile,
    });
    paylinesDiagram.x = (INNER_W - paylinesDiagramW) / 2;
    paylinesDiagram.y = cursorY;
    content.addChild(paylinesDiagram);
    const paylinesImgH = Number(paylinesDiagram.__infoPaylineHeight) || 0;
    registerInfoBlock(paylinesDiagram, scaledText(18), {
      heightFor: () => paylinesImgH,
    });
    cursorY += paylinesImgH + scaledText(18);

    const rulesPara1 = makeBodyFromKey('INFO_RULES_PARA1', INNER_W);
    rulesPara1.x = 0;
    rulesPara1.y = cursorY;
    content.addChild(rulesPara1);
    registerInfoBlock(rulesPara1, scaledText(12));
    cursorY += rulesPara1.height + scaledText(12);

    const rulesPara2 = makeBodyFromKey('INFO_RULES_PARA2', INNER_W);
    rulesPara2.x = 0;
    rulesPara2.y = cursorY;
    content.addChild(rulesPara2);
    registerInfoBlock(rulesPara2, scaledText(12));
    cursorY += rulesPara2.height + scaledText(12);

    const rulesPara3 = makeBodyFromKey('INFO_RULES_PARA3', INNER_W);
    rulesPara3.x = 0;
    rulesPara3.y = cursorY;
    content.addChild(rulesPara3);
    registerInfoBlock(rulesPara3, scaledText(18));
    cursorY += rulesPara3.height + scaledText(18);

    const rulesDivider = makeDivider();
    rulesDivider.y = cursorY;
    content.addChild(rulesDivider);
    registerInfoBlock(rulesDivider, scaledText(18), { heightFor: () => 2 });
    cursorY += scaledText(18);

    /* ── HOW TO BET ──────────────────────────────────────────────── */
    const howToBetHeading = makeHeadingFromKey('INFO_HEADING_HOW_TO_BET');
    howToBetHeading.anchor.set(0.5, 0);
    howToBetHeading.x = INNER_W / 2;
    howToBetHeading.y = cursorY;
    content.addChild(howToBetHeading);
    registerInfoBlock(howToBetHeading, scaledText(26), { centerX: true });
    cursorY += scaledText(26);

    const bullet1 = makeBodyFromKey('INFO_HOW_TO_BET_BULLET1', INNER_W);
    bullet1.x = 0;
    bullet1.y = cursorY;
    content.addChild(bullet1);
    registerInfoBlock(bullet1, scaledText(10));
    cursorY += bullet1.height + scaledText(10);

    const bullet2 = makeBodyFromKey('INFO_HOW_TO_BET_BULLET2', INNER_W);
    bullet2.x = 0;
    bullet2.y = cursorY;
    content.addChild(bullet2);
    registerInfoBlock(bullet2, scaledText(18));
    cursorY += bullet2.height + scaledText(18);

    /* ── GAME CONTROLS ───────────────────────────────────────────── */
    const gameControlsHeading = makeHeadingFromKey('INFO_HEADING_GAME_CONTROLS');
    gameControlsHeading.anchor.set(0.5, 0);
    gameControlsHeading.x = INNER_W / 2;
    gameControlsHeading.y = cursorY;
    content.addChild(gameControlsHeading);
    registerInfoBlock(gameControlsHeading, scaledText(26), { centerX: true });
    cursorY += scaledText(26);

    const fieldsIntro = makeBodyFromKey('INFO_FIELDS_INTRO', INNER_W);
    fieldsIntro.x = 0;
    fieldsIntro.y = cursorY;
    content.addChild(fieldsIntro);
    registerInfoBlock(fieldsIntro, scaledText(10));
    cursorY += fieldsIntro.height + scaledText(10);

    const fieldBalance = makeBodyFromKey('INFO_FIELD_BALANCE', INNER_W);
    fieldBalance.x = 0;
    fieldBalance.y = cursorY;
    content.addChild(fieldBalance);
    registerInfoBlock(fieldBalance, scaledText(10));
    cursorY += fieldBalance.height + scaledText(10);

    const fieldWin = makeBodyFromKey('INFO_FIELD_WIN', INNER_W);
    fieldWin.x = 0;
    fieldWin.y = cursorY;
    content.addChild(fieldWin);
    registerInfoBlock(fieldWin, scaledText(10));
    cursorY += fieldWin.height + scaledText(10);

    const fieldWinLine = makeBodyFromKey('INFO_FIELD_WINLINE', INNER_W);
    fieldWinLine.x = 0;
    fieldWinLine.y = cursorY;
    content.addChild(fieldWinLine);
    registerInfoBlock(fieldWinLine, scaledText(14));
    cursorY += fieldWinLine.height + scaledText(14);

    const buttonsIntro = makeBodyFromKey('INFO_BUTTONS_INTRO', INNER_W);
    buttonsIntro.x = 0;
    buttonsIntro.y = cursorY;
    content.addChild(buttonsIntro);
    registerInfoBlock(buttonsIntro, scaledText(14));
    cursorY += buttonsIntro.height + scaledText(14);

    /* ── Buttons table (BUTTON | NAME | DESCRIPTION) ─────────────── */
    /* Preload every real button asset used by this table; loaded in parallel. */
    const buttonAssetPaths = {
      bet: '/assets/bet.png',
      stop: '/assets/stop.png',
      start: '/assets/start.png',
      collectWin: '/assets/collect_win.png',
      gamble: '/assets/gamble.svg',
      betOptions: '/assets/bet.png',
      quickBetNormal: '/assets/quick_bet.png',
      quickBetSelected: '/assets/selected_quick_bet.png',
      autoplay: '/assets/autoplay.png',
      autoplaySettings: '/assets/settings/autoplay.svg',
      settings: '/assets/settings/settings.svg',
      sound: '/assets/sound_on.png',
      fullscreen: '/assets/full_screen.png',
      menu: '/assets/menu.png',
      close: '/assets/close_button.png',
      betSpeed: '/assets/bet_speed.png',
      betSpeedActiveArrow: '/assets/bet_speed_active_arrow.png',
      betSpeedInactiveArrow: '/assets/bet_speed_inactive_arrow.png',
    };
    const buttonTex = {};
    await Promise.all(
      Object.entries(buttonAssetPaths).map(async ([k, p]) => {
        try { buttonTex[k] = await Assets.load(p); } catch { buttonTex[k] = dummyIconTex; }
      }),
    );

    /** Build a sprite icon at a target box size, preserving aspect ratio. */
    const makeIconSprite = (texKey, boxW, boxH) => {
      const normTex = buttonTex.quickBetNormal ?? dummyIconTex;
      const selTex = buttonTex.quickBetSelected ?? normTex;
      const sampleBet =
        Number(this.slotMachine?.hud?.betValue) ||
        Number(this.slotMachine?.hud?.quickBets?.[0]) ||
        gameConfig.minBet ||
        1;
      const makeQuickBetChipIcon = ({ bottomText, selected }) => {
        const chip = createQuickBetChipPixi({
          betValue: sampleBet,
          selected,
          textureNormal: normTex,
          textureSelected: selTex,
          cellW: boxW,
          cellH: boxH,
          bottomText,
          currencyLabel: this._currencyLabel(),
          currencyPrecision: this._currencyPrecision,
          fontFamily: '"Montserrat", "Arial", sans-serif',
        });
        chip.eventMode = 'none';
        chip.__width = boxW;
        chip.__height = boxH;
        return chip;
      };
      if (texKey === 'quickBetBet' && normTex?.width > 0) {
        return makeQuickBetChipIcon({
          bottomText: this._tu('BET'),
          selected: false,
        });
      }
      if (texKey === 'quickBetStopAll' && normTex?.width > 0) {
        return makeQuickBetChipIcon({
          bottomText: this._tu('STOP'),
          selected: false,
        });
      }
      if (texKey === 'quickBetValue' && normTex?.width > 0) {
        return makeQuickBetChipIcon({
          bottomText: '',
          selected: false,
        });
      }

      const bgTex = buttonTex.betSpeed ?? dummyIconTex;
      const activeArrowTex = buttonTex.betSpeedActiveArrow;
      const inactiveArrowTex = buttonTex.betSpeedInactiveArrow;
      if (
        texKey === 'betSpeed'
        && bgTex?.width > 0
        && activeArrowTex?.width > 0
        && inactiveArrowTex?.width > 0
      ) {
        const wrap = new Container();
        wrap.eventMode = 'none';

        const bg = new Sprite(bgTex);
        const bgScale = Math.min(boxW / bgTex.width, boxH / bgTex.height);
        bg.width = bgTex.width * bgScale;
        bg.height = bgTex.height * bgScale;
        bg.x = (boxW - bg.width) / 2;
        bg.y = (boxH - bg.height) / 2;
        wrap.addChild(bg);

        const arrowW = Math.max(4, bg.width * (8 / 82));
        const gapBetweenArrows = Math.max(2, bg.width * (4 / 82));
        const totalArrowRowW = 3 * arrowW + 2 * gapBetweenArrows;
        const arrowRow = new Container();
        arrowRow.x = boxW / 2;
        arrowRow.y = bg.y + bg.height * 0.48;
        for (let i = 0; i < 3; i++) {
          const arrow = new Sprite(activeArrowTex);
          arrow.anchor.set(0.5);
          arrow.width = arrowW;
          arrow.height = (activeArrowTex.height / activeArrowTex.width) * arrowW;
          arrow.x = -totalArrowRowW / 2 + arrowW / 2 + i * (arrowW + gapBetweenArrows);
          arrowRow.addChild(arrow);
        }
        wrap.addChild(arrowRow);

        wrap.__width = boxW;
        wrap.__height = boxH;
        return wrap;
      }

      const tex = buttonTex[texKey] ?? dummyIconTex;
      const s = new Sprite(tex);
      if (tex && tex.width > 0 && tex.height > 0) {
        const k = Math.min(boxW / tex.width, boxH / tex.height);
        s.width = tex.width * k;
        s.height = tex.height * k;
      } else {
        s.width = boxW;
        s.height = boxH;
      }
      s.eventMode = 'none';
      s.__width = s.width;
      s.__height = s.height;
      return s;
    };

    /* Slightly narrower than full content width on phone; centred in the scroll column. */
    const TABLE_W = infoMobile ? Math.round(INNER_W * 0.93) : INNER_W;
    const tableX = (INNER_W - TABLE_W) / 2;

    /* Column widths: Figma proportions (81 / 81 / 408 of 568), then nudge NAME from DESC if the name column is too narrow to wrap labels. */
    let colBtn = Math.round((81 / 568) * TABLE_W);
    let colName = Math.round((81 / 568) * TABLE_W);
    let colDesc = TABLE_W - colBtn - colName;
    const nameMin = Math.min(56, Math.max(28, Math.floor(TABLE_W * 0.16)));
    if (colName < nameMin && colDesc > nameMin + Math.floor(TABLE_W * 0.28)) {
      const delta = nameMin - colName;
      colName = nameMin;
      colDesc -= delta;
    }
    const tableCols = { btn: colBtn, name: colName, desc: colDesc };
    const colXBtn = 0;
    const colXName = tableCols.btn;
    const colXDesc = tableCols.btn + tableCols.name;

    const tableHeaderH = scaledText(infoMobile ? 32 : 36);
    const tableBorderColor = T.modalStroke;
    const tableBorderAlpha = 0.85;
    const tableRadius = 10;

    /* Build the table inside its own container so we can clip and outline it cleanly. */
    const tableRoot = new Container();
    tableRoot.x = tableX;
    tableRoot.y = cursorY;
    tableRoot.eventMode = 'passive';
    const tableContent = new Container();
    tableContent.eventMode = 'passive';
    tableRoot.addChild(tableContent);
    content.addChild(tableRoot);

    let tableY = 0;

    /** Header row. */
    const headerEl = new Container();
    headerEl.y = tableY;
    const headerBg = new Graphics();
    headerBg.rect(0, 0, TABLE_W, tableHeaderH).fill({ color: 0x000000, alpha: 0 });
    headerBg.eventMode = 'none';
    headerEl.addChild(headerBg);
    const headerStyle = {
      fontFamily: 'Montserrat',
      fontSize: scaledText(infoMobile ? 10 : 12),
      fontWeight: '700',
      fill: T.modalHeading,
      letterSpacing: 0.4,
    };
    const hdrBtn = this._bindLocalizedText(new Text({ text: '', style: headerStyle }), 'INFO_TABLE_HDR_BUTTON');
    hdrBtn.anchor.set(0.5, 0.5);
    hdrBtn.x = colXBtn + tableCols.btn / 2;
    hdrBtn.y = tableHeaderH / 2;
    const hdrName = this._bindLocalizedText(new Text({ text: '', style: headerStyle }), 'INFO_TABLE_HDR_NAME');
    hdrName.anchor.set(0.5, 0.5);
    hdrName.x = colXName + tableCols.name / 2;
    hdrName.y = tableHeaderH / 2;
    const hdrDesc = this._bindLocalizedText(new Text({ text: '', style: headerStyle }), 'INFO_TABLE_HDR_DESC');
    hdrDesc.anchor.set(0.5, 0.5);
    hdrDesc.x = colXDesc + tableCols.desc / 2;
    hdrDesc.y = tableHeaderH / 2;
    headerEl.addChild(hdrBtn, hdrName, hdrDesc);
    tableContent.addChild(headerEl);
    tableY += tableHeaderH;

    /** Data rows.
     * `icons` is a list of texture keys; multiple icons stack vertically inside the BUTTON cell.
     * `iconBox` is the target box for each icon ([w, h]).
     * `minH` mirrors the row heights from the Figma spec (the row will grow if text needs more).
     */
    const buttonRowsBase = [
      { icons: ['quickBetBet'], iconBox: [56, 53], nameKey: 'INFO_CTRL_BET_NAME', bodyKey: 'INFO_CTRL_BET_BODY', minH: 66 },
      { icons: ['quickBetStopAll'], iconBox: [56, 53], nameKey: 'INFO_CTRL_STOP_ALL_NAME', bodyKey: 'INFO_CTRL_STOP_ALL_BODY', minH: 86 },
      { icons: ['quickBetStopAll', 'collectWin'], iconBox: [56, 53], nameKey: 'INFO_CTRL_COLLECT_NAME', bodyKey: 'INFO_CTRL_COLLECT_BODY', minH: 173 },
      { icons: ['start'], iconBox: [56, 56], nameKey: 'INFO_CTRL_START_NAME', bodyKey: 'INFO_CTRL_START_BODY', minH: 86 },
      { icons: ['collectWin'], iconBox: [56, 56], nameKey: 'INFO_CTRL_COLLECT_WIN_NAME', bodyKey: 'INFO_CTRL_COLLECT_WIN_BODY', minH: 86 },
      { icons: ['betOptions'], iconBox: [54, 54], nameKey: 'INFO_CTRL_BET_OPTIONS_NAME', bodyKey: 'INFO_CTRL_BET_OPTIONS_BODY', minH: 86 },
      { icons: ['autoplaySettings'], iconBox: [54, 54], nameKey: 'INFO_CTRL_AUTOPLAY_SETTINGS_NAME', bodyKey: 'INFO_CTRL_AUTOPLAY_SETTINGS_BODY', minH: 86 },
      { icons: ['settings'], iconBox: [54, 54], nameKey: 'INFO_CTRL_GAME_SETTINGS_NAME', bodyKey: 'INFO_CTRL_GAME_SETTINGS_BODY', minH: 86, iconAlpha: 0.6 },
      { icons: ['quickBetValue'], iconBox: [56, 53], nameKey: 'INFO_CTRL_BET_VALUE_NAME', bodyKey: 'INFO_CTRL_BET_VALUE_BODY', minH: 86 },
      { icons: ['autoplay'], iconBox: [54, 54], nameKey: 'INFO_CTRL_AUTOPLAY_MODE_NAME', bodyKey: 'INFO_CTRL_AUTOPLAY_MODE_BODY', minH: 86 },
      { icons: ['stop'], iconBox: [54, 54], nameKey: 'INFO_CTRL_STOP_AUTO_NAME', bodyKey: 'INFO_CTRL_STOP_AUTO_BODY', minH: 86 },
      { icons: ['betSpeed'], iconBox: [56, 32], nameKey: 'INFO_CTRL_FAST_PLAY_NAME', bodyKey: 'INFO_CTRL_FAST_PLAY_BODY', minH: 76 },
      { icons: ['sound'], iconBox: [34, 34], nameKey: 'INFO_CTRL_SOUND_CONTROL_NAME', bodyKey: 'INFO_CTRL_SOUND_CONTROL_BODY', minH: 76 },
      { icons: ['fullscreen'], iconBox: [34, 34], nameKey: 'INFO_CTRL_SCREEN_SIZE_NAME', bodyKey: 'INFO_CTRL_SCREEN_SIZE_BODY', minH: 76 },
      { icons: ['menu'], iconBox: [34, 34], nameKey: 'INFO_CTRL_MENU_NAME', bodyKey: 'INFO_CTRL_MENU_BODY', minH: 76 },
      { icons: ['close'], iconBox: [34, 34], nameKey: 'INFO_CTRL_CLOSE_NAME', bodyKey: 'INFO_CTRL_CLOSE_BODY', minH: 76 },
    ];
    const buttonRows = buttonRowsBase.map((row) => ({
      ...row,
      iconBox: [scaledIcon(row.iconBox[0]), scaledIcon(row.iconBox[1])],
      minH: scaledText(row.minH ?? 70),
    }));

    /* Row content insets — must never exceed the column width or wordWrapWidth becomes larger than the cell and text draws outside the grid. */
    const ROW_PAD_X = scaledText(10);
    const ROW_PAD_Y = scaledText(10);
    const hPadForCol = (colW) =>
      Math.min(ROW_PAD_X, Math.max(2, Math.floor(colW * 0.12)));
    const padDesc = hPadForCol(tableCols.desc);
    const padName = hPadForCol(tableCols.name);
    const descInterior = Math.max(1, tableCols.desc - padDesc * 2);
    const nameInterior = Math.max(1, tableCols.name - padName * 2);
    /** Shrink NAME labels when the column is tight; mobile uses a smaller base so copy fits the column. */
    const nameFontRef = infoMobile ? 8 : 10;
    const nameBaseFontSize = nameInterior < 40
      ? Math.max(7, Math.floor((nameInterior / 40) * nameFontRef))
      : nameFontRef;
    const nameFontSize = scaledText(nameBaseFontSize);
    const nameLineHeight = Math.round(nameFontSize * 1.3);
    /** Per-row final height; used to draw the internal grid lines below. */
    const rowHeights = [];
    /** @type {Array<{ rowEl: import('pixi.js').Container, rowBg: import('pixi.js').Graphics, nameText: import('pixi.js').Text, desc: import('pixi.js').Text, iconSprites: import('pixi.js').Sprite[], minH?: number, nameKey: string, bodyKey: string }>} */
    const layoutRows = [];
    const ICON_STACK_GAP = scaledText(8);

    buttonRows.forEach((row) => {
      const nameText = new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: nameFontSize,
          lineHeight: nameLineHeight,
          fontWeight: '600',
          fill: INFO_TAB_BODY_FILL,
          align: 'center',
          wordWrap: false,
        },
      });
      applyInfoTableCellText(nameText, this._t(row.nameKey), nameInterior, {
        fontFamily: 'Montserrat',
        fontSize: nameFontSize,
        fontWeight: '600',
      });

      const desc = new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(10),
          lineHeight: scaledText(12),
          fontWeight: '600',
          fill: INFO_TAB_BODY_FILL,
          align: 'left',
          wordWrap: false,
        },
      });
      applyInfoTableCellText(desc, this._t(row.bodyKey), descInterior, {
        fontFamily: 'Montserrat',
        fontSize: scaledText(10),
        fontWeight: '600',
      });

      const iconSprites = row.icons.map((k) => makeIconSprite(k, row.iconBox[0], row.iconBox[1]));
      if (typeof row.iconAlpha === 'number') {
        iconSprites.forEach((s) => { s.alpha = row.iconAlpha; });
      }
      const iconStackH = iconSprites.reduce(
        (s, sp, idx) => s + (sp.__height ?? 0) + (idx > 0 ? ICON_STACK_GAP : 0),
        0,
      );

      /* Row must fit wrapped NAME and DESCRIPTION (minH is only a design minimum). */
      const textBlockH = Math.max(
        nameText.height + ROW_PAD_Y * 2,
        desc.height + ROW_PAD_Y * 2,
      );
      const cellH = Math.max(row.minH ?? scaledText(70), textBlockH, iconStackH + scaledText(20));
      rowHeights.push(cellH);

      const rowEl = new Container();
      rowEl.y = tableY;
      rowEl.eventMode = 'passive';

      /* No fill — keep the background transparent so the panel blur shows through. */
      const rowBg = new Graphics();
      rowBg.rect(0, 0, TABLE_W, cellH).fill({ color: 0x000000, alpha: 0 });
      rowBg.eventMode = 'none';
      rowEl.addChild(rowBg);

      /* Stack icons vertically and centre the stack inside the BUTTON cell. */
      let stackY = (cellH - iconStackH) / 2;
      iconSprites.forEach((sp, idx) => {
        sp.x = colXBtn + (tableCols.btn - (sp.__width ?? 0)) / 2;
        sp.y = stackY;
        rowEl.addChild(sp);
        stackY += (sp.__height ?? 0) + (idx < iconSprites.length - 1 ? ICON_STACK_GAP : 0);
      });

      /* Name column — wrapped; row height already includes full bounds. */
      nameText.anchor.set(0.5, 0.5);
      nameText.x = colXName + tableCols.name / 2;
      nameText.y = cellH / 2;
      rowEl.addChild(nameText);

      /* Description column — wrapped to new lines within the column width (wordWrapWidth === interior width). */
      desc.anchor.set(0, 0);
      desc.x = colXDesc + padDesc;
      desc.y = Math.max(0, (cellH - desc.height) / 2);
      rowEl.addChild(desc);

      tableContent.addChild(rowEl);
      tableY += cellH;

      layoutRows.push({
        rowEl,
        rowBg,
        nameText,
        desc,
        iconSprites,
        minH: row.minH,
        nameKey: row.nameKey,
        bodyKey: row.bodyKey,
      });
    });

    /* Total table height; used for outer border + internal divider lines. */
    const tableTotalH = tableY;

    /* Internal grid: vertical column separators and horizontal row separators.
       Drawn after the rows so they sit on top. */
    const tableGrid = new Graphics();
    /* Vertical column separators (full table height). */
    tableGrid.moveTo(colXName, 0).lineTo(colXName, tableTotalH);
    tableGrid.moveTo(colXDesc, 0).lineTo(colXDesc, tableTotalH);
    /* Horizontal separators: header bottom + each row bottom except the last. */
    let yWalker = tableHeaderH;
    tableGrid.moveTo(0, yWalker).lineTo(TABLE_W, yWalker);
    rowHeights.forEach((h, idx) => {
      yWalker += h;
      if (idx < rowHeights.length - 1) {
        tableGrid.moveTo(0, yWalker).lineTo(TABLE_W, yWalker);
      }
    });
    tableGrid.stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
    tableGrid.eventMode = 'none';
    tableContent.addChild(tableGrid);

    /* Outer rounded border. */
    const outerBorder = new Graphics();
    outerBorder.roundRect(0.5, 0.5, TABLE_W - 1, tableTotalH - 1, tableRadius)
      .stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
    outerBorder.eventMode = 'none';
    tableContent.addChild(outerBorder);

    const relayoutInfoButtonTable = () => {
      let ty = tableHeaderH;
      const newRowHeights = [];
      layoutRows.forEach((r) => {
        applyInfoTableCellText(r.nameText, this._t(r.nameKey), nameInterior, {
          fontFamily: 'Montserrat',
          fontSize: nameFontSize,
          fontWeight: '600',
        });
        applyInfoTableCellText(r.desc, this._t(r.bodyKey), descInterior, {
          fontFamily: 'Montserrat',
          fontSize: scaledText(10),
          fontWeight: '600',
        });
        const stackH = r.iconSprites.reduce(
          (s, sp, idx) => s + (sp.__height ?? 0) + (idx > 0 ? ICON_STACK_GAP : 0),
          0,
        );
        const textBlockH = Math.max(
          r.nameText.height + ROW_PAD_Y * 2,
          r.desc.height + ROW_PAD_Y * 2,
        );
        const cellH = Math.max(r.minH ?? scaledText(70), textBlockH, stackH + scaledText(20));
        newRowHeights.push(cellH);
        r.rowEl.y = ty;
        r.rowBg.clear().rect(0, 0, TABLE_W, cellH).fill({ color: 0x000000, alpha: 0 });
        let stackY = (cellH - stackH) / 2;
        r.iconSprites.forEach((sp, idx) => {
          sp.x = colXBtn + (tableCols.btn - (sp.__width ?? 0)) / 2;
          sp.y = stackY;
          stackY += (sp.__height ?? 0) + (idx < r.iconSprites.length - 1 ? ICON_STACK_GAP : 0);
        });
        r.nameText.y = cellH / 2;
        r.desc.y = Math.max(0, (cellH - r.desc.height) / 2);
        ty += cellH;
      });
      const newTotal = ty;
      this._infoLastTableTotalH = newTotal;
      tableGrid.clear();
      tableGrid.moveTo(colXName, 0).lineTo(colXName, newTotal);
      tableGrid.moveTo(colXDesc, 0).lineTo(colXDesc, newTotal);
      let yWalker2 = tableHeaderH;
      tableGrid.moveTo(0, yWalker2).lineTo(TABLE_W, yWalker2);
      newRowHeights.forEach((h, idx) => {
        yWalker2 += h;
        if (idx < newRowHeights.length - 1) {
          tableGrid.moveTo(0, yWalker2).lineTo(TABLE_W, yWalker2);
        }
      });
      tableGrid.stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
      outerBorder.clear();
      outerBorder.roundRect(0.5, 0.5, TABLE_W - 1, newTotal - 1, tableRadius)
        .stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
    };

    this._infoLastTableTotalH = tableTotalH;
    this._infoButtonTableRelayout = relayoutInfoButtonTable;
    registerInfoBlock(tableRoot, 16, { heightFor: () => this._infoLastTableTotalH ?? tableTotalH });

    cursorY += tableTotalH + 16;

    /* ── Closing paragraphs after the buttons table ──────────────── */
    const tableFooter1 = makeBodyFromKey('INFO_TABLE_FOOTER_CLOCK', INNER_W);
    tableFooter1.x = 0;
    tableFooter1.y = cursorY;
    content.addChild(tableFooter1);
    registerInfoBlock(tableFooter1, 12);
    cursorY += tableFooter1.height + 12;

    const tableFooter2 = makeBodyFromKey('INFO_TABLE_FOOTER_SPACE_KEY', INNER_W);
    tableFooter2.x = 0;
    tableFooter2.y = cursorY;
    content.addChild(tableFooter2);
    registerInfoBlock(tableFooter2, 18);
    cursorY += tableFooter2.height + 18;

    const postTableDivider = makeDivider();
    postTableDivider.y = cursorY;
    content.addChild(postTableDivider);
    registerInfoBlock(postTableDivider, 18, { heightFor: () => 2 });
    cursorY += 18;

    /* ── RULES ───────────────────────────────────────────────────── */
    const rulesHeading = makeHeadingFromKey('INFO_HEADING_RULES');
    rulesHeading.anchor.set(0.5, 0);
    rulesHeading.x = INNER_W / 2;
    rulesHeading.y = cursorY;
    content.addChild(rulesHeading);
    registerInfoBlock(rulesHeading, scaledText(26), { centerX: true });
    cursorY += scaledText(26);

    const ruleBulletSpecs = [
      { key: 'INFO_RULE_BULLET_1' },
      { key: 'INFO_RULE_BULLET_2' },
      { key: 'INFO_RULE_BULLET_3' },
      { key: this._buyBonusEnabled ? 'INFO_RULE_BULLET_SPACE_BUY_BONUS' : 'INFO_RULE_BULLET_SPACE' },
    ];
    if (this._buyBonusEnabled) ruleBulletSpecs.push({ key: 'INFO_RULE_BULLET_BUY_BONUS' });
    ruleBulletSpecs.forEach((spec) => {
      const t = makeBodyFromKey(spec.key, INNER_W, { replace: spec.replace });
      t.x = 0;
      t.y = cursorY;
      content.addChild(t);
      registerInfoBlock(t, scaledText(10));
      cursorY += t.height + scaledText(10);
    });

    const minMaxLine = makeRuleLimitLine('INFO_RULE_BULLET_MINMAX', () => ({
      min: this._formatCurrencyAmount(gameConfig.minBet),
      max: this._formatCurrencyAmount(gameConfig.maxBet),
    }));
    minMaxLine.x = 0;
    minMaxLine.y = cursorY;
    content.addChild(minMaxLine);
    registerInfoBlock(minMaxLine, scaledText(17) + scaledText(10), {
      heightFor: () => Math.max(minMaxLine.height, scaledText(17)),
    });
    cursorY += scaledText(17) + scaledText(10);

    const maxWinLine = makeRuleLimitLine('INFO_RULE_BULLET_MAXWIN', () => {
      const maxOdd = Number(gameConfig.maxOdd);
      return {
        maxWin:
          Number.isFinite(maxOdd) && maxOdd > 0 ? this._formatMaxWinMultiplier(maxOdd) : '',
      };
    });
    maxWinLine.x = 0;
    maxWinLine.y = cursorY;
    const maxOddAtBuild = Number(gameConfig.maxOdd);
    maxWinLine.visible = Number.isFinite(maxOddAtBuild) && maxOddAtBuild > 0;
    this._infoRuleMaxWinLines.push(maxWinLine);
    content.addChild(maxWinLine);
    registerInfoBlock(maxWinLine, 8, {
      heightFor: () => (maxWinLine.visible ? Math.max(maxWinLine.height, scaledText(17)) : 0),
    });
    cursorY += (maxWinLine.visible ? scaledText(17) + scaledText(10) : 0) + 8;

    /* ── GAME INTERRUPTIONS ──────────────────────────────────────── */
    const interruptionsHeading = makeHeadingFromKey('INFO_HEADING_GAME_INTERRUPTIONS');
    interruptionsHeading.anchor.set(0.5, 0);
    interruptionsHeading.x = INNER_W / 2;
    interruptionsHeading.y = cursorY;
    content.addChild(interruptionsHeading);
    registerInfoBlock(interruptionsHeading, 22, { centerX: true });
    cursorY += 22;

    const fullRecoveryHeading = makeHeadingFromKey('INFO_HEADING_FULL_GAME_RECOVERY');
    fullRecoveryHeading.anchor.set(0.5, 0);
    fullRecoveryHeading.x = INNER_W / 2;
    fullRecoveryHeading.y = cursorY;
    content.addChild(fullRecoveryHeading);
    registerInfoBlock(fullRecoveryHeading, 24, { centerX: true });
    cursorY += 24;

    const fullRecoveryBody = makeBodyFromKey('INFO_FULL_RECOVERY_BODY', INNER_W);
    fullRecoveryBody.x = 0;
    fullRecoveryBody.y = cursorY;
    content.addChild(fullRecoveryBody);
    registerInfoBlock(fullRecoveryBody, 18);
    cursorY += fullRecoveryBody.height + 18;

    const cancellationHeading = makeHeadingFromKey('INFO_HEADING_CANCELLATION');
    cancellationHeading.anchor.set(0.5, 0);
    cancellationHeading.x = INNER_W / 2;
    cancellationHeading.y = cursorY;
    content.addChild(cancellationHeading);
    registerInfoBlock(cancellationHeading, 24, { centerX: true });
    cursorY += 24;

    const cancellationBody = makeBodyFromKey('INFO_CANCELLATION_BODY', INNER_W);
    cancellationBody.x = 0;
    cancellationBody.y = cursorY;
    content.addChild(cancellationBody);
    registerInfoBlock(cancellationBody, 22);
    cursorY += cancellationBody.height + 22;

    /* ── RESPONSIBLE GAMING ──────────────────────────────────────── */
    const responsibleHeading = makeHeadingFromKey('INFO_HEADING_RESPONSIBLE_GAMING');
    responsibleHeading.anchor.set(0.5, 0);
    responsibleHeading.x = INNER_W / 2;
    responsibleHeading.y = cursorY;
    content.addChild(responsibleHeading);
    registerInfoBlock(responsibleHeading, 22, { centerX: true });
    cursorY += 22;

    const autoplayUseHeading = makeHeadingFromKey('INFO_HEADING_HOW_TO_USE_AUTOPLAY');
    autoplayUseHeading.anchor.set(0.5, 0);
    autoplayUseHeading.x = INNER_W / 2;
    autoplayUseHeading.y = cursorY;
    content.addChild(autoplayUseHeading);
    registerInfoBlock(autoplayUseHeading, 24, { centerX: true });
    cursorY += 24;

    const autoplayUseBody = makeBodyFromKey('INFO_AUTOPLAY_USE_BODY', INNER_W);
    autoplayUseBody.x = 0;
    autoplayUseBody.y = cursorY;
    content.addChild(autoplayUseBody);
    registerInfoBlock(autoplayUseBody, 24);
    cursorY += autoplayUseBody.height + 24;

    const reflowInfoLayoutStack = () => {
      let y = 4;
      for (const block of infoLayoutBlocks) {
        if (block.skip) continue;
        y += block.gapBefore;
        const node = block.node;
        if (block.centerX && node.anchor) node.x = INNER_W / 2;
        node.y = y;
        const measured = typeof block.heightFor === 'function'
          ? block.heightFor()
          : (node.height ?? 0);
        const h = Math.max(0, measured);
        y += h + block.gapAfter;
      }
      this._infoScrollContentHeight = y;
      this._refreshInfoScrollBounds(
        this._infoViewportTop ?? VIEWPORT_TOP,
        this._infoViewportBottom ?? VIEWPORT_BOTTOM,
      );
    };
    this._infoContentRelayout = reflowInfoLayoutStack;
    reflowInfoLayoutStack();

    /* ── Wire scrolling state ────────────────────────────────────── */

    const setScrollY = (next) => {
      const c = this._infoScrollClamp;
      if (!c) return;
      scroll.y = Math.max(c.minY, Math.min(c.maxY, next));
    };

    this._infoScroll = {
      reset: () => {
        const c = this._infoScrollClamp;
        if (c) scroll.y = c.naturalTop;
      },
      scrollBy: (dy) => {
        const c = this._infoScrollClamp;
        if (!c) return;
        scroll.y = Math.max(c.minY, Math.min(c.maxY, scroll.y - dy));
      },
    };

    /* Wheel scroll over the info viewport (Pixi v8 federated wheel events). */
    view.on('wheel', (e) => {
      if (!view.visible) return;
      const dy = typeof e.deltaY === 'number' ? e.deltaY : 0;
      if (dy === 0) return;
      setScrollY(scroll.y - dy);
      if (typeof e.preventDefault === 'function') e.preventDefault();
    });

    /* Pointer drag scroll for touch devices. */
    let dragging = false;
    let lastY = 0;
    let pointerId = null;
    view.on('pointerdown', (e) => {
      if (!view.visible) return;
      dragging = true;
      lastY = e.global?.y ?? 0;
      pointerId = e.pointerId;
    });
    const endDrag = (e) => {
      if (pointerId != null && e?.pointerId !== pointerId) return;
      dragging = false;
      pointerId = null;
    };
    view.on('pointermove', (e) => {
      if (!dragging) return;
      if (pointerId != null && e.pointerId !== pointerId) return;
      const ny = e.global?.y ?? 0;
      const dy = ny - lastY;
      lastY = ny;
      setScrollY(scroll.y + dy);
    });
    view.on('pointerup', endDrag);
    view.on('pointerupoutside', endDrag);
    view.on('pointercancel', endDrag);

    this._infoSettingsLayoutRefs = {
      mask,
      scroll,
      content,
      view,
    };

    return view;
  }

  /**
   * Only one blocking modal at a time (settings / bet / autoplay / connection).
   * @param {'settings'|'bet'|'autoplay'|'connection'|null} [keep] null = close all
   */
  _dismissOverlappingModals(keep = null) {
    if (keep !== 'settings') this._hideSettingsPanel();
    if (keep !== 'bet') this._hideBetPopup();
    if (keep !== 'autoplay') this._hideAutoplayPicker();
    if (keep !== 'buyBonus') this._hideBuyBonusConfirm();
    if (keep !== 'connection') this._hideConnectionLostModal();
  }

  /** Connection / duplicate-session shell is active (flags or already painted). */
  _isConnectionModalBlocking() {
    return !!(
      this._systemMessagePanel?.isVisible?.() ||
      this._connectionLostUIOpen ||
      this._sessionLoadFailed ||
      this._duplicateSessionEnded
    );
  }

  /** Post-load intro is visible — blocks spin and other modals until Continue. */
  _isStartScreenBlocking() {
    return !!this._startScreenPanel?.isVisible?.();
  }

  _ensureStartScreenPanel() {
    if (this._startScreenPanel) return this._startScreenPanel;
    const host = this._containerEl ?? (typeof document !== 'undefined' ? document.body : null);
    this._startScreenPanel = createGameStartScreen({
      mountEl: host,
      logoSrc: this._gameLogoAssetPath(),
      shotSrc: this._startScreenPreviewPath(),
      onContinue: () => this._dismissStartScreen(),
      onSoundToggle: () => {
        this._settingsSoundOn = !this._settingsSoundOn;
        sound.setMuted(!this._settingsSoundOn);
        this._syncCanvasSoundIcon();
        this._refreshStartScreenCopy();
        sound.play('uiClick');
      },
    });
    return this._startScreenPanel;
  }

  _refreshStartScreenCopy() {
    const maxOdd = Number(gameConfig.maxOdd);
    const maxWinMultiplier =
      Number.isFinite(maxOdd) && maxOdd > 0 ? this._formatMaxWinMultiplier(maxOdd) : '';
    const rtp = Number(gameConfig.rtpPercent);
    const rtpDisplay = Number.isFinite(rtp) ? String(rtp) : '96.34';
    const useDesktopStartHint =
      typeof window !== 'undefined'
      && window.matchMedia('(min-width: 641px) and (pointer: fine)').matches;
    this._startScreenPanel?.update({
      gameTitle: this._tu('GAME_TITLE'),
      featureLabel: this._tu('START_SCREEN_FEATURE_LABEL'),
      maxWinLabel: maxWinMultiplier ? this._t('START_SCREEN_MAX_WIN', { maxWin: maxWinMultiplier }) : '',
      rtpLabel: this._t('START_SCREEN_RTP', { rtp: rtpDisplay }),
      continueLabel: this._tu('START_SCREEN_CONTINUE'),
      tapHint: this._t(useDesktopStartHint ? 'START_SCREEN_CLICK_HINT' : 'START_SCREEN_TAP_HINT'),
      previewBadge: this._tu('START_SCREEN_PREVIEW_BADGE'),
      soundOn: this._settingsSoundOn,
      soundLabel: this._tu('SOUND'),
    });
  }

  async _showStartScreenAndWait() {
    if (this._sessionLoadFailed || !this._gameDataReady) return;
    try {
      await Assets.load(this._startScreenPreviewPath());
    } catch {
      /* optional promo art — start screen still usable without it */
    }
    const panel = this._ensureStartScreenPanel();
    this._refreshStartScreenCopy();
    this.slotMachine?.hud?.setSpinEnabled(false);
    return new Promise((resolve) => {
      this._startScreenResolve = resolve;
      panel.show();
    });
  }

  _dismissStartScreen() {
    if (!this._isStartScreenBlocking()) return;
    sound.play('uiPopupClose');
    this._startScreenPanel?.hide();
    this.slotMachine?.hud?.setSpinEnabled(
      this._winCollectPending ? false : this._shouldShowQuickBets(),
    );
    if (this._startScreenResolve) {
      const done = this._startScreenResolve;
      this._startScreenResolve = null;
      done();
    }
  }

  /** @param {'settings'|'bet'|'autoplay'|'connection'|'start'|null} [except] */
  _isBlockingModalOpen(except = null) {
    if (except !== 'start' && this._isStartScreenBlocking()) return true;
    if (except !== 'connection' && this._isConnectionModalBlocking()) return true;
    if (except !== 'settings' && this._settingsRoot?.visible) return true;
    if (except !== 'bet' && this._betPopupDimRoot?.visible) return true;
    if (except !== 'autoplay' && this._autoplayPickerOpen) return true;
    if (except !== 'buyBonus' && this._buyBonusConfirmOpen) return true;
    return false;
  }

  _createBuyBonusButton(controls, texture) {
    const w = BUY_BONUS_BTN_W;
    const h = BUY_BONUS_BTN_H;
    const root = new Container();
    root.eventMode = 'static';
    root.cursor = 'pointer';
    root.zIndex = 16;

    const tex = texture?.width ? texture : Texture.EMPTY;
    if (tex?.source) tex.source.scaleMode = 'linear';
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5);
    sprite.width = w;
    sprite.height = h;
    sprite.eventMode = 'none';
    sprite.roundPixels = true;

    const label = new Text({
      text: 'BUY BONUS',
      style: {
        fontFamily: T.fontUi,
        fontSize: Math.max(15, Math.round(h * 0.4)),
        fontWeight: '900',
        fill: 0xfff6d8,
        align: 'center',
        letterSpacing: 0.8,
        stroke: { width: 3, color: 0x4a0c08, join: 'round' },
        dropShadow: {
          color: 0x2a0604,
          alpha: 0.55,
          blur: 2,
          distance: 1,
          angle: Math.PI / 2,
        },
      },
    });
    label.anchor.set(0.5);
    label.position.set(0, -1);
    label.eventMode = 'none';

    const visual = new Container();
    visual.eventMode = 'none';
    visual.pivot.set(0, 0);
    visual.position.set(w / 2, h / 2);
    visual.addChild(sprite, label);

    root.addChild(visual);
    root.hitArea = new Rectangle(0, 0, w, h);
    const stopBubble = (e) => {
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    };
    const pressIn = () => {
      gsap.killTweensOf(visual.scale);
      gsap.to(visual.scale, { x: 0.96, y: 0.96, duration: 0.08, overwrite: true });
    };
    const pressOut = () => this._paintBuyBonusButton();
    root.on('pointerdown', (e) => {
      stopBubble(e);
      if (!isPrimaryPointerAction(e)) return;
      pressIn();
    });
    root.on('pointerup', (e) => {
      stopBubble(e);
      pressOut();
      if (!isPrimaryPointerAction(e)) return;
      this._onBuyBonusButtonTap();
    });
    root.on('pointerupoutside', (e) => {
      stopBubble(e);
      pressOut();
    });
    root.on('pointerover', () => {
      if (this._buyBonusArmed != null) return;
      gsap.killTweensOf(visual.scale);
      gsap.to(visual.scale, { x: 1.03, y: 1.03, duration: 0.12, overwrite: true });
    });
    root.on('pointerout', () => pressOut());
    controls.addChild(root);
    this._buyBonusButton = root;
    this._buyBonusButtonVisual = visual;
    this._buyBonusButtonSprite = sprite;
    this._buyBonusButtonLabelPrimary = label;
    this._buyBonusButtonLabelSecondary = null;
    this._buyBonusButtonW = w;
    this._buyBonusButtonH = h;
    this._bindLocalizedText(label, 'BUY_BONUS', {
      upper: true,
      afterApply: () => this._fitBuyBonusButtonLabel(),
    });
    this._fitBuyBonusButtonLabel();
    this._paintBuyBonusButton();
  }

  /** Keep “BUY BONUS” inside the ruby center, not over the gold end caps. */
  _fitBuyBonusButtonLabel() {
    const label = this._buyBonusButtonLabelPrimary;
    const w = this._buyBonusButtonW;
    const h = this._buyBonusButtonH;
    if (!label || !(w > 0) || !(h > 0)) return;
    label.scale.set(1);
    const maxW = w * 0.54;
    const maxH = h * 0.46;
    const sx = label.width > maxW ? maxW / label.width : 1;
    const sy = label.height > maxH ? maxH / label.height : 1;
    label.scale.set(Math.min(sx, sy));
  }

  _paintBuyBonusButton() {
    const visual = this._buyBonusButtonVisual;
    const sprite = this._buyBonusButtonSprite;
    if (!visual || !sprite) return;
    const armed = this._buyBonusArmed != null;
    sprite.tint = 0xffffff;
    sprite.alpha = 1;
    gsap.killTweensOf(visual.scale);
    visual.scale.set(1);
    if (!armed) return;
    gsap.to(visual.scale, {
      x: 1.035,
      y: 1.035,
      duration: 0.85,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  /**
   * Pull the plaque back inside the 1922×1080 stage after layout.
   * @param {number} [minLocalX]
   */
  _clampBuyBonusButtonOnStage(minLocalX = -Infinity) {
    const btn = this._buyBonusButton;
    if (!btn?.parent) return;
    const margin = 16;
    const stageW = 1922;
    const sx = Math.abs(btn.worldTransform.a) || 1;
    if (Number.isFinite(minLocalX) && btn.x < minLocalX) btn.x = minLocalX;
    let bounds = btn.getBounds();
    const overflowRight = bounds.x + bounds.width - (stageW - margin);
    if (overflowRight > 0) {
      btn.x -= overflowRight / sx;
      bounds = btn.getBounds();
    }
    if (bounds.x < margin) btn.x += (margin - bounds.x) / sx;
  }

  /**
   * Desktop: regular spin + autoplay + bet stack stays put. The plaque sits
   * under that whole group, centered on spin and the side buttons together.
   */
  _layoutBuyBonusButtonDesktop(spinX, rightColLeft, side, stackTop, stackH) {
    const btn = this._buyBonusButton;
    if (!btn) return;
    const w = this._buyBonusButtonW || BUY_BONUS_BTN_W;
    const h = this._buyBonusButtonH || BUY_BONUS_BTN_H;
    btn.scale.set(1);
    const sectionLeft = spinX;
    const sectionRight = rightColLeft + side;
    btn.x = Math.round((sectionLeft + sectionRight) / 2 - w / 2);
    btn.y = Math.round(stackTop + stackH + 12);
    this._clampBuyBonusButtonOnStage();
  }

  /**
   * Portrait: drop the spin (and the turbo plate on it) a little so the
   * buy-bonus plaque can sit on the top of the spin. Never let the turbo
   * plate end up lower than the sound / menu icons.
   */
  _nudgeMobileSpinForBuyBonus() {
    if (!this._isPortraitMobileViewport()) return;
    const spin = this._spinButtonRoot;
    const turbo = this._betSpeedWrap;
    const btn = this._buyBonusButton;
    if (!spin || !turbo) return;
    const show =
      !!btn?.visible && this._buyBonusEnabled && this._buyBonusServerAllowed !== false;
    if (!show) return;

    const extra = 16;
    spin.y += extra;
    turbo.y += extra;
    if (btn) btn.y += extra;

    const bottomBar = this._canvasBottomBar;
    const sound = this._canvasIconSprites?.[2];
    const menu = this._canvasIconSprites?.[3];
    const icon = sound?.visible ? sound : menu?.visible ? menu : null;
    if (!bottomBar || !icon) return;
    const iconBottom = bottomBar.y + icon.y + (icon.height || 0);
    const plateH = (this._betSpeedPlateH || 40) * (Math.abs(turbo.scale?.y) || 1);
    const turboBottom = turbo.y + plateH / 2;
    const overflow = turboBottom - iconBottom;
    if (overflow <= 0) return;
    const lift = Math.min(overflow, extra);
    spin.y -= lift;
    turbo.y -= lift;
    if (btn) btn.y -= lift;
  }

  /**
   * Portrait plaque size. Capped to the desktop art size and to a fraction of
   * the spin button so it stays a secondary control, not a full-width bar.
   * @param {number} spinW
   */
  _buyBonusMobileDrawnSize(spinW) {
    const w = this._buyBonusButtonW || BUY_BONUS_BTN_W;
    const h = this._buyBonusButtonH || BUY_BONUS_BTN_H;
    const targetW = Math.round(Math.min(w, Math.max(168, spinW * 0.84)));
    const scale = targetW / w;
    return { w, h, targetW, scale, drawnH: h * scale };
  }

  _layoutBuyBonusButtonMobile(reelCx, side) {
    const btn = this._buyBonusButton;
    const spin = this._spinButtonRoot;
    if (!btn || !spin) return;
    const spinW = this._spinButtonSprite?.width ?? side;
    const { targetW, scale, drawnH } = this._buyBonusMobileDrawnSize(spinW);
    btn.scale.set(scale);
    btn.x = Math.round(reelCx - targetW / 2);
    const onSpin = Math.round(drawnH * 0.34);
    btn.y = Math.round(spin.y - spinW / 2 - (drawnH - onSpin));
  }

  /**
   * @param {boolean} offered
   */
  _setBuyBonusOffered(offered) {
    this._buyBonusServerAllowed = offered;
    const btn = this._buyBonusButton;
    if (!btn) return;
    btn.visible = this._buyBonusEnabled && offered;
    if (this._isPortraitMobileViewport()) {
      this._applyCanvasControlsViewportLayout();
      return;
    }
    if (this._spinButtonRoot) {
      const spinX = this._spinButtonRoot.x - (this._spinButtonSprite?.width ?? SPIN_BTN_REF_SIZE) / 2;
      const spinY = this._spinButtonRoot.y - (this._spinButtonSprite?.height ?? SPIN_BTN_REF_SIZE) / 2;
      this._layoutDesktopSpinSideButtons(spinX, spinY);
    }
  }

  _onBuyBonusButtonTap() {
    if (!this._buyBonusEnabled || this._buyBonusServerAllowed === false) return;
    if (this._spinCycleActive || this._spinning || this._autoplaying || this._initializing) return;
    if (this._connectionLostUIOpen || this._duplicateSessionEnded || !this._gameDataReady) return;
    if (this._isWinCollectDismissActive() || this._isBlockingModalOpen()) return;
    if (this._buyBonusArmed) {
      this._buyBonusArmed = null;
      this._paintBuyBonusButton();
      this._refreshQuickBets();
      sound.play('uiClick');
      return;
    }
    this._showBuyBonusConfirm();
  }

  _buyBonusBetList() {
    const bets = this.slotMachine?.hud?.quickBets ?? [];
    if (bets.length) return bets.map((n) => Number(n)).filter((n) => Number.isFinite(n));
    const current = Number(this.slotMachine?.hud?.betValue);
    return Number.isFinite(current) ? [current] : [];
  }

  _ensureBuyBonusConfirm() {
    if (this._buyBonusConfirmRoot || !this.world) return;
    const root = new Container();
    root.visible = false;
    root.zIndex = 40;
    root.eventMode = 'static';

    const dim = new Graphics();
    dim.rect(-8000, -8000, 16000, 16000).fill({ color: T.modalDim, alpha: 0.78 });
    dim.eventMode = 'static';
    dim.on('pointerup', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._hideBuyBonusConfirm();
    });

    const shell = new Container();
    shell.eventMode = 'passive';
    const panel = new Container();
    panel.eventMode = 'static';

    const backdrop = new Sprite(Texture.EMPTY);
    backdrop.eventMode = 'none';
    applySpriteBackdropBlur(backdrop);
    const mask = new Graphics();
    mask.eventMode = 'none';
    backdrop.mask = mask;

    const plate = new Graphics();
    plate.eventMode = 'none';
    const border = new Graphics();
    border.eventMode = 'none';
    const frame = new NineSliceSprite({
      texture: Texture.EMPTY,
      leftWidth: BUY_BONUS_FRAME_LEFT,
      rightWidth: BUY_BONUS_FRAME_RIGHT,
      topHeight: BUY_BONUS_FRAME_TOP,
      bottomHeight: BUY_BONUS_FRAME_BOTTOM,
      width: 4,
      height: 4,
    });
    frame.eventMode = 'none';
    frame.visible = false;

    const titleGlow = new Text({
      text: '',
      style: {
        fontFamily: BUY_BONUS_TITLE_FONT,
        fontSize: 36,
        fontWeight: '900',
        fill: 0xffc44a,
        align: 'center',
        letterSpacing: 2.6,
      },
    });
    titleGlow.anchor.set(0.5, 0);
    titleGlow.eventMode = 'none';
    titleGlow.alpha = 0.72;
    titleGlow.filters = [new BlurFilter({ strength: 8, strengthX: 8, strengthY: 8 })];
    const title = new Text({
      text: '',
      style: {
        fontFamily: BUY_BONUS_TITLE_FONT,
        fontSize: 36,
        fontWeight: '900',
        fill: BUY_BONUS_TITLE_FILL,
        align: 'center',
        letterSpacing: 2.6,
        stroke: { width: 6, color: 0x2a0c04, join: 'round' },
        dropShadow: {
          alpha: 0.85,
          angle: Math.PI / 2,
          blur: 5,
          color: 0x1a0602,
          distance: 3,
        },
      },
    });
    title.anchor.set(0.5, 0);
    title.eventMode = 'none';
    this._bindLocalizedText(title, 'BUY_BONUS_MODAL_TITLE', {
      upper: true,
      afterApply: () => {
        titleGlow.text = title.text;
      },
    });

    const subtitle = new Text({
      text: '',
      style: {
        fontFamily: BUY_BONUS_TITLE_FONT,
        fontSize: 17,
        fontWeight: '700',
        fill: BUY_BONUS_SUBTITLE_FILL,
        align: 'center',
        wordWrap: true,
        letterSpacing: 0.4,
        stroke: { width: 3, color: 0x3a1606, join: 'round' },
        dropShadow: {
          alpha: 0.7,
          angle: Math.PI / 2,
          blur: 4,
          color: 0x1a0804,
          distance: 2,
        },
      },
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.eventMode = 'none';
    this._bindLocalizedText(subtitle, 'BUY_BONUS_SUBTITLE');

    const divider = new Graphics();
    divider.eventMode = 'none';

    const close = new Container();
    close.eventMode = 'static';
    close.cursor = 'pointer';
    close.hitArea = new Rectangle(0, 0, 44, 44);
    const closeMark = new Text({
      text: '×',
      style: { fontFamily: T.fontUi, fontSize: 32, fontWeight: '700', fill: T.goldBright },
    });
    closeMark.anchor.set(0.5);
    closeMark.position.set(22, 20);
    closeMark.eventMode = 'none';
    const closeBtn = new Sprite(Texture.EMPTY);
    closeBtn.anchor.set(0.5);
    closeBtn.position.set(22, 22);
    closeBtn.eventMode = 'none';
    close.addChild(closeMark, closeBtn);
    close.on('pointerup', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
      sound.play('uiClick');
      this._hideBuyBonusConfirm();
    });
    const applyCloseTex = (tex) => {
      if (!tex?.width || closeBtn.destroyed) return;
      closeBtn.texture = tex;
      closeBtn.width = 44;
      closeBtn.height = 44;
      setModalCloseButtonHitArea(closeBtn);
      closeMark.visible = false;
    };
    try {
      applyCloseTex(Assets.get('/assets/close_button.png'));
    } catch {
      /* loaded on first open */
    }
    void Assets.load('/assets/close_button.png').then(applyCloseTex).catch(() => {});

    const makeWildCell = () => {
      const cell = new Container();
      cell.eventMode = 'none';
      const frame = new Graphics();
      frame.eventMode = 'none';
      const inset = 6;
      const inner = BUY_BONUS_WILD_BOX - inset * 2;
      frame
        .roundRect(inset, inset, inner, inner, 16)
        .fill({ color: T.reelWell, alpha: 0.78 })
        .stroke({ width: 2, color: T.gold, alpha: 0.92 });
      const symbol = createInfoSymbolOrBadge(
        INFO_WILD_SYMBOL_ID,
        this.spineAssets ?? {},
        BUY_BONUS_WILD_BOX,
        BUY_BONUS_WILD_BOX,
        { fallbackLabel: 'WILD', fallbackAccent: T.gold },
        (label, w, h, accent) => {
          const badge = new Container();
          const bg = new Graphics();
          bg.roundRect(10, 10, w - 20, h - 20, 12).fill({ color: T.panelDeep, alpha: 1 });
          const mark = new Text({
            text: label,
            style: {
              fontFamily: T.fontUi,
              fontSize: 13,
              fontWeight: '800',
              fill: accent ?? T.gold,
              align: 'center',
            },
          });
          mark.anchor.set(0.5);
          mark.position.set(w / 2, h / 2);
          badge.addChild(bg, mark);
          return badge;
        },
      );
      cell.addChild(frame, symbol);
      return cell;
    };

    /** @type {Array<{ root: Container, face: NineSliceSprite, title: Text, symbols: Container, mul: Text, price: Text, wildCount: number, w: number, h: number }>} */
    const cards = [];
    for (let i = 0; i < 3; i++) {
      const wildCount = i + 1;
      const card = new Container();
      card.eventMode = 'static';
      card.cursor = 'pointer';
      const face = new NineSliceSprite({
        texture: Texture.EMPTY,
        leftWidth: 72,
        rightWidth: 72,
        topHeight: 48,
        bottomHeight: 48,
        width: 4,
        height: 4,
      });
      face.eventMode = 'none';
      face.visible = false;
      const cardTitle = new Text({
        text: '',
        style: {
          fontFamily: T.fontUi,
          fontSize: 16,
          fontWeight: '800',
          fill: 0x4a1608,
          align: 'center',
          wordWrap: true,
          stroke: { width: 3, color: 0xfff6d4, join: 'round' },
        },
      });
      cardTitle.eventMode = 'none';
      this._bindLocalizedText(cardTitle, i === 0 ? 'BUY_BONUS_1_WILD' : 'BUY_BONUS_N_WILDS', {
        replace: i === 0 ? undefined : { n: wildCount },
      });
      const symbols = new Container();
      symbols.eventMode = 'none';
      for (let n = 0; n < wildCount; n++) symbols.addChild(makeWildCell());
      const mul = new Text({
        text: '',
        style: {
          fontFamily: T.fontUi,
          fontSize: 16,
          fontWeight: '800',
          fill: 0x6b2808,
          align: 'center',
          letterSpacing: 0.6,
          stroke: { width: 3, color: 0xfff6d4, join: 'round' },
        },
      });
      mul.eventMode = 'none';
      const price = new Text({
        text: '',
        style: {
          fontFamily: T.fontDisplay,
          fontSize: 26,
          fontWeight: '800',
          fill: 0x3d1208,
          align: 'center',
          stroke: { width: 4, color: 0xfff6d4, join: 'round' },
        },
      });
      price.eventMode = 'none';
      card.addChild(face, symbols, cardTitle, mul, price);
      card.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        if (typeof e.stopPropagation === 'function') e.stopPropagation();
        sound.play('uiClick');
        this._buyBonusSelectedTier = i;
        this._refreshBuyBonusPanel();
      });
      cards.push({ root: card, face, title: cardTitle, symbols, mul, price, wildCount, w: 0, h: 0 });
    }

    const guaranteed = new Container();
    guaranteed.eventMode = 'static';
    guaranteed.cursor = 'pointer';
    const guaranteedFace = new NineSliceSprite({
      texture: Texture.EMPTY,
      leftWidth: 90,
      rightWidth: 90,
      topHeight: 38,
      bottomHeight: 38,
      width: 4,
      height: 4,
    });
    guaranteedFace.eventMode = 'none';
    guaranteedFace.visible = false;
    const guaranteedLabel = new Text({
      text: '',
      style: {
        fontFamily: T.fontUi,
        fontSize: 20,
        fontWeight: '800',
        fill: T.textBright,
        align: 'center',
        stroke: { width: 4, color: 0x1a0804, join: 'round' },
      },
    });
    guaranteedLabel.anchor.set(0.5, 0.5);
    guaranteedLabel.eventMode = 'none';
    guaranteed.addChild(guaranteedFace, guaranteedLabel);
    const guaranteedPressIn = () => {
      gsap.killTweensOf(guaranteed.scale);
      gsap.to(guaranteed.scale, { x: 0.985, y: 0.985, duration: 0.07, ease: 'power2.out' });
    };
    const guaranteedPressOut = () => {
      gsap.killTweensOf(guaranteed.scale);
      gsap.to(guaranteed.scale, { x: 1, y: 1, duration: 0.14, ease: 'back.out(2)' });
    };
    guaranteed.on('pointerdown', guaranteedPressIn);
    guaranteed.on('pointerup', guaranteedPressOut);
    guaranteed.on('pointerupoutside', guaranteedPressOut);
    guaranteed.on('pointerup', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
      sound.play('uiClick');
      this._buyBonusGuaranteed = !this._buyBonusGuaranteed;
      this._refreshBuyBonusPanel();
    });
    const applyGuaranteedSwitchArt = (onTex, offTex) => {
      if (guaranteedFace.destroyed) return;
      const ui = this._buyBonusUi;
      if (!ui) return;
      ui.guaranteedOnTex = onTex?.width > 1 ? onTex : null;
      ui.guaranteedOffTex = offTex?.width > 1 ? offTex : null;
      if (this._buyBonusConfirmOpen) this._layoutBuyBonusConfirm();
      else this._refreshBuyBonusPanel();
    };
    try {
      applyGuaranteedSwitchArt(
        Assets.get(BUY_BONUS_GUARANTEED_ON_URL),
        Assets.get(BUY_BONUS_GUARANTEED_OFF_URL),
      );
    } catch {
      /* loaded below */
    }
    void Promise.all([
      Assets.load(BUY_BONUS_GUARANTEED_ON_URL).catch(() => Texture.EMPTY),
      Assets.load(BUY_BONUS_GUARANTEED_OFF_URL).catch(() => Texture.EMPTY),
    ]).then(([onTex, offTex]) => applyGuaranteedSwitchArt(onTex, offTex));

    const stakeLabel = new Text({
      text: '',
      style: {
        fontFamily: T.fontUi,
        fontSize: 13,
        fontWeight: '800',
        fill: T.modalAccentHi,
        align: 'center',
        letterSpacing: 1.4,
      },
    });
    stakeLabel.anchor.set(0.5, 0);
    stakeLabel.eventMode = 'none';
    this._bindLocalizedText(stakeLabel, 'BET', { upper: true });

    const betText = new Text({
      text: '',
      style: {
        fontFamily: T.fontDisplay,
        fontSize: 28,
        fontWeight: '800',
        fill: T.goldBright,
        align: 'center',
        stroke: { width: 4, color: T.panelShadow, join: 'round' },
      },
    });
    betText.anchor.set(0.5);
    betText.eventMode = 'none';

    const makeStep = (glyph, dir) => {
      const size = 56;
      const btn = new Container();
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.hitArea = new Rectangle(0, 0, size, size);
      const face = new Graphics();
      paintModalSecondaryButton(face, size, size, 14);
      face.position.set(size / 2, size / 2);
      const mark = new Text({
        text: glyph,
        style: { fontFamily: T.fontUi, fontSize: 32, fontWeight: '800', fill: T.textBright },
      });
      mark.anchor.set(0.5);
      mark.position.set(size / 2, size / 2 - 1);
      mark.eventMode = 'none';
      btn.addChild(face, mark);
      btn.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        if (typeof e.stopPropagation === 'function') e.stopPropagation();
        this._stepBuyBonusBet(dir);
      });
      return btn;
    };
    const minus = makeStep('−', -1);
    const plus = makeStep('+', 1);

    const buy = new Container();
    buy.eventMode = 'static';
    buy.cursor = 'pointer';
    buy.sortableChildren = true;
    const buyBanner = new Sprite(Texture.EMPTY);
    buyBanner.eventMode = 'none';
    buyBanner.visible = false;
    buyBanner.zIndex = 0;
    const buyTitle = new Text({
      text: '',
      style: {
        fontFamily: BUY_BONUS_TITLE_FONT,
        fontSize: 22,
        fontWeight: '900',
        fill: 0x4a1206,
        align: 'center',
        letterSpacing: 1.8,
        stroke: { width: 4, color: 0xfff6d4, join: 'round' },
      },
    });
    buyTitle.anchor.set(0.5);
    buyTitle.eventMode = 'none';
    this._bindLocalizedText(buyTitle, 'BUY_BONUS_CTA', { upper: true });
    const buyPrice = new Text({
      text: '',
      style: {
        fontFamily: BUY_BONUS_TITLE_FONT,
        fontSize: 18,
        fontWeight: '700',
        fill: 0x3d1006,
        align: 'center',
        letterSpacing: 0.4,
        stroke: { width: 3, color: 0xfff6d4, join: 'round' },
      },
    });
    buyPrice.anchor.set(0.5);
    buyPrice.eventMode = 'none';
    buyTitle.zIndex = 3;
    buyPrice.zIndex = 3;
    buy.addChild(buyBanner, buyTitle, buyPrice);
    const applyPurchaseBannerTex = (tex) => {
      if (buyBanner.destroyed) return;
      const ui = this._buyBonusUi;
      if (!ui) return;
      ui.buyBannerTex = tex?.width > 1 ? tex : null;
      if (this._buyBonusConfirmOpen) this._layoutBuyBonusConfirm();
    };
    try {
      applyPurchaseBannerTex(Assets.get(BUY_BONUS_PURCHASE_BANNER_URL));
    } catch {
      /* loaded below */
    }
    void Assets.load(BUY_BONUS_PURCHASE_BANNER_URL).then(applyPurchaseBannerTex).catch(() => {});
    const buyPressIn = () => {
      gsap.killTweensOf(buy.scale);
      gsap.to(buy.scale, { x: 0.97, y: 0.97, duration: 0.07, ease: 'power2.out' });
    };
    const buyPressOut = () => {
      gsap.killTweensOf(buy.scale);
      gsap.to(buy.scale, { x: 1, y: 1, duration: 0.14, ease: 'back.out(2)' });
    };
    buy.on('pointerdown', buyPressIn);
    buy.on('pointerup', buyPressOut);
    buy.on('pointerupoutside', buyPressOut);
    buy.on('pointerup', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
      sound.play('uiClick');
      this._confirmBuyBonus();
    });

    const applyBuyBonusFrameTex = (tex) => {
      if (frame.destroyed) return;
      const ui = this._buyBonusUi;
      if (!ui) return;
      ui.frameTex = tex?.width > 1 ? tex : null;
      if (this._buyBonusConfirmOpen) this._layoutBuyBonusConfirm();
    };
    try {
      applyBuyBonusFrameTex(Assets.get(BUY_BONUS_MODAL_FRAME_URL));
    } catch {
      /* loaded below */
    }
    void Assets.load(BUY_BONUS_MODAL_FRAME_URL).then(applyBuyBonusFrameTex).catch(() => {});
    const applyOptionFrameTex = (tex) => {
      const ui = this._buyBonusUi;
      if (!ui) return;
      ui.optionFrameTex = tex?.width > 1 ? tex : null;
      if (this._buyBonusConfirmOpen) this._layoutBuyBonusConfirm();
      else this._refreshBuyBonusPanel();
    };
    try {
      applyOptionFrameTex(Assets.get(BUY_BONUS_OPTION_FRAME_URL));
    } catch {
      /* loaded below */
    }
    void Assets.load(BUY_BONUS_OPTION_FRAME_URL).then(applyOptionFrameTex).catch(() => {});

    panel.addChild(backdrop, mask, plate, border, frame, divider, titleGlow, title, subtitle, close);
    for (const card of cards) panel.addChild(card.root);
    panel.addChild(guaranteed, stakeLabel, betText, minus, plus, buy);
    shell.addChild(panel);
    root.addChild(dim, shell);
    this.world.addChild(root);

    this._buyBonusConfirmRoot = root;
    this._buyBonusUi = {
      shell,
      panel,
      backdrop,
      mask,
      plate,
      border,
      frame,
      frameTex: null,
      optionFrameTex: null,
      titleGlow,
      title,
      subtitle,
      divider,
      close,
      cards,
      guaranteed,
      guaranteedFace,
      guaranteedLabel,
      guaranteedOnTex: null,
      guaranteedOffTex: null,
      stakeLabel,
      betText,
      minus,
      plus,
      buy,
      buyBanner,
      buyBannerTex: null,
      buyTitle,
      buyPrice,
      stacked: false,
    };
  }

  _showBuyBonusConfirm() {
    if (!this._buyBonusEnabled) return;
    if (this._isBlockingModalOpen('buyBonus')) return;
    this._dismissOverlappingModals('buyBonus');
    this._ensureBuyBonusConfirm();
    const root = this._buyBonusConfirmRoot;
    if (!root || !this.world) return;
    this._layoutBuyBonusConfirm();
    this._buyBonusConfirmOpen = true;
    root.visible = true;
    if (this._buyBonusUi?.titleGlow) {
      gsap.killTweensOf(this._buyBonusUi.titleGlow);
      gsap.fromTo(
        this._buyBonusUi.titleGlow,
        { alpha: 0.42 },
        { alpha: 0.88, duration: 1.15, ease: 'sine.inOut', yoyo: true, repeat: -1 },
      );
    }
    this.world.sortableChildren = true;
    this.world.reparentChildAt(root, this.world.children.length);
    sound.play('uiPopupOpen');
  }

  _hideBuyBonusConfirm() {
    const wasOpen = this._buyBonusConfirmOpen;
    this._buyBonusConfirmOpen = false;
    if (this._buyBonusUi?.titleGlow) gsap.killTweensOf(this._buyBonusUi.titleGlow);
    if (this._buyBonusUi?.buy) gsap.killTweensOf(this._buyBonusUi.buy.scale);
    if (this._buyBonusUi?.guaranteed) {
      gsap.killTweensOf(this._buyBonusUi.guaranteed.scale);
      this._buyBonusUi.guaranteed.scale.set(1);
    }
    if (this._buyBonusConfirmRoot) this._buyBonusConfirmRoot.visible = false;
    if (wasOpen) sound.play('uiPopupClose');
  }

  _layoutBuyBonusConfirm() {
    const ui = this._buyBonusUi;
    if (!ui?.shell || !ui.panel) return;

    const designW = 1922;
    const designH = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const scaleX = this._lastScales?.x > 0 ? this._lastScales.x : cw / designW;
    const scaleY = this._lastScales?.y > 0 ? this._lastScales.y : ch / designH;
    const k = scaleX > 0 && scaleY > 0 ? scaleY / scaleX : 1;
    const world = this.world;
    const worldS = world?.scale?.x > 0 ? world.scale.x : 1;
    const portrait = ch > cw;
    const margin = portrait ? 0 : 28;
    const maxStageW = designW - margin * 2;
    const maxStageH = designH - margin * 2;
    const maxLocalW = maxStageW / Math.max(k * worldS, 0.001);
    const stacked = portrait || maxLocalW < 780;
    ui.stacked = stacked;

    const pad = stacked ? 88 : 96;
    const cardGap = 0;
    const headerH = stacked ? 204 : 196;
    const guaranteedH = stacked ? 108 : 118;
    const betBlockH = 88;
    const sectionGap = 14;
    const corner = 22;

    let panelW;
    let cardW;
    let cardH;
    const bannerAspect = ui.buyBannerTex?.width > 1
      ? ui.buyBannerTex.height / ui.buyBannerTex.width
      : BUY_BONUS_BANNER_ASPECT;
    if (stacked) {
      cardH = 228;
      const cardsStackH = cardH * 3 + cardGap * 2;
      const fixedH = headerH + cardsStackH + sectionGap * 3 + guaranteedH + betBlockH + pad;
      // After the modal is scaled to fit the screen height, this width lands on the screen edges.
      const stageRatio = maxStageW / Math.max(1, k * maxStageH);
      const denom = 1 - stageRatio * bannerAspect;
      const solvedW = denom > 0.08
        ? (stageRatio * (fixedH - 2 * pad * bannerAspect)) / denom
        : maxLocalW;
      panelW = Math.max(320, solvedW);
      cardW = Math.max(120, panelW - pad * 2);
    } else {
      panelW = Math.max(760, Math.min(1360, maxLocalW));
      cardW = (panelW - pad * 2 - cardGap * 2) / 3;
      cardH = 336;
    }

    const cardsBlockH = stacked ? cardH * 3 + cardGap * 2 : cardH;
    const contentW = panelW - pad * 2;
    const bannerW = stacked ? contentW : Math.min(contentW, BUY_BONUS_BANNER_DESKTOP_W);
    const buyH = bannerW * bannerAspect;
    const panelH =
      headerH + cardsBlockH + sectionGap + guaranteedH + sectionGap + betBlockH + sectionGap + buyH + pad;
    let fit = Math.min(
      1,
      maxStageW / Math.max(1, panelW * k * worldS),
      maxStageH / Math.max(1, panelH * worldS),
    );
    if (!Number.isFinite(fit) || fit <= 0) fit = 1;

    const pivotX = world?.pivot?.x ?? designW / 2;
    const pivotY = world?.pivot?.y ?? designH / 2;
    const worldX = world?.x ?? designW / 2;
    const worldY = world?.y ?? designH / 2;
    ui.shell.scale.set(k * fit, fit);
    ui.shell.position.set(
      pivotX + (designW / 2 - worldX) / worldS,
      pivotY + (designH / 2 - worldY) / worldS,
    );
    ui.panel.position.set(-panelW / 2, -panelH / 2);
    ui.panel.hitArea = new Rectangle(0, 0, panelW, panelH);

    const frameReady = !!(ui.frame && ui.frameTex);
    if (frameReady) {
      applyBuyBonusModalFrame(ui.frame, ui.frameTex, panelW, panelH);
      ui.plate.visible = false;
      ui.border.visible = false;
      ui.backdrop.visible = false;
    } else {
      if (ui.frame) ui.frame.visible = false;
      ui.plate.visible = true;
      ui.border.visible = true;
      ui.backdrop.visible = true;
      this._syncModalBackdropSprite(ui.backdrop, panelW, panelH);
      ui.backdrop.filters = shouldUseBackdropBlur() ? [ensureBuyBonusModalBackdropBlur()] : null;
      ui.mask.clear();
      ui.mask.roundRect(0, 0, panelW, panelH, corner).fill(0xffffff);
      paintModalPanelBackground(ui.plate, panelW, panelH, corner);
      ui.plate.alpha = MODAL_PANEL_GLASS_ALPHA;
      ui.border.clear();
      ui.border.roundRect(0, 0, panelW, panelH, corner).stroke({
        width: 3,
        fill: BET_POPUP_BORDER_GRADIENT,
        join: 'round',
      });
    }

    ui.title.style.fontSize = stacked ? 26 : 38;
    ui.title.style.letterSpacing = stacked ? 1.2 : 2.8;
    ui.title.style.wordWrap = true;
    ui.title.style.wordWrapWidth = Math.max(140, panelW - pad * 2 - 56);
    ui.title.position.set(panelW / 2, stacked ? 74 : 80);
    if (ui.titleGlow) {
      ui.titleGlow.style.fontSize = ui.title.style.fontSize;
      ui.titleGlow.style.letterSpacing = ui.title.style.letterSpacing;
      ui.titleGlow.style.wordWrap = true;
      ui.titleGlow.style.wordWrapWidth = ui.title.style.wordWrapWidth;
      ui.titleGlow.text = ui.title.text;
      if (typeof ui.titleGlow.updateText === 'function') ui.titleGlow.updateText(true);
      ui.titleGlow.position.set(ui.title.x, ui.title.y + 1);
    }
    if (typeof ui.title.updateText === 'function') ui.title.updateText(true);
    ui.subtitle.style.fontSize = stacked ? 15 : 18;
    ui.subtitle.style.wordWrapWidth = Math.max(120, panelW - pad * 2 - (stacked ? 28 : 36));
    ui.subtitle.position.set(panelW / 2, ui.title.y + ui.title.height + (stacked ? 4 : 6));
    if (typeof ui.subtitle.updateText === 'function') ui.subtitle.updateText(true);
    ui.close.position.set(panelW - 136, stacked ? 80 : 84);

    const stackBottom = headerH + (stacked ? cardH * 3 + cardGap * 2 : cardH);
    let cardsY = headerH;
    let layoutCardH = cardH;
    if (stacked) {
      const textBottom = ui.subtitle.y + (ui.subtitle.height || 0);
      const tightY = textBottom + 8;
      if (tightY < headerH - 4) cardsY = tightY;
      layoutCardH = Math.max(120, (stackBottom - cardsY - cardGap * 2) / 3);
    }
    ui.divider.position.set(pad, stacked ? cardsY - 6 : headerH - 14);
    paintModalDividerLine(ui.divider, panelW - pad * 2, 2);

    for (let i = 0; i < ui.cards.length; i++) {
      const card = ui.cards[i];
      card.w = cardW;
      card.h = layoutCardH;
      card.root.position.set(
        stacked ? pad : pad + i * (cardW + cardGap),
        stacked ? cardsY + i * (layoutCardH + cardGap) : cardsY,
      );
      card.root.hitArea = new Rectangle(0, 0, cardW, layoutCardH);
      const insetX = Math.max(28, Math.min(96, Math.round(cardW * 0.26)));
      const insetY = Math.max(22, Math.min(68, Math.round(layoutCardH * 0.18)));
      card.title.visible = false;
      card.mul.visible = false;
      card.price.style.align = 'center';
      card.price.style.fontSize = stacked ? 22 : 28;
      card.price.anchor.set(0.5, 1);
      card.price.position.set(cardW / 2, layoutCardH - insetY - (stacked ? 0 : 4));
      const priceBlock = stacked ? 32 : 40;
      const bandY = insetY;
      const bandH = Math.max(48, layoutCardH - bandY - insetY - priceBlock);
      const drawn = layoutBuyBonusWildRow(
        card.symbols,
        Math.max(48, cardW - insetX * 2 - 8),
        bandH,
        !stacked && card.wildCount === 1,
      );
      const centeredY = layoutCardH / 2 - drawn.h / 2;
      const minY = bandY;
      const maxY = Math.max(minY, card.price.y - priceBlock - drawn.h);
      card.symbols.position.set(
        (cardW - drawn.w) / 2,
        Math.max(minY, Math.min(maxY, centeredY)),
      );
    }

    const afterCards = stackBottom + sectionGap;
    const rowW = panelW - pad * 2;
    const guaranteedW = stacked ? rowW : panelW * 0.3;
    ui.guaranteed.pivot.set(guaranteedW / 2, guaranteedH / 2);
    ui.guaranteed.position.set(panelW / 2, afterCards + guaranteedH / 2);
    ui.guaranteed.scale.set(1);
    ui.guaranteed.hitArea = new Rectangle(0, 0, guaranteedW, guaranteedH);
    ui.guaranteedRowH = guaranteedH;
    ui.guaranteedRowW = guaranteedW;

    const betY = afterCards + guaranteedH + sectionGap;
    ui.stakeLabel.position.set(panelW / 2, betY);
    ui.betText.position.set(panelW / 2, betY + 50);
    ui.minus.position.set(panelW / 2 - 124, betY + 22);
    ui.plus.position.set(panelW / 2 + 68, betY + 22);

    ui.buy.position.set((panelW - bannerW) / 2, panelH - pad - buyH);
    ui.buy.scale.set(1);
    ui.buy.hitArea = new Rectangle(0, 0, bannerW, buyH);
    if (ui.buyBanner) applyBuyBonusPurchaseBanner(ui.buyBanner, ui.buyBannerTex, bannerW);
    ui.buyLayoutW = bannerW;
    ui.buyLayoutH = buyH;
    ui.buyTitle.style.fontSize = Math.round(Math.min(stacked ? 44 : 58, Math.max(32, buyH * 0.36)));
    ui.buyTitle.style.letterSpacing = stacked ? 1 : 1.4;
    ui.buyPrice.style.fontSize = Math.round(Math.min(stacked ? 26 : 32, Math.max(20, buyH * 0.2)));

    this._refreshBuyBonusPanel();
  }

  _refreshBuyBonusPanel() {
    const ui = this._buyBonusUi;
    if (!ui?.cards) return;
    const bet = Number(this.slotMachine?.hud?.betValue);
    const guaranteed = this._buyBonusGuaranteed === true;
    for (let i = 0; i < ui.cards.length; i++) {
      const card = ui.cards[i];
      const selected = i === this._buyBonusSelectedTier;
      const type = /** @type {1 | 2 | 3} */ (i + 1);
      const w = card.w;
      const h = card.h;
      if (w > 0 && h > 0) {
        applyBuyBonusOptionFrame(card.face, ui.optionFrameTex, w, h);
      }
      card.root.alpha = selected ? 1 : 0.78;
      card.title.visible = false;
      card.mul.visible = false;
      card.price.style.fill = 0x3d1208;
      card.price.text = this._formatCurrencyAmount(buyBonusPrice(bet, type, guaranteed));
    }

    const rowW = ui.guaranteedRowW ?? 0;
    const rowH = ui.guaranteedRowH ?? 0;
    let cap = 90;
    if (ui.guaranteedFace && rowW > 0 && rowH > 0) {
      const faceTex = guaranteed ? ui.guaranteedOnTex : ui.guaranteedOffTex;
      cap = applyGuaranteedSwitchFace(ui.guaranteedFace, faceTex, rowW, rowH).cap;
    }
    if (ui.guaranteedLabel && rowW > 0 && rowH > 0) {
      ui.guaranteedLabel.text = this._t(
        guaranteed ? 'BUY_BONUS_GUARANTEED_ENABLED' : 'BUY_BONUS_GUARANTEED_DISABLED',
      );
      ui.guaranteedLabel.style.fill = T.textBright;
      ui.guaranteedLabel.style.align = 'center';
      let fontSize = ui.stacked ? 16 : 18;
      const maxW = Math.max(80, rowW - cap * 2 - 28);
      ui.guaranteedLabel.style.fontSize = fontSize;
      if (typeof ui.guaranteedLabel.updateText === 'function') ui.guaranteedLabel.updateText(true);
      while (fontSize > 12 && ui.guaranteedLabel.width > maxW) {
        fontSize -= 1;
        ui.guaranteedLabel.style.fontSize = fontSize;
        if (typeof ui.guaranteedLabel.updateText === 'function') ui.guaranteedLabel.updateText(true);
      }
      ui.guaranteedLabel.anchor.set(0.5, 0.5);
      ui.guaranteedLabel.position.set(rowW / 2, rowH / 2);
    }

    if (ui.betText) ui.betText.text = this._formatCurrencyAmount(bet);

    const type = /** @type {1 | 2 | 3} */ (this._buyBonusSelectedTier + 1);
    const priceText = this._formatCurrencyAmount(buyBonusPrice(bet, type, guaranteed));
    const wildName = type === 1
      ? this._t('BUY_BONUS_1_WILD')
      : this._t('BUY_BONUS_N_WILDS', { n: type });
    if (ui.buyPrice) {
      ui.buyPrice.text = ui.stacked
        ? this._t('BUY_BONUS_CTA_SUB', { price: priceText })
        : this._t('BUY_BONUS_CTA_SUB_DETAIL', { wild: wildName, price: priceText });
    }
    const bannerW = ui.buyLayoutW ?? 0;
    const bannerH = ui.buyLayoutH ?? 0;
    if (ui.buyTitle && ui.buyPrice && bannerW > 0 && bannerH > 0) {
      const maxTextW = bannerW * 0.72;
      if (typeof ui.buyTitle.updateText === 'function') ui.buyTitle.updateText(true);
      while (ui.buyTitle.style.fontSize > 24 && ui.buyTitle.width > maxTextW) {
        ui.buyTitle.style.fontSize -= 2;
        if (typeof ui.buyTitle.updateText === 'function') ui.buyTitle.updateText(true);
      }
      if (typeof ui.buyPrice.updateText === 'function') ui.buyPrice.updateText(true);
      while (ui.buyPrice.style.fontSize > 16 && ui.buyPrice.width > maxTextW) {
        ui.buyPrice.style.fontSize -= 1;
        if (typeof ui.buyPrice.updateText === 'function') ui.buyPrice.updateText(true);
      }
      const gap = 0;
      const titleH = ui.buyTitle.height;
      const priceH = ui.buyPrice.height;
      const blockH = titleH + gap + priceH;
      const mid = bannerH * 0.5;
      ui.buyTitle.position.set(bannerW / 2, mid - blockH / 2 + titleH / 2);
      ui.buyPrice.position.set(bannerW / 2, mid + blockH / 2 - priceH / 2);
    }
  }

  _stepBuyBonusBet(dir) {
    const bets = this._buyBonusBetList();
    if (!bets.length || !this.slotMachine?.hud) return;
    const current = Number(this.slotMachine.hud.betValue);
    let idx = bets.findIndex((n) => Math.abs(n - current) < 0.001);
    if (idx < 0) idx = 0;
    const next = Math.max(0, Math.min(bets.length - 1, idx + dir));
    if (next === idx) return;
    sound.play('uiClick');
    this.slotMachine.hud.setBet(bets[next]);
    this._updateCanvasBetPrompt(bets[next]);
    this._refreshQuickBets();
    this._refreshBuyBonusPanel();
  }

  _confirmBuyBonus() {
    if (!this._buyBonusConfirmOpen) return;
    const type = /** @type {1 | 2 | 3} */ (this._buyBonusSelectedTier + 1);
    const guaranteedWins = this._buyBonusGuaranteed === true;
    const bet = Number(this.slotMachine?.hud?.betValue);
    const price = buyBonusPrice(bet, type, guaranteedWins);
    const balance = Number(this.slotMachine?.hud?.balanceValue);
    if (!Number.isFinite(balance) || balance < price) {
      sound.play('uiError');
      showSystemToast({
        message: this._t('HUB_ACTION_INSUFFICIENT_BALANCE'),
        durationMs: 3500,
      });
      return;
    }
    this._buyBonusArmed = { type, guaranteedWins };
    this._paintBuyBonusButton();
    this._refreshQuickBets();
    this._hideBuyBonusConfirm();
    void this.handleSpin();
  }

  /** PLAY NOW handler for the autoplay-settings modal. */
  _startAutoplayFromModal() {
    const spins = this._autoplayModalSpinsSelected;
    this._hideSettingsPanel();
    this._startAutoplayWithRounds(spins ?? null);
  }

  _shouldShowQuickBets() {
    if (this._sessionLoadFailed || this._connectionLostUIOpen || this._duplicateSessionEnded) {
      return false;
    }
    if (!this._gameDataReady) return false;
    const bets = this.slotMachine?.hud?.quickBets ?? [];
    return bets.length > 0;
  }

  /** Session-ended always wins over connection-lost (same modal shell). */
  _isDuplicateSessionModalActive() {
    return (
      this._duplicateSessionEnded ||
      this._forceDisconnectReceived ||
      this._connModalMode === 'duplicate'
    );
  }

  _shouldShowConnectionLostModal() {
    if (this._initializing || this._loadingRoot) return false;
    if (this._isDuplicateSessionModalActive()) {
      return this._connectionLostUIOpen;
    }
    return this._sessionLoadFailed || this._connectionLostUIOpen;
  }

  /** Force duplicate copy/layout; never downgrade to connection-lost. */
  _prioritizeDuplicateSessionModal() {
    if (!this._isDuplicateSessionModalActive()) return false;
    this._connModalMode = 'duplicate';
    this._connectionLostUIOpen = true;
    this._hadConnectionLoss = false;
    this._reconnectAfterTabVisible = false;
    this._stopConnectionAutoRetry();
    this._clearConnectionLostGraceTimer();
    this._clearDisconnectResolveTimer();
    this._setConnectionModalReconnecting(false);
    this._refreshConnectionLostModalCopy();
    return true;
  }

  _clearConnectionLostGraceTimer() {
    if (this._connLostGraceTimer != null) {
      clearTimeout(this._connLostGraceTimer);
      this._connLostGraceTimer = null;
    }
  }

  _clearDisconnectResolveTimer() {
    if (this._disconnectResolveTimer != null) {
      clearTimeout(this._disconnectResolveTimer);
      this._disconnectResolveTimer = null;
    }
  }

  _scheduleDisconnectResolution(delayMs = FORCE_DISCONNECT_WAIT_MS) {
    if (!this._connectionMonitoringEnabled || this._isDuplicateSessionModalActive()) return;
    if (this._isPostBootHubGraceActive()) return;
    this._clearDisconnectResolveTimer();
    if (typeof window === 'undefined') return;
    const ms = Number.isFinite(delayMs) ? delayMs : FORCE_DISCONNECT_WAIT_MS;
    this._disconnectResolveTimer = window.setTimeout(() => {
      this._disconnectResolveTimer = null;
      void this._resolvePostHubDisconnect();
    }, ms);
  }

  _isOnline() {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  _isPostBootHubGraceActive() {
    return Date.now() < this._hubIgnoreUntilTs;
  }

  _armPostBootHubGrace() {
    this._hubIgnoreUntilTs = Date.now() + POST_BOOT_HUB_GRACE_MS;
  }

  _attachHubConnectionHandlers() {
    this.backend.setConnectionHandlers({
      onDisconnected: () => this._handleHubDisconnected(),
      onReconnecting: () => this._handleHubReconnecting(),
      onConnected: () => this._handleHubReconnected(),
      onForceDisconnect: () => this._handleForceDisconnect(),
    });
  }

  _unwrapInitialPayload(state) {
    // StartGameAction / MessagePack codec already returns a flat session object.
    if (state && typeof state === 'object' && !Array.isArray(state)) {
      if (
        Array.isArray(state.quickBets) ||
        Array.isArray(state.betOptions) ||
        state.defaultBetAmount != null ||
        state.defaultBet != null ||
        state.matrix != null ||
        state.balance != null
      ) {
        return state;
      }
    }
    let payload = state?.arguments?.[0] ?? state;
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload?.result != null && typeof payload.result === 'object' && !Array.isArray(payload.result)) {
      payload = payload.result;
    } else if (payload?.Result != null && typeof payload.Result === 'object' && !Array.isArray(payload.Result)) {
      payload = payload.Result;
    }
    return payload;
  }

  _stopConnectionAutoRetry() {
    if (this._connectionAutoRetryTimer != null) {
      clearInterval(this._connectionAutoRetryTimer);
      this._connectionAutoRetryTimer = null;
    }
    this._setConnectionModalReconnecting(false);
  }

  _setConnectionModalReconnecting(active) {
    if (this._isDuplicateSessionModalActive()) {
      this._connectionModalReconnecting = false;
      return;
    }
    this._connectionModalReconnecting = !!active;
    this._syncConnectionModalStatus();
  }

  _startConnectionAutoRetry() {
    if (this._connectionAutoRetryTimer != null) return;
    if (this._isDuplicateSessionModalActive()) return;
    const tick = async () => {
      if (!this._connectionLostUIOpen || this._duplicateSessionEnded) {
        this._stopConnectionAutoRetry();
        return;
      }
      await this._runConnectionCheck({ allowShowModal: true });
    };
    void tick();
    this._connectionAutoRetryTimer = window.setInterval(() => {
      void tick();
    }, CONNECTION_AUTO_RETRY_MS);
  }

  /** Tab return / background: reconnect without opening the connection-lost modal. */
  async _attemptSilentReconnect({ restartHubFirst = false, pingOnly = false } = {}) {
    return this._runConnectionCheck({ restartHubFirst, pingOnly, allowShowModal: false });
  }

  /** Debounced hub ping (InitialState). Modal only opens if the ping fails. */
  _readBalanceFromRecord(record) {
    if (record == null) return null;
    const fieldNames = [
      'balance',
      'Balance',
      'currentBalance',
      'CurrentBalance',
      'walletBalance',
      'WalletBalance',
      'playerBalance',
      'PlayerBalance',
      'totalBalance',
      'TotalBalance',
    ];
    /** @type {unknown[]} */
    const queue = [record];
    const seen = new Set();
    while (queue.length) {
      const obj = queue.shift();
      if (!obj || typeof obj !== 'object' || seen.has(obj)) continue;
      seen.add(obj);
      for (const name of fieldNames) {
        if (!(name in obj)) continue;
        const n = Number(obj[name]);
        if (Number.isFinite(n)) return n;
      }
      for (const key of ['result', 'Result', 'model', 'Model', 'data', 'Data', 'state', 'State']) {
        const child = obj[key];
        if (child && typeof child === 'object') queue.push(child);
      }
      const args = obj.arguments;
      if (Array.isArray(args) && args[0] && typeof args[0] === 'object') {
        queue.push(args[0]);
      }
    }
    return null;
  }

  _readSessionBalance(initial) {
    const payload = this._unwrapInitialPayload(initial) ?? initial;
    return this._readBalanceFromRecord(payload) ?? this._readBalanceFromRecord(initial);
  }

  _readCurrencyCodeFromRecord(record) {
    if (record == null) return null;
    const fieldNames = [
      'currencyCode',
      'CurrencyCode',
      'Currency',
      'currency',
    ];
    /** @type {unknown[]} */
    const queue = [record];
    const seen = new Set();
    while (queue.length) {
      const obj = queue.shift();
      if (!obj || typeof obj !== 'object' || seen.has(obj)) continue;
      seen.add(obj);
      for (const name of fieldNames) {
        if (!(name in obj)) continue;
        const raw = obj[name];
        if (raw == null) continue;
        const code = String(raw).trim().toUpperCase();
        if (code) return code;
      }
      for (const key of ['result', 'Result', 'model', 'Model', 'data', 'Data', 'state', 'State']) {
        const child = obj[key];
        if (child && typeof child === 'object') queue.push(child);
      }
      const args = obj.arguments;
      if (Array.isArray(args) && args[0] && typeof args[0] === 'object') {
        queue.push(args[0]);
      }
    }
    return null;
  }

  _readSessionCurrencyCode(initial) {
    const payload = this._unwrapInitialPayload(initial) ?? initial;
    return (
      this._readCurrencyCodeFromRecord(payload) ?? this._readCurrencyCodeFromRecord(initial)
    );
  }

  /** Launch URL currency (e.g. Currency=AMD) — takes precedence over InitialState. */
  _pickLaunchCurrencyCode() {
    const fromSession = this._sessionParams?.currencyCode;
    if (fromSession) return fromSession;
    if (typeof window === 'undefined') return null;
    const raw = getParamsFromUrl(window.location.search);
    const code = (
      raw.Currency ??
      raw.currency ??
      raw.CurrencyCode ??
      raw.currencyCode
    )
      ?.trim()
      .toUpperCase();
    return code && /^[A-Z]{3}$/.test(code) ? code : null;
  }

  _roundStatusStorageKey() {
    const session = this._sessionParams ?? this._resolveSessionAuth?.() ?? {};
    const partnerId = String(session.partnerId ?? '');
    const gameName = String(session.gameName ?? '');
    if (!partnerId || !gameName) return null;
    return roundStatusStorageKey(partnerId, gameName);
  }

  /** @param {import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot} status */
  _persistRoundStatus(status) {
    this._roundStatusLive = { ...status, savedAt: Date.now() };
    const key = this._roundStatusStorageKey();
    if (key) saveRoundStatus(key, status);
    saveRoundStatus(ROUND_STATUS_FALLBACK_KEY, status);
  }

  /** @returns {import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null} */
  _loadRoundStatus() {
    const key = this._roundStatusStorageKey();
    const keyed = key ? loadRoundStatus(key) : null;
    if (keyed) return keyed;
    return loadRoundStatus(ROUND_STATUS_FALLBACK_KEY);
  }

  _clearRoundStatus() {
    this._roundStatusLive = null;
    const key = this._roundStatusStorageKey();
    if (key) clearRoundStatus(key);
    clearRoundStatus(ROUND_STATUS_FALLBACK_KEY);
  }

  _installRoundStatusPageHideFlush() {
    if (this._roundStatusPageHideFlush || typeof window === 'undefined') return;
    this._roundStatusPageHideFlush = () => {
      if (!this._roundStatusLive || !this._isPendingRoundPhase(this._roundStatusLive.phase)) return;
      this._persistRoundStatus(this._roundStatusLive);
    };
    window.addEventListener('pagehide', this._roundStatusPageHideFlush);
  }

  /** @param {string | undefined} phase */
  _isPendingRoundPhase(phase) {
    return phase === 'celebrating' || phase === 'collect_hold' || phase === 'rollup_collect';
  }

  /**
   * Server-reported uncollected win only — not the “last win” HUD stat.
   * @param {Record<string, unknown>} initial
   * @returns {number}
   */
  _readExplicitPendingWinFromInitial(initial) {
    const unfinished = initial?.unfinishedSpin ?? initial?.UnfinishedSpin;
    const fromUnfinished = Number(
      unfinished?.pendingWin ?? unfinished?.winAmount ?? unfinished?.win ?? unfinished?.WinAmount ?? 0,
    );
    if (fromUnfinished > 0) return fromUnfinished;

    const direct = Number(
      initial?.pendingWin ??
        initial?.PendingWin ??
        initial?.uncollectedWin ??
        initial?.UncollectedWin ??
        0,
    );
    if (direct > 0) return direct;

    const roundState = String(initial?.roundState ?? initial?.RoundState ?? '').toLowerCase();
    const needsCollect =
      initial?.needsCollect === true ||
      initial?.NeedsCollect === true ||
      initial?.awaitingCollect === true ||
      initial?.AwaitingCollect === true ||
      roundState === 'collect' ||
      roundState === 'pendingcollect' ||
      roundState === 'pending';

    if (needsCollect) {
      const w = Number(
        initial?.winAmount ??
          initial?.WinAmount ??
          initial?.roundWin ??
          initial?.RoundWin ??
          0,
      );
      if (w > 0) return w;
    }
    return 0;
  }

  /**
   * @param {Record<string, unknown>} initial
   * @returns {number | null}
   */
  _readRoundBetFromInitial(initial) {
    const candidates = [
      initial?.betAmount,
      initial?.BetAmount,
      initial?.lastBetAmount,
      initial?.LastBetAmount,
      initial?.roundBetAmount,
      initial?.RoundBetAmount,
      initial?.stake,
      initial?.Stake,
    ];
    for (const raw of candidates) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }

  /**
   * Merge server InitialState with client session snapshot for interrupted rounds.
   * @param {Record<string, unknown>} initial
   * @returns {{ pendingWin: number, betAmount: number | null, snapshot: import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null, source: string } | null}
   */
  _resolveInterruptedRound(initial) {
    const snapshot = this._loadRoundStatus();
    const serverPending = this._readExplicitPendingWinFromInitial(initial);

    if (
      snapshot &&
      this._isPendingRoundPhase(snapshot.phase) &&
      Number(snapshot.pendingWin) > 0
    ) {
      const snapWin = Number(snapshot.pendingWin);
      const serverMatches =
        serverPending > 0 && Math.abs(serverPending - snapWin) < 0.001;
      return {
        pendingWin: serverMatches ? serverPending : snapWin,
        betAmount:
          Number(snapshot.betAmount) > 0
            ? Number(snapshot.betAmount)
            : this._readRoundBetFromInitial(initial),
        snapshot,
        source: serverMatches ? 'server+client' : 'client',
      };
    }

    if (serverPending > 0) {
      return {
        pendingWin: serverPending,
        betAmount:
          (snapshot?.betAmount > 0 ? Number(snapshot.betAmount) : null) ??
          this._readRoundBetFromInitial(initial),
        snapshot,
        source: 'server',
      };
    }

    return null;
  }

  /**
   * Play deferred big-win overlay after bootstrap loading screen is gone.
   * @param {{ replaySounds?: boolean }} [opts]
   */
  _scheduleDeferredRoundResumeVisuals({ replaySounds = false } = {}) {
    const run = () => this._applyDeferredRoundResumeVisuals({ replaySounds });
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      run();
    }
  }

  /**
   * Play deferred big-win overlay after bootstrap loading screen is gone.
   * @param {{ replaySounds?: boolean }} [opts]
   */
  _applyDeferredRoundResumeVisuals({ replaySounds = false } = {}) {
    const d = this._deferredRoundResumeVisuals;
    if (!d || !this.slotMachine) return;
    this._deferredRoundResumeVisuals = null;

    const { winMult, pendingWin, celebrationMode, showCelebration, statusBase, elapsedSec } = d;
    if (!showCelebration) return;

    const onBuildupComplete = () => {
      this.slotMachine?.resumeWinLineSounds();
      this._clearRoundStatus();
      if (this._spinButtonSprite) {
        this._setSpinButtonTexture(this._autoplaying ? this._spinStopTex : this._spinStartTex);
      }
      this.slotMachine?.hud?.setSpinEnabled(this._shouldShowQuickBets());
      this._setBetPromptVisible(true);
      this._refreshQuickBets();
    };

    if (celebrationMode === 'static') {
      this.slotMachine.showWinCelebrationStaticHold(winMult, pendingWin);
      onBuildupComplete();
      return;
    }

    this._persistRoundStatus({
      ...statusBase,
      phase: 'celebrating',
      celebrationStartedAt: Date.now(),
    });
    this.slotMachine.playWinCelebrationContinue(
      winMult,
      pendingWin,
      elapsedSec ?? 0,
      onBuildupComplete,
    );
  }

  /**
   * Restore an unfinished round from InitialState (reload, reconnect, or tab return).
   * @param {Record<string, unknown>} initial
   * @param {number} pendingWin
   * @param {unknown} wins
   * @param {{ betAmount?: number | null, snapshot?: import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null }} [opts]
   */
  _resumePendingRoundFromServer(initial, pendingWin, wins, { betAmount: betOverride = null, snapshot = null } = {}) {
    if (!this.slotMachine || !(pendingWin > 0)) return;
    if (this._spinCycleActive || this._isWinCollectDismissActive()) return;
    if (this.slotMachine.reelContainer?._winLineCycleActive) return;

    this._applySessionMatrixToReels(initial, { force: true });

    const ui = this.slotMachine.hud;
    const snap = snapshot ?? this._loadRoundStatus();
    const betAmount =
      (betOverride != null && betOverride > 0 ? betOverride : null) ??
      (snap?.betAmount > 0 ? Number(snap.betAmount) : null) ??
      this._readRoundBetFromInitial(initial) ??
      ui.betValue;
    if (Number.isFinite(betAmount) && betAmount > 0) {
      ui.setBet(betAmount);
      this._updateCanvasBetPrompt(betAmount);
    }

    let winMult = betAmount > 0 ? pendingWin / betAmount : 0;
    if (snap && Number(snap.winMult) > 0) {
      winMult = Number(snap.winMult);
    }
    let showCelebration =
      !!snap?.showCelebration ||
      (betAmount > 0 && winMult >= winCelebrationMinMultiple());

    const grid = this._getDisplayGridFromInitial(initial);
    const hits = Array.isArray(wins) ? this._mapBackendWinsToHits(wins, grid) : [];

    ui.setWin(pendingWin, hits);
    this._updateCanvasWin(pendingWin);

    const winMatches =
      !!snap && Math.abs(Number(snap.pendingWin) - pendingWin) < 0.001;

    /** @type {'continue' | 'static' | 'none'} */
    let celebrationMode = 'none';
    if (showCelebration) {
      if (winMatches && snap?.phase === 'collect_hold') {
        celebrationMode = 'static';
      } else {
        celebrationMode = 'continue';
      }
    }

    if (hits.length) {
      const hitsSorted = [...hits].sort(
        (a, b) =>
          (a.paylineIndex ?? Number.MAX_SAFE_INTEGER) - (b.paylineIndex ?? Number.MAX_SAFE_INTEGER),
      );
      this._beginPostSpinWin(hitsSorted, pendingWin, {
        suppressLineSound: showCelebration && celebrationMode !== 'static',
      });
    }

    const statusBase = {
      phase: /** @type {const} */ (
        showCelebration && celebrationMode === 'static' ? 'collect_hold' : 'rollup_collect'
      ),
      pendingWin,
      betAmount,
      winMult,
      showCelebration,
    };

    let elapsedSec = 0;
    if (celebrationMode === 'continue' && snap?.celebrationStartedAt) {
      elapsedSec = Math.max(0, (Date.now() - Number(snap.celebrationStartedAt)) / 1000);
    }

    if (showCelebration && celebrationMode === 'continue') {
      const seqSec = getCelebrationSequenceDurationSec(this.spineAssets, winMult, false);
      this._animateLastWinRollup(pendingWin, betAmount, {
        fixedDurationSec: seqSec,
        celebrationMultiple: winMult,
      });
    } else if (showCelebration && celebrationMode === 'static') {
      this._animateLastWinRollup(pendingWin, betAmount, { immediate: true });
    } else {
      this._animateLastWinRollup(pendingWin, betAmount, { immediate: true });
      this._clearRoundStatus();
    }

    if (showCelebration) {
      this._resumeRoundSkipCollect = true;
      if (this._spinButtonSprite) {
        this._setSpinButtonTexture(this._autoplaying ? this._spinStopTex : this._spinStartTex);
      }
      ui.setSpinEnabled(this._shouldShowQuickBets());
      this._deferredRoundResumeVisuals = {
        winMult,
        pendingWin,
        celebrationMode,
        showCelebration,
        statusBase,
        elapsedSec,
      };
      if (!this._loadingRoot) {
        this._scheduleDeferredRoundResumeVisuals({ replaySounds: true });
      }
    }

    this._refreshQuickBets();
  }

  _scheduleConnectionCheck(delayMs = CONNECTION_LOST_GRACE_MS) {
    if (!this._connectionMonitoringEnabled) return;
    if (this._isDuplicateSessionModalActive()) return;
    if (this._isPostBootHubGraceActive()) return;
    this._clearConnectionLostGraceTimer();
    if (typeof window === 'undefined') return;
    const ms = Number.isFinite(delayMs) ? delayMs : CONNECTION_LOST_GRACE_MS;
    this._connLostGraceTimer = window.setTimeout(() => {
      this._connLostGraceTimer = null;
      void this._runConnectionCheck();
    }, ms);
  }

  _refreshConnectionLostModalCopy() {
    const duplicate = this._isDuplicateSessionModalActive();
    const titleKey = duplicate ? 'DUPLICATE_SESSION_TITLE' : 'CONNECTION_LOST_TITLE';
    const bodyKey = duplicate ? 'DUPLICATE_SESSION_BODY' : 'CONNECTION_LOST_BODY';
    this._systemMessagePanel?.update({
      title: this._tu(titleKey),
      body: this._t(bodyKey),
      icon: duplicate ? 'session' : 'connection',
      primaryLabel: this._tu('RELOAD_PAGE'),
      showPrimaryButton: true,
      showLogo: true,
    });
  }

  _syncConnectionModalStatus() {
    this._systemMessagePanel?.update({
      statusText: '',
      statusVisible: false,
    });
  }

  _ensureSystemMessagePanel() {
    if (this._systemMessagePanel) return this._systemMessagePanel;
    const host = this._containerEl ?? (typeof document !== 'undefined' ? document.body : null);
    this._systemMessagePanel = createSystemMessagePanel({
      mountEl: host,
      onPrimaryClick: () => {
        sound.play('uiClick');
        if (typeof window !== 'undefined') window.location.reload();
      },
    });
    this._refreshConnectionLostModalCopy();
    return this._systemMessagePanel;
  }

  _installHubConnectionMonitor() {
    if (this._hubMonitorDispose || typeof window === 'undefined') return;
    this._hubMonitorDispose = installHubConnectionMonitor({
      onOffline: () => this._onBrowserOffline(),
      onOnline: () => this._onBrowserOnline(),
      onForeground: (detail) => this._onAppForeground(detail),
      onBackground: () => this._onAppBackground(),
    });
  }

  _onBrowserOffline() {
    if (!this._connectionMonitoringEnabled) return;
    if (this._duplicateSessionEnded || this._sessionLoadFailed || !this._gameDataReady) return;
    this._hadConnectionLoss = true;
    this._suspendPlayForConnectivity();
    if (typeof document !== 'undefined' && document.hidden) {
      void this._runConnectionCheck({ allowShowModal: true });
      return;
    }
    this._scheduleConnectionCheck(CONNECTION_OFFLINE_GRACE_MS);
  }

  _onBrowserOnline() {
    if (!this._connectionMonitoringEnabled) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._duplicateSessionEnded || !this._gameDataReady) return;
    if (!this._hadConnectionLoss && !this._connectionLostUIOpen && this.backend.isConnected()) {
      return;
    }
    void this._runConnectionCheck();
  }

  _onAppBackground() {
    if (!this._connectionMonitoringEnabled) return;
    if (this._duplicateSessionEnded || this._sessionLoadFailed || !this._gameDataReady) return;
    if (this._isDuplicateSessionModalActive()) return;
    this._clearConnectionLostGraceTimer();
    if (
      this._connectionLostUIOpen ||
      this._reconnectAfterTabVisible ||
      this._hadConnectionLoss ||
      !this.backend.isConnected()
    ) {
      void this._runConnectionCheck({ allowShowModal: true });
    }
  }

  _onAppForeground({ hiddenMs = 0 } = {}) {
    if (!this._connectionMonitoringEnabled) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._duplicateSessionEnded || this._sessionLoadFailed || !this._gameDataReady) return;
    if (this._isDuplicateSessionModalActive()) return;
    this._lastForegroundHiddenMs = hiddenMs;
    this._clearDisconnectResolveTimer();
    this._clearConnectionLostGraceTimer();
    this._reconnectAfterTabVisible = false;
    const restartHub = hiddenMs >= IOS_BACKGROUND_RESTART_MS;
    if (this._connectionLostUIOpen) {
      void this._showConnectionLostModal();
      void this._runConnectionCheck({
        restartHubFirst: restartHub,
        allowShowModal: true,
        foregroundReturn: true,
      });
      return;
    }
    void this._runConnectionCheck({
      restartHubFirst: restartHub,
      allowShowModal: true,
      foregroundReturn: true,
    });
  }

  _shouldKeepSpinDisabled() {
    return (
      this._duplicateSessionEnded ||
      this._sessionLoadFailed ||
      this._connectionLostUIOpen ||
      !this._gameDataReady ||
      this._sessionSyncInFlight
    );
  }

  _suspendPlayForConnectivity() {
    this._hubConnected = false;
    this.slotMachine?.hud?.setSpinEnabled(false);
    if (this._autoplaying) this.handleAutoplayToggle(false);
    if (this._isConnectionModalBlocking()) {
      this._dismissOverlappingModals('connection');
      return;
    }
    if (this._hadConnectionLoss || this._connLostGraceTimer != null) {
      this._dismissOverlappingModals(null);
    }
  }

  async _fetchSessionPayload({ retryHub = true } = {}) {
    const session = this._sessionParams ?? this._resolveSessionAuth();
    const state = await this.backend.pingSession({
      isDemo: this._isDemo,
      gameName: session.backendGameName || session.gameName,
      partnerId: session.partnerId,
    });
    const initial = this._unwrapInitialPayload(state) ?? state;
    if (this._readSessionBalance(initial) == null) {
      if (retryHub && this.backend.isConnected()) {
        await this.backend.restartConnection();
        return this._fetchSessionPayload({ retryHub: false });
      }
      throw new Error('Session ping missing balance');
    }
    return initial;
  }

  _isDuplicateSessionError(err) {
    const parts = [
      err?.message,
      err?.error,
      err?.code,
      err?.reason,
      typeof err === 'string' ? err : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return (
      parts.includes('duplicate') ||
      parts.includes('force disconnect') ||
      parts.includes('forcedisconnect') ||
      parts.includes('another session') ||
      parts.includes('another tab') ||
      parts.includes('other window') ||
      parts.includes('session ended') ||
      parts.includes('session replaced') ||
      parts.includes('session already') ||
      parts.includes('logged in elsewhere') ||
      parts.includes('multi session') ||
      parts.includes('multisession')
    );
  }

  _isLatestSessionInstance() {
    return isLatestSessionInstance(this._sessionInstanceId);
  }

  /**
   * @param {{ fromServer?: boolean }} [opts]
   *   `fromServer` — ForceDisconnect from hub (always end session; ignore local “latest tab” claim).
   */
  _showDuplicateSessionEndedModal({ fromServer = false } = {}) {
    if (this._initializing) return;
    if (!fromServer && this._isLatestSessionInstance()) return;
    this._reconnectAfterTabVisible = false;
    this._clearConnectionLostGraceTimer();
    this._clearDisconnectResolveTimer();
    this._stopConnectionAutoRetry();
    void this.backend.ceaseSession();
    this._forceDisconnectReceived = true;
    this._duplicateSessionEnded = true;
    this._connModalMode = 'duplicate';
    this._hubConnected = false;
    this._connectionLostUIOpen = true;
    this._prioritizeDuplicateSessionModal();
    this._dismissOverlappingModals('connection');
    this._syncConnectionLostModal();
    this._refreshQuickBets();
    this.slotMachine?.hud?.setSpinEnabled(false);
    if (this._autoplaying) this.handleAutoplayToggle(false);
  }

  _showConnectionLostAfterRequestFailure() {
    if (this._isDuplicateSessionModalActive()) {
      this._prioritizeDuplicateSessionModal();
      this._syncConnectionLostModal();
      return;
    }
    const alreadyOpen =
      this._connectionLostUIOpen &&
      this._connModalMode === 'lost' &&
      !this._isDuplicateSessionModalActive();
    this._dismissOverlappingModals('connection');
    this._connModalMode = 'lost';
    this._connectionLostUIOpen = true;
    this._hubConnected = false;
    this._hadConnectionLoss = true;
    this._refreshConnectionLostModalCopy();
    this._syncConnectionLostModal();
    this._refreshQuickBets();
    this.slotMachine?.hud?.setSpinEnabled(false);
    if (!alreadyOpen) this._startConnectionAutoRetry();
  }

  _clearConnectionLostAfterRequestSuccess(wasDisconnected) {
    if (this._isDuplicateSessionModalActive()) return;
    this._hubConnected = true;
    this._lastForegroundHiddenMs = 0;
    this._reconnectAfterTabVisible = false;
    this._stopConnectionAutoRetry();
    this._clearConnectionLostGraceTimer();
    this._connectionLostUIOpen = false;
    this._hideConnectionLostModal();
    this._refreshQuickBets();
    if (!this._spinCycleActive && !this._winCollectPending) {
      this.slotMachine?.hud?.setSpinEnabled(true);
    }
    if (wasDisconnected) this._showConnectionRestoredToast();
    this._hadConnectionLoss = false;
  }

  /**
   * @param {{ restartHubFirst?: boolean, pingOnly?: boolean, allowShowModal?: boolean, bypassGridPreserve?: boolean, foregroundReturn?: boolean }} [opts]
   * @returns {Promise<boolean>}
   */
  async _runConnectionCheck(opts = {}) {
    if (
      !this._connectionMonitoringEnabled ||
      this._isDuplicateSessionModalActive() ||
      this._sessionLoadFailed ||
      !this._gameDataReady
    ) {
      return false;
    }
    if (this._sessionSyncPromise) {
      return this._sessionSyncPromise;
    }
    const run = this._runConnectionCheckImpl(opts);
    this._sessionSyncPromise = run;
    try {
      return await run;
    } finally {
      if (this._sessionSyncPromise === run) {
        this._sessionSyncPromise = null;
      }
    }
  }

  /**
   * @param {{ restartHubFirst?: boolean, pingOnly?: boolean, allowShowModal?: boolean, bypassGridPreserve?: boolean, foregroundReturn?: boolean }} [opts]
   * @returns {Promise<boolean>}
   */
  async _runConnectionCheckImpl({
    restartHubFirst = false,
    pingOnly = false,
    allowShowModal = true,
    bypassGridPreserve = false,
    foregroundReturn = false,
  } = {}) {
    if (this._shouldPreserveVisibleGrid() && !bypassGridPreserve && !foregroundReturn) {
      this._scheduleConnectionCheck(CONNECTION_LOST_GRACE_MS);
      return false;
    }

    this._sessionSyncInFlight = true;
    const wasDisconnected = this._connectionLostUIOpen || this._hadConnectionLoss;
    if (allowShowModal) this._setConnectionModalReconnecting(true);
    try {
      if (!this._isOnline()) throw new Error('Browser offline');
      if (this.backend.isReconnecting()) {
        if (allowShowModal) {
          this._scheduleConnectionCheck(foregroundReturn ? 250 : 1000);
        }
        return false;
      }
      if (pingOnly) {
        if (!this.backend.isConnected()) return false;
      } else if (restartHubFirst && this.backend.isConnected()) {
        await this.backend.restartConnection();
      } else if (!this.backend.isConnected()) {
        await this.backend.connect(this._accessToken);
      }
      if (this._isDuplicateSessionModalActive()) return false;
      const initial = await this._fetchSessionPayload();
      if (this._isDuplicateSessionModalActive()) return false;
      this._applyServerSessionState(initial, {
        resumeInterruptedRound: true,
        preserveCurrentBet: true,
      });
      this._clearConnectionLostAfterRequestSuccess(wasDisconnected);
      return true;
    } catch (err) {
      console.warn('Session ping failed', err);
      if (this._isDuplicateSessionModalActive()) return false;
      if (err instanceof HubActionError && this._handleHubActionError(err)) {
        return false;
      }
      if (this._isDuplicateSessionError(err)) {
        this._showDuplicateSessionEndedModal({ fromServer: true });
        return false;
      }
      if (!allowShowModal || pingOnly) return false;
      try {
        if (restartHubFirst) {
          await this.backend.restartConnection();
        } else if (!this.backend.isConnected()) {
          await this.backend.connect(this._accessToken);
        }
        if (this._isDuplicateSessionModalActive()) return false;
        const initial = await this._fetchSessionPayload();
        if (this._isDuplicateSessionModalActive()) return false;
        this._applyServerSessionState(initial, {
          resumeInterruptedRound: true,
          preserveCurrentBet: true,
        });
        this._clearConnectionLostAfterRequestSuccess(wasDisconnected);
        return true;
      } catch (err2) {
        console.warn('Session ping retry failed', err2);
        if (this._isDuplicateSessionModalActive()) return false;
        if (
          (err instanceof HubActionError && this._handleHubActionError(err)) ||
          (err2 instanceof HubActionError && this._handleHubActionError(err2))
        ) {
          return false;
        }
        if (this._isDuplicateSessionError(err) || this._isDuplicateSessionError(err2)) {
          this._showDuplicateSessionEndedModal({ fromServer: true });
          return false;
        }
        if (!this._isDuplicateSessionModalActive()) {
          this._showConnectionLostAfterRequestFailure();
        }
        return false;
      }
    } finally {
      if (allowShowModal && !this._isDuplicateSessionModalActive()) {
        this._setConnectionModalReconnecting(false);
      }
      this._sessionSyncInFlight = false;
    }
  }

  async _resolvePostHubDisconnect() {
    if (
      !this._connectionMonitoringEnabled ||
      this._isDuplicateSessionModalActive() ||
      this._isPostBootHubGraceActive()
    ) {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this._reconnectAfterTabVisible = true;
      void this._runConnectionCheck({ allowShowModal: true });
      return;
    }
    if (this.backend.isConnected()) {
      await this._attemptSilentReconnect({ pingOnly: true });
      return;
    }
    if (this._reconnectAfterTabVisible) {
      await this._attemptSilentReconnect();
      return;
    }
    await this._runConnectionCheck({ allowShowModal: true });
  }

  _showConnectionRestoredToast() {
    if (typeof document === 'undefined') return;
    this._hideConnectionRestoredToast();
    const host = this._containerEl ?? document.body;
    const toast = showSystemToast({
      mountEl: host,
      message: this._t('CONNECTION_RESTORED'),
      durationMs: CONNECTION_RESTORED_TOAST_MS,
    });
    this._connRestoredToastHide = toast.hide;
  }

  _hideConnectionRestoredToast() {
    this._connRestoredToastHide?.();
    this._connRestoredToastHide = null;
  }

  _handleForceDisconnect() {
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) return;
    this._showDuplicateSessionEndedModal({ fromServer: true });
  }

  /**
   * Apply InitialState payload (bootstrap + reconnect recovery).
   * @param {Record<string, unknown>} initial
   * @param {{ resumeInterruptedRound?: boolean, preserveCurrentBet?: boolean }} [opts]
   */
  _applyServerSessionState(initial, { resumeInterruptedRound = false, preserveCurrentBet = false } = {}) {
    if (!this.slotMachine || !initial) return;
    const ui = this.slotMachine.hud;

    const paytable = initial?.paytable ?? initial?.Paytable ?? initial?.PayTable;
    if (Array.isArray(paytable) && paytable.length) {
      setSessionPaytable(paytable);
    }

    const quickBets = initial?.quickBets ?? initial?.QuickBets ?? initial?.betOptions ?? initial?.BetOptions;
    if (Array.isArray(quickBets) && quickBets.length) {
      const bets = quickBets.map((v) => Number(v)).filter((n) => Number.isFinite(n));
      if (bets.length) {
        gameConfig.minBet = Math.min(...bets);
        gameConfig.maxBet = Math.max(...bets);
        ui.setQuickBets([...bets]);
      }
    }

    const maxOdd = Number(initial?.maxOdd ?? initial?.MaxOdd);
    if (Number.isFinite(maxOdd) && maxOdd > 0) {
      gameConfig.maxOdd = maxOdd;
    }

    if (this._buyBonusEnabled) {
      const flag = initial?.buyBonusAvailable;
      this._setBuyBonusOffered(flag !== false);
    }

    const launchCurrencyCode = this._pickLaunchCurrencyCode();
    const serverCurrencyCode = this._readSessionCurrencyCode(initial);
    const initialCurrencyCode =
      launchCurrencyCode ?? serverCurrencyCode ?? (this._isDemo ? 'COIN' : this._currencyCode);
    const initialCurrencySymbol = launchCurrencyCode
      ? undefined
      : (initial.currencySymbol ?? initial.CurrencySymbol ?? undefined);
    this._setCurrency({
      currencyCode: initialCurrencyCode,
      currencyPrecision:
        initial.precision ??
        initial.Precision ??
        initial.currencyPrecision ??
        initial.CurrencyPrecision ??
        this._currencyPrecision,
      currencySymbol: initialCurrencySymbol,
    });

    const balance = this._readSessionBalance(initial);
    if (balance != null) {
      ui.setBalance(balance);
      this._updateCanvasBalance(balance);
    }

    const defaultBet = Number(
      initial?.defaultBetAmount ?? initial?.DefaultBetAmount ?? initial?.defaultBet ?? initial?.DefaultBet,
    );
    const wins = initial?.wins ?? initial?.Wins ?? initial?.additional?.wins ?? initial?.Additional?.Wins;
    const interrupted = resumeInterruptedRound ? this._resolveInterruptedRound(initial) : null;

    if (!interrupted) {
      const currentBet = Number(ui.betValue);
      let betAmount = null;
      if (preserveCurrentBet && Number.isFinite(currentBet) && currentBet > 0) {
        betAmount = Math.min(Math.max(currentBet, gameConfig.minBet), gameConfig.maxBet);
      } else if (Number.isFinite(defaultBet)) {
        betAmount = Math.min(Math.max(defaultBet, gameConfig.minBet), gameConfig.maxBet);
      }
      if (betAmount != null) {
        ui.setBet(betAmount);
        this._updateCanvasBetPrompt(betAmount);
      }
    }

    this._applySessionMatrixToReels(initial);

    if (interrupted) {
      this._resumePendingRoundFromServer(initial, interrupted.pendingWin, wins, {
        betAmount: interrupted.betAmount,
        snapshot: interrupted.snapshot,
      });
    } else if (!this._winCollectPending) {
      ui.setWin(0);
      this._updateCanvasWin(0);
    }

    this._refreshInfoPayoutAmounts();
  }

  /** True while reels / collect / celebration / payline cycling should keep the on-screen grid. */
  _shouldPreserveVisibleGrid() {
    const rc = this.slotMachine?.reelContainer;
    return !!(
      this._spinCycleActive ||
      this._spinning ||
      this._reelsSpinning ||
      this._isWinCollectDismissActive() ||
      rc?._winLineCycleActive
    );
  }

  /**
   * Server matrix plus expanding-wild overlay (matches post-spin display grid).
   * @param {Record<string, unknown>} initial
   * @returns {number[][] | null}
   */
  _getDisplayGridFromInitial(initial) {
    const matrix = initial?.matrix ?? initial?.Matrix;
    if (!Array.isArray(matrix)) return null;
    const baseGrid = this._normalizeMatrix(matrix);
    if (!baseGrid) return null;
    const expandingWild =
      initial?.additional?.expandingWild ??
      initial?.Additional?.ExpandingWild ??
      initial?.expandingWild ??
      initial?.ExpandingWild;
    const wins = initial?.additional?.wins ?? initial?.Additional?.Wins;
    return this._applyExpandingWild(
      baseGrid,
      this._filterExpandingWildToWinLines(expandingWild, wins),
    );
  }

  /**
   * @param {Record<string, unknown>} initial
   * @param {{ force?: boolean }} [opts] When true, always repaint (interrupted-round resume).
   */
  /*
  _applyOpeningPhotoLayoutIfEnabled() {
    if (!isOpeningPhotoLayoutEnabled()) return;
    this._applyOpeningPhotoLayout();
  }

  _applyOpeningPhotoLayout({ force = false } = {}) {
    if (!force && !isOpeningPhotoLayoutEnabled()) return;
    const rc = this.slotMachine?.reelContainer;
    if (!rc) return;

    rc.prepareForNewRound?.();
    rc.setGridInstant(buildBugsWildsPhotoBaseMatrix());
    rc.showAllVisibleSymbolsSharp();
    rc.showExpandingWildColumnsHold(buildBugsWildsPhotoExpandingWild());
    this._applyOpeningPhotoHud();
  }

  _applyOpeningPhotoHud() {
    const hud = getOpeningPhotoHudValues();
    const ui = this.slotMachine?.hud;
    if (!hud || !ui) return;

    this._setCurrency({
      currencyCode: hud.currencyCode,
      currencySymbol: hud.currencySymbol,
      currencyPrecision: hud.currencyPrecision,
    });

    const bets = [...(ui.quickBets ?? [])];
    if (!bets.includes(hud.bet)) {
      bets.push(hud.bet);
      bets.sort((a, b) => a - b);
      ui.setQuickBets(bets);
    }

    ui.setBet(hud.bet);
    ui.setBalance(hud.balance);
    ui.setWin(hud.maxWin);

    this._updateCanvasBalance(hud.balance);
    this._updateCanvasWin(hud.maxWin);
    this._refreshQuickBets();
    this._refreshInfoPayoutAmounts();
  }
  */

  _applySessionMatrixToReels(initial, { force = false } = {}) {
    if (!this.slotMachine) return;
    if (!force && this._shouldPreserveVisibleGrid()) return;
    // After bootstrap, keep what the player sees — server ping matrix is not the last spin grid.
    if (!force && this._gameDataReady) return;
    // Never repaint reel cards while win-line animations are playing — this is the only safe
    // guard that covers the tab-switch foreground reconnect path even when force=true.
    if (this.slotMachine.reelContainer?._winLineCycleActive) return;
    if (this._shouldPreserveVisibleGrid()) return;
    const displayGrid = this._getDisplayGridFromInitial(initial);
    if (!displayGrid) return;
    const rc = this.slotMachine.reelContainer;
    rc.setGridInstant(displayGrid);
    const expandingWild =
      initial?.additional?.expandingWild ??
      initial?.Additional?.ExpandingWild ??
      initial?.expandingWild ??
      initial?.ExpandingWild;
    const wins = initial?.additional?.wins ?? initial?.Additional?.Wins;
    const multipliers =
      initial?.additional?.expandingWildMultipliers ??
      initial?.additional?.exwm ??
      initial?.Additional?.ExpandingWildMultipliers;
    rc.setWildReelMultipliers(
      multipliers,
      this._filterExpandingWildToWinLines(expandingWild, wins),
    );
  }

  _isHubSessionPlayable() {
    return this._gameDataReady && !this._connectionLostUIOpen && !this._duplicateSessionEnded;
  }

  /** Verify hub + InitialState before accepting a bet (iOS resume / stale socket). */
  async _ensureSessionReadyForBet() {
    const staleAfterBackground = this._lastForegroundHiddenMs >= IOS_BACKGROUND_RESTART_MS;
    const needsRefresh =
      staleAfterBackground ||
      this._hadConnectionLoss ||
      this._reconnectAfterTabVisible ||
      !this.backend.isConnected() ||
      this.backend.isReconnecting() ||
      !!this._sessionSyncPromise;
    if (!needsRefresh) return true;
    return this._runConnectionCheck({
      restartHubFirst: staleAfterBackground,
      allowShowModal: true,
    });
  }

  _notifyBetBlocked() {
    if (this._duplicateSessionEnded || this._connectionLostUIOpen) return;
    sound.play('uiError');
    if (!this._sessionSyncInFlight && !this._sessionSyncPromise) {
      void this._runConnectionCheck({ allowShowModal: true });
    }
  }

  /**
   * Map hub `actionStatus` refusals to player-facing feedback (MessagePack contract).
   * @param {unknown} err
   * @returns {boolean} true when the error was handled as a hub action refusal
   */
  _handleHubActionError(err) {
    const status =
      err instanceof HubActionError
        ? Number(err.actionStatus)
        : Number(err?.actionStatus);
    if (!Number.isFinite(status) || status === ACTION_STATUS.SUCCESS) return false;

    if (status === ACTION_STATUS.TOKEN_REJECTED) {
      this._showDuplicateSessionEndedModal({ fromServer: true });
      return true;
    }
    if (status === ACTION_STATUS.GAME_UNAVAILABLE) {
      this._sessionLoadFailed = true;
      this._connectionLostUIOpen = true;
      this._connModalMode = 'lost';
      this._refreshConnectionLostModalCopy();
      this._syncConnectionLostModal();
      sound.play('uiError');
      return true;
    }

    const keyByStatus = {
      [ACTION_STATUS.REJECTED]: 'HUB_ACTION_REJECTED',
      [ACTION_STATUS.INSUFFICIENT_BALANCE]: 'HUB_ACTION_INSUFFICIENT_BALANCE',
      [ACTION_STATUS.SERVER_FAILURE]: 'HUB_ACTION_SERVER_FAILURE',
    };
    const key = keyByStatus[status] ?? 'HUB_ACTION_REJECTED';
    sound.play('uiError');
    showSystemToast({
      message: this._t(key),
      durationMs: 3500,
    });
    return true;
  }

  /** Gold gradient strip used on settings / game modals. */
  _getPanelBorderGradientTexture() {
    if (this._panelBorderGradTex) return this._panelBorderGradTex;
    const gradCanvas = document.createElement('canvas');
    gradCanvas.width = 512;
    gradCanvas.height = 2;
    const gradCtx = gradCanvas.getContext('2d');
    if (gradCtx) {
      const g = gradCtx.createLinearGradient(0, 0, gradCanvas.width, 0);
      g.addColorStop(0, themeHex(T.modalBorderDark));
      g.addColorStop(0.25, themeHex(T.modalBorderGoldLo));
      g.addColorStop(0.5, themeHex(T.modalBorderGoldHi));
      g.addColorStop(0.75, themeHex(T.modalAccentHi));
      g.addColorStop(1, themeHex(T.modalBorderDark));
      gradCtx.fillStyle = g;
      gradCtx.fillRect(0, 0, gradCanvas.width, gradCanvas.height);
    }
    this._panelBorderGradTex = Texture.from(gradCanvas);
    return this._panelBorderGradTex;
  }

  _syncConnectionLostModal() {
    if (this._prioritizeDuplicateSessionModal()) {
      this._dismissOverlappingModals('connection');
      void this._showConnectionLostModal();
      return;
    }
    if (this._shouldShowConnectionLostModal()) {
      this._dismissOverlappingModals('connection');
      void this._showConnectionLostModal();
      return;
    }
    this._hideConnectionLostModal();
  }

  _showConnectionLostModal() {
    if (!this._connectionLostShowPromise) {
      this._connectionLostShowPromise = this._showConnectionLostModalImpl().finally(() => {
        this._connectionLostShowPromise = null;
      });
    }
    return this._connectionLostShowPromise;
  }

  async _showConnectionLostModalImpl() {
    if (!this._shouldShowConnectionLostModal() && !this._isDuplicateSessionModalActive()) {
      return;
    }
    this._dismissOverlappingModals('connection');
    if (!this._shouldShowConnectionLostModal() && !this._isDuplicateSessionModalActive()) {
      return;
    }
    this._dismissOverlappingModals('connection');
    this._prioritizeDuplicateSessionModal();
    const panel = this._ensureSystemMessagePanel();
    this._refreshConnectionLostModalCopy();
    this._syncConnectionModalStatus();
    panel.show();
  }

  _hideConnectionLostModal() {
    this._stopConnectionAutoRetry();
    this._systemMessagePanel?.hide();
  }

  _clearCanvasQuickBetStrip() {
    const group = this._canvasQbGroup;
    if (!group) return;
    group.removeChildren();
    this._canvasQbScrollWrapper = null;
    this._canvasQbBetCount = 0;
  }

  _updateQbArrowVisibility() {
    const show = this._shouldShowQuickBets();
    const arL = this._qbArrowLeft;
    const arR = this._qbArrowRight;
    if (!arL && !arR) return;
    if (!show) {
      if (arL) arL.visible = false;
      if (arR) arR.visible = false;
      return;
    }
    if (this._isPortraitStackedHud()) {
      if (arL) arL.visible = false;
      if (arR) arR.visible = false;
      return;
    }
    if (arL) arL.visible = true;
    if (arR) arR.visible = true;
  }

  _handleHubReconnecting() {
    if (!this._connectionMonitoringEnabled || this._initializing) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) {
      return;
    }
    this._hadConnectionLoss = true;
    this._suspendPlayForConnectivity();
    if (this._connectionLostUIOpen) {
      this._setConnectionModalReconnecting(true);
    }
  }

  _handleHubReconnected() {
    if (!this._connectionMonitoringEnabled || this._initializing) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) {
      return;
    }
    this._setConnectionModalReconnecting(false);
    void this._attemptSilentReconnect({ pingOnly: true });
  }

  _handleHubDisconnected() {
    if (!this._connectionMonitoringEnabled || this._initializing) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) {
      return;
    }
    this._hadConnectionLoss = true;
    this._suspendPlayForConnectivity();
    if (typeof document !== 'undefined' && document.hidden) {
      this._reconnectAfterTabVisible = true;
      void this._runConnectionCheck({ allowShowModal: true });
      return;
    }
    this._scheduleDisconnectResolution(FORCE_DISCONNECT_WAIT_MS);
  }

  _refreshQuickBets() {
    if (!this.slotMachine) return;
    if (this._isConnectionModalBlocking()) {
      if (this._shouldShowConnectionLostModal()) {
        this._syncConnectionLostModal();
      }
      this._clearCanvasQuickBetStrip();
      this._updateQbArrowVisibility();
      return;
    }
    this._hideConnectionLostModal();
    const ui = this.slotMachine.hud;
    const bets = ui.quickBets ?? [];
    const current = ui.betValue;
    this._buildCanvasQuickBets(bets, current);
    this._updateQbArrowVisibility();
  }

  _updateCanvasBalance(balance) {
    const n = Number(balance);
    const formatted = this._formatCurrencyDigits(n);
    if (this._canvasBalanceText) {
      this._canvasBalanceText.text = formatted;
    }
    if (this._portraitBalanceValueText) {
      this._portraitBalanceValueText.text = formatted;
    }
    this._canvasBalancePanelRelayout?.();
    this._relayoutPortraitBalanceStack?.();
  }

  /**
   * Wallet after stake + win for HUD. Some spin payloads return post-stake balance
   * without the win credited yet — after optimistic debit that looks like a no-op.
   * @param {number|null|undefined} serverBalance
   * @param {number|null|undefined} balanceBeforeBet
   * @param {number} stake
   * @param {number} [totalWin]
   * @returns {number|null}
   */
  _spinSettledBalance(serverBalance, balanceBeforeBet, stake, totalWin = 0) {
    const server = Number(serverBalance);
    const before = Number(balanceBeforeBet);
    const bet = Number(stake);
    const win = Math.max(0, Number(totalWin) || 0);
    if (Number.isFinite(before) && Number.isFinite(bet) && bet >= 0) {
      const settled = Math.max(0, before - bet + win);
      if (!Number.isFinite(server)) return settled;
      if (Math.abs(server - settled) <= 0.0001) return server;
      /* Server still at post-stake while a win is showing — credit the win locally. */
      if (win > 0 && Math.abs(server - (before - bet)) <= 0.0001) return settled;
      return server;
    }
    return Number.isFinite(server) ? server : null;
  }

  _applySpinBalance(balance) {
    const n = Number(balance);
    if (!Number.isFinite(n) || !this.slotMachine) return;
    this.slotMachine.hud.setBalance(n);
    this._updateCanvasBalance(n);
  }

  _updateCanvasWin(win) {
    const n = Number(win);
    const formatted = this._formatCurrencyDigits(n);
    if (this._canvasWinText) {
      this._canvasWinText.text = formatted;
    }
    if (this._portraitWinValueText) {
      this._portraitWinValueText.text = formatted;
    }
    this._canvasWinPanelRelayout?.();
    this._relayoutPortraitWinStack?.();
  }

  _stopWinRollup() {
    if (this._winRollupRaf != null) {
      cancelAnimationFrame(this._winRollupRaf);
      this._winRollupRaf = null;
    }
    this._winRollupTarget = null;
    if (this._winRollupResolve) {
      const r = this._winRollupResolve;
      this._winRollupResolve = null;
      r();
    }
  }

  /** Skip count-up; show final win on the canvas (used by collect tap). */
  _skipWinRollupToFinal() {
    if (this._winRollupRaf != null) {
      cancelAnimationFrame(this._winRollupRaf);
      this._winRollupRaf = null;
    }
    if (this._winRollupTarget != null) {
      this._updateCanvasWin(this._winRollupTarget);
      this._winRollupTarget = null;
    }
    if (this._winRollupResolve) {
      const r = this._winRollupResolve;
      this._winRollupResolve = null;
      r();
    }
  }

  /**
   * @param {number} targetWin
   * @param {number} [stakeAmount] Spin stake; when set, rollup duration follows payout multiple (win/stake).
   * @param {{ immediate?: boolean, fixedDurationSec?: number, celebrationMultiple?: number }} [opts] Celebration: `celebrationMultiple` + `fixedDurationSec` sync count-up with popup tiers. Regular wins: `lastWinRollupDurationSec`. `immediate` = full amount at once.
   */
  _animateLastWinRollup(targetWin, stakeAmount, opts = {}) {
    this._stopWinRollup();
    const target = Math.max(0, Number(targetWin) || 0);
    this._winRollupTarget = target;

    if (opts.immediate) {
      this._updateCanvasWin(target);
      this._winRollupTarget = null;
      return Promise.resolve();
    }

    const stake = Math.max(0, Number(stakeAmount) || 0);
    const celebrationMultiple = Number(opts.celebrationMultiple);
    const timeline =
      Number.isFinite(celebrationMultiple) && celebrationMultiple > 0 && stake > 0
        ? computeCelebrationAmountTimeline(
            this.spineAssets,
            celebrationMultiple,
            stake,
            target,
          )
        : null;

    let durationSec;
    if (timeline) {
      durationSec = Math.max(0.05, timeline.totalSec);
    } else if (opts.fixedDurationSec != null && Number.isFinite(opts.fixedDurationSec)) {
      durationSec = Math.max(0.05, Number(opts.fixedDurationSec));
    } else {
      const raw = Number(gameConfig.lastWinRollupDurationSec);
      durationSec = Math.max(0.05, Number.isFinite(raw) && raw > 0 ? raw : 1);
    }
    const effectiveRate = target / durationSec;
    if (target <= 0) {
      this._updateCanvasWin(0);
      this._winRollupTarget = null;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this._winRollupResolve = resolve;
      const startTs = performance.now();
      const tick = () => {
        const elapsed = (performance.now() - startTs) / 1000;
        const shown = timeline
          ? celebrationAmountAtElapsed(timeline, elapsed)
          : Math.min(target, elapsed * effectiveRate);
        this._updateCanvasWin(shown);
        const done = timeline
          ? elapsed >= timeline.totalSec || shown >= target
          : shown >= target;
        if (done) {
          this._updateCanvasWin(target);
          this._winRollupRaf = null;
          this._winRollupTarget = null;
          const r = this._winRollupResolve;
          this._winRollupResolve = null;
          r();
          return;
        }
        this._winRollupRaf = requestAnimationFrame(tick);
      };
      this._winRollupRaf = requestAnimationFrame(tick);
    });
  }

  _updateCanvasBetPrompt(_bet) {
    if (this._winLinePromptActive || this.slotMachine?.reelContainer?._winLineCycleActive) return;
    const prompt = this._tu('PLEASE_PLACE_YOUR_BETS');
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.text = prompt;
    }
    if (this._portraitTopPromptText) {
      this._portraitTopPromptText.text = this._twoLineText('PLEASE_PLACE_YOUR_BET');
    }
  }

  /** Shown under the reels while they spin (replaces "place your bets"). */
  _showGoodLuckPrompt() {
    this._winLinePromptActive = false;
    const text = this._tu('GOOD_LUCK');
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.text = text;
    }
    if (this._portraitTopPromptText) {
      this._portraitTopPromptText.text = this._twoLineText('GOOD_LUCK');
    }
  }

  /**
   * @param {{ paylineIndex?: number | null, lineNo?: number, payout?: number, displayMode?: string } | null | undefined} hit
   */
  _showWinLinePrompt(hit) {
    if (!hit) return;
    const amount = this._formatCurrencyAmount(hit.payout ?? 0);
    let canvasText;
    let portraitText;
    if (hit.displayMode === 'symbols') {
      canvasText = this._t('WIN_SYMBOL_PAYOUT', { amount });
      portraitText = `${this._tu('WIN')}\n${amount}`;
    } else {
      const line = Number.isFinite(hit.lineNo)
        ? hit.lineNo
        : (hit.paylineIndex != null ? hit.paylineIndex + 1 : 0);
      canvasText = this._t('WIN_LINE_PAYOUT', { line, amount });
      portraitText = `${this._tu('LINE')} ${line}\n${amount}`;
    }
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.text = canvasText;
    }
    if (this._portraitTopPromptText) {
      this._portraitTopPromptText.text = portraitText;
    }
    this._winLinePromptActive = true;
    this._setBetPromptVisible(true);
  }

  /**
   * @param {import('./logic/winEvaluator.js').WinResult['hits']} hitsSorted
   * @param {number} totalWinAmount
   * @param {{ suppressLineSound?: boolean }} [opts]
   */
  _beginPostSpinWin(hitsSorted, totalWinAmount, opts = {}) {
    if (!hitsSorted.length) return;
    this.slotMachine.beginPostSpinWin(hitsSorted, totalWinAmount, (hit) => {
      this._showWinLinePrompt(hit);
    }, opts);
  }

  /** Single source of truth for prompt visibility across desktop and portrait-mobile top bar. */
  _setBetPromptVisible(visible) {
    this._betPromptVisible = !!visible;
    const stacked = this._isPortraitStackedHud();
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.visible = this._betPromptVisible && !stacked;
    }
    if (this._portraitTopPromptText) {
      this._portraitTopPromptText.visible = this._betPromptVisible && stacked;
    }
  }

  _toggleSettingsPanel() {
    if (!this._settingsRoot) return;
    const willOpen = !this._settingsRoot.visible;
    if (willOpen) {
      if (this._isBlockingModalOpen('settings')) return;
      this._dismissOverlappingModals('settings');
      this._syncSettingsModalForViewport();
      if (this._settingsModalActiveTab === 'autoplay') {
        this._refreshAutoplaySettingsView();
      } else if (this._settingsModalActiveTab === 'info') {
        this._refreshInfoPayoutAmounts();
      }
    }
    this._settingsRoot.visible = willOpen;
    sound.play(willOpen ? 'uiPopupOpen' : 'uiPopupClose');
  }

  handleAutoplayToggle(on) {
    this._autoplaying = on;
    if (on) {
      this._autoplayStartBalance = Number(this.slotMachine?.hud?.balanceValue);
      if (!Number.isFinite(this._autoplayStartBalance)) this._autoplayStartBalance = null;
      this._autoplayLastSingleWin = 0;
      this._runAutoplayLoop();
    } else {
      this._autoplayRoundsRemaining = null;
      this._autoplayStartBalance = null;
      this._autoplayLastSingleWin = 0;
      if (this._autoplayTimer != null) {
        clearTimeout(this._autoplayTimer);
        this._autoplayTimer = null;
      }
      /* handleSpin() finally still sees autoplay on and sets stop; restore start when idle. */
      if (
        this._spinButtonSprite &&
        !this._spinning &&
        !this._spinCycleActive &&
        !this._winCollectPending
      ) {
        this._setSpinButtonTexture(this._spinStartTex);
      }
    }
    this._refreshSpinAutoplayRemainLabel();
  }

  async _runAutoplayLoop() {
    while (this._autoplaying) {
      if (this._spinCycleActive || this._initializing) {
        await new Promise((r) => { this._autoplayTimer = setTimeout(r, 150); });
        continue;
      }
      /* Decrement BEFORE the spin so the spin-button counter reflects
       * "spins left after this one" — e.g. preset 10 displays 9, 8, …, 0. */
      if (
        this._autoplayRoundsRemaining != null &&
        Number.isFinite(this._autoplayRoundsRemaining)
      ) {
        this._autoplayRoundsRemaining -= 1;
      }
      if (!this._spinning && this._spinButtonSprite) {
        this._setSpinButtonTexture(this._spinStopTex);
        this._refreshSpinAutoplayRemainLabel();
      }
      await this.handleSpin();
      if (!this._autoplaying) break;
      if (this._shouldStopAutoplayForThresholds()) {
        this.handleAutoplayToggle(false);
        break;
      }
      if (
        this._autoplayRoundsRemaining != null &&
        Number.isFinite(this._autoplayRoundsRemaining)
      ) {
        this._refreshSpinAutoplayRemainLabel();
        if (this._autoplayRoundsRemaining <= 0) {
          this.handleAutoplayToggle(false);
          break;
        }
      }
      if (this._autoplaying) {
        const tier = Math.min(3, Math.max(1, this._betSpeedTier || 1));
        const delayMs = this._resolveAutoplayInterSpinDelayMs(tier);
        await new Promise((r) => { this._autoplayTimer = setTimeout(r, delayMs); });
      }
    }
  }

  /**
   * Evaluate the three Stop-Autoplay thresholds set in the autoplay-settings modal.
   * A threshold of 0 is treated as disabled (matches the design’s default = 0.00 FUN).
   * Compares against the most recent spin’s win and the balance recorded when autoplay started.
   */
  _shouldStopAutoplayForThresholds() {
    const ui = this.slotMachine?.hud;
    if (!ui) return false;
    const balanceNow = Number(ui.balanceValue);
    if (!Number.isFinite(balanceNow)) return false;
    const start = Number(this._autoplayStartBalance);
    const lastWin = Number(this._autoplayLastSingleWin) || 0;

    const decBy = Number(this._autoplayStopOnBalanceDecreaseBy) || 0;
    if (decBy > 0 && Number.isFinite(start) && start - balanceNow >= decBy) return true;

    const incBy = Number(this._autoplayStopOnBalanceIncreaseBy) || 0;
    if (incBy > 0 && Number.isFinite(start) && balanceNow - start >= incBy) return true;

    const winLimit = Number(this._autoplayStopOnSingleWinExceeds) || 0;
    if (winLimit > 0 && lastWin >= winLimit) return true;

    return false;
  }

  _syncBetSpeedArrows() {
    const sprites = this._betSpeedArrowSprites;
    if (!sprites?.length) return;
    const tier = Math.min(3, Math.max(1, this._betSpeedTier || 1));
    const active = this._betSpeedActiveTex;
    const inactive = this._betSpeedInactiveTex;
    if (!active || !inactive) return;
    for (let i = 0; i < 3; i++) {
      sprites[i].texture = i < tier ? active : inactive;
    }
  }

  handleMaxBet() {
    if (!this.slotMachine) return;
    sound.play('uiClick');
    this.slotMachine.hud.setBet(gameConfig.maxBet);
    this._updateCanvasBetPrompt(gameConfig.maxBet);
    this._refreshQuickBets();
  }

  _setSpinButtonHoldProgress(progress) {
    const root = this._spinButtonRoot;
    if (!root) return;
    const t = Math.max(0, Math.min(1, progress));
    const eased = 1 - (1 - t) ** 3;
    const scale = this._spinButtonBaseScale + (this._spinHoldMaxScale - this._spinButtonBaseScale) * eased;
    root.scale.set(scale);
  }

  _resetSpinButtonHoldVisual() {
    const root = this._spinButtonRoot;
    if (!root) return;
    root.scale.set(this._spinButtonBaseScale);
  }

  _refreshSpinAutoplayRemainLabel() {
    const label = this._spinAutoplayRemainText;
    const img = this._spinButtonSprite;
    const stopTex = this._spinStopTex;
    if (!label || !img || !stopTex) return;
    const show = !!this._autoplaying && img.texture === stopTex;
    label.visible = show;
    if (!show) return;
    const r = this._autoplayRoundsRemaining;
    const mobile = this._isPortraitMobileViewport();
    const d = SPIN_AUTOPLAY_REMAIN_DESKTOP;

    if (mobile) {
      const spinW = Math.max(1, img.width);
      const k = (spinW / SPIN_BTN_REF_SIZE) * 1.14;
      label.anchor.set(0.5, 0.5);
      const nudge = SPIN_AUTOPLAY_REMAIN_MOBILE_NUDGE;
      label.x = nudge.x;
      label.y = nudge.y;
      if (r != null && Number.isFinite(r)) {
        label.text = String(r);
        label.style.fontSize = Math.round(d.fontSize * k);
      } else {
        label.text = '∞';
        label.style.fontSize = Math.round(d.fontInf * k);
      }
      return;
    }

    label.anchor.set(0.5, 0.5);
    label.x = d.x;
    label.y = d.y;
    if (r != null && Number.isFinite(r)) {
      label.text = String(r);
      label.style.fontSize = d.fontSize;
    } else {
      label.text = '∞';
      label.style.fontSize = d.fontInf;
    }
  }

  _stopSpinHoldTracking() {
    if (this._spinHoldTimer != null) {
      clearTimeout(this._spinHoldTimer);
      this._spinHoldTimer = null;
    }
    if (this._spinHoldRaf != null) {
      cancelAnimationFrame(this._spinHoldRaf);
      this._spinHoldRaf = null;
    }
    this._detachSpinHoldWindowReleaseGuard();
    const canvas = this.app?.canvas;
    const capPid = this._spinHoldPointerId;
    if (
      canvas &&
      this._spinHoldPointerCaptureActive &&
      capPid != null &&
      typeof canvas.releasePointerCapture === 'function'
    ) {
      try {
        if (typeof canvas.hasPointerCapture === 'function' && canvas.hasPointerCapture(capPid)) {
          canvas.releasePointerCapture(capPid);
        }
      } catch {
        /* ignore */
      }
    }
    this._spinHoldPointerCaptureActive = false;
    this._spinHoldInProgress = false;
    this._spinHoldStartTs = 0;
    this._spinHoldPointerId = null;
  }

  _activateTurboFromHold() {
    if (!this._spinHoldInProgress || this._spinHoldTriggeredTurbo) return;
    this._spinHoldTriggeredTurbo = true;
    this._turboHoldActive = true;
    this._resetSpinButtonHoldVisual();
    /* Autoplay loop owns the first spin — do not call handleSpin() here too. */
    this.handleAutoplayToggle(true);
  }

  /** Turbo / hand-stop: skip cruise, play shortened decel + bounce landing. */
  _turboLandStopOpts() {
    const scale = gameConfig.turboLandTimeScale;
    const staggerFrames = Math.max(0, Math.floor(Number(gameConfig.handStopStaggerFrames) || 0));
    return {
      instant: false,
      landTimeScale: Number.isFinite(scale) && scale > 0 ? scale : 1.85,
      staggerFrames,
    };
  }

  /**
   * Drive GSAP from the Pixi ticker (same pattern as Blazing Sevens configuratedStage).
   * Keeps reel timelines in lockstep with WebGL presents when maxFPS is capped.
   */
  _bindGsapToPixiTicker() {
    this._unbindGsapFromPixiTicker();
    if (!this.app?.ticker) return;
    gsap.ticker.remove(gsap.updateRoot);
    this._gsapPixiTick = () => {
      gsap.updateRoot(performance.now() / 1000);
    };
    this.app.ticker.add(this._gsapPixiTick);
  }

  _unbindGsapFromPixiTicker() {
    if (this._gsapPixiTick && this.app?.ticker) {
      this.app.ticker.remove(this._gsapPixiTick);
    }
    this._gsapPixiTick = null;
    // Restore default GSAP clock if Pixi is gone / rebound later.
    gsap.ticker.remove(gsap.updateRoot);
    gsap.ticker.add(gsap.updateRoot);
  }

  /**
   * Brief pause after Quick Spin lands before expanding-wild / result flow (ms).
   * Keep tiny so manual re-spins feel instant.
   * @returns {number}
   */
  _resolveTurboResultHoldMs() {
    const base =
      Number.isFinite(gameConfig.turboSnapResultHoldMs) && gameConfig.turboSnapResultHoldMs >= 0
        ? gameConfig.turboSnapResultHoldMs
        : 40;
    const p = this._rendererProfile ?? getPixiRendererProfile();
    const androidExtra =
      p.isAndroid || p.lowMemory
        ? Math.max(0, Number(gameConfig.androidTurboResultHoldMs) || 0)
        : 0;
    return base + androidExtra;
  }

  /**
   * @param {number} tier
   * @returns {number}
   */
  _resolveAutoplayInterSpinDelayMs(tier) {
    if (this._turboHoldActive) {
      return Math.max(60, Number(gameConfig.turboHoldAutoplayDelayMs) || 100);
    }
    if (tier >= 3) {
      return Math.max(50, Number(gameConfig.turboAutoplayDelayMs) || 80);
    }
    if (tier >= 2) return 280;
    return 500;
  }

  /**
   * Manual re-spin cooldown after a cycle — short so Quick Spin taps feel responsive.
   * @param {boolean} [_usedTurbo]
   * @returns {number}
   */
  _resolvePostSpinCooldownMs(_usedTurbo) {
    const p = this._rendererProfile ?? getPixiRendererProfile();
    if (p.isAndroid || p.lowMemory) {
      return Math.max(
        40,
        Number(gameConfig.androidPostSpinRespinCooldownMs) || 60,
        Number(gameConfig.postSpinRespinCooldownMs) || 50,
      );
    }
    return Math.max(30, Number(gameConfig.postSpinRespinCooldownMs) || 50);
  }

  /** Same as tapping the main stop control while reels are in motion. */
  _requestStopSpin() {
    const willTakeEffect = this._reelsSpinning && !this._stopRequested;
    this._stopRequested = true;
    this.handleAutoplayToggle(false);
    /* Only forward to reels while they are actually moving — otherwise the container
     * stop flag sticks until the next spinToGrid and forces an instant fastStop snap. */
    if (this._reelsSpinning) {
      this.slotMachine?.reelContainer?.requestStop(this._turboLandStopOpts());
    }
    if (willTakeEffect) sound.play('spinStop');
  }

  /** Tear down win lines / celebration / orphan reel tweens before the next spin animates. */
  async _teardownWinVisualsBeforeSpin() {
    this._stopWinRollup();
    this._winLinePromptActive = false;
    const rc = this.slotMachine?.reelContainer;
    rc?.stopWinLineCycle();
    rc?.setWinLineSoundSuppressed(false);
    this.slotMachine?.winFloater?.reset?.();
    this._cabinetJuice?.clearFxParticles?.();
    if (this._isWinCelebrationVisible()) {
      await (this.slotMachine?.skipWinCelebration?.() ?? Promise.resolve());
    }
    rc?._lowerAllLiftedSymbols?.();
    if (rc) {
      for (const reel of rc.reels) {
        reel.cancelActiveSpinAnimation?.();
        reel.clearStripParticles?.();
      }
    }
  }

  /**
   * End the collect / rollup phase after a win so the next spin can run full reel motion.
   * Keeps payline cycling + win floater until the next spin (torn down in
   * {@link #_teardownWinVisualsBeforeSpin} / prepareForNewRound).
   */
  async _completeWinCollectPhase() {
    this._stopWinRollup();
    /* Do NOT stopWinLineCycle / reset floater / lower lifted symbols here —
     * that made paylines vanish the instant collect finished. */
    if (this._isWinCelebrationVisible()) {
      await (this.slotMachine?.skipWinCelebration?.() ?? Promise.resolve());
    }
    this._winCollectPending = false;
    this._winCelebrationShownAt = null;
    this._resumeRoundSkipCollect = false;
    if (this._resolveWinCollectUser) {
      const r = this._resolveWinCollectUser;
      this._resolveWinCollectUser = null;
      r();
    }
    this._clearRoundStatus();
  }

  /** Same as tapping the main button when it shows collect (skip rollup + continue spin flow). */
  async _performWinCollectTap() {
    if (!this._isWinCollectDismissActive()) return;
    if (this._winCollectHandling) return;
    if (performance.now() < this._winCollectTapLockUntil) return;

    if (this.slotMachine?.isWinCelebrationInBuildup?.()) {
      if (this._isWinCelebrationSkipGuarded()) return;
      sound.play('uiClick');
      this._cabinetJuice?.playCollectBurst?.();
      this._skipWinRollupToFinal();
      this.slotMachine.skipWinCelebrationToHold();
      this._winCollectTapLockUntil = performance.now() + 150;
      return;
    }

    if (this._isWinCelebrationSkipGuarded() && !this._isWinCelebrationVisible()) return;
    /* The original tap's `skipWinCelebration()` is still awaiting the tier `endXxxWin` clip;
     * `skip()` is idempotent so additional taps must NOT abort or restart it. */
    this._winCollectHandling = true;
    this._winCollectPending = false;
    try {
      sound.play('uiClick');
      this._cabinetJuice?.playCollectBurst?.();
      this._skipWinRollupToFinal();
      await (this.slotMachine?.skipWinCelebration?.() ?? Promise.resolve());
      this._clearRoundStatus();
      if (this._resolveWinCollectUser) {
        const r = this._resolveWinCollectUser;
        this._resolveWinCollectUser = null;
        r();
      }
      this._winCelebrationShownAt = null;
    } finally {
      this._winCollectHandling = false;
      if (!this._spinCycleActive && !this._shouldKeepSpinDisabled()) {
        this.slotMachine?.hud?.setSpinEnabled(true);
        this._updateCanvasBetPrompt();
        this._setBetPromptVisible(true);
      }
    }
  }

  /**
   * Canvas quick-bet chips: set bet + start spin when idle; while spinning, act as stop.
   */
  _handleCanvasQuickBet(amount) {
    if (this._isWinCollectDismissActive()) {
      this._pendingSpinAfterWinClose = true;
      void this._performWinCollectTap();
      return;
    }
    if (this._reelsSpinning) {
      this._requestStopSpin();
      return;
    }
    this.slotMachine?.hud.setBet(amount);
    this._updateCanvasBetPrompt(amount);
    if (this._autoplaying) this.handleAutoplayToggle(false);
    const now = performance.now();
    if (now < this._spinToggleLockUntilTs || now < this._respinBlockUntilTs) {
      this._refreshQuickBets();
      return;
    }
    this._spinToggleLockUntilTs = now + this._spinToggleCooldownMs;
    void this.handleSpin();
    this._refreshQuickBets();
  }

  async handleSpin() {
    const armed = this._buyBonusEnabled ? this._buyBonusArmed : null;
    if (this._spinCycleActive || this._initializing || !this.slotMachine) return;
    if (this._connectionLostUIOpen || this._duplicateSessionEnded) return;
    this._spinCycleActive = true;
    this._spinning = true;
    this._stopRequested = false;
    this.slotMachine?.reelContainer?.clearStopRequest();
    const ui = this.slotMachine.hud;
    /** Pre-spin wallet — restore on failure after optimistic debit. */
    let balanceBeforeBet = null;
    try {
      const sessionOk = await this._ensureSessionReadyForBet();
      if (!sessionOk) {
        this._notifyBetBlocked();
        return;
      }
    const amount = ui.betValue;
    const stake = armed
      ? buyBonusPrice(amount, armed.type, armed.guaranteedWins)
      : amount;
    if (armed && !(Number(ui.balanceValue) >= stake)) {
      sound.play('uiError');
      showSystemToast({
        message: this._t('HUB_ACTION_INSUFFICIENT_BALANCE'),
        durationMs: 3500,
      });
      return;
    }
    const betTier = Math.min(3, Math.max(1, this._betSpeedTier || 1));
    const holdTurbo = this._turboHoldActive;
    /** Dedicated short Quick Spin timeline (tier 3 + long-press turbo). Tier 2 uses faster stagger only. */
    const useTurboSnap = holdTurbo || betTier >= 3;
    /** Turbo / hand-stop: skip heavy payline cycle and do not block on collect tap. */
    let fastWinPresentation = useTurboSnap || this._stopRequested;
    this._lastSpinUsedTurbo = useTurboSnap;
    const tier2StaggerScale =
      Number.isFinite(gameConfig.betSpeedTier2StaggerScale) && gameConfig.betSpeedTier2StaggerScale > 0
        ? gameConfig.betSpeedTier2StaggerScale
        : 0.5;

    this._winCollectPending = false;
    this._resumeRoundSkipCollect = false;
    this._resolveWinCollectUser = null;
    this._winCelebrationShownAt = null;
    this._clearRoundStatus();
    this._showGoodLuckPrompt();
    this._setBetPromptVisible(true);
    if (this._spinButtonSprite) this._setSpinButtonTexture(this._spinStopTex);
    this._refreshSpinAutoplayRemainLabel();
    this._refreshQuickBets();
    ui.setSpinEnabled(false);
    await this._teardownWinVisualsBeforeSpin();
    this.slotMachine.prepareForNewRound();
    this.debug.refreshOverlay();
    /* Silence any leftover one-shots from the previous round (popup tails,
     * symbol chimes, win tier audio) before the new spin starts. Bg music
     * keeps playing — it lives on its own bus. Landing thuds are exempt in stopAllSfx. */
    sound.stopAllSfx();
    sound.play('spinStart');

      /* Show stake leave the wallet immediately — server balance arrives after reels settle. */
      balanceBeforeBet = Number(ui.balanceValue);
      if (Number.isFinite(balanceBeforeBet) && Number.isFinite(stake) && stake > 0) {
        const optimisticBalance = Math.max(0, balanceBeforeBet - stake);
        ui.setBalance(optimisticBalance);
        this._updateCanvasBalance(optimisticBalance);
      }

      if (!this._isDemo && !this._accessToken) {
        throw new Error('Missing access token');
      }
      const rc = this.slotMachine.reelContainer;
      rc.beginPendingSpin();
      const spinState = await this.backend.spin(
        amount,
        armed
          ? { buyBonus: true, buyBonusType: armed.type, guaranteedWins: armed.guaranteedWins }
          : undefined,
      );
      const envelope = spinState?.arguments?.[0] ?? spinState;
      const actionResult = envelope?.result ?? envelope?.Result ?? envelope;
      const gameResult = actionResult?.result ?? actionResult?.Result ?? actionResult;
      const baseGrid = this._normalizeMatrix(gameResult?.matrix ?? gameResult?.Matrix);
      const expandingWild = gameResult?.additional?.expandingWild ?? gameResult?.Additional?.ExpandingWild;
      const expandingWildMultipliers =
        gameResult?.additional?.expandingWildMultipliers ??
        gameResult?.additional?.exwm ??
        gameResult?.Additional?.ExpandingWildMultipliers;
      /** Per-reel landing thud + wild/scatter/coin chimes. Natural stop (regular,
       *  tier 2, turbo snap): `reelDefaultStop` — one per reel when staggered, one
       *  total when reels land together. Hand stop: `reelStopper` (single thud).
       *
       *  When quick-stopped or turbo-snapped (hold turbo / tier 3), symbol chimes
       *  are suppressed. Win tier audio still fires later, after the reels settle. */
      if (this._buyBonusEnabled) {
        rc.armWildReelMultipliers(expandingWildMultipliers, expandingWild);
      }
      let reelLandSoundPlayed = false;
      const onReelLanded = (col) => {
        /* Read _stopRequested when each reel lands — not when the API returns. */
        if (this._stopRequested) {
          if (!reelLandSoundPlayed) {
            reelLandSoundPlayed = true;
            sound.play('reelStopper', { allowOverlap: true });
          }
        } else if (useTurboSnap) {
          if (!reelLandSoundPlayed) {
            reelLandSoundPlayed = true;
            sound.play('reelDefaultStop', { allowOverlap: true });
          }
        } else {
          sound.play('reelDefaultStop', { allowOverlap: true });
        }
        if (this._stopRequested || useTurboSnap) return;
        const reelSymbols = baseGrid && baseGrid[col];
        if (!reelSymbols) return;
        let hasWild = false;
        let hasScatter = false;
        let hasCoin = false;
        for (let r = 0; r < reelSymbols.length; r++) {
          const sid = reelSymbols[r];
          if (sid === 9) hasWild = true;
          else if (sid === 10) hasCoin = true;
          else if (sid === 11) hasScatter = true;
        }
        /* Per-key throttle in soundManager dedupes adjacent reels landing
         * within ~40 ms, so back-to-back wilds across reels still feel clean. */
        if (hasWild) sound.play('wild');
        if (hasScatter) sound.play('scatter');
        if (hasCoin) sound.play('coin');
        const fastVisuals = this._stopRequested || useTurboSnap;
        this.slotMachine?.reelContainer?.handleReelLandedVisuals(col, baseGrid, {
          fastStop: fastVisuals,
        });
      };
      if (baseGrid) {
        this._reelsSpinning = true;
        this._ambientFx?.syncSpinRingVisible(true);
        // Idle-only cruise for every tier (Blazing Sevens) — no Spine spinning/blur clips.
        if (this._stopRequested) {
          /** Stop was tapped during the API wait — short turbo timeline, never a blank snap. */
          rc.clearStopRequest();
          await rc.spinToGrid(baseGrid, {
            turboMode: true,
            onReelLanded,
          });
        } else if (useTurboSnap) {
          // Dedicated short Quick Spin timeline (industry pattern) — no full-travel + seek.
          await rc.spinToGrid(baseGrid, {
            turboMode: true,
            onReelLanded,
          });
        } else if (betTier === 2) {
          await rc.spinToGrid(baseGrid, {
            reelStopStaggerScale: tier2StaggerScale,
            onReelLanded,
          });
        } else {
          await rc.spinToGrid(baseGrid, { onReelLanded });
        }
        this._reelsSpinning = false;
        this._ambientFx?.syncSpinRingVisible(false);
        if (useTurboSnap || this._stopRequested) {
          const holdMs = this._resolveTurboResultHoldMs();
          if (holdMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, holdMs));
          }
        }
        this._lastSpinUsedTurbo = useTurboSnap || this._stopRequested;
      } else {
        rc.setAllVisibleSymbolsSpinning(false);
      }
      this._setBetPromptVisible(false);
      const wins = gameResult?.additional?.wins ?? gameResult?.Additional?.Wins;
      const expandingWildForWin = this._filterExpandingWildToWinLines(expandingWild, wins);
      const grid = this._applyExpandingWild(baseGrid, expandingWildForWin);
      /** Re-read stop flag — hand-stop can flip mid-spin after the initial turbo check. */
      fastWinPresentation = useTurboSnap || this._stopRequested;
      if (baseGrid && Array.isArray(expandingWildForWin) && expandingWildForWin.some((v) => Number(v) > 0)) {
        // Play expanding-wild spine on each winning-line column, then reveal full wild stacks.
        await rc.animateExpandingWildColumns(expandingWildForWin, {
          turbo: !!fastWinPresentation,
          multipliers: this._buyBonusEnabled ? expandingWildMultipliers : undefined,
        });
      }
      const newBalance =
        this._readBalanceFromRecord(actionResult) ??
        this._readBalanceFromRecord(envelope) ??
        this._readBalanceFromRecord(spinState);
      if (newBalance == null) {
        throw new Error('Spin result missing balance');
      }
      const totalWin =
        Number(
          actionResult?.winAmount ??
            actionResult?.WinAmount ??
            actionResult?.win ??
            actionResult?.Win,
        ) || 0;
      this._autoplayLastSingleWin = totalWin;
      const settledBalance = this._spinSettledBalance(
        newBalance,
        balanceBeforeBet,
        stake,
        totalWin,
      );
      const hits = this._mapBackendWinsToHits(wins, grid);
      let rollupPromise = null;
      if (totalWin > 0) {
        ui.setWin(totalWin, hits);
        const winMult = amount > 0 ? totalWin / amount : 0;
        const showCelebration =
          amount > 0 && winMult >= winCelebrationMinMultiple();
        /* Regular wins: credit as soon as the result is on the reels.
         * Big-win tiers: wait until celebration/collect finishes. */
        if (!showCelebration && settledBalance != null) {
          this._applySpinBalance(settledBalance);
          sound.play('win');
        }
        /** Autoplay uses celebration length; last-win HUD stays hidden until popup ends. */
        let celebrationSeqSec = 0;
        if (showCelebration && this.spineAssets) {
          celebrationSeqSec = getCelebrationSequenceDurationSec(
            this.spineAssets,
            winMult,
            !!fastWinPresentation,
          );
        }
        this._spinning = false;
        if (this._spinButtonSprite) this._setSpinButtonTexture(this._spinCollectTex ?? this._spinStartTex);
        this._refreshSpinAutoplayRemainLabel();
        const userCollectPromise = new Promise((resolve) => {
          this._resolveWinCollectUser = resolve;
        });
        if (showCelebration) {
          this._winCelebrationShownAt = Date.now();
        } else {
          this._winCelebrationShownAt = null;
        }
        this._winCollectPending = true;
        this._refreshQuickBets();
        const roundStatusBase = {
          pendingWin: totalWin,
          betAmount: amount,
          winMult,
          showCelebration,
        };
        this._persistRoundStatus({
          ...roundStatusBase,
          phase: showCelebration ? 'celebrating' : 'rollup_collect',
          celebrationStartedAt: showCelebration ? Date.now() : undefined,
        });
        /* Celebration: do not count up Last Win during the popup (reveals the result early). */
        rollupPromise = showCelebration
          ? null
          : this._animateLastWinRollup(totalWin, amount, { immediate: true });
        if (hits.length) {
          const hitsSorted = [...hits].sort(
            (a, b) => (a.paylineIndex ?? Number.MAX_SAFE_INTEGER) - (b.paylineIndex ?? Number.MAX_SAFE_INTEGER),
          );
          if (!showCelebration) {
            /* Always show paylines (including Quick Spin / hand-stop). Next spin tears them down. */
            this._beginPostSpinWin(hitsSorted, totalWin);
          }
        }
        if (showCelebration) {
          // Temporary QA delay to verify post-wild timing before popup celebration starts.
          // await new Promise((resolve) => setTimeout(resolve, 500));
          this.slotMachine.playWinCelebration(winMult, !!fastWinPresentation, totalWin, () => {
            this._persistRoundStatus({
              ...roundStatusBase,
              phase: 'collect_hold',
            });
          });
        }
        if (showCelebration && this._autoplaying) {
          const delayMs = Math.max(0, Math.round(celebrationSeqSec * 1000)) + 1000;
          void (async () => {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            if (this._isWinCollectDismissActive()) {
              await this._performWinCollectTap();
            }
          })();
        }
        if (showCelebration) {
          /* Celebration tiers must close only through explicit collect/end animation flow.
           * Never allow timer/rollup completion to auto-advance the round. */
          await userCollectPromise;
        } else {
          await (rollupPromise ?? Promise.resolve());
          if (fastWinPresentation && !showCelebration) {
            this._winCollectPending = false;
            if (this._resolveWinCollectUser) {
              const r = this._resolveWinCollectUser;
              this._resolveWinCollectUser = null;
              r();
            }
          } else if (this._autoplaying) {
            if (this._isWinCollectDismissActive()) {
              await this._performWinCollectTap();
            }
          } else {
            await userCollectPromise;
          }
        }
        await this._completeWinCollectPhase();
        if (showCelebration) {
          if (settledBalance != null) this._applySpinBalance(settledBalance);
          /* Reveal Last Win only after big-win popup/outro is fully done. */
          this._animateLastWinRollup(totalWin, amount, { immediate: true });
        }
        this._resolveWinCollectUser = null;
        /* After big-win outro, cycle paylines until the next spin (skip if collect already queued a respin). */
        if (
          showCelebration &&
          hits.length &&
          !this._pendingSpinAfterWinClose
        ) {
          const hitsSorted = [...hits].sort(
            (a, b) => (a.paylineIndex ?? Number.MAX_SAFE_INTEGER) - (b.paylineIndex ?? Number.MAX_SAFE_INTEGER),
          );
          this._beginPostSpinWin(hitsSorted, totalWin);
        }
        if (!showCelebration && betTier === 2 && !this._stopRequested) {
          await new Promise((resolve) =>
            setTimeout(resolve, gameConfig.betSpeedTier2PostWinPadMs),
          );
        }
        this._refreshQuickBets();
      } else {
        if (settledBalance != null) this._applySpinBalance(settledBalance);
        this._spinning = false;
        if (this._spinButtonSprite) {
          this._setSpinButtonTexture(this._autoplaying ? this._spinStopTex : this._spinStartTex);
        }
        this._refreshSpinAutoplayRemainLabel();
        this._refreshQuickBets();
        ui.setSpinEnabled(true);
      }
    } catch (err) {
      if (String(err?.message || '').includes('Spin superseded')) return;
      console.error('Spin failed', err);
      this.slotMachine?.reelContainer.setAllVisibleSymbolsSpinning(false);
      /* Roll back optimistic stake if the round never settled on the client. */
      if (Number.isFinite(balanceBeforeBet)) {
        ui.setBalance(balanceBeforeBet);
        this._updateCanvasBalance(balanceBeforeBet);
      }
      if (this._handleHubActionError(err)) {
        /* hub actionStatus refusal — not a transport failure */
      } else if (this._isDuplicateSessionError(err)) {
        this._showDuplicateSessionEndedModal({ fromServer: true });
      } else {
        const sessionOk = await this._runConnectionCheck({
          allowShowModal: true,
          bypassGridPreserve: true,
        });
        if (sessionOk) sound.play('uiError');
      }
    } finally {
      const pendingSpin = this._pendingSpinAfterWinClose;
      this._pendingSpinAfterWinClose = false;
      this._resolveWinCollectUser = null;
      this._winCollectPending = false;
      this.debug.refreshOverlay();
      this._spinCycleActive = false;
      this._spinning = false;
      this._reelsSpinning = false;
      this._stopRequested = false;
      /* Enforce a short idle gap so manual taps can't chain near-instant spins.
       * Dedicated timestamp (NOT the toggle lock) so it isn't clobbered by the
       * anti-instant-stop cooldown the pointer-down handler sets at press time.
       * Autoplay and post-win pending spins call handleSpin() directly and bypass this. */
      this._respinBlockUntilTs =
        performance.now() + this._resolvePostSpinCooldownMs(!!this._lastSpinUsedTurbo);
      this._lastSpinUsedTurbo = false;
      this.slotMachine?.reelContainer?.clearStopRequest();
      if (this._spinButtonSprite) {
        if (this._autoplaying) {
          this._setSpinButtonTexture(this._spinStopTex);
        } else {
          this._setSpinButtonTexture(this._spinStartTex);
        }
      }
      this._refreshSpinAutoplayRemainLabel();
      this._refreshQuickBets();
      if (!this._shouldKeepSpinDisabled()) {
        ui.setSpinEnabled(true);
        this._updateCanvasBetPrompt();
        this._setBetPromptVisible(true);
      }
      if (pendingSpin && !this._shouldKeepSpinDisabled()) {
        queueMicrotask(() => {
          void this.handleSpin();
        });
      }
    }
  }

  async _loadInitialState() {
    if (!this.slotMachine) return;
    this._initializing = true;
    this._sessionLoadFailed = false;
    this._connectionLostUIOpen = false;
    this._duplicateSessionEnded = false;
    this._connectionMonitoringEnabled = false;
    this._forceDisconnectReceived = false;
    this._reconnectAfterTabVisible = false;
    this._clearDisconnectResolveTimer();
    this._hadConnectionLoss = false;
    this._connModalMode = 'lost';
    this._connGraceMs = CONNECTION_LOST_GRACE_MS;
    this._clearConnectionLostGraceTimer();
    this._hideConnectionLostModal();
    this.slotMachine.hud.setSpinEnabled(false);
    try {
      const session = this._resolveSessionAuth();
      const { isDemo, oneTimeToken } = session;
      this._isDemo = isDemo;
      this._oneTimeToken = oneTimeToken;
      this._sessionParams = session;
      if (!session.gameName || !session.partnerId) {
        throw new Error('GameName and PartnerId are required in URL params');
      }
      if (!isDemo && !oneTimeToken) {
        throw new Error('OneTimeToken is required when not in demo mode');
      }
      if (!isDemo && oneTimeToken) {
        this._accessToken = await this._exchangeOtp(oneTimeToken);
      }
      this._setLoadingProgressTarget(0.90);
      if (typeof document !== 'undefined' && session.culture) {
        document.documentElement.lang = session.culture.split('-')[0] || 'en';
      }
      await loadTranslations({
        accessToken: this._accessToken,
        gameName: session.translationGameName || session.gameName,
        culture: session.culture,
        translationStore: this.translation,
      });
      this._refreshLocalizedText();
      this._setLoadingProgressTarget(0.93);
      this._sessionInstanceId = claimLatestSessionInstance();
      await this.backend.connect(this._accessToken);
      this._hubConnected = true;
      this._setLoadingProgressTarget(0.96);
      const state = await this.backend.getInitialState({
        isDemo,
        gameName: session.backendGameName || session.gameName,
        partnerId: session.partnerId,
      });
      const initial = this._unwrapInitialPayload(state) ?? state;
      const ui = this.slotMachine.hud;
      if (this._readSessionBalance(initial) == null) throw new Error('StartGameAction missing balance');
      const defaultBetAmount = Number(
        initial?.defaultBetAmount ?? initial?.DefaultBetAmount ?? initial?.defaultBet ?? initial?.DefaultBet,
      );
      if (!Number.isFinite(defaultBetAmount)) {
        throw new Error('StartGameAction missing defaultBet');
      }
      const quickBets = initial?.quickBets ?? initial?.QuickBets ?? initial?.betOptions ?? initial?.BetOptions;
      if (!Array.isArray(quickBets) || !quickBets.length) {
        throw new Error('StartGameAction missing betOptions');
      }
      const matrix = initial?.matrix ?? initial?.Matrix ?? initial?.initialBoard ?? initial?.InitialBoard;
      if (!Array.isArray(matrix)) throw new Error('StartGameAction missing initialBoard');

      this._applyServerSessionState(initial, { resumeInterruptedRound: true });
      this._refreshQuickBets();

      const initialGrid = this._normalizeMatrix(matrix);
      if (!initialGrid) throw new Error('StartGameAction matrix shape mismatch');
      this._gameDataReady = true;
      this._hubConnected = true;
      this._clearConnectionLostGraceTimer();
      this._clearDisconnectResolveTimer();
      this._connectionLostUIOpen = false;
      this._hideConnectionLostModal();
      this._armPostBootHubGrace();
      this._attachHubConnectionHandlers();
      this._connectionMonitoringEnabled = true;
      this._installHubConnectionMonitor();
      this._startIdleReloadWatch();
      this._setLoadingProgressTarget(1);
    } catch (err) {
      console.error('StartGameAction failed', err);
      relinquishSessionInstance(this._sessionInstanceId);
      this._gameDataReady = false;
      this._hubConnected = false;
      this._connectionMonitoringEnabled = false;
      this._sessionLoadFailed = true;
      this._connectionLostUIOpen = true;
      this._connModalMode = 'lost';
      this._refreshConnectionLostModalCopy();
      this._clearConnectionLostGraceTimer();
      this._setLoadingProgressTarget(1);
    }
    this._initializing = false;
    this._refreshQuickBets();
    this._updateQbArrowVisibility();
    this.slotMachine.hud.setSpinEnabled(
      this._winCollectPending ? false : this._shouldShowQuickBets(),
    );
  }

  async _exchangeOtp(oneTimeToken) {
    const res = await fetch(
      `https://launcher.inhousegames.win/api/token/otp?oneTimeToken=${encodeURIComponent(oneTimeToken)}`,
    );
    const data = await res.json();
    if (data?.result?.accessToken) {
      return data.result.accessToken;
    }
    throw new Error('Failed to exchange OneTimeToken for access token');
  }

  /** Load UI copy from URL culture as early as the loading overlay is visible. */
  async _prefetchTranslationsFromUrl() {
    const { gameName, translationGameName, culture } = this._resolveSessionAuth();
    if (typeof document !== 'undefined' && culture) {
      document.documentElement.lang = culture.split('-')[0] || 'en';
    }
    setTranslationCulture(culture);
    await loadTranslations({
      gameName: translationGameName || gameName,
      culture,
      translationStore: this.translation,
    });
    this._refreshLocalizedText();
  }

  _resolveSessionAuth() {
    const raw = getParamsFromUrl(window.location.search);
    const mode = raw.Mode || null;
    const isDemo = mode === 'demo';
    const partnerId = raw.PartnerId?.trim() || null;
    const gameName = raw.GameName?.trim() || null;
    const variant = resolveGameVariant(gameName);
    applyLaunchLayout(variant);
    this._buyBonusEnabled = variant.buyBonus;
    this._lines40Enabled = variant.lines40;
    this._lines100Enabled = variant.lines100;
    setTranslationVariant({ lines40: variant.lines40, lines100: variant.lines100 });
    this._backendGameName = variant.backendGameName;
    const culture = (raw.Culture ?? raw.culture ?? 'en').trim() || 'en';
    setTranslationCulture(culture);
    const currencyRaw = (
      raw.Currency ??
      raw.currency ??
      raw.CurrencyCode ??
      raw.currencyCode
    )
      ?.trim()
      .toUpperCase();
    const currencyCode =
      currencyRaw && /^[A-Z]{3}$/.test(currencyRaw) ? currencyRaw : null;
    let oneTimeToken = raw.OneTimeToken?.trim() || null;
    if (!isDemo && !oneTimeToken) {
      const entered = window.prompt('Enter OneTimeToken');
      oneTimeToken = entered?.trim() || null;
    }
    return {
      isDemo,
      oneTimeToken,
      gameName,
      translationGameName: variant.translationGameName || gameName,
      backendGameName: variant.backendGameName || gameName,
      partnerId,
      culture,
      currencyCode,
    };
  }

  _syncLobbyUrlFromSearch() {
    if (typeof window === 'undefined') return;
    const raw = getParamsFromUrl(window.location.search);
    this._lobbyUrl = String(raw.LobbyUrl ?? raw.lobbyUrl ?? raw.lobbyURL ?? '').trim();
  }

  _startIdleReloadWatch() {
    if (this._idleReloadDispose || typeof window === 'undefined') return;
    this._idleReloadDispose = installGameIdleReload({
      onIdle: () => {
        this._idleReloadDispose?.();
        this._idleReloadDispose = null;
        this._showIdleTimeoutErrorAndReload();
      },
    });
  }

  _showIdleTimeoutErrorAndReload() {
    if (this._idleTimeoutOverlay || typeof document === 'undefined') return;
    const host = this._containerEl ?? document.body;
    const panel = createSystemMessagePanel({ mountEl: host });
    panel.update({
      title: this._tu('GAME_INTERRUPTION'),
      body: this._t('IDLE_TIMEOUT_RELOAD'),
      icon: 'idle',
      showPrimaryButton: false,
      showLogo: true,
      statusVisible: false,
      statusText: '',
    });
    panel.show();
    this._idleTimeoutOverlay = panel.el;
    window.setTimeout(() => {
      window.location.reload();
    }, 2500);
  }

  _ensureTokenForCurrentMode() {
    if (this._isDemo) return null;
    if (this._oneTimeToken) return this._oneTimeToken;
    const entered = window.prompt('Enter OneTimeToken');
    const token = entered?.trim() || null;
    if (token) {
      this._oneTimeToken = token;
      window.localStorage.setItem('shiningCrownOneTimeToken', token);
    }
    return token;
  }

  /** @returns {'mobile' | 'tablet' | 'desktop'} */
  _getLoadingViewportProfile(cw, ch) {
    const width = cw ?? this._readContainerSize().width;
    const height = ch ?? this._readContainerSize().height;
    const portrait = height > width;
    // Portrait phones + tablets share the mobile loading shell.
    if (portrait || Math.min(width, height) <= 760) return 'mobile';
    if (this._isTabletViewport()) return 'tablet';
    return 'desktop';
  }

  _getLoadingCanvasTransform(cw, ch) {
    const width = cw ?? this._readContainerSize().width;
    const height = ch ?? this._readContainerSize().height;
    const scaleX = width / LOADING_DESIGN_W;
    const scaleY = height / LOADING_DESIGN_H;
    if (this._loadingUseUniformCanvas()) {
      const u = Math.max(scaleX, scaleY);
      return {
        scaleX,
        scaleY,
        u,
        tx: (width - LOADING_DESIGN_W * u) / 2,
        ty: (height - LOADING_DESIGN_H * u) / 2,
      };
    }
    return { scaleX, scaleY };
  }

  _loadingCssToDesignX(cssX, transform) {
    if (Number.isFinite(transform.u)) return (cssX - transform.tx) / transform.u;
    const sx = transform.scaleX;
    return sx > 0 ? cssX / sx : cssX;
  }

  _loadingCssToDesignY(cssY, transform) {
    if (Number.isFinite(transform.u)) return (cssY - transform.ty) / transform.u;
    const sy = transform.scaleY;
    return sy > 0 ? cssY / sy : cssY;
  }

  _pickLoadingBackgroundTexture(cw, ch) {
    const profile = this._getLoadingViewportProfile(cw, ch);
    if (profile === 'mobile') {
      return this._loadingBgTexMobile || this._bgTexMobile || this._loadingBgTexDesktop || this._bgTexDesktop || Texture.EMPTY;
    }
    return this._loadingBgTexDesktop || this._bgTexDesktop || Texture.EMPTY;
  }

  /** Cover-fit background to the visible viewport region (matches portrait/mobile asset aspect). */
  _layoutLoadingBackgroundCover(sprite, tex, cw, ch, transform) {
    if (!sprite || !tex?.width) return;
    const visibleW = Number.isFinite(transform?.u) ? cw / transform.u : LOADING_DESIGN_W;
    const visibleH = Number.isFinite(transform?.u) ? ch / transform.u : LOADING_DESIGN_H;
    const visibleAspect = visibleW / visibleH;
    const bleed = LOADING_BG_BLEED;
    const tw = tex.width;
    const th = tex.height;
    const texAspect = tw / th;
    let bgW;
    let bgH;
    if (texAspect > visibleAspect) {
      bgH = visibleH + bleed * 2;
      bgW = bgH * texAspect;
    } else {
      bgW = visibleW + bleed * 2;
      bgH = bgW / texAspect;
    }
    sprite.texture = tex;
    sprite.width = bgW;
    sprite.height = bgH;
    sprite.x = LOADING_DESIGN_W / 2 - bgW / 2;
    sprite.y = LOADING_DESIGN_H / 2 - bgH / 2;
  }

  _layoutLoadingScreen() {
    const gameLogo = this._loadingGameLogoSprite;
    const barTrack = this._loadingBarTrack;
    if (!gameLogo || !barTrack) return;

    const { width: cw, height: ch } = this._readContainerSize();
    const transform = this._getLoadingCanvasTransform(cw, ch);

    const tex = this._pickLoadingBackgroundTexture(cw, ch);
    if (this._loadingBgSprite && tex?.width > 0) {
      this._layoutLoadingBackgroundCover(this._loadingBgSprite, tex, cw, ch, transform);
    }

    /* Game logo + progress bar stack. */
    const MAX_GAME_H = 408;
    const MAX_GAME_W = 708;
    const MAX_BAR_W = 420;
    const MAX_BAR_H = 28;

    const GAP2_R = 0.04; // game logo bottom -> bar top, relative to game H
    const BAR_H_R = MAX_BAR_H / MAX_GAME_H; // 0.0539
    const BAR_W_R = MAX_BAR_W / MAX_GAME_W; // 0.5932
    const STACK_TOTAL = 1.0 + GAP2_R + BAR_H_R;

    const HORIZ_PAD = 0.86;
    const VERT_PAD = 0.78;

    const gameAspect =
      gameLogo.texture?.width > 0 && gameLogo.texture?.height > 0
        ? gameLogo.texture.width / gameLogo.texture.height
        : MAX_GAME_W / MAX_GAME_H;

    /* Reserve bottom space for powered-by so % never sits under it on short devices. */
    const isLoadingMobile = this._getLoadingViewportProfile(cw, ch) === 'mobile';
    const poweredBy = this._loadingPoweredBySprite;
    const bottomPad_css = isLoadingMobile ? 18 : 26;
    let poweredByH_css = 0;
    let poweredByW_css = 0;
    if (poweredBy?.texture?.width > 0) {
      poweredByW_css = isLoadingMobile ? Math.min(cw * 0.56, 210) : Math.min(cw * 0.3, 240);
      poweredByH_css = poweredByW_css / (poweredBy.texture.width / poweredBy.texture.height);
    }
    const bottomClear_css = bottomPad_css + poweredByH_css + 12;

    const cwAvail = cw * HORIZ_PAD;
    const chAvail = Math.min(ch * VERT_PAD, Math.max(80, ch - bottomClear_css - 48));

    const gameH_css = Math.max(
      1,
      Math.min(
        chAvail / STACK_TOTAL,
        cwAvail / gameAspect,
        cwAvail / (BAR_W_R * gameAspect),
        MAX_GAME_H,
        MAX_GAME_W / gameAspect,
        MAX_BAR_W / (BAR_W_R * gameAspect),
        MAX_BAR_H / BAR_H_R,
      ),
    );

    const gameW_css = gameH_css * gameAspect;
    const barW_css = gameW_css * BAR_W_R;
    const barH_css = gameH_css * BAR_H_R;

    const totalStackH_css = gameH_css * STACK_TOTAL;
    const percentBlock_css =
      LOADING_BAR_FRAME_PAD * 2 + Math.max(8, barH_css * 0.35) + Math.max(20, Math.round(barH_css * 1.55)) * 1.25;
    const stackWithPercentH_css = totalStackH_css + percentBlock_css;
    let stackTop_css = (ch - totalStackH_css) / 2;
    const maxStackTop = Math.max(8, ch - bottomClear_css - stackWithPercentH_css);
    if (stackTop_css > maxStackTop) stackTop_css = maxStackTop;

    const gameCenterY_css = stackTop_css + gameH_css / 2;
    const barY_css = stackTop_css + gameH_css + gameH_css * GAP2_R;

    const { scaleX, scaleY, u } = transform;
    const sizeDivX = Number.isFinite(u) ? u : scaleX;
    const sizeDivY = Number.isFinite(u) ? u : scaleY;
    const centerX_design = this._loadingCssToDesignX(cw / 2, transform);

    const logoBoxW = gameW_css / sizeDivX;
    const logoBoxH = gameH_css / sizeDivY;
    const logoCenterY = this._loadingCssToDesignY(gameCenterY_css, transform);
    gameLogo.anchor.set(0.5, 0.5);
    gameLogo.width = logoBoxW;
    gameLogo.height = logoBoxH;
    gameLogo.x = centerX_design;
    gameLogo.y = logoCenterY;

    this._loadingBarW = Math.round((barW_css / sizeDivX) * 2) / 2;
    this._loadingBarH = Math.round((barH_css / sizeDivY) * 2) / 2;
    paintLoadingBarFrame(barTrack, this._loadingBarW, this._loadingBarH);
    barTrack.x = Math.round(this._loadingCssToDesignX((cw - barW_css) / 2, transform));
    barTrack.y = Math.round(this._loadingCssToDesignY(barY_css, transform));

    const caption = this._loadingCaptionText;
    if (caption) {
      caption.anchor.set(0.5, 1);
      caption.x = centerX_design;
      caption.y = barTrack.y - Math.max(6, this._loadingBarH * 0.35);
      caption.style.fontSize = Math.max(11, Math.round(this._loadingBarH * 0.85));
      applyLoadingGoldTextStyle(caption, { large: false });
    }

    const percent = this._loadingPercentText;
    if (percent) {
      percent.anchor.set(0.5, 0);
      percent.x = centerX_design;
      percent.y = barTrack.y + this._loadingBarH + LOADING_BAR_FRAME_PAD * 2 + Math.max(8, this._loadingBarH * 0.35);
      percent.style.fontSize = Math.max(20, Math.round(this._loadingBarH * 1.55));
      applyLoadingGoldTextStyle(percent, { large: true });
    }

    if (poweredBy?.texture?.width > 0) {
      poweredBy.anchor.set(0.5, 1);
      poweredBy.width = poweredByW_css / sizeDivX;
      poweredBy.height = poweredByH_css / sizeDivY;
      poweredBy.x = centerX_design;
      poweredBy.y = this._loadingCssToDesignY(ch - bottomPad_css, transform);
      poweredBy.alpha = 0.94;
    }

    if (this._loadingOverlay) {
      this._loadingOverlay.clear();
      this._loadingOverlay
        .rect(0, 0, LOADING_DESIGN_W, LOADING_DESIGN_H)
        .fill({ color: T.modalDim, alpha: shouldUseBackdropBlur() ? 0.38 : 0.52 });
    }

    this._redrawLoadingBarFill();
    this._syncLoadingBarShimmer();
  }

  _syncLoadingBarShimmer() {
    this._loadingBarShimmerFx?.stop();
    this._loadingBarShimmerFx = null;
    const track = this._loadingBarTrack;
    const host = this._loadingContent;
    if (!track || !host) return;
    this._loadingBarShimmerFx = attachLuxuryGoldFrameFx(
      host,
      {
        x: track.x,
        y: track.y,
        w: this._loadingBarW,
        h: this._loadingBarH,
        r: this._loadingBarH / 2,
      },
      this.animationController,
      {
        loop: true,
        shimmerColor: LOADING_GOLD.white,
        shimmerAlpha: 0.62,
      },
    );
  }

  _updateLoadingPercentLabel() {
    if (!this._loadingPercentText) return;
    const pct = Math.round(Math.min(1, Math.max(0, this._loadingProgress)) * 100);
    this._loadingPercentText.text = `${pct}%`;
  }

  _redrawLoadingBarFill() {
    const track = this._loadingBarTrack;
    if (!track) return;
    syncLoadingBarFillLayers({
      glow: this._loadingBarGlow,
      mask: this._loadingBarFillMask,
      sprite: this._loadingBarFillSprite,
      gloss: this._loadingBarFillGloss,
      trackX: track.x,
      trackY: track.y,
      barW: this._loadingBarW,
      barH: this._loadingBarH,
      progress: this._loadingProgress,
    });
    this._updateLoadingPercentLabel();
  }

  _setLoadingProgressTarget(value) {
    if (!this._loadingRoot) return;
    const next = Math.max(0, Math.min(1, Number(value) || 0));
    this._loadingProgressTarget = Math.max(this._loadingProgressTarget ?? 0, next);
  }

  _syncLoadingProgressTick() {
    if (!this._loadingRoot) return;
    const target = this._loadingProgressTarget ?? 0;
    const current = this._loadingProgress ?? 0;
    const delta = target - current;
    if (Math.abs(delta) < 0.001) {
      if (current !== target) {
        this._loadingProgress = target;
        this._redrawLoadingBarFill();
      }
      return;
    }
    this._loadingProgress = current + delta * 0.14;
    this._redrawLoadingBarFill();
  }

  /** 40-line, 100-line, and buy bonus use the tighter cabinet. The live 10-line game does not. */
  _usesCompactCabinet() {
    return this._lines40Enabled || this._lines100Enabled || this._buyBonusEnabled;
  }

  /**
   * Asset folder for a 5×4 launch logo and feature still.
   * @returns {'40_lines' | '100_lines' | null}
   */
  _wideLinesFolder() {
    if (this._lines100Enabled) return '100_lines';
    if (this._lines40Enabled) return '40_lines';
    if (typeof window === 'undefined') return null;
    const variant = resolveGameVariant(getParamsFromUrl(window.location.search).GameName);
    if (variant.lines100) return '100_lines';
    if (variant.lines40) return '40_lines';
    return null;
  }

  /** 40- and 100-line launches use their own logo; the live title keeps Logo0. */
  _isWideLinesLaunch() {
    return this._wideLinesFolder() != null;
  }

  _gameLogoAssetPaths() {
    const folder = this._wideLinesFolder();
    if (folder === '100_lines') {
      return ['/assets/100_lines/100_lines_logo.png', 'assets/100_lines/100_lines_logo.png'];
    }
    if (folder === '40_lines') {
      return ['/assets/40_lines/40_lines_logo.png', 'assets/40_lines/40_lines_logo.png'];
    }
    return ['/assets/Logo0.png', 'assets/Logo0.png', '/Logo0.png'];
  }

  _gameLogoAssetPath() {
    return this._gameLogoAssetPaths()[0];
  }

  /** Start-screen feature still. 40- and 100-line use their own 4-row preview. */
  _startScreenPreviewPath() {
    const folder = this._wideLinesFolder();
    if (folder === '100_lines') return '/assets/100_lines/max-win-expanded.png';
    if (folder === '40_lines') return '/assets/40_lines/max-win-expanded.png';
    return '/assets/max-win-expanded.png';
  }

  /**
   * Fit a 40- or 100-line logo's artwork into the box Logo0 occupies at `legacyWidth`
   * (top-center anchor). `dx`/`dy` are added to that legacy position.
   * @param {number} legacyWidth
   */
  _fitWideLogo(legacyWidth) {
    const legacy = LEGACY_LOGO_METRICS;
    const next = this._wideLinesFolder() === '100_lines' ? LINES100_LOGO_METRICS : LINES40_LOGO_METRICS;
    const legacyH = legacyWidth * (legacy.canvasH / legacy.canvasW);
    const scaleX = legacyWidth / legacy.canvasW;
    const scaleY = legacyH / legacy.canvasH;
    const artX = legacy.artMinX * scaleX;
    const artY = legacy.artMinY * scaleY;
    const artW = (legacy.artMaxX - legacy.artMinX) * scaleX;
    const artH = (legacy.artMaxY - legacy.artMinY) * scaleY;
    const nextArtW = next.artMaxX - next.artMinX;
    const nextArtH = next.artMaxY - next.artMinY;
    const fit = Math.min(artW / nextArtW, artH / nextArtH);
    const width = next.canvasW * fit;
    const height = next.canvasH * fit;
    const drawnArtW = nextArtW * fit;
    const drawnArtH = nextArtH * fit;
    const left = artX + (artW - drawnArtW) / 2 - next.artMinX * fit;
    const top = artY + (artH - drawnArtH) / 2 - next.artMinY * fit;
    return {
      width,
      height,
      dx: left + width / 2 - legacyWidth / 2,
      dy: top,
    };
  }

  async _showLoading() {
    if (!this.app?.stage || this._loadingRoot) return;
    this._loadingCanvasUniform = true;

    const loadTex = async (path) => {
      try {
        return await Assets.load(path);
      } catch {
        return Texture.EMPTY;
      }
    };

    if (!this._bgTexDesktop?.width) {
      this._bgTexDesktop = await loadTex('/assets/background.png');
    }
    this._loadingBgTexDesktop = this._bgTexDesktop;
    if (!this._bgTexMobile?.width) {
      this._bgTexMobile = await loadTex('/assets/mobile_background.png');
    }
    this._loadingBgTexMobile = this._bgTexMobile?.width ? this._bgTexMobile : this._loadingBgTexDesktop;
    this._setLoadingProgressTarget(0.03);
    if (!this._loadingPoweredByTex) {
      this._loadingPoweredByTex = await loadTex('/assets/yoriginal-white.svg');
    }
    let gameLogoTex = Texture.EMPTY;
    for (const p of this._gameLogoAssetPaths()) {
      try {
        gameLogoTex = await Assets.load(p);
        if (gameLogoTex?.width > 0) break;
      } catch {
        /* try next candidate path */
      }
    }
    if (!gameLogoTex?.width) {
      gameLogoTex = await loadTex('/assets/info/logo_mini.png');
    }
    this._setLoadingProgressTarget(0.06);

    const { width: cw, height: ch } = this._readContainerSize();
    const bgTex = this._pickLoadingBackgroundTexture(cw, ch);
    const loadingTransform = this._getLoadingCanvasTransform(cw, ch);

    const root = new Container();
    root.eventMode = 'static';
    root.zIndex = 999999;
    root.scale.set(1, 1);
    root.x = 0;

    const bg = new Sprite(bgTex);
    this._layoutLoadingBackgroundCover(bg, bgTex, cw, ch, loadingTransform);
    if (shouldUseBackdropBlur()) {
      bg.filters = [
        new BlurFilter({
          strength: LOADING_BLUR_STRENGTH,
          strengthX: LOADING_BLUR_STRENGTH,
          strengthY: LOADING_BLUR_STRENGTH,
        }),
      ];
    }
    bg.eventMode = 'none';

    const overlay = new Graphics();
    overlay.eventMode = 'none';

    const content = new Container();
    content.eventMode = 'none';

    const poweredBy = new Sprite(this._loadingPoweredByTex ?? Texture.EMPTY);
    poweredBy.roundPixels = true;
    poweredBy.eventMode = 'none';

    const gameLogo = new Sprite(gameLogoTex);
    gameLogo.roundPixels = true;
    gameLogo.eventMode = 'none';

    const barGlow = new Graphics();
    barGlow.eventMode = 'none';
    if (shouldUseBackdropBlur()) {
      barGlow.filters = [
        new BlurFilter({
          strength: 3,
          strengthX: 3,
          strengthY: 3,
        }),
      ];
    }

    const barTrack = new Graphics();
    barTrack.roundPixels = true;
    barTrack.eventMode = 'none';

    const barFillMask = new Graphics();
    barFillMask.roundPixels = true;
    barFillMask.eventMode = 'none';

    const barFillRoot = new Container();
    barFillRoot.eventMode = 'none';
    barFillRoot.mask = barFillMask;

    const barFillSprite = new Sprite(getLoadingBarGoldFillTexture());
    barFillSprite.roundPixels = true;
    barFillSprite.eventMode = 'none';

    const barFillGloss = new Graphics();
    barFillGloss.roundPixels = true;
    barFillGloss.eventMode = 'none';

    barFillRoot.addChild(barFillSprite, barFillGloss);

    const loadingCaption = this._bindLocalizedText(
      new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: 14,
          fontWeight: '700',
          fill: LOADING_GOLD.core,
          letterSpacing: 4,
          align: 'center',
        },
      }),
      'LOADING_GAME',
      {
        upper: true,
        transform: (value) => String(value).replace(/\.+$/, ''),
      },
    );
    loadingCaption.eventMode = 'none';
    applyLoadingGoldTextStyle(loadingCaption, { large: false });

    const percentText = new Text({
      text: '0%',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 28,
        fontWeight: '700',
        fill: LOADING_GOLD.core,
        letterSpacing: 1,
        align: 'center',
      },
    });
    percentText.eventMode = 'none';
    applyLoadingGoldTextStyle(percentText, { large: true });

    content.addChild(
      gameLogo,
      barGlow,
      barTrack,
      barFillMask,
      barFillRoot,
      loadingCaption,
      percentText,
      poweredBy,
    );
    root.addChild(bg, overlay, content);

    this.app.stage.sortableChildren = true;
    this.app.stage.addChild(root);

    this._loadingRoot = root;
    this._loadingContent = content;
    this._loadingBgSprite = bg;
    this._loadingOverlay = overlay;
    this._loadingPoweredBySprite = poweredBy;
    this._loadingGameLogoSprite = gameLogo;
    this._loadingBarGlow = barGlow;
    this._loadingBarTrack = barTrack;
    this._loadingBarFillMask = barFillMask;
    this._loadingBarFillRoot = barFillRoot;
    this._loadingBarFillSprite = barFillSprite;
    this._loadingBarFillGloss = barFillGloss;
    this._loadingCaptionText = loadingCaption;
    this._loadingPercentText = percentText;
    this._loadingProgress = 0;
    this._loadingProgressTarget = 0;
    this._loadingProgressStartedAt = performance.now();
    this._loadingShownAt = this._loadingProgressStartedAt;
    this._loadingProgressTick = () => this._syncLoadingProgressTick();
    this.app.ticker.add(this._loadingProgressTick);

    this._fitRendererToContainer();
  }

  async _prepareGameRevealFromLoading() {
    if (this._isPortraitMobileViewport()) return;
    this._fitRendererToContainer();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this._fitRendererToContainer();
  }

  async _ensureLoadingMinDisplayElapsed() {
    if (this._loadingShownAt == null) return;
    const deadline = this._loadingShownAt + LOADING_MIN_DISPLAY_MS;
    while (true) {
      const minOk = performance.now() >= deadline;
      const progressOk =
        (this._loadingProgressTarget ?? 0) >= 1 && (this._loadingProgress ?? 0) >= 0.995;
      if (minOk && progressOk) return;
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  }

  _hideLoading() {
    if (this._loadingProgressTick && this.app?.ticker) {
      this.app.ticker.remove(this._loadingProgressTick);
    }
    this._loadingProgressTick = null;
    this._loadingBarShimmerFx?.stop();
    this._loadingBarShimmerFx = null;
    if (this._isPortraitMobileViewport()) {
      this._loadingCanvasUniform = false;
    }
    this._loadingRoot?.destroy({ children: true });
    this._loadingRoot = null;
    this._loadingContent = null;
    this._loadingBgSprite = null;
    this._loadingOverlay = null;
    this._loadingPoweredBySprite = null;
    this._loadingGameLogoSprite = null;
    this._loadingBarGlow = null;
    this._loadingBarTrack = null;
    this._loadingBarFillMask = null;
    this._loadingBarFillRoot = null;
    this._loadingBarFillSprite = null;
    this._loadingBarFillGloss = null;
    this._loadingCaptionText = null;
    this._loadingPercentText = null;
    this._loadingProgress = 0;
    this._loadingProgressTarget = 0;
    this._loadingShownAt = null;
    this._loadingCanvasUniform = true;
    this._fitRendererToContainer();
    if (this._isConnectionModalBlocking()) {
      this._syncConnectionLostModal();
    }
  }

  _hideSettingsPanel() {
    const root = this._settingsRoot;
    const wasOpen = !!(root && root.visible);
    if (root) root.visible = false;
    if (wasOpen) sound.play('uiPopupClose');
  }

  _normalizeMatrix(matrix) {
    if (!Array.isArray(matrix)) return null;
    // MessagePack packed int[,]: [reels, rows, flatRowMajor]
    let cols = matrix;
    if (
      matrix.length === 3 &&
      typeof matrix[0] === 'number' &&
      typeof matrix[1] === 'number' &&
      Array.isArray(matrix[2])
    ) {
      const [reels, rows, flat] = matrix;
      cols = Array.from({ length: reels }, (_, i) =>
        flat.slice(i * rows, (i + 1) * rows),
      );
    }
    if (cols.length !== gameConfig.reelCount) return null;
    const normalized = cols.map((col) => {
      if (!Array.isArray(col) || col.length !== gameConfig.rowCount) return null;
      const values = col.map((symbol) => Number(symbol));
      if (values.some((v) => !Number.isFinite(v))) return null;
      return values;
    });
    if (normalized.some((col) => col == null)) return null;
    return normalized;
  }

  /**
   * Expanding wild fills its whole reel (all rows) so it can substitute on every
   * payline through that column. Server wins are computed after that expand, so a
   * winning line often uses a different row than where the wild landed.
   *
   * Keep expand only when that reel participates in a payline win (reel < count).
   * Do not require the landed wild cell to already sit on that line — one winning
   * cell on the column is enough to expand the entire column. Scatter/coin wins
   * never unlock expansion; orphan wilds with no line win stay unexpanded.
   * @param {number[] | null | undefined} expandingWild
   * @param {unknown} wins
   * @returns {number[]}
   */
  _filterExpandingWildToWinLines(expandingWild, wins) {
    if (!Array.isArray(expandingWild) || !expandingWild.length) {
      return Array.isArray(expandingWild) ? expandingWild : [];
    }
    /** @type {Set<number>} */
    const winLineReels = new Set();
    const coinSymbolId = 10;
    const scatterSymbolId = 11;
    if (Array.isArray(wins)) {
      for (const w of wins) {
        const symbolId = Number(Array.isArray(w) ? w[0] : (w?.symbol ?? w?.Symbol));
        if (symbolId === coinSymbolId || symbolId === scatterSymbolId) continue;
        const count = Math.max(
          0,
          Math.min(
            gameConfig.reelCount,
            Number(Array.isArray(w) ? w[2] : (w?.count ?? w?.Count)) || 0,
          ),
        );
        if (count <= 0) continue;
        for (let reel = 0; reel < count; reel++) {
          winLineReels.add(reel);
        }
      }
    }
    return expandingWild.map((id, reel) => {
      const wildSymbolId = Number(id);
      if (!Number.isFinite(wildSymbolId) || wildSymbolId <= 0) return 0;
      return winLineReels.has(reel) ? wildSymbolId : 0;
    });
  }

  _applyExpandingWild(grid, expandingWild) {
    if (!Array.isArray(grid)) return null;
    if (!Array.isArray(expandingWild) || !expandingWild.length) return grid;
    const next = grid.map((col) => [...col]);
    for (let reel = 0; reel < Math.min(next.length, expandingWild.length); reel++) {
      const wildSymbolId = Number(expandingWild[reel]);
      if (!Number.isFinite(wildSymbolId) || wildSymbolId <= 0) continue;
      for (let row = 0; row < next[reel].length; row++) {
        next[reel][row] = wildSymbolId;
      }
    }
    return next;
  }

  _mapBackendWinsToHits(wins, grid) {
    if (!Array.isArray(wins)) return [];
    const coinSymbolId = 10;
    const scatterSymbolId = 11;
    return wins
      .map((w) => {
        // Positional MessagePack win line: [symbol, line, count, lineOdd, multiplier, lineWinAmount, ...]
        const lineNo = Number(Array.isArray(w) ? w[1] : (w?.line ?? w?.Line));
        if (!Number.isFinite(lineNo)) return null;
        const count = Math.max(
          0,
          Math.min(
            gameConfig.reelCount,
            Number(Array.isArray(w) ? w[2] : (w?.count ?? w?.Count)) || 0,
          ),
        );
        const payout = Number(
          Array.isArray(w) ? w[5] : (w?.lineWinAmount ?? w?.LineWinAmount),
        );
        if (!Number.isFinite(payout)) return null;
        const symbolId = Number(Array.isArray(w) ? w[0] : (w?.symbol ?? w?.Symbol));
        if (!Number.isFinite(symbolId)) return null;
        const isCoinHit = symbolId === coinSymbolId && count >= 3;
        const isScatterHit = symbolId === scatterSymbolId && count >= 3;
        const isSymbolOnlyHit = isCoinHit || isScatterHit;
        let paylineIndex = null;
        let cells = [];

        if (isSymbolOnlyHit) {
          // Coin/scatter wins are symbol-only: animate each matching symbol, no line path.
          cells = this._cellsForSymbolFromGrid(grid, symbolId, count);
        } else {
          paylineIndex = Math.max(0, lineNo - 1);
          const line = gameConfig.paylines[paylineIndex];
          if (!line) return null;
          cells = this._cellsFromBackendSpin({
            grid,
            count,
            lineTemplate: line,
          });
        }

        if (!cells.length) return null;
        return {
          paylineIndex,
          lineNo,
          symbolId,
          count,
          payout,
          cells,
          displayMode: isSymbolOnlyHit ? 'symbols' : 'line',
        };
      })
      .filter(Boolean);
  }

  _cellsFromBackendSpin({ grid, count, lineTemplate }) {
    if (!Array.isArray(grid) || count <= 0) return [];
    const cells = [];
    for (let reel = 0; reel < count; reel++) {
      const col = grid[reel];
      if (!Array.isArray(col)) return [];
      const targetRow = lineTemplate[reel];
      if (!Number.isInteger(targetRow) || targetRow < 0 || targetRow >= col.length) return [];
      cells.push({ reel, row: targetRow });
    }
    return cells;
  }

  _cellsForSymbolFromGrid(grid, symbolId, count) {
    if (!Array.isArray(grid) || count <= 0) return [];
    const cells = [];
    for (let reel = 0; reel < grid.length; reel++) {
      const col = grid[reel];
      if (!Array.isArray(col)) continue;
      for (let row = 0; row < col.length; row++) {
        if (Number(col[row]) === symbolId) {
          cells.push({ reel, row });
          if (cells.length >= count) return cells;
        }
      }
    }
    return cells;
  }

  destroy() {
    if (this._resizeRaf != null) cancelAnimationFrame(this._resizeRaf);
    this._resizeRaf = null;
    if (this._orientTimer) clearTimeout(this._orientTimer);
    this._orientTimer = null;
    if (this._orientRaf != null) cancelAnimationFrame(this._orientRaf);
    this._orientRaf = null;
    if (this._autoplayTimer != null) clearTimeout(this._autoplayTimer);
    this._autoplayTimer = null;
    this._idleReloadDispose?.();
    this._idleReloadDispose = null;
    this._landscapeBlocker?.dispose?.();
    this._landscapeBlocker = null;
    this._hubMonitorDispose?.();
    this._hubMonitorDispose = null;
    this._hideConnectionRestoredToast();
    this._stopConnectionAutoRetry();
    this._clearConnectionLostGraceTimer();
    this._clearDisconnectResolveTimer();
    if (this._idleTimeoutOverlay) {
      this._idleTimeoutOverlay.remove();
      this._idleTimeoutOverlay = null;
    }
    this._stopWinRollup();
    this._stopQbScrollInertia();
    this._stopSpinHoldTracking();
    this._resetSpinButtonHoldVisual();
    this._spinHoldTriggeredTurbo = false;
    this._turboHoldActive = false;
    this._autoplaying = false;
    if (this._roundStatusPageHideFlush) {
      window.removeEventListener('pagehide', this._roundStatusPageHideFlush);
      this._roundStatusPageHideFlush = null;
    }
    window.removeEventListener('resize', this._onResize);
    window.visualViewport?.removeEventListener('resize', this._onResize);
    window.removeEventListener('orientationchange', this._onOrientation);
    document.removeEventListener('fullscreenchange', this._onFullscreenChange);
    window.removeEventListener('keydown', this._onKey);
    this._detachWinCollectWindowPointerGuard();
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._settingsRoot?.destroy({ children: true });
    this._settingsRoot = null;
    this._settingsPanelRoot = null;
    this._settingsModalRefs = null;
    this._autoplayModalLayoutParts = null;
    this._autoplaySettingsLayoutRefs = null;
    this._autoplayScrollContentHeight = 0;
    this._autoplayScrollClamp = null;
    this._infoSettingsLayoutRefs = null;
    this._infoScrollContentHeight = 0;
    this._infoScrollClamp = null;
    this._backdropRoot?.destroy({ children: true });
    this._backdropRoot = null;
    this._betPopupDimRoot?.destroy({ children: true });
    this._betPopupDimRoot = null;
    this._betPopupPanelRoot?.destroy({ children: true });
    this._betPopupPanelRoot = null;
    this._betPopupDim = null;
    this._betPopupChipsArea = null;
    this._betPopupBgSprite = null;
    this._betPopupBgTexDefault = null;
    this._betPopupBgTexMobile = null;
    this._betPopupTitle = null;
    this._betPopupCloseBtn = null;
    this._betPopupPanel = null;
    this._betPopupPanelFill = null;
    this._betPopupRoundMask = null;
    this._betPopupPanelBorder = null;
    this._betPopupTriggerBtn = null;
    this._hideAutoplayPicker();
    this._autoplayPixiDim = null;
    this._betTapZone?.remove();
    this._betTapZone = null;
    this._controlObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this._controlObjectUrls = [];
    this._hideLoading();
    this._connectionLostShowPromise = null;
    this._systemMessagePanel?.dispose();
    this._systemMessagePanel = null;
    this._startScreenPanel?.dispose();
    this._startScreenPanel = null;
    this._startScreenResolve = null;
    this._ambientFx?.destroy();
    this._ambientFx = null;
    this._cabinetJuice?.destroy();
    this._cabinetJuice = null;
    this.animationController.killAll();
    drainSymbolSpinePool();
    this.backend.disconnect().catch(() => {});
    this._unbindGsapFromPixiTicker();
    this.app?.destroy(true, { children: true, texture: false });
  }
}
